// DESIGN_AUDIT Phase 11C: hardcoded colors replaced with CSS vars
// DESIGN_AUDIT Phase 11B: all modals converted to ReactDOM.createPortal
import ReactDOM from 'react-dom';
import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import DarkSelect from '../components/DarkSelect.tsx';
import DatePicker from '../components/DatePicker.tsx';
import '../styles/buttonAnimations.css';
import { spinIconOut } from '../utils/iconAnimations.ts';
import {
  Plus, Search, Package, AlertTriangle, Edit, Eye, ShoppingCart, Trash2,
  Camera, X, ExternalLink, ChevronDown, ChevronRight, Upload, ImageOff,
  Sparkles, Wrench, Zap, Droplets, Wind, FileText, Heart, ShieldCheck,
  UtensilsCrossed, Building2, Scan, Paintbrush, Accessibility, Gauge,
  Thermometer, List, Check, RefreshCw, FolderOpen, Truck, Layers,
  FlaskConical, Scissors, FolderPlus, AlertCircle, Clock, CheckCircle, Network,
  Download, Pencil, QrCode, ArrowUp, ArrowLeft, Bed, BedDouble, ArrowUpFromLine, Key, Home, Flame, LayoutGrid, MapPin, Map, BookOpen,
  LogIn, LogOut, Link2, Calendar, MessageCircle, Send, Copy, RotateCw,
  Image as ImageIcon,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import {
  FaFire, FaFireExtinguisher, FaBell, FaTriangleExclamation, FaShieldHalved, FaTruck,
  FaFireFlameSimple, FaHelmetSafety, FaRadiation, FaHandshake,
  FaWrench, FaScrewdriverWrench, FaGears, FaGear, FaTemperatureHalf, FaTemperatureFull, FaWind, FaFan,
  FaIndustry, FaOilCan, FaMotorcycle,
  FaBolt, FaPlug, FaBatteryFull, FaLightbulb,
  FaSolarPanel, FaChargingStation, FaNetworkWired, FaMicrochip,
  FaDroplet, FaWater, FaShower, FaFaucet,
  FaToilet, FaSink,
  FaBuilding, FaHouse, FaDoorOpen, FaDoorClosed, FaWarehouse, FaHospital, FaHotel,
  FaCity, FaSchool, FaChurch, FaStore, FaLandmark,
  FaBroom, FaRecycle, FaTrash, FaTrashCan,
  FaSoap, FaPumpSoap, FaSprayCan,
  FaHeart, FaHeartPulse, FaPills, FaSyringe, FaStethoscope, FaKitMedical, FaBandage,
  FaHospitalUser, FaUserNurse, FaWheelchair,
  FaUtensils, FaMugHot, FaKitchenSet, FaBlender,
  FaPizzaSlice, FaBreadSlice, FaCarrot,
  FaLock, FaLockOpen, FaKey, FaCamera, FaEye, FaUserShield,
  FaUserSecret, FaFingerprint, FaIdCard, FaPassport,
  FaClipboard, FaFolder, FaFolderOpen, FaFileLines, FaChartBar, FaPenToSquare, FaThumbtack, FaPaperclip,
  FaMoneyBill, FaClockRotateLeft, FaDiagramProject, FaListCheck,
  FaToolbox, FaHammer, FaRuler, FaCompass, FaPaintRoller, FaBucket,
  FaRulerCombined, FaVial, FaFlask, FaMagnet,
  FaCar, FaTruckMoving, FaBus, FaBicycle, FaHelicopter,
  FaTaxi, FaShip, FaPlane,
  FaBox, FaBoxOpen, FaDatabase, FaServer, FaLaptop, FaPrint, FaWifi, FaSatelliteDish,
  FaFileInvoice, FaReceipt,
} from 'react-icons/fa6';
import {
  MdFireExtinguisher, MdFireTruck, MdNotificationsActive, MdWarning, MdSecurity, MdLocalPolice,
  MdLocalFireDepartment, MdEmergency,
  MdBuild, MdSettings, MdThermostat, MdAir, MdHvac, MdConstruction, MdHandyman, MdPlumbing,
  MdCable, MdOutlet, MdSolarPower,
  MdElectricBolt, MdPower, MdBatteryFull, MdLightbulb, MdFlashOn, MdElectricalServices,
  MdWater, MdWaterDrop, MdShower, MdBathtub,
  MdPool, MdInvertColors,
  MdApartment, MdDomain, MdHome, MdHotel, MdLocalHospital, MdWarehouse, MdMeetingRoom,
  MdSchool, MdStore, MdVilla,
  MdCleaningServices, MdDelete, MdRecycling, MdAutoAwesome, MdSpa,
  MdLocalLaundryService, MdDryCleaning,
  MdMedicalServices, MdVaccines, MdMonitorHeart, MdMedication, MdHealthAndSafety,
  MdBiotech, MdBloodtype,
  MdRestaurant, MdKitchen, MdCoffeeMaker, MdMicrowave, MdOutdoorGrill, MdDinnerDining,
  MdLunchDining, MdRoomService,
  MdLock, MdLockOpen, MdKey, MdCamera, MdVideocam, MdSecurityUpdateGood, MdVerifiedUser,
  MdFingerprint, MdFace,
  MdAssignment, MdFolder, MdFolderOpen, MdInsertDriveFile, MdBarChart, MdEdit, MdAttachFile, MdBookmark,
  MdDashboard, MdEventNote,
  MdBuildCircle, MdHomeRepairService, MdPrecisionManufacturing, MdEngineering, MdFormatPaint,
  MdAgriculture, MdCarpenter,
  MdDirectionsCar, MdLocalShipping, MdDirectionsBus, MdPedalBike, MdAirportShuttle,
  MdFlight, MdTrain,
  MdInventory, MdInventory2, MdStorefront, MdStorage, MdComputer, MdPrint, MdWifi, MdRouter,
  MdPhoneAndroid, MdQrCode2,
} from 'react-icons/md';
import {
  BsExclamationTriangle, BsExclamationCircle, BsBellFill, BsShieldFill, BsShieldCheck,
  BsAlarmFill, BsAlarm,
  BsWrench, BsWrenchAdjustable, BsGear, BsGearFill, BsThermometer, BsThermometerHigh, BsFan, BsWind,
  BsSnow, BsCloudRain,
  BsLightningFill, BsPlugFill, BsBatteryFull, BsLightbulbFill, BsLightbulb, BsToggleOn,
  BsDropletFill, BsDroplet, BsWater,
  BsBuilding, BsBuildingFill, BsHouseFill, BsHouse, BsDoorOpenFill, BsDoorClosedFill, BsHospitalFill, BsBank,
  BsBuildings, BsBuildingsFill,
  BsTrashFill, BsTrash, BsRecycle, BsBrush, BsBrushFill,
  BsTrash2, BsTrash2Fill, BsStars,
  BsHeartFill, BsHeart, BsHeartPulse, BsCapsulePill, BsBandaid, BsBandaidFill, BsActivity,
  BsEmojiSmile,
  BsCupHotFill, BsCupHot, BsFire, BsUpc,
  BsLockFill, BsLock, BsKeyFill, BsKey, BsCameraFill, BsCamera, BsEyeFill, BsShieldLock,
  BsFingerprint, BsPersonBadge, BsPersonBadgeFill,
  BsClipboardFill, BsClipboard, BsFolderFill, BsFolder, BsFileText, BsBarChart, BsPenFill, BsPaperclip, BsBookmark,
  BsCalendar, BsCalendarFill, BsJournalText, BsSticky, BsStickyFill,
  BsTools, BsHammer, BsPaintBucket, BsBucket, BsRulers,
  BsUpcScan,
  BsCarFront, BsTruck, BsBusFront, BsBicycle, BsAirplane,
  BsAirplaneFill,
  BsBoxFill, BsBox, BsArchiveFill, BsArchive, BsDatabaseFill, BsDatabase, BsLaptop, BsPrinter, BsWifi, BsRouter,
  BsHddFill, BsHdd, BsGlobe, BsGlobe2,
} from 'react-icons/bs';
import apiClient, { api } from '../api/client.ts';
import { useFacility } from '../contexts/FacilityContext.tsx';
import { useDropdownOptions, invalidateDropdowns } from '../hooks/useDropdownOptions.ts';
import { useOrderSuppliers } from '../hooks/useOrderSuppliers.ts';
import { PdfViewerButton } from '../components/PdfViewer.tsx';
import { NotesThread, type NoteEntry } from '../components/NotesThread.tsx';
import { BomSelector } from '../components/BomSelector.tsx';
import { FloatingActionButton } from '../components/FloatingActionButton.tsx';
import { KeyGlyph, getKeyGlyphTint } from '../components/KeyGlyph.tsx';
import { CategoryManager } from '../components/inventory/category-manager/CategoryManager.tsx';
import { useCategoryColorMap, resolveCategoryColor } from '../components/inventory/category-manager/useCategoryColors.ts';
import { BomPanel } from './BOMView.tsx';
import { EntryFormModal, EntryDetailModal, type EntryEquipmentScope, type LearningHubEntry } from './LearningHub.tsx';
import { InventoryItem, Category, FacilitySupply } from '../types/index.ts';
import { useAuth } from '../contexts/AuthContext';
import { getImageSrc } from '../utils/imageUrl.ts';
import { KNOWN_SUPPLIERS } from '../constants/suppliers.ts';
import { downloadExport as triggerExport } from '../utils/exportHelper.ts';
import PaintUsedInPanel from '../components/PaintUsedInPanel';
import FacilityMap from './FacilityMap';
import LogbookTemplates from './LogbookTemplates';
import Toast, { useToast } from '../components/Toast';

const USER_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#14b8a6',
  '#6366f1','#a855f7','#e11d48','#0ea5e9','#22c55e',
  '#eab308','#f43f5e','#7c3aed','#0891b2','#65a30d',
];
const getUserColor = (userId: number) => USER_COLORS[userId % USER_COLORS.length];
const deptBadge = (color: string, _name: string) => ({
  fontSize: 10, padding: '1px 6px', borderRadius: 20,
  background: color + '22', border: `1px solid ${color}66`, color,
  whiteSpace: 'nowrap' as const, display: 'inline-block',
});
function avatarInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ─── Local types ─────────────────────────────────────────────────────────────
interface CategoryChild {
  id: number;
  name: string;
  icon_path: string | null;
  item_count: number;
}

// ─── Category icon map (match by lowercase name fragment) ────────────────────
function getCategoryIcon(name: string) {
  const n = name.toLowerCase();
  if (n.includes('clean') || n.includes('janitor') || n.includes('hygiene')) return <Sparkles size={16} />;
  if (n.includes('medical') || n.includes('nurs') || n.includes('health')) return <Heart size={16} />;
  if (n.includes('office') || n.includes('admin')) return <FileText size={16} />;
  if (n.includes('kitchen') || n.includes('food') || n.includes('diet')) return <UtensilsCrossed size={16} />;
  if (n.includes('electric') || n.includes('lighting')) return <Zap size={16} />;
  if (n.includes('plumb') || n.includes('water')) return <Droplets size={16} />;
  if (n.includes('hvac') || n.includes('heat') || n.includes('cool') || n.includes('ventil')) return <Wind size={16} />;
  if (n.includes('safety') || n.includes('fire') || n.includes('security')) return <ShieldCheck size={16} />;
  if (n.includes('maint') || n.includes('repair') || n.includes('tool')) return <Wrench size={16} />;
  if (n.includes('paint')) return <Paintbrush size={16} />;
  if (n.includes('laundry') || n.includes('linen')) return <RefreshCw size={16} />;
  return <Package size={16} />;
}

// Deterministic pastel color from category id
export const CATEGORY_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#ec4899','#14b8a6','#f97316','#6366f1','#84cc16',
];
export function getCategoryColor(id: number) { return CATEGORY_COLORS[id % CATEGORY_COLORS.length]; }

// ─── Blank form state ────────────────────────────────────────────────────────
function blankItem(): Partial<InventoryItem> {
  return {
    name: '', part_number: '', price: 0, product_link: '', supplier: '', brand: '',
    product_description: '', used_for: [], department: '', notes: '',
    barcode: '', quantity: 0, unit: 'ea', sold_in: 1, reorder_point: undefined,
    location: '', status: 'Active', category_id: 0, sourcing_status: 'sourced',
  };
}

function blankSupply(type: string): Partial<FacilitySupply> {
  return {
    supply_type: type as FacilitySupply['supply_type'],
    name: '', brand: '', model: '', serial_number: '', location: '',
    quantity: 1, condition: 'good', color: '', finish: '', room_area: '', notes: '',
    category: '',
  };
}

const SUPPLY_TYPES = [
  { id: 'paint', label: 'Paint', icon: <Paintbrush size={15} /> },
  { id: 'bed', label: 'Beds', icon: <BedDouble size={15} /> },
  { id: 'concentrators', label: 'Concentrators', icon: <Wind size={15} /> },
  { id: 'wheelchair', label: 'Wheelchairs', icon: <Accessibility size={15} /> },
  { id: 'lift', label: 'Lifts', icon: <ArrowUpFromLine size={15} /> },
  { id: 'hvac', label: 'HVAC', icon: <Thermometer size={15} /> },
  { id: 'extinguishers', label: 'Extinguishers', icon: <Flame size={15} /> },
  { id: 'other', label: 'Other', icon: <Package size={15} /> },
];

type BomEntry = { id: number; type: string; displayName: string; brand: string | null; model: string | null; facilitySupplyType: string | null };
const DEPT_OPTIONS = ['Administration', 'Maintenance', 'Nursing', 'Dietary', 'Activities', 'Housekeeping', 'Social Services'];
const UNIT_OPTIONS = ['ea', 'pack', 'case', 'box', 'bag', 'roll', 'gallon', 'qt', 'oz', 'lb', 'ft', 'yd', 'pair', 'set', 'dozen'];
const CONDITION_OPTIONS = ['excellent', 'good', 'fair', 'poor'];
const FINISH_OPTIONS = ['Matte', 'Eggshell', 'Satin', 'Semi-Gloss', 'Gloss'];

// ─── Supply type colors / icons ──────────────────────────────────────────────
const SUPPLY_TYPE_COLORS: Record<string, string> = {
  paint: '#ec4899', bed: '#3b82f6', concentrators: '#8b5cf6',
  wheelchair: '#10b981', lift: '#f59e0b', hvac: '#6366f1', other: '#94a3b8',
};
function getSupplyTypeColor(type: string): string { return SUPPLY_TYPE_COLORS[type] ?? '#94a3b8'; }
function getSupplyTypeIcon(type: string, size = 40): React.ReactElement {
  const map: Record<string, React.ReactElement> = {
    paint: <Paintbrush size={size} />, bed: <Building2 size={size} />,
    wheelchair: <Accessibility size={size} />, lift: <Gauge size={size} />,
    hvac: <Thermometer size={size} />, other: <List size={size} />,
  };
  return map[type] ?? <Package size={size} />;
}


// ─── Camera Scanner Modal ────────────────────────────────────────────────────
function CameraScannerModal({ onDetect, onClose }: { onDetect: (value: string) => void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<'loading' | 'scanning' | 'error'>('loading');
  const [error, setError] = useState('');
  const streamRef = useRef<MediaStream | null>(null);
  const readerRef = useRef<{ reset?: () => void }>({});
  const mouseDownTarget = useRef<EventTarget | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    try { readerRef.current.reset?.(); } catch { /* ignore */ }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    let active = true;
    let streamCheckInterval: ReturnType<typeof setInterval> | null = null;
    async function startScan() {
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        const { DecodeHintType } = await import('@zxing/library');
        // TRY_HARDER makes zxing's 1D readers (UPC/EAN/Code128/etc.) retry a failed decode
        // against a 90°-rotated copy of the frame -- without it, a barcode that ends up
        // sideways in the camera frame is never even attempted at that angle (task #61).
        //
        // IMPORTANT: this file's lucide-react import list includes `Map` (the map/atlas icon,
        // used for the Facility Map nav item), which shadows the global Map constructor for
        // this entire file. A bare `new Map()` here would try to construct that icon component
        // instead of a real Map, throwing "TypeError: ... is not a constructor" -- this is the
        // confirmed (not guessed) root cause of a crash hit earlier this week. Always use
        // globalThis.Map explicitly here; do not let this get "simplified" back to `new Map()`.
        const hints = new globalThis.Map();
        hints.set(DecodeHintType.TRY_HARDER, true);
        const codeReader = new BrowserMultiFormatReader(hints);
        readerRef.current.reset = () => {
          try { (codeReader as unknown as { reset(): void }).reset(); } catch { /* ignore */ }
          try { BrowserMultiFormatReader.releaseAllStreams(); } catch { /* ignore */ }
        };
        if (!active || !videoRef.current) return;
        setStatus('scanning');
        codeReader.decodeFromVideoDevice(undefined, videoRef.current, (result) => {
          if (!active) return;
          if (result) {
            active = false;
            stopCamera();
            onDetect(result.getText());
          }
        });
        // Poll until the video srcObject is available, then store it for reliable cleanup
        streamCheckInterval = setInterval(() => {
          if (videoRef.current?.srcObject instanceof MediaStream) {
            streamRef.current = videoRef.current.srcObject;
            if (streamCheckInterval) { clearInterval(streamCheckInterval); streamCheckInterval = null; }
          }
        }, 100);
      } catch {
        if (active) { setStatus('error'); setError('Camera access denied or not available.'); }
      }
    }
    startScan();
    return () => {
      active = false;
      if (streamCheckInterval) clearInterval(streamCheckInterval);
      stopCamera();
    };
  }, [onDetect, stopCamera]);

  function handleClose() {
    stopCamera();
    onClose();
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) handleClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3 className="modal-title"><Camera size={18} style={{ marginRight: 8 }} />Scan Barcode</h3>
          <button className="modal-close" onClick={handleClose}><X size={18} /></button>
        </div>
        <div style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#000', aspectRatio: '4/3' }}>
          <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          {status === 'loading' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' }}>
              <div className="loading-spinner" />
            </div>
          )}
          {status === 'scanning' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: '70%', height: 3, background: 'rgba(59,130,246,0.8)', boxShadow: '0 0 12px rgba(59,130,246,0.8)', animation: 'scanLine 2s ease-in-out infinite alternate' }} />
            </div>
          )}
          {status === 'error' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.8)', color: '#fff', gap: 8, textAlign: 'center', padding: 24 }}>
              <Camera size={32} style={{ opacity: 0.5 }} />
              <p style={{ margin: 0 }}>{error}</p>
            </div>
          )}
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', marginTop: 12 }}>
          Point camera at barcode — detected automatically
        </p>
      </div>
      <style>{`@keyframes scanLine { from { transform: translateY(-40px); } to { transform: translateY(40px); } }`}</style>
    </div>,
    document.body
  );
}

// ─── Add to Order mini-modal ─────────────────────────────────────────────────

export function AddToOrderModal({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Load supplier list from settings (shared with Orders.tsx's ItemModal — same cache entry)
  const { supplierList, isLoaded: supplierLoaded } = useOrderSuppliers();

  const [qty, setQty] = useState(1);
  const [unitPrice, setUnitPrice] = useState<string>(Number(item.price) > 0 ? String(Number(item.price).toFixed(2)) : '');
  // Pre-fill orderedFrom from item.supplier; fall back to settings list once loaded
  const [orderedFrom, setOrderedFrom] = useState(item.supplier || supplierList[0] || 'DSSI');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  // Once settings load, only override orderedFrom if item had no supplier
  const [fromInit, setFromInit] = useState(false);
  if (supplierLoaded && !fromInit) {
    if (!item.supplier) setOrderedFrom(supplierList[0] ?? 'DSSI');
    setFromInit(true);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setOrderError(null);
    setSaving(true);
    try {
      const res = await api.post<import('../types/index.ts').Order>('/orders', {
        inventory_item_id: item.id,
        product_name: item.name,
        part_number: item.part_number,
        product_link: item.product_link,
        category: item.category_name,
        quantity: qty,
        unit: item.unit ?? 'ea',
        unit_price: unitPrice ? parseFloat(unitPrice) : null,
        ordered_from: orderedFrom || 'TBD',
        po_number: poNumber || null,
        status: 'pending',
        notes,
      });
      const newOrder = (res as unknown as { data: import('../types/index.ts').Order }).data;
      // Notify Orders page (even if not currently mounted) via custom event
      if (newOrder) {
        window.dispatchEvent(new CustomEvent('orderCreated', { detail: newOrder }));
      }
      // Also invalidate for when Orders.tsx is already mounted
      qc.invalidateQueries({ queryKey: ['orders'], refetchType: 'all' });
      setToast(true);
      setTimeout(() => { setToast(false); onClose(); }, 1500);
    } catch (err) {
      setOrderError(err instanceof Error ? err.message : 'Failed to create order. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // Placeholder (not-yet-sourced) parts have no real supplier/price -- never let an order be
  // placed against one. Swap the whole form out for a redirect-to-promotion message instead.
  if (item.sourcing_status === 'placeholder') {
    return ReactDOM.createPortal(
      <div className="modal-overlay"
        onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
        onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
          <div className="modal-header">
            <h3 className="modal-title"><ShoppingCart size={16} style={{ marginRight: 6 }} />Add to Order</h3>
            <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
          <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: 'var(--accent-warning)', fontSize: 13, marginBottom: 16 }}>
            ⚠ <strong>{item.name}</strong> hasn't been sourced yet — no supplier or pricing on file, so it can't be ordered. Edit it and use "Enter into Inventory" once you've found a supplier.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => { onClose(); navigate(`/inventory/item/${item.id}`); }}
            >
              Edit Item
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        {toast ? (
          <div style={{ textAlign: 'center', padding: '28px 0' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
              <Check size={24} color="var(--accent-success)" />
            </div>
            <h3 style={{ margin: '0 0 6px' }}>Added to orders</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{item.name}</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="modal-header">
              <h3 className="modal-title"><ShoppingCart size={16} style={{ marginRight: 6 }} />Add to Order</h3>
              <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
            </div>
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}>
              <div style={{ fontWeight: 600 }}>{item.name}</div>
              {item.part_number && <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontFamily: 'monospace' }}>#{item.part_number}</div>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Quantity</label>
                <input className="form-input" type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Unit Price</label>
                <input className="form-input" type="number" min={0} step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">PO # <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                <input className="form-input" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="e.g. PO-1234" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Order From</label>
                <select className="form-select" value={orderedFrom} onChange={(e) => setOrderedFrom(e.target.value)}>
                  {supplierList.map((s) => <option key={s} value={s}>{s}</option>)}
                  {orderedFrom && !supplierList.includes(orderedFrom) && <option value={orderedFrom}>{orderedFrom}</option>}
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ minHeight: 56 }} />
              </div>
            </div>
            {orderError && (
              <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 6, color: 'var(--accent-danger)', fontSize: '0.8125rem' }}>
                {orderError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Creating…' : 'Create Order'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── Item Card ────────────────────────────────────────────────────────────────
// ─── SubfolderCard ────────────────────────────────────────────────────────────
function SubfolderCard({ cat, onClick }: { cat: CategoryChild; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'var(--bg-elevated)' : 'var(--bg-surface)',
        border: `1px solid ${hovered ? 'var(--border-default)' : 'var(--border-subtle)'}`,
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color var(--transition), box-shadow var(--transition), background var(--transition)',
        boxShadow: hovered ? 'var(--shadow-md)' : undefined,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{
        height: 150,
        background: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0.05))',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 8, position: 'relative',
      }}>
        <CategoryIcon iconName={cat.icon_path} size={40} color="var(--accent-primary)" />
        {cat.item_count > 0 && (
          <span className="badge badge-purple" style={{ position: 'absolute', top: 8, right: 8 }}>
            {cat.item_count}
          </span>
        )}
      </div>
      <div style={{ padding: '12px 14px', flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.3 }}>{cat.name}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {cat.item_count} item{cat.item_count !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
}

// ─── ItemCard ─────────────────────────────────────────────────────────────────
function ItemCard({
  item,
  onEdit,
  onView,
  onOrder,
  onDelete,
  isSelected,
  onSelect,
  categoryPath,
  categoryColor,
}: {
  item: InventoryItem;
  onEdit: () => void;
  onView: () => void;
  onOrder: () => void;
  onDelete: () => void;
  isSelected?: boolean;
  onSelect?: () => void;
  // Item 3c: full folder breadcrumb, passed only while a cross-folder search is active (the
  // catalogue tab's getCategoryFullPath) -- replaces the plain category_name line below with the
  // full path so a result from outside the folder you were standing in is still clearly
  // identifiable, not just correctly returned.
  categoryPath?: string;
  // Categories B, Part 2: resolved by the caller (saved color override, else the sidebar's own
  // positional slot) via resolveCategoryColor/useCategoryColorMap -- replaces this component's
  // own getCategoryColor(item.category_id) id%10 hash, which disagreed with the sidebar.
  categoryColor: string;
}) {
  const [hovered, setHovered] = useState(false);
  const isLowStock = item.reorder_point != null && Number(item.quantity) <= Number(item.reorder_point);

  return (
    <div
      className="card" // DS: ITEM_CARD use .card base class
      style={{
        // background intentionally omitted (undefined) so .card's glass surface applies, EXCEPT
        // for placeholder (not-yet-sourced) items, which get the same light persistent gray tint
        // used for placeholder rows in BOMView's "Linked via Used For" list -- consistency across
        // the two places a placeholder item is rendered as a card/row.
        background: item.sourcing_status === 'placeholder' ? 'rgba(107,114,128,0.06)' : undefined,
        border: `1px solid ${isSelected ? 'var(--accent-primary)' : hovered ? 'rgba(255,255,255,0.18)' : 'var(--border-subtle)'}`, // DS: ITEM_CARD hover border rgba(255,255,255,0.18)
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s', // DS: ITEM_CARD transition border+shadow only
        boxShadow: isSelected ? '0 0 0 2px rgba(59,130,246,0.15)' : hovered ? '0 4px 16px rgba(0,0,0,0.25)' : undefined, // DS: ITEM_CARD hover shadow 0 4px 16px rgba(0,0,0,0.25)
        display: 'flex',
        flexDirection: 'column',
        padding: 0, // DS: ITEM_CARD override .card default padding so image flush
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onView}
    >
      {/* Image / placeholder */}
      <div style={{
        position: 'relative',
        height: 150,
        background: `linear-gradient(135deg, ${categoryColor}18, ${categoryColor}08)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {/* Checkbox — shown on hover or when selected */}
        {onSelect && (
          <div
            style={{ position: 'absolute', top: 8, left: 8, zIndex: 3, opacity: isSelected || hovered ? 1 : 0, transition: 'opacity 0.15s' }}
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
          >
            <input
              type="checkbox"
              checked={isSelected ?? false}
              onChange={onSelect}
              onClick={(e) => e.stopPropagation()}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent-primary)' }}
            />
          </div>
        )}

        {getImageSrc(item.thumbnail_path) ? (
          <img
            src={getImageSrc(item.thumbnail_path)!}
            alt={item.name}
            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 12 }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <Package size={40} color={categoryColor} style={{ opacity: 0.35 }} />
        )}

        {/* Low stock badge */}
        {isLowStock && (
          <span className="badge badge-amber" style={{ position: 'absolute', bottom: 8, left: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
            <AlertTriangle size={10} /> Low Stock
          </span>
        )}

        {/* Hover action overlay */}
        {hovered && (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.55)',
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: 1,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {[
              { icon: <Edit size={18} />, label: 'Edit', action: onEdit, color: 'var(--accent-primary)' },
              { icon: <Eye size={18} />, label: 'View', action: onView, color: 'var(--accent-success)' },
              { icon: <ShoppingCart size={18} />, label: 'Order', action: onOrder, color: 'var(--accent-warning)' },
              { icon: <Trash2 size={18} />, label: 'Delete', action: onDelete, color: 'var(--accent-danger)' },
            ].map(({ icon, label, action, color }) => (
              <button
                key={label}
                onClick={(e) => { e.stopPropagation(); action(); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 4, background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer',
                  color, padding: 8, transition: 'background var(--transition)',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.16)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)'; }}
              >
                {icon}
                <span style={{ fontSize: '0.75rem', color: '#fff', opacity: 0.85 }}>{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div
          title={item.name}
          style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} // DS: ITEM_CARD name font-size 13 weight 500 clamp 2
        >
          {item.name}
        </div>
        {categoryPath ? (
          <div style={{ fontSize: '0.72rem', color: categoryColor, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 3, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={categoryPath}>
            <FolderOpen size={10} style={{ flexShrink: 0 }} /> {categoryPath}
          </div>
        ) : item.category_name && (
          <div style={{ fontSize: '0.75rem', color: categoryColor, fontWeight: 500 }}>{item.category_name}</div>
        )}
        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{/* DS: ITEM_CARD qty font-size 12 text-muted */}
            {Number(item.quantity)} {item.unit}
          </span>
          {Number(item.price) > 0 && (
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginTop: 4 }}>{/* DS: ITEM_CARD price font-size 12 weight 500 text-muted */}
              ${Number(item.price).toFixed(2)}
            </span>
          )}
        </div>
        {item.part_number && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
            #{item.part_number}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Supplier combobox (known list + free-text fallback) ─────────────────────
function SupplierField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => { setInputVal(value); }, [value]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))
      ) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
    setOpen(true);
  }

  const filtered = KNOWN_SUPPLIERS.filter((s) => s.toLowerCase().includes(inputVal.toLowerCase()));

  return (
    <div ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={inputVal}
        onChange={(e) => { setInputVal(e.target.value); onChange(e.target.value); }}
        onFocus={openDropdown}
        placeholder="Select or type supplier name..."
        style={{
          width: '100%',
          boxSizing: 'border-box' as const,
          padding: '6px 10px',
          fontSize: 12,
          height: 34,
          background: 'var(--bg-elevated)',
          border: '1px solid rgba(255,255,255,0.13)',
          borderRadius: 6,
          color: 'var(--text-primary)',
          outline: 'none',
        }}
      />
      {open && filtered.length > 0 && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 10001,
            background: 'var(--bg-elevated)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 6,
            color: 'var(--text-primary)',
            maxHeight: 220,
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {filtered.map((s) => (
            <div
              key={s}
              onMouseDown={(e) => { e.preventDefault(); setInputVal(s); onChange(s); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', background: 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {s}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Bulk Order Form (used inside BulkOrder modal) ───────────────────────────
function BulkOrderForm({
  items: selectedItems,
  onClose,
  onDone,
  defaultOrderFrom,
}: {
  items: InventoryItem[];
  onClose: () => void;
  onDone: () => void;
  defaultOrderFrom: string;
  defaultPoNumber: string;
}) {
  const qc = useQueryClient();
  const [orderFrom, setOrderFrom] = useState(defaultOrderFrom);
  const [poNumber, setPoNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      for (const item of selectedItems) {
        await api.post('/orders', {
          inventory_item_id: item.id,
          product_name: item.name,
          part_number: item.part_number,
          product_link: item.product_link,
          category: item.category_name,
          quantity: 1,
          unit: item.unit ?? 'ea',
          unit_price: item.price ? Number(item.price) : null,
          ordered_from: orderFrom || 'TBD',
          po_number: poNumber || null,
          status: 'pending',
        });
      }
      qc.invalidateQueries({ queryKey: ['orders'], refetchType: 'all' });
      window.dispatchEvent(new CustomEvent('ordersCreated'));
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create orders. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ padding: '0 0 4px' }}>
      <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {selectedItems.map((item) => (
          <div key={item.id} style={{ fontSize: '0.8125rem', padding: '4px 0', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 8 }}>
            <span style={{ flex: 1 }}>{item.name}</span>
            {item.part_number && <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>#{item.part_number}</span>}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div className="form-group">
          <label className="form-label">Order From</label>
          <SupplierField value={orderFrom} onChange={setOrderFrom} />
        </div>
        <div className="form-group">
          <label className="form-label">PO # <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
          <input className="form-input" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="PO-1234" />
        </div>
      </div>
      {error && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 6, color: 'var(--accent-danger)', fontSize: '0.8125rem' }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? `Creating ${selectedItems.length} orders…` : `Create ${selectedItems.length} Order${selectedItems.length > 1 ? 's' : ''}`}
        </button>
      </div>
    </form>
  );
}

// ─── Rich Text Editor (shared) ───────────────────────────────────────────────
export const _mdToHtml = (md: string): string => {
  if (!md) return '';
  return md
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/~~(.+?)~~/g, '<s>$1</s>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');
};

const _htmlToMd = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
    .replace(/<b>(.*?)<\/b>/gi, '**$1**')
    .replace(/<em>(.*?)<\/em>/gi, '*$1*')
    .replace(/<i>(.*?)<\/i>/gi, '*$1*')
    .replace(/<s>(.*?)<\/s>/gi, '~~$1~~')
    .replace(/<strike>(.*?)<\/strike>/gi, '~~$1~~')
    .replace(/<ul>([\s\S]*?)<\/ul>/gi, (_, inner) =>
      inner.replace(/<li>(.*?)<\/li>/gi, '- $1\n')
    )
    .replace(/<ol>([\s\S]*?)<\/ol>/gi, (_, inner) => {
      let i = 1;
      return inner.replace(/<li>(.*?)<\/li>/gi,
        (_: string, c: string) => `${i++}. ${c}\n`);
    })
    .replace(/<li>(.*?)<\/li>/gi, '- $1\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<p>(.*?)<\/p>/gi, '$1\n\n')
    .replace(/<h[1-3]>(.*?)<\/h[1-3]>/gi, '$1\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
};

interface RichTextEditorProps {
  value: string;
  onChange: (md: string) => void;
  placeholder?: string;
  rows?: number;
  style?: React.CSSProperties;
}

const INV_TOOLBAR_BUTTONS = [
  { cmd: 'bold',                icon: 'B',   title: 'Bold (Ctrl+B)',        style: { fontWeight: 700 } as React.CSSProperties },
  { cmd: 'italic',              icon: 'I',   title: 'Italic (Ctrl+I)',      style: { fontStyle: 'italic' } as React.CSSProperties },
  { cmd: 'strikeThrough',       icon: 'S̶',  title: 'Strikethrough',        style: { textDecoration: 'line-through' } as React.CSSProperties },
  { type: 'divider' } as const,
  { cmd: 'insertUnorderedList', icon: '• —', title: 'Bullet list',          style: {} as React.CSSProperties },
  { cmd: 'insertOrderedList',   icon: '1.',  title: 'Numbered list',        style: {} as React.CSSProperties },
  { type: 'divider' } as const,
  { cmd: 'removeFormat',        icon: 'Tx',  title: 'Clear formatting',     style: {} as React.CSSProperties },
];

function RichTextEditor({ value, onChange, placeholder, rows = 3, style }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!editorRef.current || isInternalChange.current) return;
    const newHtml = _mdToHtml(value);
    if (editorRef.current.innerHTML !== newHtml) {
      editorRef.current.innerHTML = newHtml;
    }
  }, [value]);

  const handleInput = () => {
    if (!editorRef.current) return;
    isInternalChange.current = true;
    onChange(_htmlToMd(editorRef.current.innerHTML));
    setTimeout(() => { isInternalChange.current = false; }, 0);
  };

  const execCmd = (cmd: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false);
    handleInput();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b': e.preventDefault(); execCmd('bold'); break;
        case 'i': e.preventDefault(); execCmd('italic'); break;
        case 'x': if (e.shiftKey) { e.preventDefault(); execCmd('strikeThrough'); } break;
      }
    }
  };

  const minHeight = `${rows * 24}px`;

  return (
    <div style={{
      border: `1px solid ${isFocused ? 'var(--accent-primary)' : 'var(--border-default)'}`,
      borderRadius: 6,
      background: 'var(--bg-elevated)',
      overflow: 'hidden',
      transition: 'border-color 0.15s',
      ...style,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 2,
        padding: '4px 6px',
        borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
        flexWrap: 'wrap',
      }}>
        {INV_TOOLBAR_BUTTONS.map((btn, i) => {
          if ('type' in btn && btn.type === 'divider') return (
            <div key={i} style={{ width: 1, height: 16, background: 'var(--border-default)', margin: '0 3px' }} />
          );
          const b = btn as Exclude<typeof INV_TOOLBAR_BUTTONS[number], { type: 'divider' }>;
          return (
            <button key={i}
              type="button"
              title={b.title}
              onMouseDown={e => { e.preventDefault(); execCmd(b.cmd!); }}
              style={{
                ...b.style,
                padding: '2px 7px', fontSize: 12,
                border: 'none', borderRadius: 4,
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer', lineHeight: '20px',
                minWidth: 28, textAlign: 'center',
              }}
              onMouseEnter={e => { (e.target as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { (e.target as HTMLElement).style.background = 'transparent'; }}
            >
              {b.icon}
            </button>
          );
        })}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-placeholder={placeholder ?? 'Write here...'}
        style={{
          minHeight,
          padding: '8px 10px',
          fontSize: 13,
          color: 'var(--text-primary)',
          outline: 'none',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          overflowY: 'auto',
          maxHeight: `${rows * 48}px`,
        }}
      />
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: var(--text-muted);
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}

// ─── Add/Edit Item Modal ──────────────────────────────────────────────────────
export function ItemModal({
  initial,
  categories,
  onClose,
  onSaved,
  onViewItem,
}: {
  initial: Partial<InventoryItem>;
  categories: Category[];
  onClose: () => void;
  onSaved: (msg?: string) => void;
  onViewItem?: (id: number) => void;
}) {
  const isEdit = !!initial.id;
  // Categories B, Part 2: duplicate-snapshot popup below used getCategoryColor(id%10), which
  // disagrees with the sidebar's positional coloring -- switched to the same resolved color
  // (saved override, else positional slot) everything else in this feature now uses.
  const categoryColorMap = useCategoryColorMap(categories);
  const [form, setForm] = useState<Partial<InventoryItem>>({ ...blankItem(), ...initial });
  const [saving, setSaving] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [dupWarning, setDupWarning] = useState<{ type: 'same' | 'other'; facilityName?: string; existingId?: number; existingName?: string } | null>(null);
  const [dupConfirm, setDupConfirm] = useState(false);
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageInputMode, setImageInputMode] = useState<'file' | 'url'>('file');
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [snapId, setSnapId] = useState<number | null>(null);
  const [snapItem, setSnapItem] = useState<InventoryItem | null>(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const barcodeRef = useRef<HTMLInputElement>(null);
  const dupConfirmRef = useRef<HTMLDivElement>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);
  const { data: bomEquipmentData = { types: [] } } = useQuery({
    queryKey: ['bom-equipment-types'],
    queryFn: async () => (await api.get('/inventory/bom-equipment')) as unknown as { types: any[] },
  });

  function handleImageSelect(file: File) {
    setPendingImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  function handleImageDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) handleImageSelect(file);
  }

  const set = (field: keyof InventoryItem, value: unknown) =>
    setForm((f) => ({ ...f, [field]: value }));

  async function checkDuplicate(name?: string, partNumber?: string) {
    if (isEdit) return; // only warn for new items
    const params = new URLSearchParams();
    if (name) params.set('name', name);
    if (partNumber) params.set('partNumber', partNumber);
    if (!params.toString()) return;
    try {
      const res = await api.get<{ sameFacility: { id: number; name: string; part_number: string } | null; otherFacility: { facility_name: string } | null }>(
        `/catalogue/items/check-duplicate?${params.toString()}`
      );
      const data = (res as unknown as { data: { sameFacility: { id: number; name: string; part_number: string } | null; otherFacility: { facility_name: string } | null } }).data;
      if (data.sameFacility) {
        setDupWarning({ type: 'same', existingId: data.sameFacility.id, existingName: data.sameFacility.name });
      } else if (data.otherFacility) {
        setDupWarning({ type: 'other', facilityName: data.otherFacility.facility_name });
      } else {
        setDupWarning(null);
      }
    } catch {
      // ignore errors
    }
  }

  // Auto-fill name from product link (fetch page title via proxy if needed)
  async function autoFill() {
    if (!form.product_link) return;
    // Minimal auto-fill: just focus the name field
    const nameField = document.getElementById('item-name-field') as HTMLInputElement | null;
    nameField?.focus();
  }

  // Prevent background scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (dupConfirm) dupConfirmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [dupConfirm]);

  async function loadSnap(id: number) {
    setSnapId(id);
    setSnapItem(null);
    setSnapLoading(true);
    try {
      const res = await api.get<InventoryItem>(`/inventory/items/${id}`);
      setSnapItem((res as unknown as { data: InventoryItem }).data);
    } catch { /* ignore */ }
    finally { setSnapLoading(false); }
  }

  // Item 2 fix: the duplicate-conflict snapshot used to be a pure read-only preview with no way
  // back to the existing row -- confirmed a genuine dead end (no status shown, no action at all).
  // A Discontinued item is still a real row (only 'Archived' is excluded from the duplicate check
  // server-side), so "reactivate" is a real, correct action here, not a guess -- just flips status
  // back to Active via the same PUT /inventory/items/:id the Edit form itself uses.
  async function handleReactivate() {
    if (!snapItem) return;
    setReactivating(true);
    try {
      await api.put(`/inventory/items/${snapItem.id}`, { ...snapItem, status: 'Active' });
      setSnapItem({ ...snapItem, status: 'Active' });
      onSaved('Item reactivated');
      onClose();
    } finally {
      setReactivating(false);
    }
  }

  // USB barcode scanner: listens for rapid keypresses + Enter
  const barcodeBuffer = useRef('');
  const barcodeTimer = useRef<ReturnType<typeof setTimeout>>();
  function handleBarcodeKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      // barcode fully scanned, value already set via onChange
    }
  }

  async function uploadImage(itemId: number) {
    if (!pendingImage) return;
    const fd = new FormData();
    fd.append('image', pendingImage);
    await api.postForm(`/inventory/items/${itemId}/image`, fd);
  }

  async function doSave(force = false) {
    if (!form.name?.trim() || !form.category_id) return;
    setSaving(true);
    const useFormData = imageInputMode === 'file' && pendingImage !== null;
    try {
      if (useFormData) {
        // Send all fields + image in a single multipart request
        const fd = new FormData();
        (Object.keys(form) as (keyof InventoryItem)[]).forEach((k) => {
          const v = form[k];
          if (v === null || v === undefined) return;
          if (Array.isArray(v)) fd.append(k, JSON.stringify(v));
          else fd.append(k, String(v));
        });
        fd.append('image', pendingImage!);
        const token = localStorage.getItem('accessToken');
        const baseUrl = apiClient.defaults.baseURL ?? '/api';
        if (isEdit) {
          const res = await fetch(`${baseUrl}/inventory/items/${initial.id}`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` },
            body: fd,
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
        } else {
          const urlPath = force ? `${baseUrl}/inventory/items?force=true` : `${baseUrl}/inventory/items`;
          const res = await fetch(urlPath, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: fd,
          });
          if (!res.ok) {
            if (res.status === 409) {
              const errData = await res.json().catch(() => ({})) as { existing?: { id?: number; name?: string } };
              if (errData.existing?.id) setDupWarning({ type: 'same', existingId: errData.existing.id, existingName: errData.existing.name });
              setSaving(false);
              setDupConfirm(true);
              return;
            }
            throw new Error(`HTTP ${res.status}`);
          }
        }
      } else {
        // JSON payload (URL mode or no image)
        const payload: Record<string, unknown> = { ...form };
        if (imageInputMode === 'url' && imageUrlInput.trim()) payload.image_url = imageUrlInput.trim();
        if (isEdit) {
          await api.put(`/inventory/items/${initial.id}`, payload);
        } else {
          const urlPath = force ? '/inventory/items?force=true' : '/inventory/items';
          await api.post<InventoryItem>(urlPath, payload);
        }
      }
      setSaving(false);
      onSaved(isEdit ? 'Item updated successfully' : 'Item added successfully');
      onClose();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        const existing = (err as { response?: { data?: { existing?: { id?: number; name?: string } } } })?.response?.data?.existing;
        if (existing?.id) setDupWarning({ type: 'same', existingId: existing.id, existingName: existing.name });
        setSaving(false);
        setDupConfirm(true);
      } else {
        setSaving(false);
      }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name?.trim() || !form.category_id) return;
    // Block save if there's a same-facility warning and user hasn't confirmed
    if (!isEdit && dupWarning?.type === 'same' && !dupConfirm) {
      setDupConfirm(true);
      return;
    }
    await doSave(dupConfirm);
  }

  return (
    <>
      {ReactDOM.createPortal(<div className="modal-overlay"
        onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
        onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
      >
        <div
          className="modal"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: 720, width: '100%', padding: 0, overflow: 'hidden' }}
        >
          {/* Modal header */}
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="modal-title">
              {form.sourcing_status === 'placeholder' ? (isEdit ? 'Edit Placeholder Part' : 'Add Placeholder Part') : (isEdit ? 'Edit Item' : 'Add Item')}
            </h3>
            <button className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', overflow: 'hidden' }}>
              {/* Left: form fields */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: '70vh', display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Placeholder-part banner — explicit "Enter into Inventory" control (never auto-flips
                    sourcing_status from editing unrelated fields; only this button changes it) */}
                {form.sourcing_status === 'placeholder' && (
                  <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: 'var(--accent-warning)', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <span>⚠ Placeholder part — not yet sourced. No supplier or pricing on file.</span>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => set('sourcing_status', 'sourced')}
                      style={{ flexShrink: 0 }}
                    >
                      Enter into Inventory
                    </button>
                  </div>
                )}

                {/* Product link + autofill */}
                <div className="form-group">
                  <label className="form-label">Product Link</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      className="form-input"
                      type="text"
                      value={form.product_link ?? ''}
                      onChange={(e) => set('product_link', e.target.value)}
                      placeholder="https://..."
                      style={{ flex: 1 }}
                    />
                    {form.product_link && (
                      <a
                        href={form.product_link.startsWith('http') ? form.product_link : `https://${form.product_link}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost btn-sm"
                        style={{ flexShrink: 0 }}
                      >
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>

                {/* Name */}
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    id="item-name-field"
                    className="form-input"
                    type="text"
                    required
                    value={form.name ?? ''}
                    onChange={(e) => { set('name', e.target.value); setDupWarning(null); }}
                    onBlur={(e) => checkDuplicate(e.target.value || undefined, form.part_number || undefined)}
                    placeholder="Item name"
                  />
                  {dupWarning?.type === 'same' && (
                    <div style={{ marginTop: 4, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: 'var(--accent-warning)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>{/* DS: DUP_WARNING_BANNER amber bg border radius font-size */}
                      ⚠ A similar item already exists in this facility
                      {dupWarning.existingName && dupWarning.existingId && (
                        <>
                          {': '}
                          <span
                            style={{ textDecoration: 'underline', color: 'var(--accent-primary)', cursor: 'pointer' }}
                            onClick={() => loadSnap(dupWarning.existingId!)}
                          >
                            {dupWarning.existingName}
                          </span>
                        </>
                      )}
                      .
                    </div>
                  )}
                  {dupWarning?.type === 'other' && (
                    <div style={{ marginTop: 4, padding: '6px 10px', borderRadius: 6, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', color: 'var(--accent-primary)', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 6 }}> {/* DS: dup info banner color */}
                      ℹ A similar item exists at <strong style={{ marginLeft: 3 }}>{dupWarning.facilityName}</strong>.
                    </div>
                  )}
                </div>

                {/* Category */}
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <CategoryPicker
                    categories={categories}
                    value={form.category_id}
                    onChange={(id) => set('category_id', id)}
                  />
                </div>

                {/* Part number + Price row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label className="form-label">Part Number</label>
                    <input
                      className="form-input"
                      type="text"
                      value={form.part_number ?? ''}
                      onChange={(e) => { set('part_number', e.target.value); setDupWarning(null); }}
                      onBlur={(e) => checkDuplicate(form.name || undefined, e.target.value || undefined)}
                      placeholder="MFR-0001"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price ($)</label>
                    <input
                      className="form-input"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.price ?? 0}
                      onChange={(e) => set('price', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>

                {/* Supplier */}
                <div className="form-group">
                  <label className="form-label">Supplier</label>
                  <SupplierField
                    value={form.supplier ?? ''}
                    onChange={(v) => set('supplier', v)}
                  />
                </div>

                {/* Brand */}
                <div className="form-group">
                  <label className="form-label">Brand</label>
                  <input
                    className="form-input"
                    type="text"
                    value={form.brand ?? ''}
                    onChange={(e) => set('brand', e.target.value)}
                    placeholder="Manufacturer / brand"
                  />
                </div>

                {/* Used for + Department */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label className="form-label">Used For</label>
                    <BomSelector
                      selected={Array.isArray(form.used_for) ? (form.used_for as string[]) : []}
                      onChange={(v) => set('used_for', v)}
                      equipmentData={bomEquipmentData}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <DarkSelect value={form.department ?? ''} onChange={(v) => set('department', v)} options={DEPT_OPTIONS} placeholder="Select…" />
                  </div>
                </div>

                {/* Qty + Unit + Sold In row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label className="form-label">Qty on Hand</label>
                    <input
                      className="form-input"
                      type="number"
                      min={0}
                      value={form.quantity ?? 0}
                      onChange={(e) => set('quantity', Number(e.target.value))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit</label>
                    <select className="form-select" value={form.unit ?? 'ea'} onChange={(e) => set('unit', e.target.value)}>
                      {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sold In (qty)</label>
                    <input
                      className="form-input"
                      type="number"
                      min={1}
                      value={form.sold_in ?? 1}
                      onChange={(e) => set('sold_in', Number(e.target.value))}
                    />
                  </div>
                </div>

                {/* Location + Reorder Point */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label className="form-label">Storage Location</label>
                    <input
                      className="form-input"
                      type="text"
                      value={form.location ?? ''}
                      onChange={(e) => set('location', e.target.value)}
                      placeholder="Closet 3B, Shelf 2"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reorder Point</label>
                    <input
                      className="form-input"
                      type="number"
                      min={0}
                      value={form.reorder_point ?? ''}
                      onChange={(e) => set('reorder_point', e.target.value === '' ? null : Number(e.target.value))}
                      placeholder="e.g. 5"
                    />
                  </div>
                </div>

                {/* Barcode */}
                <div className="form-group">
                  <label className="form-label">Barcode</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      ref={barcodeRef}
                      className="form-input"
                      type="text"
                      value={form.barcode ?? ''}
                      onChange={(e) => set('barcode', e.target.value)}
                      onKeyDown={handleBarcodeKeyDown}
                      placeholder="Scan or type barcode"
                      style={{ flex: 1, fontFamily: 'monospace' }}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowCamera(true)}
                      title="Use camera to scan"
                      style={{ flexShrink: 0 }}
                    >
                      <Camera size={15} />
                    </button>
                  </div>
                </div>

                {/* Status */}
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status ?? 'Active'} onChange={(e) => set('status', e.target.value)}>
                    <option value="Active">Active</option>
                    <option value="Discontinued">Discontinued</option>
                  </select>
                </div>

                {/* Description */}
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <RichTextEditor
                    value={form.product_description ?? ''}
                    onChange={(v) => set('product_description', v)}
                    placeholder="Brief product description"
                    rows={3}
                  />
                </div>

                {/* Notes */}
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <RichTextEditor
                    value={form.notes ?? ''}
                    onChange={(v) => set('notes', v)}
                    placeholder="Internal notes"
                    rows={2}
                  />
                </div>

                {/* Duplicate confirmation banner — inside scroll area so it's always visible */}
                {dupConfirm && (
                  <div ref={dupConfirmRef} style={{
                    padding: '8px 12px', // DS: DUP_CONFIRM_BANNER padding
                    background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', // DS: DUP_CONFIRM_BANNER bg border
                    borderRadius: 'var(--radius-sm)', fontSize: 13, // DS: DUP_CONFIRM_BANNER radius font-size
                  }}>
                    <div style={{ fontWeight: 600, color: 'var(--accent-warning)', marginBottom: 6 }}>{/* DS: DUP_CONFIRM_BANNER title color */}
                      ⚠ A similar item already exists in this facility
                      {dupWarning?.existingName && dupWarning?.existingId && (
                        <>
                          {': '}
                          <span
                            style={{ textDecoration: 'underline', color: 'var(--accent-primary)', cursor: 'pointer', fontWeight: 400 }}
                            onClick={() => loadSnap(dupWarning.existingId!)}
                          >
                            {dupWarning.existingName}
                          </span>
                        </>
                      )}
                      .
                    </div>
                    <div style={{ color: 'var(--text-secondary)', marginBottom: 10 }}>
                      Are you sure you want to add a duplicate?
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDupConfirm(false)}>Cancel</button>
                      <button type="button" className="btn btn-sm" style={{ background: 'var(--accent-warning)', color: '#fff', border: 'none' }}
                        onClick={() => { setDupConfirm(false); void doSave(true); }}>
                        Add Anyway
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: image upload */}
              <div style={{
                borderLeft: '1px solid var(--border-subtle)',
                background: 'var(--bg-surface)',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '20px 16px', gap: 10,
              }}>
                {/* Upload File / Paste URL toggle */}
                <div style={{ display: 'flex', width: '100%', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border-default)' }}>
                  {(['file', 'url'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setImageInputMode(mode)}
                      style={{
                        flex: 1, padding: '5px 0', border: 'none', cursor: 'pointer',
                        fontSize: '0.75rem', fontWeight: 500,
                        background: imageInputMode === mode ? 'var(--accent-primary)' : 'transparent',
                        color: imageInputMode === mode ? '#fff' : 'var(--text-muted)',
                        transition: 'background 0.15s, color 0.15s',
                      }}
                    >
                      {mode === 'file' ? 'Upload File' : 'Paste URL'}
                    </button>
                  ))}
                </div>

                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleImageSelect(f);
                    e.target.value = '';
                  }}
                />

                {imageInputMode === 'url' ? (
                  /* Paste URL mode */
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input
                      className="form-input"
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={imageUrlInput}
                      onChange={(e) => setImageUrlInput(e.target.value)}
                      style={{ fontSize: '0.78rem' }}
                    />
                    {imageUrlInput.trim() && (
                      <div style={{
                        width: '100%', aspectRatio: '1',
                        background: 'var(--bg-elevated)',
                        borderRadius: 'var(--radius-md)',
                        overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <img
                          src={imageUrlInput.trim()}
                          alt="Preview"
                          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }}
                          onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  /* Upload File mode — Preview / drop zone */
                  <div
                    onClick={() => imageInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleImageDrop}
                    style={{
                      width: '100%', aspectRatio: '1',
                      background: 'var(--bg-elevated)',
                      border: '2px dashed var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: 8, color: 'var(--text-muted)', cursor: 'pointer',
                      overflow: 'hidden', position: 'relative',
                      transition: 'border-color var(--transition)',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--accent-primary)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-default)'; }}
                  >
                    {(imagePreview ?? getImageSrc(form.thumbnail_path)) ? (
                      <>
                        <img
                          src={imagePreview ?? getImageSrc(form.thumbnail_path)!}
                          alt="Item preview"
                          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: 'rgba(0,0,0,0.35)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          gap: 6, color: '#fff', fontSize: '0.75rem',
                          opacity: 0, transition: 'opacity 0.2s',
                        }}
                          className="img-hover-overlay"
                          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '1'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '0'; }}
                        >
                          <Upload size={18} color="#fff" /> Change
                        </div>
                      </>
                    ) : (
                      <>
                        <Upload size={24} style={{ opacity: 0.4 }} />
                        <span style={{ fontSize: '0.75rem', textAlign: 'center', lineHeight: 1.4 }}>
                          Click or drop<br/>to upload image
                        </span>
                      </>
                    )}
                  </div>
                )}

                {/* Remove button */}
                {imageInputMode === 'file' && (imagePreview ?? form.thumbnail_path) && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--accent-danger)', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (imagePreview) {
                        setPendingImage(null);
                        setImagePreview(null);
                      } else if (form.thumbnail_path && initial.id) {
                        await api.delete(`/inventory/items/${initial.id}/image`);
                        setForm((f) => ({ ...f, thumbnail_path: null }));
                        onSaved();
                      }
                    }}
                  >
                    <ImageOff size={13} /> Remove
                  </button>
                )}

                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>
                  {imageInputMode === 'file' ? 'PNG, JPG, WebP · Max 5 MB' : 'Direct link to image file'}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 24px', borderTop: '1px solid var(--border-subtle)',
              display: 'flex', justifyContent: 'flex-end', gap: 8,
              background: 'var(--bg-elevated)',
            }}>
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving || dupConfirm}>
                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Item'}
              </button>
            </div>
          </form>
        </div>
      </div>, document.body)}

      {showCamera && (
        <CameraScannerModal
          onDetect={(val) => { set('barcode', val); setShowCamera(false); }}
          onClose={() => setShowCamera(false)}
        />
      )}

      {/* Duplicate item snapshot popup */}
      {snapId !== null && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) { setSnapId(null); setSnapItem(null); } mouseDownTarget.current = null; }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-default)',
              borderRadius: 12,
              boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
              width: 320, overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>Existing Item</span>
              <button onClick={() => { setSnapId(null); setSnapItem(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <X size={16} />
              </button>
            </div>
            {snapLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                <div className="loading-spinner" />
              </div>
            ) : snapItem ? (() => {
              const snapColor = resolveCategoryColor(categories.find((c) => c.id === snapItem.category_id), categoryColorMap);
              return (
              <>
                {/* Image */}
                <div style={{ height: 160, background: `linear-gradient(135deg, ${snapColor}18, ${snapColor}08)`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {getImageSrc(snapItem.thumbnail_path) ? (
                    <img src={getImageSrc(snapItem.thumbnail_path)!} alt={snapItem.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 12 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  ) : (
                    <Package size={48} color={snapColor} style={{ opacity: 0.3 }} />
                  )}
                </div>
                {/* Details */}
                <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>{snapItem.name}</div>
                    {/* Item 2 fix: show the status that's actually WHY this didn't turn up in a
                        normal search (the dead end the task described) instead of leaving it
                        unexplained -- this badge is new, the rest of the card already existed. */}
                    {snapItem.status && snapItem.status !== 'Active' && (
                      <span style={{ flexShrink: 0, fontSize: '0.68rem', fontWeight: 700, color: '#9ca3af', background: 'rgba(107,114,128,0.15)', border: '1px solid rgba(107,114,128,0.3)', borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap' }}>
                        {snapItem.status}
                      </span>
                    )}
                  </div>
                  {snapItem.category_name && <div style={{ fontSize: '0.75rem', color: snapColor, fontWeight: 500 }}>{snapItem.category_name}</div>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 4 }}>
                    {snapItem.part_number && (
                      <div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Part #</div>
                        <div style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'var(--text-primary)' }}>{snapItem.part_number}</div>
                      </div>
                    )}
                    {snapItem.supplier && (
                      <div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Supplier</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)' }}>{snapItem.supplier}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stock</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)', fontWeight: 600 }}>{snapItem.quantity} {snapItem.unit}</div>
                    </div>
                    {snapItem.price > 0 && (
                      <div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)', fontWeight: 600 }}>${Number(snapItem.price).toFixed(2)}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Item 2 fix: a real way back to the existing row, instead of a pure read-only
                    dead end. Reactivating here closes this Add form -- the existing item IS the
                    thing the person was trying to create, there's nothing left to add. */}
                {snapItem.status && snapItem.status !== 'Active' && (
                  <div style={{ padding: '0 16px 14px' }}>
                    <button
                      type="button"
                      className="btn btn-primary combo2"
                      style={{ width: '100%', justifyContent: 'center' }}
                      disabled={reactivating}
                      onClick={handleReactivate}
                    >
                      {reactivating ? 'Reactivating…' : `Reactivate this ${snapItem.status.toLowerCase()} item`}
                    </button>
                  </div>
                )}
              </>
              );
            })() : (
              <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center', fontSize: '0.875rem' }}>Could not load item details.</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Category icon picker ─────────────────────────────────────────────────────
const ICON_OPTIONS: Array<{ name: string; node: React.ReactNode }> = [
  { name: 'Package',       node: <Package size={18} /> },
  { name: 'Sparkles',      node: <Sparkles size={18} /> },
  { name: 'Wrench',        node: <Wrench size={18} /> },
  { name: 'Zap',           node: <Zap size={18} /> },
  { name: 'Droplets',      node: <Droplets size={18} /> },
  { name: 'Wind',          node: <Wind size={18} /> },
  { name: 'FileText',      node: <FileText size={18} /> },
  { name: 'Heart',         node: <Heart size={18} /> },
  { name: 'ShieldCheck',   node: <ShieldCheck size={18} /> },
  { name: 'UtensilsCrossed', node: <UtensilsCrossed size={18} /> },
  { name: 'RefreshCw',     node: <RefreshCw size={18} /> },
  { name: 'Paintbrush',    node: <Paintbrush size={18} /> },
  { name: 'Accessibility', node: <Accessibility size={18} /> },
  { name: 'Gauge',         node: <Gauge size={18} /> },
  { name: 'Thermometer',   node: <Thermometer size={18} /> },
  { name: 'Building2',     node: <Building2 size={18} /> },
  { name: 'Truck',         node: <Truck size={18} /> },
  { name: 'FlaskConical',  node: <FlaskConical size={18} /> },
  { name: 'Scissors',      node: <Scissors size={18} /> },
];

// ─── React-icons lookup maps (fa: / md: / bs: prefixes) ──────────────────────
// Exported (here and at each definition below) so the extracted category-manager components
// (client/src/components/inventory/category-manager/) can reuse this file's existing icon
// renderer/library data instead of a second copy -- see CategoryManager.tsx's own import for
// the full list this feeds.
export type ReactIconComp = React.ComponentType<{ size?: number; color?: string }>;
export const FA_MAP: Record<string, ReactIconComp> = {
  FaFire, FaFireExtinguisher, FaBell, FaTriangleExclamation, FaShieldHalved, FaTruck,
  FaFireFlameSimple, FaHelmetSafety, FaRadiation, FaHandshake,
  FaWrench, FaScrewdriverWrench, FaGears, FaGear, FaTemperatureHalf, FaTemperatureFull, FaWind, FaFan,
  FaIndustry, FaOilCan, FaMotorcycle,
  FaBolt, FaPlug, FaBatteryFull, FaLightbulb,
  FaSolarPanel, FaChargingStation, FaNetworkWired, FaMicrochip,
  FaDroplet, FaWater, FaShower, FaFaucet,
  FaToilet, FaSink,
  FaBuilding, FaHouse, FaDoorOpen, FaDoorClosed, FaWarehouse, FaHospital, FaHotel,
  FaCity, FaSchool, FaChurch, FaStore, FaLandmark,
  FaBroom, FaRecycle, FaTrash, FaTrashCan,
  FaSoap, FaPumpSoap, FaSprayCan,
  FaHeart, FaHeartPulse, FaPills, FaSyringe, FaStethoscope, FaKitMedical, FaBandage,
  FaHospitalUser, FaUserNurse, FaWheelchair,
  FaUtensils, FaMugHot, FaKitchenSet, FaBlender,
  FaPizzaSlice, FaBreadSlice, FaCarrot,
  FaLock, FaLockOpen, FaKey, FaCamera, FaEye, FaUserShield,
  FaUserSecret, FaFingerprint, FaIdCard, FaPassport,
  FaClipboard, FaFolder, FaFolderOpen, FaFileLines, FaChartBar, FaPenToSquare, FaThumbtack, FaPaperclip,
  FaMoneyBill, FaClockRotateLeft, FaDiagramProject, FaListCheck,
  FaToolbox, FaHammer, FaRuler, FaCompass, FaPaintRoller, FaBucket,
  FaRulerCombined, FaVial, FaFlask, FaMagnet,
  FaCar, FaTruckMoving, FaBus, FaBicycle, FaHelicopter,
  FaTaxi, FaShip, FaPlane,
  FaBox, FaBoxOpen, FaDatabase, FaServer, FaLaptop, FaPrint, FaWifi, FaSatelliteDish,
  FaFileInvoice, FaReceipt,
};
export const MD_MAP: Record<string, ReactIconComp> = {
  MdFireExtinguisher, MdFireTruck, MdNotificationsActive, MdWarning, MdSecurity, MdLocalPolice,
  MdLocalFireDepartment, MdEmergency,
  MdBuild, MdSettings, MdThermostat, MdAir, MdHvac, MdConstruction, MdHandyman, MdPlumbing,
  MdCable, MdOutlet, MdSolarPower,
  MdElectricBolt, MdPower, MdBatteryFull, MdLightbulb, MdFlashOn, MdElectricalServices,
  MdWater, MdWaterDrop, MdShower, MdBathtub,
  MdPool, MdInvertColors,
  MdApartment, MdDomain, MdHome, MdHotel, MdLocalHospital, MdWarehouse, MdMeetingRoom,
  MdSchool, MdStore, MdVilla,
  MdCleaningServices, MdDelete, MdRecycling, MdAutoAwesome, MdSpa,
  MdLocalLaundryService, MdDryCleaning,
  MdMedicalServices, MdVaccines, MdMonitorHeart, MdMedication, MdHealthAndSafety,
  MdBiotech, MdBloodtype,
  MdRestaurant, MdKitchen, MdCoffeeMaker, MdMicrowave, MdOutdoorGrill, MdDinnerDining,
  MdLunchDining, MdRoomService,
  MdLock, MdLockOpen, MdKey, MdCamera, MdVideocam, MdSecurityUpdateGood, MdVerifiedUser,
  MdFingerprint, MdFace,
  MdAssignment, MdFolder, MdFolderOpen, MdInsertDriveFile, MdBarChart, MdEdit, MdAttachFile, MdBookmark,
  MdDashboard, MdEventNote,
  MdBuildCircle, MdHomeRepairService, MdPrecisionManufacturing, MdEngineering, MdFormatPaint,
  MdAgriculture, MdCarpenter,
  MdDirectionsCar, MdLocalShipping, MdDirectionsBus, MdPedalBike, MdAirportShuttle,
  MdFlight, MdTrain,
  MdInventory, MdInventory2, MdStorefront, MdStorage, MdComputer, MdPrint, MdWifi, MdRouter,
  MdPhoneAndroid, MdQrCode2,
};
export const BS_MAP: Record<string, ReactIconComp> = {
  BsExclamationTriangle, BsExclamationCircle, BsBellFill, BsShieldFill, BsShieldCheck,
  BsAlarmFill, BsAlarm,
  BsWrench, BsWrenchAdjustable, BsGear, BsGearFill, BsThermometer, BsThermometerHigh, BsFan, BsWind,
  BsSnow, BsCloudRain,
  BsLightningFill, BsPlugFill, BsBatteryFull, BsLightbulbFill, BsLightbulb, BsToggleOn,
  BsDropletFill, BsDroplet, BsWater,
  BsBuilding, BsBuildingFill, BsHouseFill, BsHouse, BsDoorOpenFill, BsDoorClosedFill, BsHospitalFill, BsBank,
  BsBuildings, BsBuildingsFill,
  BsTrashFill, BsTrash, BsRecycle, BsBrush, BsBrushFill,
  BsTrash2, BsTrash2Fill, BsStars,
  BsHeartFill, BsHeart, BsHeartPulse, BsCapsulePill, BsBandaid, BsBandaidFill, BsActivity,
  BsEmojiSmile,
  BsCupHotFill, BsCupHot, BsFire, BsUpc,
  BsLockFill, BsLock, BsKeyFill, BsKey, BsCameraFill, BsCamera, BsEyeFill, BsShieldLock,
  BsFingerprint, BsPersonBadge, BsPersonBadgeFill,
  BsClipboardFill, BsClipboard, BsFolderFill, BsFolder, BsFileText, BsBarChart, BsPenFill, BsPaperclip, BsBookmark,
  BsCalendar, BsCalendarFill, BsJournalText, BsSticky, BsStickyFill,
  BsTools, BsHammer, BsPaintBucket, BsBucket, BsRulers,
  BsUpcScan,
  BsCarFront, BsTruck, BsBusFront, BsBicycle, BsAirplane,
  BsAirplaneFill,
  BsBoxFill, BsBox, BsArchiveFill, BsArchive, BsDatabaseFill, BsDatabase, BsLaptop, BsPrinter, BsWifi, BsRouter,
  BsHddFill, BsHdd, BsGlobe, BsGlobe2,
};

// ─── Shared icon renderer: handles emoji, Lucide, fa:, md:, bs: prefixes ──────
export function renderIcon(value: string | null, size = 16, color?: string): React.ReactNode {
  if (!value) return <Package size={size} />;
  if (value.startsWith('fa:')) {
    const Comp = FA_MAP[value.slice(3)];
    return Comp ? <Comp size={size} color={color} /> : <Package size={size} />;
  }
  if (value.startsWith('md:')) {
    const Comp = MD_MAP[value.slice(3)];
    return Comp ? <Comp size={size} color={color} /> : <Package size={size} />;
  }
  if (value.startsWith('bs:')) {
    const Comp = BS_MAP[value.slice(3)];
    return Comp ? <Comp size={size} color={color} /> : <Package size={size} />;
  }
  const LucideComp = (LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number; color?: string }>>)[value];
  if (LucideComp) return <LucideComp size={size} color={color} />;
  // Emoji / text fallback
  return <span style={{ fontSize: size * 0.85, lineHeight: 1, color }}>{value}</span>;
}

export function iconByName(name: string | null, size = 16): React.ReactNode {
  return renderIcon(name, size);
}

// ─── CategoryIcon: unified icon renderer (all 5 icon types) ──────────────────
export function CategoryIcon({ iconName, size = 16, color }: { iconName: string | null; size?: number; color?: string }) {
  return <>{renderIcon(iconName, size, color)}</>;
}

// ─── CategoryPicker: drill-down navigation with branch selection ──────────────
export function getFullPath(cat: Category, all: Category[]): string {
  if (!cat.parent_id) return cat.name;
  const parent = all.find((c) => c.id === cat.parent_id);
  return parent ? `${parent.name} › ${cat.name}` : cat.name;
}

function CategoryPicker({
  categories,
  value,
  onChange,
  hasError,
}: {
  categories: Category[];
  value: number | null | undefined;
  onChange: (id: number) => void;
  hasError?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // stack of category IDs representing the current drill-down path
  const [stack, setStack] = useState<number[]>([]);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const currentParentId = stack.length > 0 ? stack[stack.length - 1] : null;
  const currentParentCat = currentParentId !== null ? categories.find((c) => c.id === currentParentId) : null;
  const currentLevelCats = categories.filter((c) => (c.parent_id ?? null) === currentParentId);
  const breadcrumb = stack.map((id) => categories.find((c) => c.id === id)?.name ?? '').filter(Boolean);

  const selectedCat = value ? categories.find((c) => c.id === value) : null;
  const displayLabel = selectedCat ? getFullPath(selectedCat, categories) : 'Select category…';

  function openDropdown() {
    setStack([]);
    setOpen(true);
    requestAnimationFrame(() => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const footerHeight = 64;
      const dropdownHeight = 280;
      const spaceBelow = window.innerHeight - rect.bottom - footerHeight;
      const spaceAbove = rect.top;
      const top = (spaceBelow < dropdownHeight && spaceAbove > spaceBelow)
        ? rect.top - dropdownHeight
        : rect.bottom + 4;
      const w = rect.width;
      const rawLeft = rect.left;
      const left = rawLeft + w > window.innerWidth ? window.innerWidth - w - 8 : rawLeft;
      setDropdownPos({ top, left, width: w });
    });
  }

  function handleCatClick(cat: Category) {
    const hasChildren = categories.some((c) => c.parent_id === cat.id);
    if (hasChildren) {
      // Auto-select this parent AND drill into its children
      onChange(cat.id);
      setStack((prev) => [...prev, cat.id]);
    } else {
      // Leaf: select and close
      onChange(cat.id);
      setOpen(false);
      setStack([]);
    }
  }

  function goBack() {
    setStack((prev) => prev.slice(0, -1));
  }

  function selectCurrentParent() {
    if (currentParentId !== null) {
      onChange(currentParentId);
      setOpen(false);
      setStack([]);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openDropdown())}
        style={{
          width: '100%', padding: '9px 12px',
          background: 'var(--bg-elevated)', border: `1px solid ${hasError ? 'var(--accent-danger)' : 'var(--border-default)'}`,
          borderRadius: 'var(--radius-md)', color: selectedCat ? 'var(--text-primary)' : 'var(--text-muted)',
          textAlign: 'left', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', fontSize: '14px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {selectedCat && <CategoryIcon iconName={selectedCat.icon_path} size={14} />}
          {displayLabel}
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>▾</span>
      </button>

      {open && dropdownPos && ReactDOM.createPortal(
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 10099 }}
            onClick={() => setOpen(false)}
          />
          <div style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)', zIndex: 10100,
            maxHeight: 280, overflowY: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}>
            {/* Breadcrumb */}
            {breadcrumb.length > 0 && (
              <div style={{
                padding: '5px 12px', fontSize: 11, color: 'var(--text-muted)',
                borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                All{breadcrumb.map((b, i) => <span key={i}> › {b}</span>)}
              </div>
            )}
            {/* Back button */}
            {stack.length > 0 && (
              <button
                type="button"
                onClick={goBack}
                style={{
                  width: '100%', padding: '8px 12px', background: 'none',
                  border: 'none', borderBottom: '1px solid var(--border-subtle)',
                  cursor: 'pointer', textAlign: 'left', fontSize: 13,
                  color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                ← Back
              </button>
            )}
            {/* "Select [Category Name]" option to save to the current branch parent */}
            {currentParentCat && (
              <div
                onClick={selectCurrentParent}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: 13, fontStyle: 'italic',
                  borderBottom: '1px solid var(--border-subtle)',
                  color: value === currentParentId ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  background: value === currentParentId ? 'rgba(59,130,246,0.1)' : 'transparent',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
                onMouseEnter={(e) => { if (value !== currentParentId) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = value === currentParentId ? 'rgba(59,130,246,0.1)' : 'transparent'; }}
              >
                <CategoryIcon iconName={currentParentCat.icon_path} size={13} />
                Select "{currentParentCat.name}"
                {value === currentParentId && <span style={{ marginLeft: 'auto', color: 'var(--accent-success)' }}>✓</span>}
              </div>
            )}
            {/* Current level categories */}
            {currentLevelCats.map((cat) => {
              const hasChildren = categories.some((c) => c.parent_id === cat.id);
              const isSelected = cat.id === value;
              return (
                <div
                  key={cat.id}
                  onClick={() => handleCatClick(cat)}
                  style={{
                    display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 8,
                    padding: '9px 14px', fontSize: 14,
                    background: isSelected ? 'rgba(59,130,246,0.12)' : 'transparent',
                    color: isSelected ? 'var(--accent-primary)' : 'var(--text-primary)',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)'; }}
                  onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                >
                  <CategoryIcon iconName={cat.icon_path} size={14} />
                  <span style={{ flex: 1 }}>{cat.name}</span>
                  {isSelected && !hasChildren && <span style={{ color: 'var(--accent-success)', fontSize: 13 }}>✓</span>}
                  {hasChildren && <ChevronRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                </div>
              );
            })}
            {currentLevelCats.length === 0 && (
              <div style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No categories</div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ─── Full icon picker groups ──────────────────────────────────────────────────
const ICON_GROUPS: [string, string[]][] = [
  ['Building & Facility', [
    'Building', 'Building2', 'Home', 'Hotel', 'Warehouse', 'Store', 'School', 'Church',
    'Castle', 'Landmark', 'DoorOpen', 'DoorClosed', 'Fence', 'Columns', 'Layers',
    'LayoutGrid', 'Map', 'MapPin', 'Navigation', 'Compass',
  ]],
  ['Tools & Maintenance', [
    'Wrench', 'Hammer', 'Screwdriver', 'Tool', 'Settings', 'Settings2', 'Cog', 'HardHat',
    'PenTool', 'Scissors', 'Ruler', 'Paintbrush', 'Paintbrush2', 'Drill', 'Shovel',
    'Axe', 'Construction', 'Flashlight', 'Lightbulb', 'Lamp',
  ]],
  ['Electrical & HVAC', [
    'Zap', 'ZapOff', 'Battery', 'BatteryCharging', 'Power', 'PowerOff', 'Plug', 'PlugZap',
    'Thermometer', 'Wind', 'Fan', 'AirVent', 'Cpu', 'CircuitBoard', 'Radio', 'Wifi',
    'Cable', 'Switch',
  ]],
  ['Plumbing', [
    'Droplets', 'Droplet', 'CloudRain', 'Waves', 'FlameKindling', 'Flame', 'Pipette',
    'Container', 'Cylinder', 'Filter', 'ShowerHead', 'Bath', 'Toilet',
  ]],
  ['Medical & Health', [
    'Heart', 'HeartPulse', 'Activity', 'Stethoscope', 'Pill', 'Pills', 'Syringe',
    'Bandage', 'Cross', 'Ambulance', 'Microscope', 'TestTube', 'TestTubes',
    'Dna', 'Eye', 'EyeOff', 'Brain', 'Bone',
  ]],
  ['Safety & Security', [
    'Shield', 'ShieldCheck', 'ShieldAlert', 'ShieldX', 'Lock', 'LockOpen', 'Key',
    'KeyRound', 'AlertTriangle', 'AlertCircle', 'AlertOctagon', 'Siren',
    'FireExtinguisher', 'HardHat', 'Video', 'Camera', 'Scan', 'ScanFace',
  ]],
  ['Office & Admin', [
    'FileText', 'File', 'Files', 'Folder', 'FolderOpen', 'ClipboardList', 'Clipboard',
    'Calendar', 'CalendarCheck', 'Clock', 'Printer', 'Monitor', 'Laptop', 'Phone',
    'Mail', 'Inbox', 'Archive', 'BookOpen', 'Book', 'Notebook', 'PenLine', 'Pen',
  ]],
  ['People & Team', [
    'User', 'Users', 'UserCheck', 'UserPlus', 'UserCog', 'PersonStanding', 'Baby',
    'Handshake', 'HeartHandshake', 'Award', 'Trophy', 'Star', 'Medal', 'Crown',
    'Smile', 'Frown', 'Meh', 'ChefHat', 'GraduationCap',
  ]],
  ['Nature & Outdoor', [
    'Tree', 'TreePine', 'Trees', 'Leaf', 'Flower', 'Flower2', 'Sun', 'Moon', 'Cloud',
    'Snowflake', 'Mountain', 'Umbrella', 'Bug', 'Bird', 'Fish',
    'Rabbit', 'Squirrel', 'Dog', 'Cat',
  ]],
  ['Transport & Vehicles', [
    'Car', 'Truck', 'Bus', 'Train', 'Plane', 'Ship', 'Bike', 'Ambulance', 'Forklift',
    'Tractor', 'Package', 'PackageOpen', 'PackageCheck', 'ShoppingCart', 'ShoppingBag',
  ]],
];

// All Lucide icon names (PascalCase exports only) for the "All Icons" tab
const ALL_ICON_NAMES = Object.keys(LucideIcons).filter(k => /^[A-Z]/.test(k)).sort();

// ─── Emoji sections for the new two-tab icon picker ───────────────────────────
export const EMOJI_SECTIONS = [
  { label: 'FIRE & SAFETY',       emojis: ['🧯','🔥','🚒','🚨','🚑','🛡️','⚠️','🔔','🔕','🪣'] },
  { label: 'HVAC & MECHANICAL',   emojis: ['❄️','🌡️','💨','🔧','🔩','⚙️','🪛','🔨','🪚','🛠️'] },
  { label: 'ELECTRICAL',          emojis: ['⚡','🔌','🔋','💡','🕯️','🔦'] },
  { label: 'PLUMBING',            emojis: ['💧','🚿','🛁','🚰','🪠','🌊'] },
  { label: 'BUILDING & STRUCTURE',emojis: ['🏗️','🏢','🚪','🪟','🛗','🪜','🏠','🏥'] },
  { label: 'CLEANING',            emojis: ['🧹','🧺','🧻','🧼','🪣','🫧','🗑️','♻️'] },
  { label: 'MEDICAL & NURSING',   emojis: ['💊','🩺','🩹','🩻','🏥','💉','🧬','🩸'] },
  { label: 'KITCHEN',             emojis: ['🍽️','🧑‍🍳','🥄','🍴','🫙','🥡','🧊','🍱'] },
  { label: 'SECURITY & KEYS',     emojis: ['🔑','🗝️','🔐','🔒','🔓','📷','👁️','🚧'] },
  { label: 'OFFICE & ADMIN',      emojis: ['📋','📁','📂','📊','📈','🗂️','📝','✏️','📌','📎'] },
  { label: 'GARDEN & OUTSIDE',    emojis: ['🌿','🌳','🌱','🍃','🌸','☀️','🌧️','❄️'] },
  { label: 'TRANSPORT',           emojis: ['🚗','🚐','🚚','🛻','🏎️','🚲','🛺'] },
  { label: 'GENERAL TOOLS',       emojis: ['📦','🗄️','🖥️','📱','🖨️','📡','🔭','🔬'] },
];

// ─── Curated Lucide icon sections for the new two-tab icon picker ─────────────
export const LUCIDE_SECTIONS = [
  { label: 'FIRE & SAFETY',     icons: ['Flame','FireExtinguisher','Siren','ShieldAlert','AlarmSmoke','Zap','Bell','BellRing','AlertTriangle','AlertCircle','Skull','ShieldX'] },
  { label: 'HVAC & MECHANICAL', icons: ['Wind','Thermometer','Fan','Gauge','Wrench','Settings','Cog','Hammer','Snowflake','Drill','Sliders','Pipette'] },
  { label: 'ELECTRICAL',        icons: ['Zap','Plug','Battery','BatteryCharging','Lightbulb','Flashlight','ZapOff','PlugZap','BatteryLow','LightbulbOff','Sun','CircuitBoard'] },
  { label: 'PLUMBING',          icons: ['Droplets','Waves','Droplet','CloudRain','CloudSnow','CloudDrizzle','FlaskConical','TestTube'] },
  { label: 'BUILDING',          icons: ['Building','Building2','Door','DoorOpen','Stairs','Home','Hotel','Warehouse','School','Church','Store','Landmark','Factory','Castle'] },
  { label: 'CLEANING',          icons: ['Trash','Trash2','Recycle','Sparkles','Brush','Wind','Layers','Filter','Eraser'] },
  { label: 'MEDICAL',           icons: ['Heart','HeartPulse','Pill','Stethoscope','Syringe','Activity','Cross','Brain','Bone','Microscope','TestTube','Ambulance'] },
  { label: 'KITCHEN',           icons: ['ChefHat','UtensilsCrossed','Utensils','Coffee','CupSoda','Refrigerator','Microwave','CookingPot','Scale','FlaskConical','Apple','Salad'] },
  { label: 'SECURITY',          icons: ['Lock','LockOpen','Key','KeyRound','Shield','ShieldCheck','Camera','Eye','Fingerprint','UserCheck','UserX','ScanFace','BadgeCheck','Webcam'] },
  { label: 'OFFICE',            icons: ['Clipboard','ClipboardList','Folder','FolderOpen','BarChart','FileText','FilePlus','PenLine','Pin','Paperclip','Calendar','CalendarCheck','BookOpen','Notebook','Tag','Bookmark'] },
  { label: 'GENERAL',           icons: ['Package','Archive','Box','Boxes','Container','Database','Server','Laptop','Printer','Wifi','Radio','Telescope','Microscope','Truck','Car','Bike','Globe','HardDrive','Monitor','Tablet','Smartphone','Battery','Cpu','Network'] },
];

// ─── Font Awesome sections ─────────────────────────────────────────────────────
export const FA_SECTIONS = [
  { label: 'FIRE & SAFETY',    icons: ['FaFire','FaFireExtinguisher','FaBell','FaTriangleExclamation','FaShieldHalved','FaTruck','FaFireFlameSimple','FaHelmetSafety','FaRadiation','FaHandshake'] },
  { label: 'HVAC & MECHANICAL',icons: ['FaWrench','FaScrewdriverWrench','FaGears','FaGear','FaTemperatureHalf','FaTemperatureFull','FaWind','FaFan','FaIndustry','FaOilCan','FaMotorcycle'] },
  { label: 'ELECTRICAL',       icons: ['FaBolt','FaPlug','FaBattery0','FaBatteryFull','FaLightbulb','FaSolarPanel','FaChargingStation','FaNetworkWired','FaMicrochip'] },
  { label: 'PLUMBING',         icons: ['FaDroplet','FaWater','FaShower','FaFaucet','FaToilet','FaSink'] },
  { label: 'BUILDING',         icons: ['FaBuilding','FaHouse','FaDoorOpen','FaDoorClosed','FaWarehouse','FaHospital','FaHotel','FaCity','FaSchool','FaChurch','FaStore','FaLandmark'] },
  { label: 'CLEANING',         icons: ['FaBroom','FaRecycle','FaTrash','FaTrashCan','FaSoap','FaPumpSoap','FaSprayCan'] },
  { label: 'MEDICAL',          icons: ['FaHeart','FaHeartPulse','FaPills','FaSyringe','FaStethoscope','FaKitMedical','FaBandage','FaHospitalUser','FaUserNurse','FaWheelchair'] },
  { label: 'KITCHEN',          icons: ['FaUtensils','FaMugHot','FaKitchenSet','FaBlender','FaPizzaSlice','FaBreadSlice','FaCarrot'] },
  { label: 'SECURITY',         icons: ['FaLock','FaLockOpen','FaKey','FaCamera','FaEye','FaUserShield','FaUserSecret','FaFingerprint','FaIdCard','FaPassport'] },
  { label: 'OFFICE',           icons: ['FaClipboard','FaFolder','FaFolderOpen','FaFileLines','FaChartBar','FaPenToSquare','FaThumbtack','FaPaperclip','FaMoneyBill','FaClockRotateLeft','FaDiagramProject','FaListCheck'] },
  { label: 'TOOLS',            icons: ['FaToolbox','FaHammer','FaRuler','FaCompass','FaPaintRoller','FaBucket','FaRulerCombined','FaVial','FaFlask','FaMagnet'] },
  { label: 'TRANSPORT',        icons: ['FaCar','FaTruckMoving','FaBus','FaBicycle','FaHelicopter','FaTaxi','FaShip','FaPlane'] },
  { label: 'GENERAL',          icons: ['FaBox','FaBoxOpen','FaDatabase','FaServer','FaLaptop','FaPrint','FaWifi','FaSatelliteDish','FaFileInvoice','FaReceipt'] },
];

// ─── Material icon sections ────────────────────────────────────────────────────
export const MD_SECTIONS = [
  { label: 'FIRE & SAFETY',    icons: ['MdFireExtinguisher','MdFireTruck','MdNotificationsActive','MdWarning','MdSecurity','MdLocalPolice','MdLocalFireDepartment','MdEmergency'] },
  { label: 'HVAC & MECHANICAL',icons: ['MdBuild','MdSettings','MdThermostat','MdAir','MdHvac','MdConstruction','MdHandyman','MdPlumbing','MdCable','MdOutlet','MdSolarPower'] },
  { label: 'ELECTRICAL',       icons: ['MdElectricBolt','MdPower','MdBatteryFull','MdLightbulb','MdFlashOn','MdElectricalServices','MdOutlet','MdSolarPower'] },
  { label: 'PLUMBING',         icons: ['MdWater','MdWaterDrop','MdShower','MdBathtub','MdPlumbing','MdPool','MdInvertColors'] },
  { label: 'BUILDING',         icons: ['MdApartment','MdDomain','MdHome','MdHotel','MdLocalHospital','MdWarehouse','MdMeetingRoom','MdSchool','MdStore','MdVilla'] },
  { label: 'CLEANING',         icons: ['MdCleaningServices','MdDelete','MdRecycling','MdAutoAwesome','MdSpa','MdLocalLaundryService','MdDryCleaning'] },
  { label: 'MEDICAL',          icons: ['MdLocalHospital','MdMedicalServices','MdVaccines','MdMonitorHeart','MdMedication','MdHealthAndSafety','MdBiotech','MdBloodtype'] },
  { label: 'KITCHEN',          icons: ['MdRestaurant','MdKitchen','MdCoffeeMaker','MdMicrowave','MdOutdoorGrill','MdDinnerDining','MdLunchDining','MdRoomService'] },
  { label: 'SECURITY',         icons: ['MdLock','MdLockOpen','MdKey','MdCamera','MdVideocam','MdSecurityUpdateGood','MdVerifiedUser','MdFingerprint','MdFace'] },
  { label: 'OFFICE',           icons: ['MdAssignment','MdFolder','MdFolderOpen','MdInsertDriveFile','MdBarChart','MdEdit','MdAttachFile','MdBookmark','MdDashboard','MdEventNote'] },
  { label: 'TOOLS',            icons: ['MdBuildCircle','MdHomeRepairService','MdPrecisionManufacturing','MdEngineering','MdFormatPaint','MdAgriculture','MdCarpenter'] },
  { label: 'TRANSPORT',        icons: ['MdDirectionsCar','MdLocalShipping','MdDirectionsBus','MdPedalBike','MdAirportShuttle','MdFlight','MdTrain'] },
  { label: 'GENERAL',          icons: ['MdInventory','MdInventory2','MdStorefront','MdStorage','MdComputer','MdPrint','MdWifi','MdRouter','MdPhoneAndroid','MdQrCode2'] },
];

// ─── Bootstrap icon sections ───────────────────────────────────────────────────
export const BS_SECTIONS = [
  { label: 'FIRE & SAFETY',    icons: ['BsExclamationTriangle','BsExclamationCircle','BsBellFill','BsShieldFill','BsShieldCheck','BsAlarmFill','BsAlarm'] },
  { label: 'HVAC & MECHANICAL',icons: ['BsWrench','BsWrenchAdjustable','BsGear','BsGearFill','BsThermometer','BsThermometerHigh','BsFan','BsWind','BsSnow','BsCloudRain'] },
  { label: 'ELECTRICAL',       icons: ['BsLightningFill','BsPlugFill','BsBatteryFull','BsLightbulbFill','BsLightbulb','BsToggleOn'] },
  { label: 'PLUMBING',         icons: ['BsDropletFill','BsDroplet','BsWater'] },
  { label: 'BUILDING',         icons: ['BsBuilding','BsBuildingFill','BsHouseFill','BsHouse','BsDoorOpenFill','BsDoorClosedFill','BsHospitalFill','BsBank','BsBuildings','BsBuildingsFill'] },
  { label: 'CLEANING',         icons: ['BsTrashFill','BsTrash','BsRecycle','BsBrush','BsBrushFill','BsTrash2','BsTrash2Fill','BsStars'] },
  { label: 'MEDICAL',          icons: ['BsHeartFill','BsHeart','BsHeartPulse','BsCapsulePill','BsBandaid','BsBandaidFill','BsActivity','BsEmojiSmile'] },
  { label: 'KITCHEN',          icons: ['BsCupHotFill','BsCupHot','BsFire','BsUpc'] },
  { label: 'SECURITY',         icons: ['BsLockFill','BsLock','BsKeyFill','BsKey','BsCameraFill','BsCamera','BsEyeFill','BsShieldLock','BsFingerprint','BsPersonBadge','BsPersonBadgeFill'] },
  { label: 'OFFICE',           icons: ['BsClipboardFill','BsClipboard','BsFolderFill','BsFolder','BsFileText','BsBarChart','BsPenFill','BsPaperclip','BsBookmark','BsCalendar','BsCalendarFill','BsJournalText','BsSticky','BsStickyFill'] },
  { label: 'TOOLS',            icons: ['BsTools','BsHammer','BsPaintBucket','BsBucket','BsRulers','BsUpcScan'] },
  { label: 'TRANSPORT',        icons: ['BsCarFront','BsTruck','BsBusFront','BsBicycle','BsAirplane','BsAirplaneFill'] },
  { label: 'GENERAL',          icons: ['BsBoxFill','BsBox','BsArchiveFill','BsArchive','BsDatabaseFill','BsDatabase','BsLaptop','BsPrinter','BsWifi','BsRouter','BsHddFill','BsHdd','BsGlobe','BsGlobe2'] },
];


// ─── Supply Detail Panel ──────────────────────────────────────────────────────
function fmtSupplyDate(val: string | null | undefined): string {
  if (!val) return '—';
  const s = String(val).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'numeric', day: 'numeric', year: '2-digit'
  });
}

function SupplyDetailPanel({
  supply,
  onClose,
  onEdit,
  onDelete,
  onBom,
  onUsedIn,
}: {
  supply: FacilitySupply;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onBom: () => void;
  onUsedIn?: () => void;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const typeColor = getSupplyTypeColor(supply.supply_type);
  const typeLabel = SUPPLY_TYPES.find((t) => t.id === supply.supply_type)?.label ?? supply.supply_type;
  const conditionColor = supply.condition === 'excellent' ? 'var(--accent-success)' :
    supply.condition === 'good' ? 'var(--accent-primary)' :
    supply.condition === 'fair' ? 'var(--accent-warning)' : 'var(--accent-danger)';

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end' }}
      onClick={onClose}
    >
      <div
        style={{
          width: isMobile ? '100%' : 420, background: 'var(--bg-base)', borderLeft: '1px solid var(--border-subtle)',
          display: 'flex', flexDirection: 'column', overflowY: 'auto',
          boxShadow: '-4px 0 32px rgba(0,0,0,0.35)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ color: typeColor }}>{getSupplyTypeIcon(supply.supply_type, 20)}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>{supply.name}</div>
              <div style={{ fontSize: '0.75rem', color: typeColor, fontWeight: 600 }}>{typeLabel}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Icon area */}
        <div style={{
          background: `linear-gradient(135deg, ${typeColor}18, ${typeColor}08)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          padding: '20px 20px 4px', minHeight: 260,
        }}>
          {supply.image_url ? (
            <img
              src={supply.image_url}
              alt={supply.name}
              style={{
                width: '100%',
                maxWidth: 280,
                height: 220,
                objectFit: 'contain',
                borderRadius: 8,
                background: 'rgba(255,255,255,0.04)',
                display: 'block',
                margin: '0 auto 16px',
                cursor: 'zoom-in',
              }}
              onClick={() => setLightboxOpen(true)}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div style={{ color: typeColor, opacity: 0.3 }}>{getSupplyTypeIcon(supply.supply_type, 72)}</div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 8, flexShrink: 0, justifyContent: 'flex-start' }}>
          {supply.supply_type === 'paint' && onUsedIn ? (
            <button className="btn btn-primary btn-sm" onClick={onUsedIn} style={{ gap: 6 }}>
              <MapPin size={14} /> Used In
            </button>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={onBom} style={{ gap: 6 }}>
              <Network size={14} /> Parts / BOM
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onEdit} style={{ gap: 6 }}>
            <Edit size={14} /> Edit
          </button>
          <button className="btn btn-danger btn-sm" onClick={onDelete} style={{ gap: 6 }}>
            <Trash2 size={14} />
          </button>
        </div>

        {/* Details */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            { label: 'Condition', value: (
              <span style={{ color: conditionColor, fontWeight: 600 }}>
                {supply.condition.charAt(0).toUpperCase() + supply.condition.slice(1)}
              </span>
            )},
            supply.brand ? { label: 'Brand', value: supply.brand } : null,
            supply.model ? { label: 'Model', value: supply.model } : null,
            supply.serial_number ? { label: 'Serial #', value: supply.serial_number } : null,
            supply.location ? { label: 'Location', value: supply.location } : null,
            { label: 'Quantity', value: String(supply.quantity) },
            supply.last_service_date ? { label: 'Last Service', value: fmtSupplyDate(supply.last_service_date) } : null,
            supply.purchased_date ? { label: 'Purchased', value: fmtSupplyDate(supply.purchased_date) } : null,
            supply.color ? { label: 'Color', value: supply.color } : null,
            supply.finish ? { label: 'Finish', value: supply.finish } : null,
            supply.room_area ? { label: 'Room / Area', value: supply.room_area } : null,
            supply.barcode ? { label: 'Barcode', value: supply.barcode } : null,
          ].filter(Boolean).map((row) => row && (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{row.label}</span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', fontWeight: 500, textAlign: 'right', maxWidth: 220 }}>{row.value}</span>
            </div>
          ))}
          {supply.notes && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6 }}>Notes</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{supply.notes}</div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
          onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
        >
          <img
            src={supply.image_url ?? undefined}
            alt={supply.name}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, cursor: 'default' }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Supply Detail Takeover (Phase 1 — wheelchairs only) ──────────────────────────────────────
// Full-page takeover replacing SupplyDetailPanel's slide-in, wheelchairs only per explicit
// scoping — every other supply_type still renders SupplyDetailPanel unchanged at the call site.
// Structurally mirrors Tasks.tsx's TaskDetailPage (Compliance PM's takeover, the actual reference
// screenshot Chris pointed to): same portal/shell/zIndex/animation, same back-button pattern
// (btn btn-ghost btn-sm + ArrowLeft), same Escape-key handler (copied verbatim from
// TaskDetailPage's own useEffect). pageTakeoverRiseIn is defined in buttonAnimations.css, already
// imported at the top of this file (line 8) -- confirmed loaded, not assumed.
interface LearningHubForEquipmentEntry {
  id: number;
  type: 'book' | 'manual' | 'video' | 'link' | 'parts_image';
  title: string;
  file_url: string | null;
  external_link_url: string | null;
}

function getReportProxyUrl(url: string): string {
  const baseUrl = import.meta.env.VITE_API_URL ?? '';
  const token = localStorage.getItem('accessToken');
  return `${baseUrl}/scheduled-tasks/report-proxy?url=${encodeURIComponent(url)}&token=${token}`;
}

// ─── Ask AI (SupplyDetailTakeover) ─────────────────────────────────────────────────────────────
// Built on POST /inventory/equipment-chat, NOT learning-hub.ts's /chat -- that endpoint is
// deliberately document/manual-only (keyword search over learning_hub_entries), which is why it
// used to answer "I'm not able to browse the internet..." here. This panel needs a general
// assistant for one equipment record, so the backend mirrors vendors.ts's POST /vendors/ai-extract
// pattern instead: Claude's native web_search tool via the 'web-search-2025-03-05' beta header —
// genuine web search, not a document match. Same paid-per-search tool as vendor-extract, now live
// in a second place. Record context (brand/model/serial/facility/condition/notes/manuals/BOM) is
// assembled client-side below and sent as a plain string the server drops into its system prompt.
interface SupplyChatMessage { role: 'user' | 'assistant'; content: string; error?: boolean; }
async function sendSupplyAiChat(question: string, context: string, priorMessages: SupplyChatMessage[]): Promise<{ answer: string }> {
  const baseUrl = (import.meta.env.VITE_API_URL as string) || apiClient.defaults.baseURL || '/api';
  const token = localStorage.getItem('accessToken');
  const resp = await fetch(`${baseUrl}/inventory/equipment-chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      question,
      context,
      history: priorMessages.map((m) => ({ role: m.role, content: m.content })),
    }),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok || !body?.success) {
    throw new Error(body?.error || 'Chat request failed');
  }
  return { answer: body.answer };
}

// Shared label/value row — reused across the sticky-sidebar Condition & Service / Location cards
// and the Specifications card in SupplyDetailTakeover's Details tab.
function SRow({ icon, label, value }: { icon?: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border-subtle)', gap: 12 }}>
      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon}{label}
      </span>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

// ─── Ask AI message content — auto-linkify URLs ──────────────────────────────
// _mdToHtml (above, ~line 1159) doesn't do link detection at all -- it's a bold/italic/list
// converter for the RichTextEditor's product_description/notes fields, not a chat-message
// renderer, so there's nothing there to reuse. No other chat surface in the app (LearningHub's
// AiChatWidget included) does link detection either -- they all share this same plain
// `whiteSpace:'pre-wrap'` bubble with no break point for a long token. Scoped the fix to just
// this panel per the ask; AiChatWidget has the identical gap but is a separate component/file.
// Copy-button convention (icon + navigator.clipboard.writeText + "copied" color swap) matches
// the one already established in Settings.tsx's table-name copy button.
const URL_RE = /(https?:\/\/[^\s]+)/g;

function truncateUrl(url: string, max = 42): string {
  return url.length > max ? `${url.slice(0, max - 1)}…` : url;
}

function MessageLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  function copy(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, maxWidth: '100%', verticalAlign: 'bottom' }}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={url}
        style={{ color: '#60a5fa', textDecoration: 'underline', overflowWrap: 'break-word', wordBreak: 'break-all' }}
      >
        {truncateUrl(url)}
      </a>
      <button
        type="button"
        className="combo-jello-spin"
        data-tint="neutral"
        onMouseLeave={spinIconOut}
        onClick={copy}
        title="Copy link"
        style={{
          background: 'none', border: '1px solid var(--border-default)', borderRadius: 4,
          padding: '1px 4px', cursor: 'pointer', flexShrink: 0,
          color: copied ? 'var(--accent-success)' : 'var(--text-muted)',
          display: 'inline-flex', alignItems: 'center',
        }}
      >
        <Copy size={11} className="btn-icon" />
      </button>
    </span>
  );
}

// content.split(URL_RE) with a capturing group alternates plain-text/match/plain-text/match/...
// -- odd indices are always the captured URLs, so no need to re-test the (stateful, global) regex.
function renderMessageContent(content: string) {
  return (content ?? '').split(URL_RE).map((part, idx) =>
    idx % 2 === 1 ? <MessageLink key={idx} url={part} /> : <Fragment key={idx}>{part}</Fragment>
  );
}

function SupplyDetailTakeover({
  supply,
  allSupplies,
  isEnterprise,
  onClose,
  onEdit,
  onDelete,
}: {
  supply: FacilitySupply;
  allSupplies: FacilitySupply[];
  isEnterprise: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Separate from lightboxOpen above (that one's tied specifically to supply.image_url) -- a
  // parts_image entry's own thumbnail in the Manuals tab list, zoomable the same way.
  const [zoomedManualImage, setZoomedManualImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'manuals' | 'bom'>('details');
  const [uploadManualOpen, setUploadManualOpen] = useState(false);
  // Item 7 -- viewing a manual/parts-image entry's own details (equipment linking included) from
  // right here, reusing LearningHub.tsx's EntryDetailModal/EntryFormModal as-is rather than
  // building a second version of either. Previously this Manuals tab had no way to open that
  // modal at all -- equipment could only ever be linked to an entry from the Learning Hub page
  // itself, since only ITS OWN entry cards opened EntryDetailModal.
  const [viewEntryId, setViewEntryId] = useState<number | null>(null);
  const [editEntryTarget, setEditEntryTarget] = useState<LearningHubEntry | null>(null);
  const { user } = useAuth();
  const canUploadManual = ['admin', 'org_admin', 'manager'].includes(user?.role ?? '');
  const [askAiOpen, setAskAiOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState<SupplyChatMessage[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [aiSending, setAiSending] = useState(false);
  const aiScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (aiScrollRef.current) aiScrollRef.current.scrollTop = aiScrollRef.current.scrollHeight;
  }, [aiMessages, aiSending, askAiOpen]);

  async function handleAiSend() {
    const question = aiInput.trim();
    if (!question || aiSending) return;
    const priorMessages = aiMessages;
    setAiMessages((m) => [...m, { role: 'user', content: question }]);
    setAiInput('');
    setAiSending(true);
    try {
      const { answer } = await sendSupplyAiChat(question, aiEquipmentContext, priorMessages);
      setAiMessages((m) => [...m, { role: 'assistant', content: answer }]);
    } catch (e: unknown) {
      setAiMessages((m) => [...m, { role: 'assistant', content: e instanceof Error ? e.message : 'Something went wrong — please try again.', error: true }]);
    } finally {
      setAiSending(false);
    }
  }

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { facilityName } = useFacility();
  const typeColor = getSupplyTypeColor(supply.supply_type);
  const typeLabel = SUPPLY_TYPES.find((t) => t.id === supply.supply_type)?.label ?? supply.supply_type;
  const conditionColor = supply.condition === 'excellent' ? 'var(--accent-success)' :
    supply.condition === 'good' ? 'var(--accent-primary)' :
    supply.condition === 'fair' ? 'var(--accent-warning)' : 'var(--accent-danger)';
  const CONDITION_SCALE = ['excellent', 'good', 'fair', 'poor'];
  const CONDITION_SCALE_LABELS: Record<string, string> = { excellent: 'Excellent', good: 'Good', fair: 'Fair', poor: 'Poor' };
  const conditionRank = CONDITION_SCALE.indexOf(supply.condition);

  // Model-wide unit count for the Specifications "On Hand" tile — client-side count over
  // allSupplies (already the full unpaginated list for this supply_type the grid itself renders
  // from, per FacilitySuppliesTab's GET /inventory/supplies?type= query), not this one record's
  // own `quantity` column. No backend change needed.
  //
  // quantity is treated as 1 per matching row when null/unset, not 0 -- confirmed live that these
  // per-unit equipment rows (e.g. individually-tracked wheelchairs) are routinely stored with
  // quantity=NULL, one row per physical unit, rather than a single row with quantity=N. The
  // previous `s.quantity || 0` coerced every null row to 0, so summing 3 real matching rows (all
  // quantity=NULL) produced 0 instead of 3 -- not a brand/model matching bug (those matched
  // exactly, same facility/org), a wrong-default-for-null bug. An explicit quantity value (0 or
  // higher) is still respected as-is; only a genuinely missing value defaults to 1.
  const modelUnitsOnHand = supply.brand && supply.model
    ? allSupplies.filter((s) => s.brand === supply.brand && s.model === supply.model).reduce((sum, s) => sum + (s.quantity ?? 1), 0)
    : (supply.quantity ?? 1);

  // Reverse lookup: this supply's type/brand/model -> matching bom_equipment row(s) -> linked
  // Learning Hub entries. Only fetched once the Manuals tab is actually opened.
  const { data: manualsRes, isLoading: manualsLoading } = useQuery({
    queryKey: ['learning-hub-for-equipment', supply.supply_type, supply.brand, supply.model],
    queryFn: () => api.get<LearningHubForEquipmentEntry[]>('/learning-hub/for-equipment', {
      type: supply.supply_type,
      brand: supply.brand ?? undefined,
      model: supply.model ?? undefined,
    }),
    enabled: activeTab === 'manuals',
  });
  const manuals = manualsRes?.data ?? [];

  // Eager fetch (not gated on the BOM tab being open, unlike the query above) — the AI Insights
  // card on the Details tab needs a real parts-linked count, same queryKey BomPanel's own BOM
  // fetch uses so the two share one cached request once the BOM tab is actually opened.
  const { data: bomCountRes } = useQuery({
    queryKey: ['bom', String(supply.id)],
    queryFn: () => api.get(`/inventory/${supply.id}/bom`),
  });
  // GET /:id/bom returns two independent relationships (server: inventory.ts ~line 1719) --
  // `data` is the formal parts_relationships BOM tree (nested children), `usedForParts` is the
  // separate used_for-tag reverse match. The Parts/BOM tab's "Parts Tree" panel renders both
  // stacked together (BOMView.tsx ~547-572), so the count here must sum both too -- counting
  // only `data.length` (as this used to) ignores every "Linked via Used For" part and undercounts
  // whenever a piece of equipment has no formal BOM entries of its own, e.g. Wheelchair 055.
  function countBomTreeNodes(nodes: { children?: unknown[] }[]): number {
    return nodes.reduce((sum, n) => sum + 1 + countBomTreeNodes((n.children as { children?: unknown[] }[]) ?? []), 0);
  }
  const bomRes = bomCountRes as unknown as { data?: { children?: unknown[] }[]; usedForParts?: unknown[] } | undefined;
  const bomPartsCount = countBomTreeNodes(bomRes?.data ?? []) + (bomRes?.usedForParts?.length ?? 0);

  // Real record context for the Ask AI system prompt — brand/model/serial/facility/condition/
  // notes are always available; manuals only if the Manuals tab has already been opened this
  // session (its fetch stays lazy, not forced eager just to feed chat context); BOM count from
  // the eager fetch just above.
  const aiEquipmentContext = [
    `Name: ${supply.name}`,
    `Type: ${typeLabel}`,
    supply.brand ? `Brand: ${supply.brand}` : null,
    supply.model ? `Model: ${supply.model}` : null,
    supply.serial_number ? `Serial #: ${supply.serial_number}` : null,
    `Facility: ${facilityName || 'unknown'}`,
    `Condition: ${CONDITION_SCALE_LABELS[supply.condition] ?? supply.condition}`,
    supply.location ? `Location: ${supply.location}` : null,
    supply.last_service_date ? `Last serviced: ${fmtSupplyDate(supply.last_service_date)}` : 'No service history on record.',
    `${bomPartsCount} part${bomPartsCount === 1 ? '' : 's'} linked in BOM.`,
    manuals.length > 0 ? `Manuals on file: ${manuals.map((m) => m.title).join(', ')}` : null,
    supply.notes ? `Notes: ${supply.notes}` : null,
  ].filter(Boolean).join('\n');

  // Manufacturer URL — lives on bom_equipment (one row per type+brand+model), not on this
  // individual facility_supplies row, so setting it once covers every unit of the same model. See
  // migration_v123.sql for why.
  const qc = useQueryClient();

  // Notes thread (item 2) — timestamped, multi-entry, newest first (facility_supply_notes,
  // migration_v126.sql). Replaces the old static "No notes on file." single-value display.
  const supplyNotesKey = ['facility-supply-notes', supply.id];
  const { data: supplyNotesRes } = useQuery({
    queryKey: supplyNotesKey,
    queryFn: () => api.get<NoteEntry[]>(`/inventory/supplies/${supply.id}/notes`),
  });
  const supplyNotes = supplyNotesRes?.data ?? [];
  const [supplyNoteSaving, setSupplyNoteSaving] = useState(false);
  async function handleAddSupplyNote(content: string) {
    setSupplyNoteSaving(true);
    try {
      await api.post(`/inventory/supplies/${supply.id}/notes`, { content });
      await qc.invalidateQueries({ queryKey: supplyNotesKey });
    } finally {
      setSupplyNoteSaving(false);
    }
  }

  const [manufacturerUrlEditing, setManufacturerUrlEditing] = useState(false);
  const [manufacturerUrlDraft, setManufacturerUrlDraft] = useState('');
  const [manufacturerUrlSaving, setManufacturerUrlSaving] = useState(false);
  const manufacturerLookupKey = ['bom-equipment-lookup', supply.supply_type, supply.brand, supply.model];
  const { data: manufacturerLookupRes } = useQuery({
    queryKey: manufacturerLookupKey,
    queryFn: () => api.get<{ id: number; manufacturer_url: string | null } | null>('/inventory/bom-equipment/lookup', {
      type: supply.supply_type,
      brand: supply.brand ?? undefined,
      model: supply.model ?? undefined,
    }),
    enabled: !!(supply.brand || supply.model),
  });
  const manufacturerUrl = manufacturerLookupRes?.data?.manufacturer_url ?? null;

  async function saveManufacturerUrl() {
    const url = manufacturerUrlDraft.trim();
    setManufacturerUrlSaving(true);
    try {
      await api.patch('/inventory/bom-equipment/manufacturer-url', {
        equipment_type: supply.supply_type,
        display_name: typeLabel,
        brand: supply.brand ?? null,
        model: supply.model ?? null,
        manufacturer_url: url || null,
      });
      await qc.invalidateQueries({ queryKey: manufacturerLookupKey });
      setManufacturerUrlEditing(false);
    } catch { /* leave editing open on failure so the user can retry */ }
    setManufacturerUrlSaving(false);
  }

  // AI Insights — deterministic template logic over real fields only, no LLM call. "Next service
  // due" has no backing column on facility_supplies (only last_service_date exists), so this uses
  // last-serviced phrasing instead of a fabricated due-date/overdue calculation.
  const aiConditionConcern = supply.condition === 'fair' || supply.condition === 'poor';
  const aiSummary = [
    `${bomPartsCount} part${bomPartsCount === 1 ? '' : 's'} linked in BOM.`,
    supply.last_service_date ? `Last serviced ${fmtSupplyDate(supply.last_service_date)}.` : 'No service history on record.',
    aiConditionConcern ? `Condition is currently ${supply.condition} — consider scheduling an inspection.` : null,
    supply.notes ? 'Notes on file for this item.' : null,
  ].filter(Boolean).join(' ');
  const aiFlag = aiConditionConcern
    ? { label: `Condition: ${CONDITION_SCALE_LABELS[supply.condition] ?? supply.condition}`, color: '#fbbf24', bg: 'rgba(245,158,11,0.14)' }
    : !supply.last_service_date
    ? { label: 'No service history', color: '#fbbf24', bg: 'rgba(245,158,11,0.14)' }
    : { label: 'No issues flagged', color: '#34d399', bg: 'rgba(16,185,129,0.14)' };

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500, overflowY: 'auto', background: 'var(--bg-base)',
      animation: 'pageTakeoverRiseIn 0.3s cubic-bezier(0.25,1,0.35,1)',
    }}>
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '24px 30px 90px' }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ gap: 6 }}>
          <ArrowLeft size={14} /> Back to {typeLabel}
        </button>

        {/* Hero */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '14px 0 20px', flexWrap: 'wrap' }}>
          <div style={{ width: 58, height: 58, borderRadius: 15, background: `linear-gradient(135deg, ${typeColor}18, ${typeColor}08)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: typeColor }}>
            {getSupplyTypeIcon(supply.supply_type, 28)}
          </div>
          <div>
            <div style={{ fontSize: '1.5em', fontWeight: 700, color: 'var(--text-primary)' }}>{supply.name}</div>
            <div style={{ fontSize: '0.85em', color: typeColor, fontWeight: 600, marginTop: 2 }}>{typeLabel}</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
            {isEnterprise && (
              <button className="btn combo4-ai" onMouseLeave={spinIconOut} onClick={() => setAskAiOpen((o) => !o)} style={{ gap: 6 }}>
                <Sparkles size={14} className="btn-icon" /> Ask AI
              </button>
            )}
            <button className="btn btn-ghost combo1" onMouseLeave={spinIconOut} onClick={onEdit} style={{ gap: 6 }}>
              <Edit size={14} className="btn-icon" /> Edit
            </button>
            <button
              className="combo-jello-spin" data-tint="red" onMouseLeave={spinIconOut} title="Delete"
              onClick={onDelete}
              style={{ width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171' }}
            >
              <Trash2 size={14} className="btn-icon" />
            </button>
          </div>
        </div>

        {/* Tabs — icons only, no count badges: parts/manuals counts would both require fetching
            eagerly just to populate a badge before either tab is ever opened (manuals is
            deliberately lazy today; parts count is now fetched eagerly for AI Insights below, but
            that's a cost paid for real content, not worth also spending on a badge alone). */}
        <div className="pill-tab-container" style={{ marginBottom: 20, width: 'fit-content' }}>
          {([
            { id: 'details', label: 'Details', icon: <Package size={14} /> },
            { id: 'bom', label: 'Parts / BOM', icon: <Network size={14} /> },
            { id: 'manuals', label: 'Manuals', icon: <BookOpen size={14} /> },
          ] as { id: 'details' | 'manuals' | 'bom'; label: string; icon: React.ReactNode }[]).map((t) => (
            <button key={t.id} className={`pill-tab${activeTab === t.id ? ' active' : ''}`} onClick={() => setActiveTab(t.id)}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {activeTab === 'details' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>
            {/* Left — pinned in place: photo, Condition & Service, Location, Manufacturer */}
            <div style={{ position: 'sticky', top: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Photo — kept theme-tinted per CLAUDE_RULES.md's LOCKED background rules (no solid
                  opaque fills on large containers), not the reference mockup's literal white card. */}
              <div style={{
                background: `linear-gradient(135deg, ${typeColor}18, ${typeColor}08)`,
                borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: 250, padding: 20,
              }}>
                {supply.image_url ? (
                  <img
                    src={supply.image_url}
                    alt={supply.name}
                    style={{ width: '100%', maxWidth: 260, height: 200, objectFit: 'contain', borderRadius: 8, background: 'rgba(255,255,255,0.04)', cursor: 'zoom-in' }}
                    onClick={() => setLightboxOpen(true)}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div style={{ color: typeColor, opacity: 0.3 }}>{getSupplyTypeIcon(supply.supply_type, 88)}</div>
                )}
              </div>

              {/* Condition & Service */}
              <div className="card" style={{ padding: '18px 20px' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14 }}>Condition &amp; Service</div>
                <div style={{ display: 'flex', gap: 6, margin: '4px 0 8px' }}>
                  {CONDITION_SCALE.map((c, i) => (
                    <div key={c} style={{ flex: 1, height: 7, borderRadius: 99, background: i <= conditionRank ? conditionColor : 'rgba(255,255,255,0.08)' }} />
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                  {CONDITION_SCALE.map((c) => (
                    <span key={c} style={{ color: c === supply.condition ? conditionColor : undefined, fontWeight: c === supply.condition ? 700 : 400 }}>
                      {CONDITION_SCALE_LABELS[c]}
                    </span>
                  ))}
                </div>
                <SRow label="Last serviced" value={supply.last_service_date ? fmtSupplyDate(supply.last_service_date) : '—'} />
                <SRow label="Next service due" value="—" />
                <button className="btn btn-ghost combo1" onMouseLeave={spinIconOut} style={{ width: '100%', justifyContent: 'center', marginTop: 14, gap: 6 }}>
                  <Wrench size={14} className="btn-icon" /> Log service
                </button>
              </div>

              {/* Location */}
              <div className="card" style={{ padding: '18px 20px' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14 }}>Location</div>
                <SRow icon={<Building2 size={14} />} label="Facility" value={facilityName || '—'} />
                <SRow icon={<MapPin size={14} />} label="Unit" value={supply.location || '—'} />
                <SRow icon={<Calendar size={14} />} label="Added" value={fmtSupplyDate(supply.created_at)} />
              </div>

              {/* Manufacturer */}
              {(supply.brand || supply.model) && (
                <div className="card" style={{ padding: '18px 20px' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14 }}>Manufacturer</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: `${typeColor}24`, color: typeColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem', flexShrink: 0 }}>
                      {(supply.brand ?? supply.model ?? '??').slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{supply.brand || '—'}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{supply.model || '—'}</div>
                    </div>
                    <button
                      className="combo-jello-spin" data-tint="neutral" onMouseLeave={spinIconOut}
                      title={manufacturerUrl ? 'Open manufacturer website' : 'Add manufacturer website'}
                      onClick={() => { if (manufacturerUrl) { window.open(manufacturerUrl, '_blank', 'noopener,noreferrer'); } else { setManufacturerUrlDraft(''); setManufacturerUrlEditing(true); } }}
                      style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: manufacturerUrl ? 'var(--accent-primary)' : 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                      <Link2 size={14} className="btn-icon" />
                    </button>
                  </div>
                  {manufacturerUrlEditing && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
                      <input
                        autoFocus
                        value={manufacturerUrlDraft}
                        onChange={(e) => setManufacturerUrlDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveManufacturerUrl(); if (e.key === 'Escape') setManufacturerUrlEditing(false); }}
                        placeholder="https://manufacturer-website.com"
                        disabled={manufacturerUrlSaving}
                        style={{ flex: 1, padding: '7px 10px', background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 6, color: 'var(--text-primary)', fontSize: '0.82rem', outline: 'none' }}
                      />
                      <button className="btn btn-primary combo2 btn-sm" onMouseLeave={spinIconOut} disabled={manufacturerUrlSaving} onClick={saveManufacturerUrl}>
                        {manufacturerUrlSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn btn-ghost combo-cancel-jello btn-sm" onClick={() => setManufacturerUrlEditing(false)}>Cancel</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right — two sub-columns, independent scroll from the sticky left column */}
            <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, alignItems: 'start' }}>
              {/* Left sub-column: Specifications, Timeline */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div className="card" style={{ padding: '18px 20px' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14 }}>Specifications</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
                      <div style={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Condition</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: '0.95rem', marginTop: 5, color: conditionColor }}>{CONDITION_SCALE_LABELS[supply.condition] ?? supply.condition}</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
                      <div style={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Brand</div>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', marginTop: 5 }}>{supply.brand || '—'}</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
                      <div style={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>On Hand</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: '0.95rem', marginTop: 5 }}>{modelUnitsOnHand}</div>
                    </div>
                  </div>
                  <SRow label="Model" value={supply.model || '—'} />
                  <SRow label="Serial #" value={<span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{supply.serial_number || '—'}</span>} />
                </div>

                {/* Timeline — real fields only (created_at / last_service_date); no fabricated
                    history events for things this app doesn't actually log. */}
                <div className="card" style={{ padding: '18px 20px' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14 }}>Timeline</div>
                  {[
                    supply.last_service_date ? { color: '#34d399', title: 'Last serviced', date: fmtSupplyDate(supply.last_service_date) } : null,
                    { color: '#3b82f6', title: `Condition set: ${CONDITION_SCALE_LABELS[supply.condition] ?? supply.condition}`, date: fmtSupplyDate(supply.updated_at) },
                    { color: 'var(--text-muted)', title: 'Asset created', date: fmtSupplyDate(supply.created_at) },
                  ].filter(Boolean).map((ev, i, arr) => ev && (
                    <div key={ev.title} style={{ position: 'relative', paddingLeft: 22, paddingBottom: i === arr.length - 1 ? 0 : 18 }}>
                      {i < arr.length - 1 && <div style={{ position: 'absolute', left: 4, top: 14, bottom: 0, width: 2, background: 'var(--border-default)' }} />}
                      <div style={{ position: 'absolute', left: 0, top: 3, width: 10, height: 10, borderRadius: 99, background: ev.color, boxShadow: '0 0 0 3px var(--bg-base)' }} />
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {ev.title}
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: 'var(--text-muted)', fontWeight: 500 }}>{ev.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right sub-column: AI Insights, Notes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{
                  background: 'linear-gradient(160deg, rgba(139,92,246,0.10), rgba(59,130,246,0.05))',
                  border: '1px solid rgba(139,92,246,0.28)', borderRadius: 'var(--radius-lg)', backdropFilter: 'blur(14px)', padding: '18px 20px',
                }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#a78bfa', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Sparkles size={14} /> AI Insights
                  </div>
                  <div style={{ fontWeight: 700, color: '#c4b5fd', marginBottom: 8, fontSize: '0.9rem' }}>Asset health — {supply.model || supply.name}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.55 }}>{aiSummary}</div>
                  <div style={{ marginTop: 14 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', fontWeight: 700, padding: '4px 11px', borderRadius: 99, background: aiFlag.bg, color: aiFlag.color }}>
                      {aiFlag.label}
                    </span>
                  </div>
                </div>

                <div className="card" style={{ padding: '18px 20px' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>Notes</div>
                  <NotesThread notes={supplyNotes} onAdd={handleAddSupplyNote} saving={supplyNoteSaving} />
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'bom' ? (
          // Parts / BOM tab — folded in from the standalone /inventory/:id/bom route (BOMView.tsx),
          // which still exists for any deep links but is no longer where this action navigates.
          <div style={{ height: 'calc(100vh - 220px)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <BomPanel supplyId={supply.id} />
          </div>
        ) : (
          // Manuals tab
          <div>
            {manualsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>Loading…</div>
            ) : manuals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                No manuals linked to this {supply.brand ? `${supply.brand} ` : ''}{typeLabel.toLowerCase()}{supply.model ? ` ${supply.model}` : ''} yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 480, marginBottom: 14 }}>
                {manuals.map((m) => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
                    {m.type === 'parts_image' && m.file_url && (
                      <img
                        src={m.file_url}
                        alt=""
                        onClick={() => setZoomedManualImage(m.file_url)}
                        style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', flexShrink: 0 }}
                      />
                    )}
                    {m.type === 'book' ? <BookOpen size={16} style={{ color: '#60a5fa', flexShrink: 0 }} />
                      : m.type === 'link' ? <ExternalLink size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      : m.type === 'parts_image' ? <ImageIcon size={16} style={{ color: '#c084fc', flexShrink: 0 }} />
                      : <FileText size={16} style={{ color: '#34d399', flexShrink: 0 }} />}
                    <span style={{ flex: 1, fontSize: '0.875rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</span>
                    {/* type-specific action -- was always PdfViewerButton regardless of type,
                        which meant a 'link' entry's external URL got wrongly routed through the
                        PDF report-proxy here. parts_image needs BOTH a zoom (the thumbnail above)
                        and this separate "Open link" to the real external parts page. */}
                    {m.type === 'link' && m.file_url && (
                      <a href={m.file_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>
                        <ExternalLink size={12} /> Open
                      </a>
                    )}
                    {m.type === 'parts_image' && m.external_link_url && (
                      <a href={m.external_link_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>
                        <ExternalLink size={12} /> Open link
                      </a>
                    )}
                    {m.type !== 'link' && m.type !== 'parts_image' && m.type !== 'video' && m.file_url && (
                      <PdfViewerButton
                        pdfUrl={`${getReportProxyUrl(m.file_url)}&filename=${encodeURIComponent(m.title)}`}
                        label="Open"
                        triggerClassName="btn btn-ghost btn-sm"
                      />
                    )}
                    {/* Item 7 -- opens the same detail modal Learning Hub's own entry cards use,
                        so equipment can be linked (or a manual/parts image edited/deleted) from
                        right here instead of only from the Learning Hub page. */}
                    <button
                      type="button"
                      onClick={() => setViewEntryId(m.id)}
                      className="btn btn-ghost btn-sm"
                      title="View details / link equipment"
                      style={{ padding: '2px 8px' }}
                    >
                      <Link2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {canUploadManual && (
              <button
                className="btn btn-primary combo2"
                onMouseLeave={spinIconOut}
                onClick={() => setUploadManualOpen(true)}
              >
                <Upload size={14} className="btn-icon" /> Upload manual
              </button>
            )}
            {uploadManualOpen && (
              <EntryFormModal
                mode="create"
                equipmentScope={{
                  type: supply.supply_type,
                  brand: supply.brand ?? null,
                  model: supply.model ?? null,
                  typeLabel,
                }}
                onClose={() => setUploadManualOpen(false)}
                onSaved={() => {
                  setUploadManualOpen(false);
                  qc.invalidateQueries({ queryKey: ['learning-hub-for-equipment', supply.supply_type, supply.brand, supply.model] });
                  // Item 9 fix -- this only invalidated the equipment-scoped query this Manuals
                  // tab itself reads (correct, and unaffected here), but never touched Learning
                  // Hub's own query keys (['learning-hub', ...] the main list, ['learning-hub-
                  // folders', ...] its folder browser), so an entry added here showed up in this
                  // tab immediately but not on the actual Learning Hub page until a hard refresh.
                  // ['learning-hub'] alone invalidates every query whose key starts with that
                  // string (React Query's default partial-match behavior on a prefix key) --
                  // covers the main list without needing every [type, search] combination.
                  qc.invalidateQueries({ queryKey: ['learning-hub'] });
                  qc.invalidateQueries({ queryKey: ['learning-hub-folders'] });
                }}
              />
            )}
            {viewEntryId !== null && (
              <EntryDetailModal
                entryId={viewEntryId}
                canManage={canUploadManual}
                onClose={() => setViewEntryId(null)}
                onEdit={(entry) => { setViewEntryId(null); setEditEntryTarget(entry); }}
                onDeleted={() => {
                  setViewEntryId(null);
                  qc.invalidateQueries({ queryKey: ['learning-hub-for-equipment', supply.supply_type, supply.brand, supply.model] });
                  qc.invalidateQueries({ queryKey: ['learning-hub'] });
                  qc.invalidateQueries({ queryKey: ['learning-hub-folders'] });
                }}
                // Manuals/parts-images are the entries reachable from here -- video playback
                // isn't a realistic path from this tab, so this is a plain fallback (open in a
                // new tab) rather than building a full video lightbox just for this edge case.
                onPlayVideo={(entry) => { if (entry.youtube_video_id) window.open(`https://www.youtube.com/watch?v=${entry.youtube_video_id}`, '_blank'); }}
              />
            )}
            {editEntryTarget && (
              <EntryFormModal
                mode="edit"
                initial={editEntryTarget}
                onClose={() => setEditEntryTarget(null)}
                onSaved={() => {
                  setEditEntryTarget(null);
                  qc.invalidateQueries({ queryKey: ['learning-hub-for-equipment', supply.supply_type, supply.brand, supply.model] });
                  qc.invalidateQueries({ queryKey: ['learning-hub'] });
                  qc.invalidateQueries({ queryKey: ['learning-hub-folders'] });
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
          onClick={() => setLightboxOpen(false)}
        >
          <img
            src={supply.image_url ?? undefined}
            alt={supply.name}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, cursor: 'default' }}
          />
        </div>
      )}

      {/* Parts-image lightbox — Manuals tab thumbnails, separate state from the supply's own
          photo lightbox above (that one is tied specifically to supply.image_url). */}
      {zoomedManualImage && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
          onClick={() => setZoomedManualImage(null)}
        >
          <img
            src={zoomedManualImage}
            alt=""
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, cursor: 'default' }}
          />
        </div>
      )}

      {/* Ask AI panel — same panel look/feel as LearningHub.tsx's AiChatWidget (fixed bottom-right,
          var(--bg-elevated) card), triggered from the hero button here instead of a floating
          bubble. Hits the same POST /learning-hub/chat endpoint, unchanged server-side. */}
      {askAiOpen && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 1000,
          width: 360, maxWidth: '90vw', maxHeight: 480, borderRadius: 16,
          background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <Sparkles size={15} style={{ color: '#a78bfa' }} />
            <span style={{ fontWeight: 700, fontSize: '0.85rem', flex: 1 }}>Ask AI — {supply.name}</span>
            <button onClick={() => setAskAiOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2, display: 'flex' }}>
              <X size={16} />
            </button>
          </div>
          <div ref={aiScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {aiMessages.length === 0 && (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Ask about this {supply.brand ? `${supply.brand} ` : ''}{supply.model || typeLabel} — service, parts, manuals on file, or general questions.
              </div>
            )}
            {aiMessages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%', padding: '9px 12px', borderRadius: 12,
                background: m.role === 'user' ? 'rgba(59,130,246,0.16)' : m.error ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.05)',
                border: m.error ? '1px solid rgba(239,68,68,0.3)' : 'none',
                fontSize: '0.83rem', color: m.error ? '#f87171' : 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
              }}>
                {renderMessageContent(m.content)}
              </div>
            ))}
            {aiSending && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Thinking…</div>}
          </div>
          <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, flexShrink: 0 }}>
            <input
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAiSend(); }}
              placeholder="Ask a question…"
              disabled={aiSending}
              style={{ flex: 1, padding: '8px 10px', background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', fontSize: '0.82rem', outline: 'none' }}
            />
            <button
              className="combo-jello-spin" data-tint="purple" onMouseLeave={spinIconOut}
              onClick={handleAiSend} disabled={!aiInput.trim() || aiSending} title="Send"
              style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid rgba(139,92,246,0.4)', background: 'transparent', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: aiInput.trim() && !aiSending ? 'pointer' : 'default', opacity: aiInput.trim() && !aiSending ? 1 : 0.5, flexShrink: 0 }}
            >
              <Send size={15} className="btn-icon" />
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

// ─── Supply Card ──────────────────────────────────────────────────────────────
// onEdit/onDelete kept as props -- still supplied by the parent grid (delete in particular is
// still needed for the batch-delete flow, which calls the same per-id delete path) -- just no
// longer rendered as hover-overlay buttons on the card itself (item 4: Edit/Delete now live only
// inside the detail page). selected/onToggleSelect are new (item 5's Ctrl+Click multi-select).
function SupplyCard({ supply, onView, onEdit, onDelete, id, selected, onToggleSelect }: {
  supply: FacilitySupply; onView: () => void; onEdit: () => void; onDelete: () => void; id?: string;
  selected?: boolean; onToggleSelect?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const typeColor = getSupplyTypeColor(supply.supply_type);

  return (
    <div
      id={id}
      style={{
        background: selected ? 'rgba(59,130,246,0.10)' : hovered ? 'var(--bg-elevated)' : 'var(--bg-surface)',
        border: selected ? '1px solid var(--accent-primary)' : '1px solid transparent',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'background var(--transition), border-color var(--transition)',
        display: 'flex',
        flexDirection: 'column',
        width: isMobile ? '100%' : undefined,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => {
        // Ctrl/Cmd+Click toggles multi-select instead of opening the detail view -- same
        // modifier-click convention as file managers/most multi-select grids.
        if ((e.ctrlKey || e.metaKey) && onToggleSelect) onToggleSelect();
        else onView();
      }}
    >
      {/* Icon area */}
      <div style={{
        position: 'relative',
        height: 150,
        background: `linear-gradient(135deg, ${typeColor}18, ${typeColor}08)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {supply.image_url ? (
          <img
            src={supply.image_url}
            alt={supply.name}
            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div style={{ color: typeColor, opacity: 0.4 }}>
            {getSupplyTypeIcon(supply.supply_type, 48)}
          </div>
        )}

        {/* Condition badge */}
        <span className={`badge ${supply.condition === 'excellent' ? 'badge-green' : supply.condition === 'good' ? 'badge-blue' : supply.condition === 'fair' ? 'badge-amber' : 'badge-red'}`} style={{ position: 'absolute', bottom: 8, left: 8 }}>
          {supply.condition}
        </span>

        {/* Selection checkbox (item 5) -- visible on hover, or always once selected. Replaces the
            old Edit/Delete hover overlay (item 4 -- those actions now live inside the detail
            page only, Delete especially since it's destructive and doesn't belong one click away
            on a card). Click still Ctrl/Cmd-toggles via the card's own onClick handler above;
            this checkbox is a redundant, more discoverable target for the same action, and
            stopPropagation isn't needed on it since toggling here should behave identically to a
            Ctrl+Click on the card (not open the detail view either way). */}
        {(hovered || selected) && onToggleSelect && (
          <div
            onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
            style={{
              position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              background: selected ? 'var(--accent-primary)' : 'rgba(0,0,0,0.5)',
              border: selected ? 'none' : '1.5px solid rgba(255,255,255,0.6)',
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
            title={selected ? 'Deselect' : 'Select'}
          >
            {selected && <Check size={13} color="#fff" strokeWidth={3} />}
          </div>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.3 }}>
          {supply.name}
        </div>
        <div style={{ fontSize: '0.75rem', color: typeColor, fontWeight: 500 }}>
          {SUPPLY_TYPES.find((t) => t.id === supply.supply_type)?.label ?? supply.supply_type}
        </div>
        {(supply.brand || supply.model) && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {[supply.brand, supply.model].filter(Boolean).join(' / ')}
          </div>
        )}
        <div style={{
          marginTop: 'auto', display: 'flex', alignItems: 'center', paddingTop: 8,
          // Paint keeps its original Qty-left/location-right split; every other type here has no
          // Qty anymore, so the chip is the only thing in this row and centers on its own.
          justifyContent: supply.supply_type === 'paint' ? 'space-between' : 'center',
        }}>
          {/* Qty removed for individually-tracked equipment (bed/wheelchair/lift/other -- one
              card is one physical unit, a bulk count doesn't apply) -- confirmed Paint is the
              only genuinely bulk/stocked-quantity supply_type of the ones this card renders
              (concentrators/hvac/extinguishers have their own separate management screens, not
              this shared card, so they were never in scope here either way). Still shown for
              paint. Location moved from a right-aligned, hard-truncated-at-80px corner label to
              a chip/pill that sizes to its own text and only THEN truncates if it's still too
              wide for the card -- was clipping past the card's own edge before on a long
              location string since maxWidth:80 was a fixed pixel cap, not relative to the card's
              actual width. */}
          {supply.supply_type === 'paint' && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Qty: {supply.quantity}</span>
          )}
          {supply.location && (
            <span
              title={supply.location}
              style={{
                fontSize: '0.7rem', fontWeight: 500, color: 'var(--text-secondary)',
                background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-subtle)',
                borderRadius: 999, padding: '3px 10px',
                maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                boxSizing: 'border-box',
              }}
            >
              {supply.location}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── HVAC Types ───────────────────────────────────────────────────────────────
// ─── Concentrators ────────────────────────────────────────────────────────────
interface Concentrator {
  id: number;
  facility_id: number;
  model: string | null;
  manufacturer: string | null;
  serial_number: string | null;
  inventory_tag: string | null;
  flow_rate: string | null;
  purchase_date: string | null;
  filter_change_date: string | null;
  next_filter_change_date: string | null;
  sent_for_service: boolean;
  sent_for_service_date: string | null;
  returned_from_service: boolean;
  returned_date: string | null;
  condition: string;
  notes: string | null;
  image_url: string | null;
  created_at: string;
  updated_at: string;
}

const CONC_CONDITION_COLORS: Record<string, string> = {
  excellent: 'var(--accent-success)', good: 'var(--accent-primary)', fair: 'var(--accent-warning)', poor: 'var(--accent-danger)', needs_service: 'var(--accent-danger)',
};

function blankConcentrator(): Partial<Concentrator> {
  return {
    model: '', manufacturer: '', serial_number: '', inventory_tag: '',
    flow_rate: '', purchase_date: '', filter_change_date: '', next_filter_change_date: '',
    sent_for_service: false, sent_for_service_date: '', returned_from_service: false, returned_date: '',
    condition: 'good', notes: '',
  };
}

interface ConcentratorServiceRecord {
  id: number;
  concentrator_id: number;
  facility_id: number;
  sent_date: string | null;
  returned_date: string | null;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

// ─── Concentrators Tab ────────────────────────────────────────────────────────
function ConcentratorsTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = ['admin', 'org_admin', 'manager'].includes(user?.role ?? '');
  const isViewer = user?.role === 'viewer';

  const { toast, showToast } = useToast();
  function demoBlock(action: () => void) {
    if (isViewer) { showToast('This action is not available in demo mode'); return; }
    action();
  }

  const { data: concRes, isLoading } = useQuery({
    queryKey: ['concentrators'],
    queryFn: () => api.get<Concentrator[]>('/concentrators'),
  });
  const [concentrators, setConcentrators] = useState<Concentrator[]>([]);
  useEffect(() => {
    const data: Concentrator[] = (concRes as any)?.data ?? [];
    setConcentrators(data);
  }, [concRes]);

  // ── Add/Edit modal ──────────────────────────────────────────────────────────
  const blankForm = () => ({
    manufacturer: '', model: '', serial_number: '', inventory_tag: '',
    flow_rate: '', condition: 'good',
    filter_change_date: '', next_filter_change_date: '',
    sent_for_service: false, sent_for_service_date: '',
    returned_from_service: false, returned_date: '',
    notes: '',
  });
  const [modal, setModal] = useState<{ open: boolean; editing: Concentrator | null }>({ open: false, editing: null });
  const [form, setForm] = useState(blankForm());

  // Brand→model filtering for concentrators
  const [concBrandId, setConcBrandId] = useState<number | null>(null);
  const { data: concBrandsData = [] } = useDropdownOptions('concentrator_brand');
  useEffect(() => {
    if (!concBrandsData.length || !form.manufacturer) { setConcBrandId(null); return; }
    const match = (concBrandsData as DropdownOption[]).find((b) => b.label === form.manufacturer);
    setConcBrandId(match?.id ?? null);
  }, [form.manufacturer, concBrandsData]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving] = useState(false);
  const concModalMouseDown = useRef<EventTarget | null>(null);

  const [editingCondition, setEditingCondition] = useState<number | null>(null);
  const [editingFilterDate, setEditingFilterDate] = useState<number | null>(null);
  const [filterDateValue, setFilterDateValue] = useState<string>('');
  const [sendingService, setSendingService] = useState<number | null>(null);
  const [sendServiceDate, setSendServiceDate] = useState<string>('');
  const [returningService, setReturningService] = useState<number | null>(null);
  const [returnServiceDate, setReturnServiceDate] = useState<string>('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Service history state ───────────────────────────────────────────────────
  const [serviceHistory, setServiceHistory] = useState<ConcentratorServiceRecord[] | null>(null);
  const [svcHistoryOpen, setSvcHistoryOpen] = useState(false);
  const [svcSendOpen, setSvcSendOpen] = useState(false);
  const [svcSendDate, setSvcSendDate] = useState('');
  const [svcSendNotes, setSvcSendNotes] = useState('');
  const [svcReturnOpen, setSvcReturnOpen] = useState(false);
  const [svcReturnDate, setSvcReturnDate] = useState('');
  const [svcReturnNotes, setSvcReturnNotes] = useState('');
  const [svcSubmitting, setSvcSubmitting] = useState(false);

  function openAdd() {
    setForm(blankForm());
    setModal({ open: true, editing: null });
    document.body.style.overflow = 'hidden';
  }
  function openEdit(c: Concentrator) {
    setForm({
      manufacturer: c.manufacturer ?? '',
      model: c.model ?? '',
      serial_number: c.serial_number ?? '',
      inventory_tag: c.inventory_tag ?? '',
      flow_rate: c.flow_rate ?? '',
      condition: c.condition ?? 'good',
      filter_change_date: c.filter_change_date ? c.filter_change_date.slice(0, 10) : '',
      next_filter_change_date: c.next_filter_change_date ? c.next_filter_change_date.slice(0, 10) : '',
      sent_for_service: c.sent_for_service ?? false,
      sent_for_service_date: c.sent_for_service_date ? c.sent_for_service_date.slice(0, 10) : '',
      returned_from_service: c.returned_from_service ?? false,
      returned_date: c.returned_date ? c.returned_date.slice(0, 10) : '',
      notes: c.notes ?? '',
    });
    setModal({ open: true, editing: c });
    document.body.style.overflow = 'hidden';
  }
  function closeModal() { setModal({ open: false, editing: null }); document.body.style.overflow = ''; }

  async function handleSave(ev: React.FormEvent) {
    ev.preventDefault();
    setSaving(true);
    try {
      const body = {
        manufacturer: form.manufacturer || null,
        model: form.model || null,
        serial_number: form.serial_number || null,
        inventory_tag: form.inventory_tag || null,
        flow_rate: form.flow_rate || null,
        condition: form.condition || 'good',
        filter_change_date: form.filter_change_date || null,
        next_filter_change_date: form.next_filter_change_date || null,
        sent_for_service: form.sent_for_service,
        sent_for_service_date: form.sent_for_service ? (form.sent_for_service_date || null) : null,
        returned_from_service: form.returned_from_service,
        returned_date: form.returned_from_service ? (form.returned_date || null) : null,
        notes: form.notes || null,
      };
      if (modal.editing) {
        await api.put(`/concentrators/${modal.editing.id}`, body);
        showToast('Concentrator updated');
      } else {
        await api.post('/concentrators', body);
        showToast('Concentrator added');
      }
      qc.invalidateQueries({ queryKey: ['concentrators'] });
      closeModal();
    } catch (err: any) { showToast(err.message ?? 'Save failed'); }
    finally { setSaving(false); }
  }

  // ── Service history loading + handlers ──────────────────────────────────────
  useEffect(() => {
    setSvcSendOpen(false);
    setSvcReturnOpen(false);
    setSvcHistoryOpen(false);
    if (!modal.editing) { setServiceHistory(null); return; }
    api.get(`/concentrators/${modal.editing.id}/service-history`).then((res: any) => {
      setServiceHistory(res.data ?? []);
    }).catch(() => setServiceHistory([]));
  }, [modal.editing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSvcSend() {
    if (!modal.editing) return;
    setSvcSubmitting(true);
    try {
      const res: any = await api.post(`/concentrators/${modal.editing.id}/service-history`, {
        sent_date: svcSendDate || null,
        notes: svcSendNotes || null,
      });
      const newRecord: ConcentratorServiceRecord = res.data;
      setServiceHistory(prev => [newRecord, ...(prev ?? [])]);
      setModal(m => m.editing ? { ...m, editing: { ...m.editing, sent_for_service: true, sent_for_service_date: svcSendDate, returned_from_service: false, returned_date: null } } : m);
      setForm(f => ({ ...f, sent_for_service: true, sent_for_service_date: svcSendDate, returned_from_service: false, returned_date: '' }));
      setSvcSendOpen(false);
      qc.invalidateQueries({ queryKey: ['concentrators'] });
      showToast('Sent for service recorded');
    } catch {
      showToast('Failed to record service');
    } finally {
      setSvcSubmitting(false);
    }
  }

  async function handleSvcReturn() {
    if (!modal.editing) return;
    const latestId = serviceHistory?.[0]?.id;
    if (!latestId) { showToast('No service record found'); return; }
    setSvcSubmitting(true);
    try {
      const res: any = await api.patch(`/concentrators/${modal.editing.id}/service-history/${latestId}/return`, {
        returned_date: svcReturnDate || null,
        notes: svcReturnNotes || null,
      });
      const updated: ConcentratorServiceRecord = res.data;
      setServiceHistory(prev => prev ? prev.map(r => r.id === latestId ? updated : r) : [updated]);
      setModal(m => m.editing ? { ...m, editing: { ...m.editing, returned_from_service: true, returned_date: svcReturnDate } } : m);
      setForm(f => ({ ...f, returned_from_service: true, returned_date: svcReturnDate }));
      setSvcReturnOpen(false);
      qc.invalidateQueries({ queryKey: ['concentrators'] });
      showToast('Return from service recorded');
    } catch {
      showToast('Failed to record return');
    } finally {
      setSvcSubmitting(false);
    }
  }

  // ── Delete modal ────────────────────────────────────────────────────────────
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/concentrators/${deleteId}`);
      qc.invalidateQueries({ queryKey: ['concentrators'] });
      setDeleteId(null);
      showToast('Concentrator deleted');
    } catch { showToast('Delete failed'); }
    finally { setDeleting(false); }
  }

  const handleFilterDateSave = async (id: number, date: string) => {
    await api.patch(`/concentrators/${id}/quick-update`, { filter_change_date: date });
    const next = new Date(date + 'T12:00:00Z');
    next.setUTCFullYear(next.getUTCFullYear() + 1);
    const nextDate = next.toISOString().slice(0, 10);
    setConcentrators(prev => prev.map(c =>
      c.id === id ? { ...c, filter_change_date: date, next_filter_change_date: nextDate } : c
    ));
    setEditingFilterDate(null);
  };

  return (
    <div style={{ background: 'transparent' }}>
      {/* Toast */}
      <Toast toast={toast} />

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700 }}>Concentrators</h3>
        {(canEdit || isViewer) && (
          <button className="btn btn-primary" onClick={() => demoBlock(openAdd)}>
            <Plus size={14} /> Add Concentrator
          </button>
        )}
      </div>

      {/* Filter change notification banner */}
      {(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueSoon = concentrators.filter(c => {
          if (!c.next_filter_change_date) return false;
          const d = Math.floor((new Date(c.next_filter_change_date.slice(0, 10) + 'T12:00:00Z').getTime() - today.getTime()) / 86400000);
          return d <= 30;
        });
        if (dueSoon.length === 0) return null;
        const overdueCount = dueSoon.filter(c => {
          const d = Math.floor((new Date(c.next_filter_change_date!.slice(0, 10) + 'T12:00:00Z').getTime() - today.getTime()) / 86400000);
          return d < 0;
        }).length;
        const color = overdueCount > 0 ? 'var(--accent-danger)' : 'var(--accent-warning)';
        const bg = overdueCount > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)';
        const border = overdueCount > 0 ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)';
        const icon = overdueCount > 0 ? '🔴' : '⚠️';
        const msg = overdueCount > 0
          ? `${overdueCount} concentrator${overdueCount > 1 ? 's are' : ' is'} overdue for a filter change.`
          : `${dueSoon.length} concentrator${dueSoon.length > 1 ? 's have' : ' has'} a filter change due within 30 days.`;
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', marginBottom: 12,
            background: bg, border: `1px solid ${border}`,
            borderRadius: 8, fontSize: 13, color,
          }}>
            <span>{icon}</span>
            <span>{msg}</span>
          </div>
        );
      })()}

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <div className="loading-spinner" />
        </div>
      ) : concentrators.length === 0 ? (
        <div style={{
          background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(8px)', borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.08)',
          textAlign: 'center', padding: '40px 24px', color: 'var(--text-muted)',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>🫁</div>
          <p style={{ margin: 0 }}>No concentrators added yet.</p>
        </div>
      ) : (
        <div style={{
          background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(8px)', borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {[
                    { label: 'Manufacturer',   align: 'left' as const },
                    { label: 'Model',           align: 'left' as const },
                    { label: 'Serial #',        align: 'left' as const },
                    { label: 'Tag',             align: 'left' as const },
                    { label: 'Flow Rate',       align: 'left' as const },
                    { label: 'Condition',       align: 'left' as const },
                    { label: 'Filter Change',   align: 'left' as const },
                    { label: 'Next Change',     align: 'left' as const },
                    { label: 'Service',         align: 'left' as const },
                    { label: 'Actions',         align: 'right' as const },
                  ].map(({ label, align }) => (
                    <th key={label} style={{
                      padding: '10px 16px', textAlign: align,
                      fontSize: '0.75rem', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                      color: 'var(--text-muted)', whiteSpace: 'nowrap',
                    }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {concentrators.map(c => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const filterDaysUntil = c.next_filter_change_date
                    ? Math.floor((new Date(c.next_filter_change_date.slice(0, 10) + 'T12:00:00Z').getTime() - today.getTime()) / 86400000)
                    : null;
                  const filterOverdue = filterDaysUntil !== null && filterDaysUntil < 0;
                  const filterDueSoon = filterDaysUntil !== null && filterDaysUntil >= 0 && filterDaysUntil <= 30;
                  const filterWarning = filterOverdue || filterDueSoon;
                  const nextFilterColor = filterDaysUntil === null ? 'var(--text-secondary)' : filterDaysUntil < 0 ? 'var(--accent-danger)' : filterDaysUntil <= 30 ? 'var(--accent-warning)' : 'var(--text-primary)';
                  const baseBg = filterOverdue ? 'rgba(239,68,68,0.04)' : filterDueSoon ? 'rgba(245,158,11,0.04)' : 'transparent';
                  const hoverBg = filterOverdue ? 'rgba(239,68,68,0.08)' : filterDueSoon ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.02)';
                  return (
                    <tr key={c.id}
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        background: baseBg,
                        transition: 'background 0.1s',
                        borderLeft: filterOverdue ? '3px solid var(--accent-danger)' : filterDueSoon ? '3px solid var(--accent-warning)' : undefined,
                      }}
                      onMouseEnter={ev => (ev.currentTarget.style.background = hoverBg)}
                      onMouseLeave={ev => (ev.currentTarget.style.background = baseBg)}
                    >
                      {/* Manufacturer */}
                      <td style={{ padding: '14px 16px', minWidth: 140 }}>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                          {c.manufacturer ?? '—'}
                        </div>
                      </td>
                      {/* Model */}
                      <td style={{ padding: '14px 16px', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        {c.model ?? '—'}
                      </td>
                      {/* Serial # */}
                      <td style={{ padding: '14px 16px', fontFamily: 'monospace', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                        {c.serial_number ?? '—'}
                      </td>
                      {/* Tag */}
                      <td style={{ padding: '14px 16px', fontFamily: 'monospace', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                        {c.inventory_tag ?? '—'}
                      </td>
                      {/* Flow Rate */}
                      <td style={{ padding: '14px 16px', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        {c.flow_rate ?? '—'}
                      </td>
                      {/* Condition */}
                      <td style={{ padding: '14px 16px' }}>
                        {editingCondition === c.id ? (
                          <select
                            autoFocus
                            value={c.condition}
                            onChange={async (e) => {
                              const val = e.target.value;
                              await api.patch(`/concentrators/${c.id}/quick-update`, { condition: val });
                              setConcentrators(prev => prev.map(x => x.id === c.id ? { ...x, condition: val } : x));
                              setEditingCondition(null);
                            }}
                            onBlur={() => setEditingCondition(null)}
                            style={{
                              background: 'var(--bg-elevated)',
                              border: '1px solid var(--accent-primary)',
                              borderRadius: 6,
                              color: 'var(--text-primary)',
                              fontSize: 12,
                              padding: '3px 6px',
                              cursor: 'pointer',
                            }}
                          >
                            <option value="good">Good</option>
                            <option value="fair">Fair</option>
                            <option value="poor">Poor</option>
                            <option value="needs_service">Needs Service</option>
                          </select>
                        ) : (
                          <span onClick={() => setEditingCondition(c.id)} style={{ cursor: 'pointer' }}>
                            {c.condition === 'good' && <span className="badge badge-green">Good</span>}
                            {c.condition === 'fair' && <span className="badge badge-amber">Fair</span>}
                            {c.condition === 'poor' && <span className="badge badge-red">Poor</span>}
                            {c.condition === 'needs_service' && <span className="badge badge-red">Needs Service</span>}
                            {!['good', 'fair', 'poor', 'needs_service'].includes(c.condition) && (
                              <span className="badge badge-gray">{c.condition}</span>
                            )}
                          </span>
                        )}
                      </td>
                      {/* Filter Change */}
                      <td style={{ padding: '14px 16px', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        {editingFilterDate === c.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <DatePicker
                              value={filterDateValue}
                              onChange={v => setFilterDateValue(v)}
                              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--accent-primary)', borderRadius: 6, fontSize: 12, padding: '3px 6px' }}
                            />
                            <button className="btn btn-primary" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => handleFilterDateSave(c.id, filterDateValue)}>Save</button>
                            <button className="btn btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setEditingFilterDate(null)}>✕</button>
                          </div>
                        ) : (
                          <span
                            onClick={() => { setEditingFilterDate(c.id); setFilterDateValue(c.filter_change_date ? String(c.filter_change_date).slice(0, 10) : new Date().toISOString().slice(0, 10)); }}
                            style={{ cursor: 'pointer', color: c.filter_change_date ? 'var(--text-secondary)' : 'var(--text-accent)', opacity: c.filter_change_date ? 1 : 0.8 }}
                          >
                            {c.filter_change_date ? fmtExtDate(c.filter_change_date) : '+ Set date'}
                          </span>
                        )}
                      </td>
                      {/* Next Change */}
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                        {filterDaysUntil === null ? (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: '0.875rem', color: nextFilterColor, fontWeight: filterWarning ? 600 : 400 }}>
                              {fmtExtDate(c.next_filter_change_date)}
                            </span>
                            {filterDaysUntil < 0 && <span className="badge badge-red">Overdue</span>}
                            {filterDaysUntil >= 0 && filterDaysUntil <= 30 && <span className="badge badge-amber">Due soon</span>}
                          </div>
                        )}
                      </td>
                      {/* Service */}
                      <td style={{ padding: '10px 16px', minWidth: 160 }}>
                        {sendingService === c.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Date sent:</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <DatePicker value={sendServiceDate} onChange={v => setSendServiceDate(v)} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--accent-primary)', borderRadius: 6, fontSize: 12, padding: '3px 6px' }} />
                              <button className="btn btn-primary" style={{ fontSize: 10, padding: '3px 8px' }} onClick={async () => {
                                await api.patch(`/concentrators/${c.id}/quick-update`, { sent_for_service: true, sent_for_service_date: sendServiceDate });
                                setConcentrators(prev => prev.map(x => x.id === c.id ? { ...x, sent_for_service: true, sent_for_service_date: sendServiceDate } : x));
                                setSendingService(null);
                              }}>Save</button>
                              <button className="btn btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setSendingService(null)}>✕</button>
                            </div>
                          </div>
                        ) : returningService === c.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Return date:</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <DatePicker value={returnServiceDate} onChange={v => setReturnServiceDate(v)} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--accent-primary)', borderRadius: 6, fontSize: 12, padding: '3px 6px' }} />
                              <button className="btn btn-primary" style={{ fontSize: 10, padding: '3px 8px' }} onClick={async () => {
                                await api.patch(`/concentrators/${c.id}/quick-update`, { returned_from_service: true, returned_date: returnServiceDate });
                                setConcentrators(prev => prev.map(x => x.id === c.id ? { ...x, returned_from_service: true, returned_date: returnServiceDate } : x));
                                setReturningService(null);
                              }}>Save</button>
                              <button className="btn btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setReturningService(null)}>✕</button>
                            </div>
                          </div>
                        ) : !c.sent_for_service ? (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}
                            onClick={() => { setSendingService(c.id); setSendServiceDate(new Date().toISOString().slice(0, 10)); }}
                          >→ Send for Service</button>
                        ) : c.sent_for_service && !c.returned_from_service ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            <span className="badge badge-amber">Out for Service</span>
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ fontSize: 10, padding: '2px 8px', marginTop: 2 }}
                              onClick={() => { setReturningService(c.id); setReturnServiceDate(new Date().toISOString().slice(0, 10)); }}
                            >Mark Returned</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span className="badge badge-green">Returned</span>
                            {c.returned_date && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtExtDate(c.returned_date)}</span>}
                          </div>
                        )}
                      </td>
                      {/* Actions */}
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        {(canEdit || isViewer) && (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                                className="btn btn-primary btn-sm"
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); sessionStorage.setItem('inventory_catalogue_state', JSON.stringify({ tab: 'concentrators', scrollY: window.scrollY })); navigate(`/inventory/${c.id}/bom?type=concentrator`); }}
                                title="Parts / BOM"
                                style={{ gap: 6 }}
                              >
                                <Network size={14} /> Parts / BOM
                              </button>
                            <button
                              type="button"
                              onClick={() => demoBlock(() => openEdit(c))}
                              title="Edit"
                              className="btn btn-ghost btn-sm"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => demoBlock(() => setDeleteId(c.id))}
                              title="Delete"
                              className="btn btn-danger btn-sm"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal.open && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', zIndex: 998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onMouseDown={(e) => { concModalMouseDown.current = e.target; }}
          onMouseUp={(e) => { if (concModalMouseDown.current === e.currentTarget && e.target === e.currentTarget) closeModal(); concModalMouseDown.current = null; }}
        >
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-default)', width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 999 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ flexShrink: 0, padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{modal.editing ? 'Edit Concentrator' : 'Add Concentrator'}</h3>
              <button type="button" onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Manufacturer</label>
                  <DropdownCombobox dropdown_type="concentrator_brand" value={form.manufacturer} onChange={(v) => { setForm(f => ({ ...f, manufacturer: v, model: '' })); }} onSelectOption={(opt) => { setConcBrandId(opt?.id ?? null); setForm(f => ({ ...f, model: '' })); }} placeholder="Select or type manufacturer…" />
                </div>
                <div className="form-group">
                  <label className="form-label">Model</label>
                  <DropdownCombobox dropdown_type="concentrator_model" value={form.model} onChange={(v) => setForm(f => ({ ...f, model: v }))} parentId={concBrandId} placeholder="Select or type model…" />
                </div>
                <div className="form-group">
                  <label className="form-label">Serial Number</label>
                  <input className="form-input" value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} placeholder="SN-12345" />
                </div>
                <div className="form-group">
                  <label className="form-label">Inventory Tag</label>
                  <input className="form-input" value={form.inventory_tag} onChange={e => setForm(f => ({ ...f, inventory_tag: e.target.value }))} placeholder="e.g. C001" />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>Used to track this unit in your facility</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Flow Rate</label>
                  <input className="form-input" value={form.flow_rate} onChange={e => setForm(f => ({ ...f, flow_rate: e.target.value }))} placeholder="e.g. 2 LPM" />
                </div>
                <div className="form-group">
                  <label className="form-label">Condition</label>
                  <select className="form-input" value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                    <option value="needs_service">Needs Service</option>
                  </select>
                </div>
                {/* Filter Change Date */}
                <div className="form-group">
                  <label className="form-label">Last Filter Change Date</label>
                  <input className="form-input" type="date" value={form.filter_change_date} onChange={e => {
                    const val = e.target.value;
                    let nextVal = '';
                    if (val) {
                      const next = new Date(val + 'T12:00:00Z');
                      next.setUTCFullYear(next.getUTCFullYear() + 1);
                      nextVal = next.toISOString().slice(0, 10);
                    }
                    setForm(f => ({ ...f, filter_change_date: val, next_filter_change_date: nextVal }));
                  }} />
                </div>
                {/* Next Filter Change (read-only) */}
                <div className="form-group">
                  <label className="form-label">Next Filter Change</label>
                  <div style={{
                    padding: '9px 12px', background: 'var(--bg-elevated)',
                    border: '1px solid var(--border-default)', borderRadius: 6,
                    fontSize: '0.875rem',
                    color: form.next_filter_change_date ? 'var(--text-secondary)' : 'var(--text-muted)',
                    fontStyle: form.next_filter_change_date ? 'normal' : 'italic',
                  }}>
                    {form.next_filter_change_date || 'Set filter change date first'}
                  </div>
                </div>
                {/* Service History */}
                {modal.editing && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {/* Status and actions */}
                      {!modal.editing.sent_for_service ? (
                        canEdit && (
                          <div>
                            {!svcSendOpen ? (
                              <button
                                type="button"
                                onClick={() => { setSvcSendOpen(true); setSvcSendDate(new Date().toISOString().slice(0, 10)); setSvcSendNotes(''); }}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(59,130,246,0.5)', background: 'rgba(59,130,246,0.08)', color: '#3b82f6', fontSize: 13, cursor: 'pointer' }}
                              >
                                + Send for Service
                              </button>
                            ) : (
                              <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>Send for Service</div>
                                <div className="form-group" style={{ margin: 0 }}>
                                  <label className="form-label">Date Sent</label>
                                  <DatePicker value={svcSendDate} onChange={v => setSvcSendDate(v)} placeholder="Select a date" />
                                </div>
                                <div className="form-group" style={{ margin: 0 }}>
                                  <label className="form-label">Notes (optional)</label>
                                  <textarea className="form-input" rows={2} value={svcSendNotes} onChange={e => setSvcSendNotes(e.target.value)} placeholder="Reason, vendor, etc." style={{ resize: 'vertical' }} />
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button type="button" disabled={svcSubmitting} onClick={handleSvcSend} className="btn btn-primary btn-sm">{svcSubmitting ? 'Saving...' : 'Confirm Send'}</button>
                                  <button type="button" onClick={() => setSvcSendOpen(false)} className="btn btn-ghost btn-sm">Cancel</button>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      ) : !modal.editing.returned_from_service ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div>
                            <span className="badge badge-amber">Out for Service · Sent {modal.editing.sent_for_service_date ? fmtExtDate(modal.editing.sent_for_service_date) : '—'}</span>
                          </div>
                          {canEdit && !svcReturnOpen && (
                            <button
                              type="button"
                              onClick={() => { setSvcReturnOpen(true); setSvcReturnDate(new Date().toISOString().slice(0, 10)); setSvcReturnNotes(''); }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(16,185,129,0.5)', background: 'rgba(16,185,129,0.08)', color: '#10b981', fontSize: 13, cursor: 'pointer', alignSelf: 'flex-start' }}
                            >
                              Log Return
                            </button>
                          )}
                          {svcReturnOpen && (
                            <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#10b981' }}>Log Return from Service</div>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label className="form-label">Date Returned</label>
                                <DatePicker value={svcReturnDate} onChange={v => setSvcReturnDate(v)} placeholder="Select a date" />
                              </div>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label className="form-label">Notes (optional)</label>
                                <textarea className="form-input" rows={2} value={svcReturnNotes} onChange={e => setSvcReturnNotes(e.target.value)} placeholder="Service notes, repairs completed, etc." style={{ resize: 'vertical' }} />
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  type="button"
                                  disabled={svcSubmitting}
                                  onClick={handleSvcReturn}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, border: 'none', background: '#10b981', color: '#fff', fontSize: 13, fontWeight: 500, cursor: svcSubmitting ? 'default' : 'pointer', opacity: svcSubmitting ? 0.7 : 1 }}
                                >
                                  {svcSubmitting ? 'Saving...' : 'Confirm Return'}
                                </button>
                                <button type="button" onClick={() => setSvcReturnOpen(false)} className="btn btn-ghost btn-sm">Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div>
                            <span className="badge badge-green">Returned · {modal.editing.returned_date ? fmtExtDate(modal.editing.returned_date) : ''}</span>
                          </div>
                          {canEdit && (
                            <div>
                              {!svcSendOpen ? (
                                <button
                                  type="button"
                                  onClick={() => { setSvcSendOpen(true); setSvcSendDate(new Date().toISOString().slice(0, 10)); setSvcSendNotes(''); }}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(59,130,246,0.5)', background: 'rgba(59,130,246,0.08)', color: '#3b82f6', fontSize: 13, cursor: 'pointer' }}
                                >
                                  + Send for Service
                                </button>
                              ) : (
                                <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>Send for Service</div>
                                  <div className="form-group" style={{ margin: 0 }}>
                                    <label className="form-label">Date Sent</label>
                                    <DatePicker value={svcSendDate} onChange={v => setSvcSendDate(v)} placeholder="Select a date" />
                                  </div>
                                  <div className="form-group" style={{ margin: 0 }}>
                                    <label className="form-label">Notes (optional)</label>
                                    <textarea className="form-input" rows={2} value={svcSendNotes} onChange={e => setSvcSendNotes(e.target.value)} placeholder="Reason, vendor, etc." style={{ resize: 'vertical' }} />
                                  </div>
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button type="button" disabled={svcSubmitting} onClick={handleSvcSend} className="btn btn-primary btn-sm">{svcSubmitting ? 'Saving...' : 'Confirm Send'}</button>
                                    <button type="button" onClick={() => setSvcSendOpen(false)} className="btn btn-ghost btn-sm">Cancel</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Service History panel */}
                      {serviceHistory !== null && serviceHistory.length > 0 && (
                        <div style={{ marginTop: 4 }}>
                          <button
                            type="button"
                            onClick={() => setSvcHistoryOpen(o => !o)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          >
                            {svcHistoryOpen ? '▲' : '▼'} View Service History ({serviceHistory.length})
                          </button>
                          {svcHistoryOpen && (
                            <div style={{ marginTop: 10, borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sent</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Returned</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Duration</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {serviceHistory.map(r => {
                                    const days = r.sent_date && r.returned_date
                                      ? Math.round((new Date(r.returned_date.slice(0, 10) + 'T12:00:00Z').getTime() - new Date(r.sent_date.slice(0, 10) + 'T12:00:00Z').getTime()) / 86400000)
                                      : null;
                                    return (
                                      <tr key={r.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                        <td style={{ padding: '8px 8px', color: 'var(--text-primary)' }}>{fmtExtDate(r.sent_date)}</td>
                                        <td style={{ padding: '8px 8px', color: 'var(--text-primary)' }}>{r.returned_date ? fmtExtDate(r.returned_date) : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>In service</span>}</td>
                                        <td style={{ padding: '8px 8px', color: 'var(--text-muted)' }}>{days !== null ? `${days}d` : '—'}</td>
                                        <td style={{ padding: '8px 8px', color: 'var(--text-muted)' }}>{r.notes || '—'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes..." style={{ resize: 'vertical' }} />
                </div>
              </div>
              </div>
              <div style={{ flexShrink: 0, padding: '12px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button type="button" className="btn btn-ghost" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : modal.editing ? 'Save Changes' : 'Add Concentrator'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirm Modal */}
      {deleteId !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-default)', padding: 28, maxWidth: 420, width: '100%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1.0625rem', fontWeight: 700 }}>Delete Concentrator</h3>
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 24px' }}>Are you sure you want to delete this concentrator? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface HvacUnit {
  id: number;
  facility_id: number;
  name: string;
  brand: string | null;
  model: string | null;
  location: string | null;
  serial_number: string | null;
  condition: string;
  notes: string | null;
  photo_url: string | null;
  last_service_date: string | null;
  next_service_date: string | null;
  created_at: string;
  updated_at: string;
  filters: HvacFilter[];
}

interface HvacFilter {
  id: number;
  hvac_id: number;
  facility_id: number;
  filter_size: string;
  filter_type: string | null;
  quantity_per_change: number;
  catalogue_item_id: number | null;
  notes: string | null;
  created_at: string;
}

interface HvacFilterReport {
  filter_size: string;
  filter_type: string | null;
  total_used: number;
  avg_per_month: number;
  unit_price: number | null;
  est_annual_cost: number;
}

// Item D — GET /hvac/filter-report-by-frequency
interface HvacFilterFrequencyReport {
  filter_size: string;
  filter_type: string | null;
  frequency_bucket: string; // e.g. "1 months", "3 months", "manual", "one-off"
  total_used: number;
}

// Item B — GET /hvac/:id/filters/:filterId/usage
interface HvacFilterUsageEntry {
  id: number;
  quantity_used: number;
  used_date: string;
  notes: string | null;
  created_at: string;
  logged_by: number | null;
  logged_by_name: string | null;
  wo_id: number | null;
  wo_number: string | null;
  wo_title: string | null;
}

const HVAC_CONDITION_COLORS: Record<string, string> = {
  excellent: '#10b981', good: '#3b82f6', fair: '#f59e0b', poor: '#ef4444',
};

function blankHvacUnit(): Partial<HvacUnit> {
  return { name: '', brand: '', model: '', location: '', serial_number: '', condition: 'good', notes: '', last_service_date: '', next_service_date: '' };
}

// Data-plate photos get captured at whatever angle the unit is mounted, so there's no reliable
// capture-orientation guarantee to rely on -- rotate90 lets the user straighten a sideways photo
// client-side, 90° at a time, before it's uploaded. Kept intentionally minimal (rotate only, no
// crop/brightness/etc) -- this is a straighten control, not an image editor.
function rotateImageToDataUrl(file: File, deg: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const rad = (deg * Math.PI) / 180;
      const swap = deg % 180 !== 0;
      const canvas = document.createElement('canvas');
      canvas.width = swap ? img.height : img.width;
      canvas.height = swap ? img.width : img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { URL.revokeObjectURL(url); reject(new Error('Canvas not supported')); return; }
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rad);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not load image')); };
    img.src = url;
  });
}
function dataUrlToBlob(dataUrl: string): Blob {
  const [header, b64] = dataUrl.split(',');
  const mime = header.match(/data:(.*?);/)?.[1] ?? 'image/jpeg';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// ─── HVAC Tab ─────────────────────────────────────────────────────────────────
function HVACTab({ navigate: _navigate }: { navigate: (path: string) => void }) {
  const queryClient = useQueryClient();
  const { user: hvacUser } = useAuth();
  const hvacFacilityId = hvacUser?.facilityId ?? null;
  const [selectedUnit, setSelectedUnit] = useState<HvacUnit | null>(null);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editUnit, setEditUnit] = useState<HvacUnit | null>(null);
  const [unitForm, setUnitForm] = useState<Partial<HvacUnit>>(blankHvacUnit());
  const [unitSaving, setUnitSaving] = useState(false);
  const [confirmDeleteUnit, setConfirmDeleteUnit] = useState<number | null>(null);

  // Filter management
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterForm, setFilterForm] = useState<{ filter_size: string; filter_type: string; quantity_per_change: number; notes: string }>({ filter_size: '', filter_type: '', quantity_per_change: 1, notes: '' });
  const [filterSaving, setFilterSaving] = useState(false);

  // Usage logging
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [usageTarget, setUsageTarget] = useState<{ unitId: number; filter: HvacFilter } | null>(null);
  const [usageForm, setUsageForm] = useState<{ quantity_used: number; used_date: string; notes: string }>({ quantity_used: 1, used_date: new Date().toISOString().slice(0, 10), notes: '' });
  const [usageSaving, setUsageSaving] = useState(false);
  const mouseDownTarget = useRef<EventTarget | null>(null);
  // Standard app toast pattern (CLAUDE_RULES.md / DESIGN_SYSTEM.md §12) -- this tab had no
  // confirmation at all after a successful "Log Usage", which was the actual bug (the POST really
  // did write a row every time -- confirmed against live data -- there was just no feedback and
  // nowhere visible to see it land).
  const { toast: usageToast, showToast: showUsageToast } = useToast();

  // Usage history -- which filter's history panel is currently expanded, inline under its row.
  const [historyFilterId, setHistoryFilterId] = useState<number | null>(null);

  // Filter report
  const [reportOpen, setReportOpen] = useState(false);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [freqReportOpen, setFreqReportOpen] = useState(false);

  // Data plate photo — pick + rotate (client-side, canvas) + upload, OR paste a direct image URL
  // (same Upload File / Paste URL toggle convention as the general item image panel ~line 1868
  // and the Add/Edit Supply modal's image field ~line 7073 — reused here rather than a new UI).
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [pendingPhotoRotation, setPendingPhotoRotation] = useState(0);
  const [pendingPhotoPreview, setPendingPhotoPreview] = useState<string | null>(null);
  const [photoSaving, setPhotoSaving] = useState(false);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [hvacPhotoMode, setHvacPhotoMode] = useState<'file' | 'url'>('file');
  const [hvacPhotoUrlInput, setHvacPhotoUrlInput] = useState('');

  const { data: unitsResp, isLoading } = useQuery({
    queryKey: ['facility-hvac'],
    queryFn: () => api.get<HvacUnit[]>('/hvac'),
  });
  const units = unitsResp?.data ?? [];

  const { data: reportResp, isLoading: reportLoading } = useQuery({
    queryKey: ['hvac-filter-report', reportYear],
    queryFn: () => api.get<HvacFilterReport[]>('/hvac/filter-report', { year: reportYear }),
    enabled: reportOpen,
  });
  const report = reportResp?.data ?? [];

  // Item D: same year selector, broken down by the recurrence frequency each usage row's work
  // order was generated under (GET /hvac/filter-report-by-frequency). Reuses reportYear rather
  // than a second year picker -- both reports describe the same year side by side.
  const { data: freqReportResp, isLoading: freqReportLoading } = useQuery({
    queryKey: ['hvac-filter-report-by-frequency', reportYear],
    queryFn: () => api.get<HvacFilterFrequencyReport[]>('/hvac/filter-report-by-frequency', { year: reportYear }),
    enabled: freqReportOpen,
  });
  const freqReport = freqReportResp?.data ?? [];

  // Item B: usage history for whichever filter's row is currently expanded.
  const { data: usageHistoryResp, isLoading: usageHistoryLoading } = useQuery({
    queryKey: ['hvac-filter-usage-history', selectedUnit?.id, historyFilterId],
    queryFn: () => api.get<HvacFilterUsageEntry[]>(`/hvac/${selectedUnit!.id}/filters/${historyFilterId}/usage`),
    enabled: historyFilterId !== null && !!selectedUnit,
  });
  const usageHistory = usageHistoryResp?.data ?? [];

  function setUF(k: keyof typeof unitForm, v: string | number) { setUnitForm((f) => ({ ...f, [k]: v })); }

  // Brand→model filtering for HVAC
  const [hvacBrandId, setHvacBrandId] = useState<number | null>(null);
  const { data: hvacBrandsData = [] } = useDropdownOptions('hvac_brand');
  useEffect(() => {
    if (!hvacBrandsData.length || !unitForm.brand) { setHvacBrandId(null); return; }
    const match = (hvacBrandsData as DropdownOption[]).find((b) => b.label === unitForm.brand);
    setHvacBrandId(match?.id ?? null);
  }, [unitForm.brand, hvacBrandsData]); // eslint-disable-line react-hooks/exhaustive-deps

  function openAdd() { setEditUnit(null); setUnitForm(blankHvacUnit()); setShowUnitModal(true); }
  function openEdit(u: HvacUnit) { setEditUnit(u); setUnitForm({ ...u }); setShowUnitModal(true); }

  async function handleUnitSubmit(e: React.FormEvent) {
    e.preventDefault();
    setUnitSaving(true);
    try {
      if (editUnit) {
        await api.put(`/hvac/${editUnit.id}`, unitForm);
      } else {
        await api.post('/hvac', unitForm);
      }
      // Register the brand/model into dropdown_options + bom_equipment (same pattern the
      // Other-equipment supply-save flow uses) so it shows up in Settings > Equipment BOM and in
      // any equipment-link picker (Learning Hub, Work Orders) — not just this form's own dropdown.
      if (unitForm.brand?.trim()) {
        try {
          await api.post('/inventory/equipment-category-brand', {
            equipmentType: 'hvac',
            displayName: 'HVAC',
            brand: unitForm.brand.trim(),
            model: unitForm.model?.trim() || undefined,
          });
          invalidateDropdowns(queryClient, 'hvac_brand');
          invalidateDropdowns(queryClient, 'hvac_model');
        } catch (linkErr) { console.error('[equipment-category-brand POST] failed:', linkErr); }
      }
      queryClient.invalidateQueries({ queryKey: ['facility-hvac'] });
      setShowUnitModal(false);
    } finally {
      setUnitSaving(false);
    }
  }

  function handlePhotoFileSelected(file: File) {
    setPendingPhotoFile(file);
    setPendingPhotoRotation(0);
    rotateImageToDataUrl(file, 0).then(setPendingPhotoPreview);
  }
  function handleRotatePendingPhoto() {
    if (!pendingPhotoFile) return;
    const nextRotation = (pendingPhotoRotation + 90) % 360;
    setPendingPhotoRotation(nextRotation);
    rotateImageToDataUrl(pendingPhotoFile, nextRotation).then(setPendingPhotoPreview);
  }
  function handleCancelPendingPhoto() {
    setPendingPhotoFile(null);
    setPendingPhotoPreview(null);
    setPendingPhotoRotation(0);
    if (photoInputRef.current) photoInputRef.current.value = '';
  }
  async function handleSavePendingPhoto() {
    if (!selectedUnit || !pendingPhotoPreview) return;
    setPhotoSaving(true);
    try {
      const blob = dataUrlToBlob(pendingPhotoPreview);
      const formData = new FormData();
      formData.append('photo', blob, 'data-plate.jpg');
      await api.postForm(`/hvac/${selectedUnit.id}/photo`, formData);
      queryClient.invalidateQueries({ queryKey: ['facility-hvac'] });
      handleCancelPendingPhoto();
    } finally {
      setPhotoSaving(false);
    }
  }
  async function handleRemovePhoto() {
    if (!selectedUnit) return;
    await api.delete(`/hvac/${selectedUnit.id}/photo`);
    queryClient.invalidateQueries({ queryKey: ['facility-hvac'] });
  }
  function openPhotoPicker() {
    setPhotoPickerOpen(true);
    setHvacPhotoMode('file');
    setHvacPhotoUrlInput('');
  }
  function closePhotoPicker() {
    setPhotoPickerOpen(false);
    setHvacPhotoUrlInput('');
    handleCancelPendingPhoto();
  }
  async function handleSaveUrlPhoto() {
    if (!selectedUnit || !hvacPhotoUrlInput.trim()) return;
    setPhotoSaving(true);
    try {
      const formData = new FormData();
      formData.append('photo_url', hvacPhotoUrlInput.trim());
      await api.postForm(`/hvac/${selectedUnit.id}/photo`, formData);
      queryClient.invalidateQueries({ queryKey: ['facility-hvac'] });
      closePhotoPicker();
    } finally {
      setPhotoSaving(false);
    }
  }

  async function handleDeleteUnit(id: number) {
    await api.delete(`/hvac/${id}`);
    queryClient.invalidateQueries({ queryKey: ['facility-hvac'] });
    setConfirmDeleteUnit(null);
    if (selectedUnit?.id === id) setSelectedUnit(null);
  }

  async function handleAddFilter(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUnit) return;
    setFilterSaving(true);
    try {
      await api.post(`/hvac/${selectedUnit.id}/filters`, filterForm);
      queryClient.invalidateQueries({ queryKey: ['facility-hvac'] });
      setShowFilterModal(false);
      setFilterForm({ filter_size: '', filter_type: '', quantity_per_change: 1, notes: '' });
    } finally {
      setFilterSaving(false);
    }
  }

  async function handleDeleteFilter(filterId: number) {
    if (!selectedUnit) return;
    await api.delete(`/hvac/${selectedUnit.id}/filters/${filterId}`);
    queryClient.invalidateQueries({ queryKey: ['facility-hvac'] });
  }

  async function handleLogUsage(e: React.FormEvent) {
    e.preventDefault();
    if (!usageTarget) return;
    setUsageSaving(true);
    try {
      await api.post(`/hvac/${usageTarget.unitId}/filters/${usageTarget.filter.id}/usage`, usageForm);
      queryClient.invalidateQueries({ queryKey: ['hvac-filter-report'] });
      queryClient.invalidateQueries({ queryKey: ['hvac-filter-report-by-frequency'] });
      queryClient.invalidateQueries({ queryKey: ['hvac-filter-usage-history', usageTarget.unitId, usageTarget.filter.id] });
      setShowUsageModal(false);
      // Item A -- the actual reported bug: the POST always wrote a real row (confirmed against
      // live data), there was just no confirmation and nowhere obviously visible to see it land.
      showUsageToast(`${usageForm.quantity_used} filter${usageForm.quantity_used !== 1 ? 's' : ''} logged as used`);
    } finally {
      setUsageSaving(false);
    }
  }

  // Keep selectedUnit in sync with fresh query data; default to the first unit once loaded
  // (matches the master-detail mockup, which always shows a unit selected rather than a blank
  // detail pane).
  useEffect(() => {
    if (selectedUnit) {
      const fresh = units.find((u) => u.id === selectedUnit.id);
      setSelectedUnit(fresh ?? null);
    } else if (units.length > 0) {
      setSelectedUnit(units[0]);
    }
  }, [units]); // eslint-disable-line react-hooks/exhaustive-deps

  const conditionBadge = (cond: string) => {
    const cls = cond === 'excellent' ? 'badge-green' : cond === 'good' ? 'badge-blue' : cond === 'fair' ? 'badge-amber' : cond === 'poor' ? 'badge-red' : 'badge-gray';
    return <span className={`badge ${cls}`}>{cond}</span>;
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
          <span style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', background: 'rgba(59,130,246,0.12)', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Thermometer size={17} />
          </span>
          HVAC Units
        </h2>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 500 }}>{units.length} unit{units.length !== 1 ? 's' : ''}</span>
        <button className="btn btn-primary combo2" style={{ marginLeft: 'auto' }} onMouseLeave={spinIconOut} onClick={openAdd}>
          <Plus size={15} className="btn-icon" /> Add Unit
        </button>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="loading-spinner" /></div>
      ) : units.length === 0 ? (
        <div className="empty-state">
          <Thermometer size={40} />
          <h3>No HVAC units</h3>
          <p>Track your HVAC equipment and filter usage here.</p>
          <button className="btn btn-primary combo2" onMouseLeave={spinIconOut} onClick={openAdd}><Plus size={15} className="btn-icon" /> Add Unit</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '290px minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
          {/* Unit list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {units.map((u) => (
              <div
                key={u.id}
                onClick={() => { setSelectedUnit(u); closePhotoPicker(); }}
                onMouseEnter={(e) => {
                  if (selectedUnit?.id === u.id) return;
                  e.currentTarget.style.borderColor = 'var(--border-default)';
                  e.currentTarget.style.background = 'var(--bg-hover)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  if (selectedUnit?.id === u.id) return;
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  e.currentTarget.style.background = 'var(--bg-elevated)';
                  e.currentTarget.style.transform = 'none';
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px',
                  background: 'var(--bg-elevated)', border: `1px solid ${selectedUnit?.id === u.id ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                  borderRadius: 'var(--radius-md)', cursor: 'pointer', transition: 'all var(--transition)',
                  boxShadow: selectedUnit?.id === u.id ? '0 0 0 1px var(--accent-primary), 0 6px 18px rgba(59,130,246,0.18)' : 'none',
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: 'rgba(59,130,246,0.12)', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Thermometer size={17} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                  {(u.brand || u.model) && (
                    <div style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace", marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[u.brand, u.model].filter(Boolean).join(' • ')}
                    </div>
                  )}
                </div>
                <span style={{ marginLeft: 'auto', width: 9, height: 9, borderRadius: 99, flexShrink: 0, background: HVAC_CONDITION_COLORS[u.condition] ?? HVAC_CONDITION_COLORS.good }} />
              </div>
            ))}
          </div>

          {/* Detail panel */}
          {selectedUnit && (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', position: 'sticky', top: 16 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 22px', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
                <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-sm)', background: 'rgba(59,130,246,0.12)', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Thermometer size={22} />
                </div>
                <div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 800 }}>{selectedUnit.name}</div>
                  {(selectedUnit.brand || selectedUnit.model) && (
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
                      {[selectedUnit.brand, selectedUnit.model].filter(Boolean).join(' • ')}
                    </div>
                  )}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm combo2" onMouseLeave={spinIconOut} onClick={() => setShowFilterModal(true)}><Plus size={13} className="btn-icon" /> Filter Spec</button>
                  <button className="btn btn-ghost btn-sm combo1" onMouseLeave={spinIconOut} onClick={() => openEdit(selectedUnit)}><Pencil size={13} className="btn-icon" /> Edit</button>
                  <button
                    className="combo-jello-spin"
                    data-tint="red"
                    onMouseLeave={spinIconOut}
                    style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setConfirmDeleteUnit(selectedUnit.id)}
                    title="Delete unit"
                  >
                    <Trash2 size={16} className="btn-icon" />
                  </button>
                </div>
              </div>

              <div style={{ padding: 22, display: 'grid', gridTemplateColumns: '280px minmax(0, 1fr)', gap: 22, alignItems: 'start' }}>
                {/* Data plate photo */}
                {/* SUGGESTION: the Cancel/Replace/Remove chip buttons below (new controls, not in
                    the mockup) currently have no hover state at all — Rotate/Save already do via
                    combo1/combo2. Out of scope for this QA pass (only the unit-delete button and
                    mockup-defined hover states were requested) — flagging for a follow-up pass. */}
                <div style={{
                  background: '#f1f5f9', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', minHeight: 200, padding: 18,
                }}>
                  {pendingPhotoPreview ? (
                    <>
                      <img src={pendingPhotoPreview} alt="Rotated preview" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
                      <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8 }}>
                        <button type="button" className="btn btn-ghost btn-sm combo1" onMouseLeave={spinIconOut} onClick={handleRotatePendingPhoto} style={{ background: 'rgba(255,255,255,0.9)', color: '#0f172a', border: '1px solid rgba(2,132,199,0.3)' }}>
                          <RotateCw size={13} className="btn-icon" /> Rotate
                        </button>
                        <button type="button" className="btn btn-sm combo2" onMouseLeave={spinIconOut} disabled={photoSaving} onClick={handleSavePendingPhoto}>
                          {photoSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" style={{ background: 'rgba(255,255,255,0.9)', color: '#0f172a' }} onClick={closePhotoPicker}>Cancel</button>
                      </div>
                    </>
                  ) : photoPickerOpen ? (
                    <div style={{ width: '100%', padding: '0 4px' }}>
                      <div style={{ display: 'flex', width: '100%', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(15,23,42,0.15)', marginBottom: 10 }}>
                        {(['file', 'url'] as const).map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            onClick={() => setHvacPhotoMode(mode)}
                            style={{
                              flex: 1, padding: '5px 0', border: 'none', cursor: 'pointer',
                              fontSize: '0.75rem', fontWeight: 500,
                              background: hvacPhotoMode === mode ? 'var(--accent-primary)' : 'transparent',
                              color: hvacPhotoMode === mode ? '#fff' : '#64748b',
                              transition: 'background 0.15s, color 0.15s',
                            }}
                          >
                            {mode === 'file' ? 'Upload File' : 'Paste URL'}
                          </button>
                        ))}
                      </div>
                      {hvacPhotoMode === 'url' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <input
                            type="url"
                            placeholder="https://example.com/image.jpg"
                            value={hvacPhotoUrlInput}
                            onChange={(e) => setHvacPhotoUrlInput(e.target.value)}
                            style={{ fontSize: '0.78rem', padding: '7px 10px', borderRadius: 6, border: '1px solid rgba(15,23,42,0.15)', color: '#0f172a', background: '#fff' }}
                          />
                          {hvacPhotoUrlInput.trim() && (
                            <img
                              src={hvacPhotoUrlInput.trim()}
                              alt="Preview"
                              style={{ width: '100%', maxHeight: 140, objectFit: 'contain', borderRadius: 6 }}
                              onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                            />
                          )}
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                            <button type="button" className="btn btn-sm combo2" onMouseLeave={spinIconOut} disabled={photoSaving || !hvacPhotoUrlInput.trim()} onClick={handleSaveUrlPhoto}>
                              {photoSaving ? 'Saving…' : 'Save'}
                            </button>
                            <button type="button" className="btn btn-ghost btn-sm" style={{ background: 'rgba(255,255,255,0.9)', color: '#0f172a' }} onClick={closePhotoPicker}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                          <div
                            onClick={() => photoInputRef.current?.click()}
                            style={{
                              width: '100%', minHeight: 100, border: '2px dashed rgba(15,23,42,0.2)', borderRadius: 8,
                              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                              gap: 6, color: '#64748b', cursor: 'pointer', fontSize: '0.75rem',
                            }}
                          >
                            <Upload size={18} /> Click to choose file
                          </div>
                          <button type="button" className="btn btn-ghost btn-sm" style={{ background: 'rgba(255,255,255,0.9)', color: '#0f172a' }} onClick={closePhotoPicker}>Cancel</button>
                        </div>
                      )}
                    </div>
                  ) : selectedUnit.photo_url ? (
                    <>
                      <img src={selectedUnit.photo_url} alt="Data plate" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
                      <button
                        type="button"
                        onClick={openPhotoPicker}
                        style={{ position: 'absolute', top: 8, right: 8, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.62rem', fontWeight: 700, color: '#0284c7', background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(2,132,199,0.3)', padding: '5px 9px', borderRadius: 99, cursor: 'pointer' }}
                      >
                        <Camera size={12} /> Replace
                      </button>
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        style={{ position: 'absolute', top: 8, left: 8, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.62rem', fontWeight: 700, color: '#b91c1c', background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(185,28,28,0.3)', padding: '5px 9px', borderRadius: 99, cursor: 'pointer' }}
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                      <span style={{ position: 'absolute', bottom: 8, right: 8, fontSize: '0.58rem', color: '#64748b', background: 'rgba(255,255,255,0.7)', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>data plate</span>
                    </>
                  ) : (
                    <>
                      <Thermometer size={64} color="#94a3b8" />
                      <button
                        type="button"
                        onClick={openPhotoPicker}
                        style={{ position: 'absolute', top: 8, right: 8, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.62rem', fontWeight: 700, color: '#0284c7', background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(2,132,199,0.3)', padding: '5px 9px', borderRadius: 99, cursor: 'pointer' }}
                      >
                        <Upload size={12} /> Upload photo
                      </button>
                      <span style={{ position: 'absolute', bottom: 8, right: 8, fontSize: '0.58rem', color: '#64748b', background: 'rgba(255,255,255,0.7)', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>unit photo</span>
                    </>
                  )}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhotoFileSelected(f); e.target.value = ''; }}
                  />
                </div>

                {/* Tiles + filters */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 6 }}>
                    <div style={{ background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '13px 14px' }}>
                      <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Location</div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem', marginTop: 6 }}>{selectedUnit.location || '—'}</div>
                    </div>
                    <div style={{ background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '13px 14px' }}>
                      <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Serial #</div>
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', marginTop: 6, fontFamily: "'JetBrains Mono', monospace" }}>{selectedUnit.serial_number || '—'}</div>
                    </div>
                    <div style={{ background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '13px 14px' }}>
                      <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Condition</div>
                      <div style={{ marginTop: 6 }}>{conditionBadge(selectedUnit.condition)}</div>
                    </div>
                  </div>

                  {selectedUnit.notes && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', background: 'var(--bg-base)', borderRadius: 6, padding: '8px 12px', marginTop: 12 }}>{selectedUnit.notes}</div>}

                  <div style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '20px 0 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    Filter Specifications
                    <span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
                    <span
                      onClick={() => setShowFilterModal(true)}
                      onMouseEnter={(e) => { e.currentTarget.style.letterSpacing = '0.3px'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.letterSpacing = 'normal'; }}
                      style={{ color: 'var(--accent-primary)', cursor: 'pointer', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'letter-spacing 150ms ease' }}
                    >
                      <Plus size={13} /> Add Filter Spec
                    </span>
                  </div>

                  {selectedUnit.filters.length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.84rem', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-md)' }}>
                      No filters added yet. Tap <b>+ Add Filter Spec</b> to add one.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {selectedUnit.filters.map((f) => (
                        <div key={f.id}>
                        <div
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', borderRadius: historyFilterId === f.id ? '8px 8px 0 0' : 'var(--radius-md)', transition: 'border-color 130ms ease' }}
                        >
                          <div style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', background: 'rgba(59,130,246,0.12)', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Wind size={15} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.9rem' }}>{f.filter_size}</div>
                            {f.filter_type && <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{f.filter_type}</div>}
                          </div>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", background: 'rgba(139,92,246,0.16)', color: '#c4b5fd', padding: '2px 10px', borderRadius: 99 }}>×{f.quantity_per_change}</span>
                          <div style={{ display: 'flex', gap: 5, marginLeft: 'auto' }}>
                            <button
                              className="btn btn-ghost btn-sm combo1"
                              style={{ fontSize: '0.72rem', padding: '3px 10px' }}
                              onMouseLeave={spinIconOut}
                              onClick={() => { setUsageTarget({ unitId: selectedUnit.id, filter: f }); setUsageForm({ quantity_used: f.quantity_per_change, used_date: new Date().toISOString().slice(0, 10), notes: '' }); setShowUsageModal(true); }}
                            >
                              Log Usage
                            </button>
                            {/* Item B -- visible usage history, inline per filter. Toggles an
                                expandable panel below this row (GET /hvac/:id/filters/:filterId/usage)
                                showing date/qty/logged-by/triggering task for every logged entry. */}
                            <button
                              className={`btn btn-ghost btn-sm${historyFilterId === f.id ? '' : ' combo1'}`}
                              style={{ fontSize: '0.72rem', padding: '3px 10px', ...(historyFilterId === f.id ? { background: 'var(--bg-hover)', color: 'var(--text-primary)' } : {}) }}
                              onMouseLeave={spinIconOut}
                              onClick={() => setHistoryFilterId((cur) => (cur === f.id ? null : f.id))}
                            >
                              History
                            </button>
                            <button
                              className="combo-jello-spin"
                              data-tint="red"
                              onMouseLeave={spinIconOut}
                              style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                              onClick={() => handleDeleteFilter(f.id)}
                              title="Remove filter spec"
                            >
                              <X size={14} className="btn-icon" />
                            </button>
                          </div>
                        </div>
                        {historyFilterId === f.id && (
                          <div style={{ border: '1px solid var(--border-subtle)', borderTop: 'none', borderRadius: '0 0 8px 8px', background: 'var(--bg-elevated)', padding: '10px 13px' }}>
                            {usageHistoryLoading ? (
                              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', padding: 8 }}>Loading…</div>
                            ) : usageHistory.length === 0 ? (
                              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', padding: 8 }}>No usage logged yet for this filter.</div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {usageHistory.map((h) => (
                                  <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.78rem', padding: '4px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                                    <span style={{ color: 'var(--text-secondary)', minWidth: 80 }}>
                                      {(() => { const [y, m, d] = h.used_date.slice(0, 10).split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); })()}
                                    </span>
                                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', minWidth: 30 }}>×{h.quantity_used}</span>
                                    <span style={{ color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {h.wo_id
                                        ? <>Auto-logged — <span style={{ color: 'var(--accent-primary)' }}>{h.wo_number}</span> {h.wo_title ? `(${h.wo_title})` : ''}</>
                                        : h.logged_by_name ? `Logged by ${h.logged_by_name}` : 'Logged manually'}
                                    </span>
                                    {h.notes && <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', flexShrink: 0 }}>{h.notes}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter usage report — page-level, real data (GET /hvac/filter-report), aggregated by
          filter_size/filter_type across all units for the selected year. Kept separate from the
          per-unit detail panel above: the mockup's per-unit "Filter Usage" 4-tile block (Changed
          YTD, Last change, Next due, Annual cost) has no backing per-unit data in this API today —
          filter usage is only ever logged/reported in aggregate, not broken out per HVAC unit — so
          shipping those tiles here would mean fabricating numbers. This report section already
          existed and already reflects real data; left as-is rather than inventing a per-unit stat
          source. */}
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginTop: 20 }}>
        <button
          onClick={() => setReportOpen((o) => !o)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', background: 'var(--bg-elevated)', border: 'none',
            cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.875rem',
          }}
        >
          <span>Filter Usage Report</span>
          {reportOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        {reportOpen && (
          <div style={{ padding: 16, background: 'var(--bg-base)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <label style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Year:</label>
              <select
                className="form-select"
                style={{ width: 100 }}
                value={reportYear}
                onChange={(e) => setReportYear(Number(e.target.value))}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            {reportLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="loading-spinner" /></div>
            ) : report.length === 0 ? (
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>No filter usage logged for {reportYear}.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                    {['Filter Size', 'Type', 'Total Used', 'Avg / Month', 'Est. Annual Cost'].map((h) => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 500 }}>{row.filter_size}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{row.filter_type ?? '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{row.total_used}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{Number(row.avg_per_month).toFixed(1)}</td>
                      <td style={{ padding: '8px 10px' }}>
                        {row.est_annual_cost > 0 ? `$${Number(row.est_annual_cost).toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Item D — Annual Usage by Frequency. Same simple-table pattern as the Filter Usage Report
          right above it (checked before inventing a new one, per the request) -- reuses the same
          year, just a different breakdown. One row per filter size/type/frequency-bucket combo
          (already grouped by filter in the query's own ORDER BY) rather than a dynamic pivot table
          with one column per frequency -- a flat table is enough for purchase planning and avoids
          a variable-column layout for what the request itself calls "a simple table/summary view,
          not a full dashboard." */}
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginTop: 12 }}>
        <button
          onClick={() => setFreqReportOpen((o) => !o)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', background: 'var(--bg-elevated)', border: 'none',
            cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.875rem',
          }}
        >
          <span>Annual Usage by Frequency</span>
          {freqReportOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        {freqReportOpen && (
          <div style={{ padding: 16, background: 'var(--bg-base)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <label style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Year:</label>
              <select
                className="form-select"
                style={{ width: 100 }}
                value={reportYear}
                onChange={(e) => setReportYear(Number(e.target.value))}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Same year as Filter Usage Report above.</span>
            </div>
            {freqReportLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="loading-spinner" /></div>
            ) : freqReport.length === 0 ? (
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>No filter usage logged for {reportYear}.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                    {['Filter Size', 'Type', 'Frequency', 'Total Used'].map((h) => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {freqReport.map((row, i) => {
                    // Visual grouping only (no data change) -- a thicker top border whenever the
                    // filter_size/type changes from the row above, so same-filter frequency rows
                    // read as one group without needing a dynamic pivot layout.
                    const prev = freqReport[i - 1];
                    const newGroup = !prev || prev.filter_size !== row.filter_size || prev.filter_type !== row.filter_type;
                    const isManualOrOneOff = row.frequency_bucket === 'manual' || row.frequency_bucket === 'one-off';
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)', borderTop: newGroup ? '2px solid var(--border-default)' : undefined }}>
                        <td style={{ padding: '8px 10px', fontWeight: 500 }}>{newGroup ? row.filter_size : ''}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{newGroup ? (row.filter_type ?? '—') : ''}</td>
                        <td style={{ padding: '8px 10px', color: isManualOrOneOff ? 'var(--text-muted)' : 'var(--text-primary)', fontStyle: isManualOrOneOff ? 'italic' : 'normal' }}>
                          {row.frequency_bucket}
                        </td>
                        <td style={{ padding: '8px 10px', fontWeight: 600 }}>{row.total_used}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Unit Modal */}
      {showUnitModal && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setShowUnitModal(false); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh' }}>
            <div style={{ flexShrink: 0, padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{editUnit ? 'Edit' : 'Add'} HVAC Unit</h3>
              <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }} onClick={() => setShowUnitModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleUnitSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-input" type="text" required value={unitForm.name ?? ''} onChange={(e) => setUF('name', e.target.value)} placeholder="Rooftop Unit 1" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Brand</label>
                  <DropdownCombobox dropdown_type="hvac_brand" value={unitForm.brand ?? ''} onChange={(v) => { setUF('brand', v); setUF('model', ''); }} onSelectOption={(opt) => { setHvacBrandId(opt?.id ?? null); setUF('model', ''); }} placeholder="Select or type brand…" />
                </div>
                <div className="form-group">
                  <label className="form-label">Model</label>
                  <DropdownCombobox dropdown_type="hvac_model" value={unitForm.model ?? ''} onChange={(v) => setUF('model', v)} parentId={hvacBrandId} placeholder="Select or type model…" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <LocationSelector
                    value={unitForm.location ?? ''}
                    onChange={(v) => setUF('location', v)}
                    facilityId={hvacFacilityId}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Serial #</label>
                  <input className="form-input" type="text" value={unitForm.serial_number ?? ''} onChange={(e) => setUF('serial_number', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Condition</label>
                <select className="form-select" value={unitForm.condition ?? 'good'} onChange={(e) => setUF('condition', e.target.value)}>
                  {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Last Service</label>
                  <DatePicker value={unitForm.last_service_date ?? ''} onChange={(v) => setUF('last_service_date', v)} placeholder="Select a date" />
                </div>
                <div className="form-group">
                  <label className="form-label">Next Service</label>
                  <DatePicker value={unitForm.next_service_date ?? ''} onChange={(v) => setUF('next_service_date', v)} placeholder="Select a date" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows={3} value={unitForm.notes ?? ''} onChange={(e) => setUF('notes', e.target.value)} style={{ resize: 'vertical' }} />
              </div>
              </div>
              <div style={{ flexShrink: 0, padding: '12px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowUnitModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={unitSaving}>{unitSaving ? 'Saving…' : editUnit ? 'Save' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Filter Spec Modal */}
      {showFilterModal && selectedUnit && (
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setShowFilterModal(false); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">Add Filter Spec — {selectedUnit.name}</h3>
              <button className="modal-close" onClick={() => setShowFilterModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleAddFilter} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Filter Size *</label>
                <input className="form-input" type="text" required value={filterForm.filter_size} onChange={(e) => setFilterForm((f) => ({ ...f, filter_size: e.target.value }))} placeholder='20x20x1"' />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Filter Type</label>
                  <input className="form-input" type="text" value={filterForm.filter_type} onChange={(e) => setFilterForm((f) => ({ ...f, filter_type: e.target.value }))} placeholder="MERV-8" />
                </div>
                <div className="form-group">
                  <label className="form-label">Qty per Change</label>
                  <input className="form-input" type="number" min={1} value={filterForm.quantity_per_change} onChange={(e) => setFilterForm((f) => ({ ...f, quantity_per_change: Number(e.target.value) }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-input" type="text" value={filterForm.notes} onChange={(e) => setFilterForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowFilterModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={filterSaving}>{filterSaving ? 'Saving…' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast toast={usageToast} />

      {/* Log Filter Usage Modal */}
      {showUsageModal && usageTarget && (
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setShowUsageModal(false); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div className="modal-header">
              <h3 className="modal-title">Log Filter Usage</h3>
              <button className="modal-close" onClick={() => setShowUsageModal(false)}><X size={18} /></button>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
              {usageTarget.filter.filter_size}{usageTarget.filter.filter_type ? ` — ${usageTarget.filter.filter_type}` : ''}
            </div>
            <form onSubmit={handleLogUsage} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Quantity Used</label>
                  <input className="form-input" type="number" min={1} value={usageForm.quantity_used} onChange={(e) => setUsageForm((f) => ({ ...f, quantity_used: Number(e.target.value) }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <DatePicker value={usageForm.used_date} onChange={(v) => setUsageForm((f) => ({ ...f, used_date: v }))} placeholder="Select a date" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-input" type="text" value={usageForm.notes} onChange={(e) => setUsageForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowUsageModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={usageSaving}>{usageSaving ? 'Saving…' : 'Log'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDeleteUnit !== null && (
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setConfirmDeleteUnit(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete HVAC Unit</h3>
              <button className="modal-close" onClick={() => setConfirmDeleteUnit(null)}><X size={18} /></button>
            </div>
            <p style={{ marginBottom: 20, color: 'var(--text-secondary)' }}>This will permanently delete the unit and all its filter specs. This cannot be undone.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDeleteUnit(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDeleteUnit(confirmDeleteUnit)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dropdown Combobox ────────────────────────────────────────────────────────
interface DropdownOption { id: number; label: string; value: string; is_active?: boolean; parent_id?: number | null; }

function DropdownCombobox({
  dropdown_type,
  value,
  onChange,
  placeholder,
  facilityId,
  externalOptions,
  parentId,
  onSelectOption,
  disableCreate,
}: {
  dropdown_type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  facilityId?: number | null;
  externalOptions?: DropdownOption[];
  parentId?: number | null;
  onSelectOption?: (opt: DropdownOption | null) => void;
  disableCreate?: boolean;
}) {
  const qc = useQueryClient();
  const { data: fetchedOptions = [] } = useDropdownOptions(dropdown_type, {
    facilityId,
    parentId,
    enabled: externalOptions === undefined,
  });
  const [localOptions, setLocalOptions] = useState<DropdownOption[]>([]);
  const options = externalOptions !== undefined ? externalOptions : fetchedOptions;
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [filtered, setFiltered] = useState<DropdownOption[]>([]);

  // localOptions holds an optimistic append from addNewOption below so the just-created option
  // shows immediately even before the invalidated query refetches (and survives a refetch that
  // hasn't landed yet). Cleared whenever the underlying query's own data already contains it.
  useEffect(() => {
    setLocalOptions((prev) => prev.filter((o) => !options.some((real) => real.id === o.id)));
  }, [options]);
  const mergedOptions = [...options, ...localOptions];

  useEffect(() => {
    setFiltered(mergedOptions.filter((o) => o.label.toLowerCase().includes(query.toLowerCase())));
  }, [query, mergedOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep query in sync when value changes externally (e.g., form reset)
  useEffect(() => { setQuery(value || ''); }, [value]);

  async function addNewOption() {
    if (!query.trim()) return;
    let newOpt: DropdownOption = { id: Date.now(), label: query.trim(), value: query.trim() };
    try {
      const body: Record<string, unknown> = { dropdown_type, label: query.trim(), value: query.trim() };
      if (facilityId) body.facility_id = facilityId;
      if (parentId != null) body.parent_id = parentId;
      const res = await api.post('/dropdowns', body);
      const saved = (res as any)?.option;
      if (saved) newOpt = saved;
      setLocalOptions((prev) => [...prev, newOpt]);
      invalidateDropdowns(qc, dropdown_type);
    } catch { /* allow free-text even if save fails */ }
    onChange(query.trim());
    onSelectOption?.(newOpt);
    setOpen(false);
  }

  const exactMatch = mergedOptions.some((o) => o.label.toLowerCase() === query.toLowerCase());

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-input"
        value={query}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && (filtered.length > 0 || (query && !exactMatch && !disableCreate)) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          zIndex: 100, maxHeight: 200,
          overflowY: 'auto', marginTop: 2,
          boxShadow: 'var(--shadow-md)',
        }}>
          {filtered.map((opt) => (
            <div
              key={opt.id}
              onMouseDown={() => { setQuery(opt.label); onChange(opt.label); onSelectOption?.(opt); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
            >
              {opt.label}
            </div>
          ))}
          {query && !exactMatch && !disableCreate && (
            <div
              onMouseDown={() => void addNewOption()}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--accent-primary)',
                borderTop: '1px solid var(--border-subtle)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
            >
              + Add "{query}" to list
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Location Selector ───────────────────────────────────────────────────────
function LocationSelector({ value, onChange, facilityId }: {
  value: string;
  onChange: (v: string) => void;
  facilityId: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [areas, setAreas] = useState<DropdownOption[]>([]);
  const [subLocations, setSubLocations] = useState<DropdownOption[]>([]);
  const [allSubLocations, setAllSubLocations] = useState<Record<number, DropdownOption[]>>({});
  const [selectedArea, setSelectedArea] = useState<DropdownOption | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ left: 0, width: 0 });
  const baseUrl = (import.meta.env.VITE_API_URL as string);
  const token = localStorage.getItem('accessToken');

  useEffect(() => {
    if (!facilityId) return;
    fetch(`${baseUrl}/dropdowns?dropdown_type=area&facility_id=${facilityId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then((data: { options?: DropdownOption[] }) => setAreas(data.options ?? []))
      .catch(() => {});
  }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fetch sub-locations for all areas so search can match across areas
  useEffect(() => {
    if (!facilityId || areas.length === 0) return;
    const tok = localStorage.getItem('accessToken');
    Promise.all(
      areas.map(area =>
        fetch(`${baseUrl}/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${area.id}`, {
          headers: { Authorization: `Bearer ${tok}` },
        })
          .then(r => r.json())
          .then((data: { options?: DropdownOption[] }) => ({ areaId: area.id, subs: data.options ?? [] }))
          .catch(() => ({ areaId: area.id, subs: [] as DropdownOption[] }))
      )
    ).then(results => {
      const map: Record<number, DropdownOption[]> = {};
      for (const { areaId, subs } of results) map[areaId] = subs;
      setAllSubLocations(map);
    });
  }, [areas, facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedArea || !facilityId) { setSubLocations([]); return; }
    fetch(`${baseUrl}/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${selectedArea.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then((data: { options?: DropdownOption[] }) => setSubLocations(data.options ?? []))
      .catch(() => {});
  }, [selectedArea?.id, facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))
      ) { setOpen(false); setSearchQuery(''); }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 280) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left, width: rect.width });
      } else {
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
      }
    }
    setOpen(prev => !prev);
  }

  function selectLocation(areaLabel: string, subLabel?: string) {
    onChange(subLabel ? `${areaLabel} › ${subLabel}` : areaLabel);
    setOpen(false);
    setSearchQuery('');
  }

  // Search results: match area names OR sub-locations, case-insensitive substring
  const searchResults = useMemo(() => {
    if (!searchQuery) return [];
    const q = searchQuery.toLowerCase();
    return areas
      .map(area => {
        const areaMatches = area.label.toLowerCase().includes(q);
        const subs = allSubLocations[area.id] ?? [];
        const matchingSubs = areaMatches ? subs : subs.filter(s => s.label.toLowerCase().includes(q));
        if (!areaMatches && matchingSubs.length === 0) return null;
        return { area, areaMatches, matchingSubs };
      })
      .filter((r): r is { area: DropdownOption; areaMatches: boolean; matchingSubs: DropdownOption[] } => r !== null);
  }, [searchQuery, areas, allSubLocations]);

  const filteredAreas = searchQuery ? searchResults.map(r => r.area) : areas;

  return (
    <div ref={containerRef}>
      <div
        onClick={openDropdown}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 10px', border: '1px solid rgba(255,255,255,0.13)',
          borderRadius: 6, background: 'var(--bg-elevated)', cursor: 'pointer',
          minHeight: 34, boxSizing: 'border-box' as const,
        }}
      >
        {!value && <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>Select location…</span>}
        {value && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px 2px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(99,102,241,0.2)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.35)' }}>
            {value}
            <button type="button" onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onChange(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#818cf8', lineHeight: 1, fontSize: '1rem' }}>×</button>
          </span>
        )}
        <ChevronDown size={14} style={{ opacity: 0.5, marginLeft: 'auto', flexShrink: 0 }} />
      </div>
      {open && ReactDOM.createPortal(
        <div ref={dropdownRef} style={{
          position: 'fixed',
          ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
          left: dropdownPos.left, minWidth: Math.max(dropdownPos.width || 0, 380),
          zIndex: 9999, background: 'var(--bg-elevated)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
          color: 'var(--text-primary)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column' as const, maxHeight: 300,
        }}>
          {/* Search bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <Search size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              autoFocus
              placeholder="Search areas…"
              value={searchQuery}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ fontSize: 12, background: 'transparent', border: 'none', outline: 'none', flex: 1, color: 'var(--text-primary)' }}
            />
          </div>

          {/* Two-column split */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, overflow: 'hidden', minHeight: 0 }}>
            {/* LEFT — areas */}
            <div style={{ borderRight: '0.5px solid rgba(255,255,255,0.08)', overflowY: 'auto' as const, maxHeight: 240 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '0.06em', padding: '5px 10px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', position: 'sticky' as const, top: 0, background: 'var(--bg-elevated)' }}>
                Area
              </div>
              {filteredAreas.map(area => {
                const isActive = selectedArea?.id === area.id;
                return (
                  <div
                    key={area.id}
                    onMouseDown={(e) => { e.stopPropagation(); setSelectedArea(isActive ? null : area); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 12px', cursor: 'pointer', background: isActive ? 'var(--bg-hover)' : 'transparent' }}
                    onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                  >
                    <MapPin size={13} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{area.label}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>›</span>
                  </div>
                );
              })}
              {filteredAreas.length === 0 && (
                <div style={{ padding: '12px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' as const }}>No areas</div>
              )}
            </div>

            {/* RIGHT — sub-locations (or grouped search results) */}
            <div style={{ overflowY: 'auto' as const, maxHeight: 240 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '0.06em', padding: '5px 10px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', position: 'sticky' as const, top: 0, background: 'var(--bg-elevated)' }}>
                {searchQuery ? 'Results' : 'Sub-location'}
              </div>
              {searchQuery ? (
                searchResults.length === 0 ? (
                  <div style={{ padding: '12px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' as const }}>No matches</div>
                ) : (
                  searchResults.map(({ area, matchingSubs }) => (
                    <div key={area.id}>
                      <div style={{ padding: '6px 12px 3px', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '0.06em', background: 'rgba(255,255,255,0.03)', borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
                        {area.label}
                      </div>
                      <div
                        onMouseDown={(e) => { e.stopPropagation(); selectLocation(area.label); }}
                        style={{ display: 'flex', alignItems: 'center', padding: '7px 12px 7px 20px', cursor: 'pointer', borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                      >
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>All of {area.label}</span>
                      </div>
                      {matchingSubs.map(sub => (
                        <div
                          key={sub.id}
                          onMouseDown={(e) => { e.stopPropagation(); selectLocation(area.label, sub.label); }}
                          style={{ display: 'flex', alignItems: 'center', padding: '8px 12px 8px 20px', cursor: 'pointer' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                        >
                          <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{sub.label}</span>
                        </div>
                      ))}
                    </div>
                  ))
                )
              ) : !selectedArea ? (
                <div style={{ padding: '20px 10px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' as const }}>Select an area</div>
              ) : (
                <>
                  <div
                    onMouseDown={(e) => { e.stopPropagation(); selectLocation(selectedArea.label); }}
                    style={{ display: 'flex', alignItems: 'center', padding: '9px 12px', cursor: 'pointer', borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>All of {selectedArea.label}</span>
                  </div>
                  {subLocations.map(sub => (
                    <div
                      key={sub.id}
                      onMouseDown={(e) => { e.stopPropagation(); selectLocation(selectedArea.label, sub.label); }}
                      style={{ display: 'flex', alignItems: 'center', padding: '9px 12px', cursor: 'pointer' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                    >
                      <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{sub.label}</span>
                    </div>
                  ))}
                  {subLocations.length === 0 && (
                    <div style={{ padding: '8px 10px', fontSize: 11, color: 'var(--text-muted)' }}>No sub-locations</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── BOM Category Combobox ────────────────────────────────────────────────────
function BomCategoryCombobox({
  value, onChange, bomTypes, onSelectEntry, onNewCategory,
}: {
  value: string;
  onChange: (v: string) => void;
  bomTypes: BomEntry[];
  onSelectEntry: (entry: BomEntry | null) => void;
  onNewCategory: (label: string) => void;
}) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  useEffect(() => { setQuery(value || ''); }, [value]);
  const filtered = bomTypes.filter(e => e.displayName.toLowerCase().includes(query.toLowerCase()));
  const exactMatch = bomTypes.some(e => e.displayName.toLowerCase() === query.toLowerCase());
  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text" className="form-input" value={query} autoComplete="off"
        placeholder="Shower Chair, IV Pump…"
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (filtered.length > 0 || (query.trim() && !exactMatch)) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, marginTop: 2,
          background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)', maxHeight: 200, overflowY: 'auto',
          boxShadow: 'var(--shadow-md)',
        }}>
          {filtered.map(entry => (
            <div key={entry.id}
              onMouseDown={() => { setQuery(entry.displayName); onChange(entry.displayName); onSelectEntry(entry); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
            >
              {entry.displayName}
              {entry.facilitySupplyType && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>→ {entry.facilitySupplyType}</span>
              )}
            </div>
          ))}
          {query.trim() && !exactMatch && (
            <div
              onMouseDown={() => { onChange(query.trim()); onSelectEntry(null); onNewCategory(query.trim()); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--accent-primary)', borderTop: '1px solid var(--border-subtle)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
            >
              + New category "{query.trim()}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Facility Supplies Tab ────────────────────────────────────────────────────
function FacilitySuppliesTab({ navigate, initialSupplyId, subParam, extinguisherId, onSupplyIdHandled, onExtinguisherIdHandled, onSubHandled }: {
  categories?: Category[];
  navigate: (path: string) => void;
  initialSupplyId?: string | null;
  subParam?: string | null;
  extinguisherId?: string | null;
  onSupplyIdHandled?: () => void;
  onExtinguisherIdHandled?: () => void;
  onSubHandled?: () => void;
}) {
  const { user } = useAuth();
  const currentFacilityId = user?.facilityId ?? null;
  const queryClient = useQueryClient();

  // Restore active supply type from sessionStorage if navigating back from BOM
  const SUPPLIES_STATE_KEY = 'inventory_supplies_state';
  const [_suppSaved] = useState<Record<string, unknown>>(() => {
    try {
      const raw = sessionStorage.getItem(SUPPLIES_STATE_KEY);
      if (!raw) return {};
      sessionStorage.removeItem(SUPPLIES_STATE_KEY);
      return JSON.parse(raw) as Record<string, unknown>;
    } catch { return {}; }
  });
  const [activeType, setActiveType] = useState(() => {
    if (typeof _suppSaved.activeType === 'string') return _suppSaved.activeType;
    const subParam = new URLSearchParams(window.location.search).get('sub');
    if (subParam && SUPPLY_TYPES.some((t) => t.id === subParam)) return subParam;
    return sessionStorage.getItem('inventory_supply_tab') ?? 'paint';
  });
  function handleSetSupplyTab(tab: string) {
    sessionStorage.setItem('inventory_supply_tab', tab);
    setActiveType(tab);
    setSelectedSupplyIds(new Set());
  }
  const [showModal, setShowModal] = useState(false);
  const [editSupply, setEditSupply] = useState<FacilitySupply | null>(null);
  const [viewSupply, setViewSupply] = useState<FacilitySupply | null>(null);
  const [viewUsedIn, setViewUsedIn] = useState<FacilitySupply | null>(null);
  const [confirmDeleteSupplyId, setConfirmDeleteSupplyId] = useState<number | null>(null);
  // Item 5 -- Ctrl+Click multi-select + floating batch-delete. Cleared on tab switch (below) so
  // selections never carry across supply_type sub-tabs, matching the existing sessionStorage-
  // scoped-to-activeType conventions already used elsewhere in this component.
  const [selectedSupplyIds, setSelectedSupplyIds] = useState<Set<number>>(new Set());
  const [confirmBatchDelete, setConfirmBatchDelete] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);
  function toggleSupplySelected(id: number) {
    setSelectedSupplyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const [supplyForm, setSupplyForm] = useState<Partial<FacilitySupply>>(blankSupply('paint'));
  const [supplySaving, setSupplySaving] = useState(false);
  const [supplyPendingImage, setSupplyPendingImage] = useState<File | null>(null);
  const [supplyImagePreview, setSupplyImagePreview] = useState<string | null>(null);
  const [supplyImageInputMode, setSupplyImageInputMode] = useState<'file' | 'url'>('file');
  const [supplyImageUrlInput, setSupplyImageUrlInput] = useState('');
  const [supplyImageFromCatalog, setSupplyImageFromCatalog] = useState(false);
  const supplyImageInputRef = useRef<HTMLInputElement>(null);
  const { toast: supplySaveToast, showToast: showSupplySaveToast } = useToast();
  const mouseDownTarget = useRef<EventTarget | null>(null);
  const [showSupplyCamera, setShowSupplyCamera] = useState(false);
  const supplyBarcodeBuffer = useRef('');
  const supplyBarcodeTimer = useRef<ReturnType<typeof setTimeout>>();
  function handleSupplyBarcodeKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); }
    clearTimeout(supplyBarcodeTimer.current);
    supplyBarcodeTimer.current = setTimeout(() => { supplyBarcodeBuffer.current = ''; }, 100);
  }
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Pre-fill the Add form when navigating from catalogue item detail (ItemDetail.tsx stores prefill in sessionStorage)
  useEffect(() => {
    const raw = sessionStorage.getItem('supply_prefill_data');
    if (!raw) return;
    try {
      const prefill = JSON.parse(raw) as { supplyType: string; name: string; brand: string; model: string; imageUrl?: string };
      if (prefill.supplyType !== activeType) return;
      sessionStorage.removeItem('supply_prefill_data');
      setSupplyForm({ ...blankSupply(prefill.supplyType), name: prefill.name, brand: prefill.brand, model: prefill.model });
      resetSupplyImageState();
      if (prefill.imageUrl) {
        setSupplyImageUrlInput(prefill.imageUrl);
        setSupplyImageInputMode('url');
        setSupplyImageFromCatalog(true);
      }
      setEditSupply(null);
      setShowModal(true);
    } catch { /* ignore */ }
  }, [activeType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill the Add form when navigating from AI analysis (Tasks.tsx stores prefill in sessionStorage)
  useEffect(() => {
    const raw = sessionStorage.getItem('supply_prefill_other');
    if (!raw || activeType !== 'other') return;
    sessionStorage.removeItem('supply_prefill_other');
    try {
      const prefill = JSON.parse(raw);
      setSupplyForm({ ...blankSupply('other'), ...prefill, supply_type: 'other' });
      resetSupplyImageState();
      setEditSupply(null);
      setShowModal(true);
    } catch { /* ignore */ }
  }, [activeType]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading } = useQuery({
    queryKey: ['facility-supplies', activeType],
    queryFn: () => api.get<FacilitySupply[]>('/inventory/supplies', { type: activeType }),
  });
  const supplies = data?.data ?? [];

  // Wheelchair brand→model filtering
  const [wheelchairBrandMatchId, setWheelchairBrandMatchId] = useState<number | null>(null);
  const { data: wcBrandsData } = useDropdownOptions('wheelchair_brand', {
    orgId: user?.orgId ?? null,
    enabled: !!user?.orgId,
  });
  const { data: wcModels = [] } = useDropdownOptions('wheelchair_model', {
    parentId: wheelchairBrandMatchId,
    enabled: !!wheelchairBrandMatchId,
  });
  useEffect(() => {
    if (!wcBrandsData || !supplyForm.brand) return;
    const match = (wcBrandsData as DropdownOption[]).find((b) => b.label === supplyForm.brand);
    setWheelchairBrandMatchId(match?.id ?? null);
  }, [supplyForm.brand, wcBrandsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generic brand→model filtering for bed/lift/other/paint
  const [brandOptId, setBrandOptId] = useState<number | null>(null);
  const brandJustSelectedRef = useRef(false);
  const effectiveFormType = supplyForm.supply_type ?? activeType;
  const brandDropdownType = effectiveFormType === 'bed' ? 'bed_brand'
    : effectiveFormType === 'lift' ? 'lift_brand'
    : effectiveFormType === 'other' ? 'other_brand'
    : effectiveFormType === 'paint' ? 'paint_brand' : null;
  const { data: brandsForType = [] } = useDropdownOptions(brandDropdownType, { enabled: !!brandDropdownType });
  useEffect(() => {
    if (brandJustSelectedRef.current) { brandJustSelectedRef.current = false; return; }
    if (!brandsForType.length || !supplyForm.brand) { setBrandOptId(null); return; }
    const match = (brandsForType as DropdownOption[]).find((b) => b.label === supplyForm.brand);
    setBrandOptId(match?.id ?? null);
  }, [supplyForm.brand, brandsForType]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setBrandOptId(null); }, [activeType]); // eslint-disable-line react-hooks/exhaustive-deps

  const [categoryRedirectLabel, setCategoryRedirectLabel] = useState<string | null>(null);
  const [pendingNewCategory, setPendingNewCategory] = useState<string | null>(null);
  // Slug (bom_equipment.equipment_type) for the currently selected "Other" category — used to
  // auto-link Brand/Model into that category's ${type}_brand/${type}_model dropdown_options
  // bucket on save. Null when no category is selected, or when the category redirects to a
  // built-in supply_type (those stay untouched by this feature).
  const [categoryEquipType, setCategoryEquipType] = useState<string | null>(null);

  const { data: bomEquipRaw } = useQuery({
    queryKey: ['bom-equipment-types'],
    queryFn: async () => (await api.get('/inventory/bom-equipment')) as unknown as { types: any[]; entries: BomEntry[] },
    enabled: activeType === 'other',
  });
  const bomTypeEntries: BomEntry[] = ((bomEquipRaw?.entries as BomEntry[]) ?? []).filter(e => e.brand === null);

  // When arriving with a supplyId (global search, or the BOM page's back link for the wheelchair
  // takeover), the sequence is: resolve the id's type -> switch to that sub-tab -> that sub-tab's
  // supplies load -> find the record -> open it. Without resolvingInitialSupply gating the render
  // below, the supplies grid paints in between each of those steps (initially for whatever type
  // was already active, then again for the just-switched-to type) before the specific record's
  // detail view finally opens on top of it -- a visible flash of the wrong list before landing on
  // the right takeover. Starts true whenever a supplyId is present so the grid never paints until
  // resolution actually finishes, one way or the other.
  const [resolvingInitialSupply, setResolvingInitialSupply] = useState(!!initialSupplyId);

  // `resolvedSupplyIdRef` remembers which id this effect has already started resolving.
  // initialSupplyId now comes from the PARENT's live searchParams (reactive, not a one-time
  // useState) -- a global search navigation to a DIFFERENT supply while this tab is already
  // mounted changes the prop value, and the parent strips the URL param once the id is handled
  // (see onSupplyIdHandled below), so initialSupplyId goes id -> null -> (a new id, on the next
  // click) rather than ever repeating the same value back-to-back. Resetting the ref back to null
  // whenever initialSupplyId goes null is what makes clicking the SAME result again later work --
  // without that reset the guard below would never fire a second time for that id.
  const resolvedSupplyIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialSupplyId) { resolvedSupplyIdRef.current = null; return; }
    if (resolvedSupplyIdRef.current === initialSupplyId) return;
    resolvedSupplyIdRef.current = initialSupplyId;
    autoOpenedRef.current = false;
    setResolvingInitialSupply(true);
    api.get(`/inventory/supplies/${initialSupplyId}`)
      .then((res: any) => {
        const supply = res?.data ?? res;
        if (supply?.supply_type) {
          handleSetSupplyTab(supply.supply_type);
        } else {
          setResolvingInitialSupply(false);
        }
      })
      .catch(() => setResolvingInitialSupply(false));
  }, [initialSupplyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // BUG FIX (item 1): initialSupplyId used to be captured ONCE from the URL's ?supplyId= at the
  // parent component (a lazy useState initializer, never updated again) and never got cleared
  // after use. This effect used to re-run and re-open the detail view every time `supplies` got a
  // new array reference -- which happens on every tab switch back to whichever sub-tab that id's
  // supply belongs to (a fresh query result) -- as long as `!viewSupply` was true, which it always
  // is right after the user deliberately closes the detail view. That reproduced exactly: open
  // item -> close -> switch tabs -> come back -> same item reopens, since closing set viewSupply
  // back to null and switching back re-triggered this effect with a fresh `supplies` array.
  // autoOpenedRef still latches true once this id has been auto-opened, so leaving and returning
  // to this tab never reopens it again -- but it's now reset to false (above) whenever a genuinely
  // NEW initialSupplyId arrives, and initialSupplyId itself is reactive + stripped from the URL
  // after handling (onSupplyIdHandled), so a second, different search-result click -- or even the
  // SAME one clicked again later -- now actually reopens, which is the bug this task fixes.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!initialSupplyId || supplies.length === 0 || autoOpenedRef.current) return;
    const supply = supplies.find(s => s.id === Number(initialSupplyId));
    autoOpenedRef.current = true;
    setResolvingInitialSupply(false);
    if (supply) {
      setViewSupply(supply);
      setTimeout(() => {
        const el = document.getElementById(`supply-card-${supply.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('task-highlight-glow');
          setTimeout(() => el.classList.remove('task-highlight-glow'), 3000);
        }
      }, 300);
    }
    // Handled either way (opened, or the id wasn't found -- deleted, wrong facility, etc.) --
    // strip the URL param so the grid isn't gated open forever and so the same id can be
    // re-clicked from search later.
    onSupplyIdHandled?.();
  }, [supplies, initialSupplyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reactive counterpart for the `sub=` deep-link param (equipment/extinguisher search results,
  // which don't carry a row id -- just "switch to this sub-tab"). Guarded by `handledSubRef` so
  // it only acts on a NEW value, never fighting the user's own manual sub-tab clicks (those call
  // handleSetSupplyTab directly and never touch the URL's `sub` param, so this effect never sees
  // a reason to fire from them). Stripped via onSubHandled the same way supplyId/extinguisherId
  // are, so clicking the same "HVAC"/"Concentrators"/"Extinguishers" search result again later --
  // after manually switching to some other sub-tab in between -- still switches back; without the
  // strip, `sub` would still equal its last-handled value and the ref guard would silently skip it.
  const handledSubRef = useRef<string | null>(null);
  useEffect(() => {
    if (!subParam) { handledSubRef.current = null; return; }
    if (!SUPPLY_TYPES.some((t) => t.id === subParam)) return;
    if (handledSubRef.current === subParam) return;
    handledSubRef.current = subParam;
    if (activeType !== subParam) handleSetSupplyTab(subParam);
    onSubHandled?.();
  }, [subParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // TEMPORARY — diagnostic logging for the supplyId flicker bug, remove once confirmed fixed.
  console.log('[FacilitySuppliesTab render]', {
    initialSupplyId, activeType, isLoading, resolvingInitialSupply,
    suppliesCount: supplies.length, viewSupplyId: viewSupply?.id ?? null,
  });

  const set = (field: keyof FacilitySupply, value: unknown) => setSupplyForm((f) => ({ ...f, [field]: value }));

  function resetSupplyImageState() {
    setSupplyPendingImage(null);
    setSupplyImagePreview(null);
    setSupplyImageInputMode('file');
    setSupplyImageUrlInput('');
    setSupplyImageFromCatalog(false);
  }

  function openAdd() {
    setEditSupply(null);
    setSupplyForm(blankSupply(activeType));
    resetSupplyImageState();
    setCategoryRedirectLabel(null);
    setPendingNewCategory(null);
    setCategoryEquipType(null);
    setShowModal(true);
  }

  function openEdit(s: FacilitySupply) {
    setEditSupply(s);
    setSupplyForm({ ...s });
    resetSupplyImageState();
    setCategoryRedirectLabel(null);
    setPendingNewCategory(null);
    // Resolve the existing category's slug so re-saving without touching Brand/Model still
    // re-links against the same ${type}_brand/${type}_model bucket (idempotent, see handleSubmit).
    const matchedType = s.supply_type === 'other'
      ? bomTypeEntries.find((e) => e.displayName.toLowerCase() === (s.category ?? '').trim().toLowerCase())
      : undefined;
    setCategoryEquipType(matchedType?.type ?? null);
    if (s.image_url) {
      setSupplyImageUrlInput(s.image_url);
      setSupplyImageInputMode('url');
    }
    setShowModal(true);
  }

  function handleSupplyImageSelect(file: File) {
    setSupplyPendingImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setSupplyImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!supplyForm.name?.trim()) return;
    setSupplySaving(true);
    try {
      const fd = new FormData();
      (Object.keys(supplyForm) as (keyof FacilitySupply)[]).forEach((k) => {
        // supply_type, category, image_url are appended explicitly below — skip here to avoid duplicates
        if (k === 'supply_type' || k === 'category' || k === 'image_url') return;
        const v = supplyForm[k];
        if (v === null || v === undefined) return;
        fd.append(k, String(v));
      });
      // Always append supply_type exactly once as a plain string
      fd.append('supply_type', String(supplyForm.supply_type ?? activeType));
      fd.append('category', String(supplyForm.category || supplyForm.supply_type || activeType));
      if (supplyImageInputMode === 'file' && supplyPendingImage) {
        fd.append('image', supplyPendingImage);
      } else if (supplyImageInputMode === 'url' && supplyImageUrlInput.trim()) {
        fd.append('image_url', supplyImageUrlInput.trim());
      }
      const token = localStorage.getItem('accessToken');
      const baseUrl = (import.meta.env.VITE_API_URL as string) || apiClient.defaults.baseURL || '/api';
      const url = editSupply
        ? `${baseUrl}/inventory/supplies/${editSupply.id}`
        : `${baseUrl}/inventory/supplies`;
      console.log('[supplies/save] baseUrl:', baseUrl);
      console.log('[supplies/save] url:', url);
      console.log('[supplies/save] editSupply:', editSupply?.id);
      console.log('[supplies/save] supplyPendingImage:', supplyPendingImage?.name);
      console.log('[supplies/save] supplyImageInputMode:', supplyImageInputMode);
      const res = await fetch(url, {
        method: editSupply ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      let toastMsg = editSupply ? 'Item updated successfully' : 'Item saved successfully';
      // Slug to auto-link Brand/Model against — either the just-created category (captured from
      // the server response below, since the slug the server assigns may not match a naive
      // client-side re-derivation) or the previously-resolved slug of an existing category.
      let linkEquipType: string | null = categoryEquipType;
      if (pendingNewCategory) {
        try {
          const catRes = await api.post('/inventory/equipment-category', { displayName: pendingNewCategory }) as unknown as { equipmentType: string; displayName: string };
          linkEquipType = catRes.equipmentType;
          queryClient.invalidateQueries({ queryKey: ['bom-equipment-types'] });
          toastMsg = `Item saved. Category "${pendingNewCategory}" created — add brands in Settings > Dropdowns.`;
        } catch (catErr) { console.error('[equipment-category POST] failed:', catErr); }
      }
      if (linkEquipType && supplyForm.supply_type === 'other' && supplyForm.brand?.trim() && supplyForm.model?.trim()) {
        try {
          await api.post('/inventory/equipment-category-brand', {
            equipmentType: linkEquipType,
            brand: supplyForm.brand.trim(),
            model: supplyForm.model.trim(),
          });
          queryClient.invalidateQueries({ queryKey: ['bom-equipment-types'] });
          invalidateDropdowns(queryClient, `${linkEquipType}_brand`);
          invalidateDropdowns(queryClient, `${linkEquipType}_model`);
        } catch (linkErr) { console.error('[equipment-category-brand POST] failed:', linkErr); }
      }
      queryClient.invalidateQueries({ queryKey: ['facility-supplies'] });
      setShowModal(false);
      setSupplyForm(blankSupply(activeType));
      resetSupplyImageState();
      setCategoryRedirectLabel(null);
      setPendingNewCategory(null);
      setCategoryEquipType(null);
      showSupplySaveToast(toastMsg);
    } catch (err) {
      console.error('[supplies/save] FULL ERROR:', err);
      const msg = err instanceof Error ? err.message : String(err);
      alert('Save failed: ' + msg);
    } finally {
      setSupplySaving(false);
    }
  }

  async function handleDelete(id: number) {
    await api.delete(`/inventory/supplies/${id}`);
    queryClient.invalidateQueries({ queryKey: ['facility-supplies'] });
    setConfirmDeleteSupplyId(null);
  }

  // Item 5 -- batch delete for the multi-selected cards. Same per-id DELETE endpoint as the
  // single-item flow above (no new server route needed), fired in parallel and settled before a
  // single invalidate + selection clear, rather than one request-then-refetch per item.
  async function handleBatchDelete() {
    setBatchDeleting(true);
    try {
      await Promise.all([...selectedSupplyIds].map((id) => api.delete(`/inventory/supplies/${id}`)));
    } finally {
      queryClient.invalidateQueries({ queryKey: ['facility-supplies'] });
      setSelectedSupplyIds(new Set());
      setConfirmBatchDelete(false);
      setBatchDeleting(false);
    }
  }

  const isPaint = activeType === 'paint';
  // Sliding highlight for the category pill row (SUPPLY_TYPES) -- same JS-measured technique as
  // Inventory's other pill switchers, reusing the existing .pill-tab-slide-highlight/-btn classes
  // rather than duplicating new ones: their colors already match this row's own hardcoded
  // rgba(255,255,255,0.15) active-background value exactly.
  const supplyTypeBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [supplyTypeHighlight, setSupplyTypeHighlight] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const el = supplyTypeBtnRefs.current[activeType];
    if (el) setSupplyTypeHighlight({ left: el.offsetLeft, width: el.offsetWidth });
  }, [activeType]);

  return (
    <div style={{ position: 'relative', background: 'transparent', paddingTop: 10 }}>
      {/* Save success toast */}
      <Toast toast={supplySaveToast} />

      {/* Type tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{
          display: 'inline-flex', gap: 2,
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 20, padding: 3, background: 'transparent',
          overflowX: isMobile ? 'auto' : 'hidden',
          position: 'relative',
        }}>
          <div className="pill-tab-slide-highlight" style={{width:supplyTypeHighlight.width,transform:`translateX(${supplyTypeHighlight.left}px)`}} />
          {SUPPLY_TYPES.map((t) => (
            <button
              key={t.id}
              ref={el => { supplyTypeBtnRefs.current[t.id] = el; }}
              onClick={() => handleSetSupplyTab(t.id)}
              className={activeType === t.id ? 'pill-tab-slide-btn active' : undefined}
              style={{
                position: 'relative', zIndex: 1,
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 14px', fontSize: 12, fontWeight: 500,
                color: activeType === t.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                borderRadius: 18, cursor: 'pointer',
                background: 'transparent',
                border: 'none', whiteSpace: 'nowrap',
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Special sub-tabs */}
      {activeType === 'hvac' ? (
        <HVACTab navigate={navigate} />
      ) : activeType === 'concentrators' ? (
        <ConcentratorsTab />
      ) : activeType === 'extinguishers' ? (
        <ExtinguishersTab extinguisherId={extinguisherId} onExtinguisherIdHandled={onExtinguisherIdHandled} />
      ) : isLoading || resolvingInitialSupply ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="loading-spinner" /></div>
      ) : supplies.length === 0 ? (
        <div className="empty-state">
          {SUPPLY_TYPES.find((t) => t.id === activeType)?.icon}
          <h3>No {SUPPLY_TYPES.find((t) => t.id === activeType)?.label} records</h3>
          <p>Track your facility {SUPPLY_TYPES.find((t) => t.id === activeType)?.label?.toLowerCase()} here.</p>
          <button className="btn btn-primary" onClick={openAdd}><Plus size={15} /> Add</button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(190px, 1fr))',
          gap: isMobile ? 10 : 14,
        }}>
          {supplies.map((s) => (
            <SupplyCard
              key={s.id}
              id={`supply-card-${s.id}`}
              supply={s}
              onView={() => setViewSupply(s)}
              onEdit={() => openEdit(s)}
              onDelete={() => setConfirmDeleteSupplyId(s.id)}
              selected={selectedSupplyIds.has(s.id)}
              onToggleSelect={() => toggleSupplySelected(s.id)}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setShowModal(false); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500, padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh' }}>
            <div style={{ flexShrink: 0, padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                {editSupply ? 'Edit' : 'Add'} {SUPPLY_TYPES.find((t) => t.id === activeType)?.label}
              </h3>
              <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }} onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-input" type="text" required value={supplyForm.name ?? ''} onChange={(e) => set('name', e.target.value)} />
              </div>

              {isPaint ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="form-group">
                      <label className="form-label">Brand</label>
                      <DropdownCombobox dropdown_type="paint_brand" value={supplyForm.brand ?? ''} onChange={(v) => { set('brand', v); set('color', ''); }} onSelectOption={(opt) => { brandJustSelectedRef.current = true; setBrandOptId(opt?.id ?? null); set('color', ''); }} placeholder="Sherwin-Williams…" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Color / Name</label>
                      <DropdownCombobox dropdown_type="paint_color" value={supplyForm.color ?? ''} onChange={(v) => set('color', v)} parentId={brandOptId} placeholder="SW 7015 Repose Gray" />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="form-group">
                      <label className="form-label">Finish</label>
                      <select className="form-select" value={supplyForm.finish ?? ''} onChange={(e) => set('finish', e.target.value)}>
                        <option value="">Select…</option>
                        {FINISH_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Room / Area</label>
                      <input className="form-input" type="text" value={supplyForm.room_area ?? ''} onChange={(e) => set('room_area', e.target.value)} placeholder="Hallway 2nd floor" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                {activeType === 'other' && (
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="form-label">Category</label>
                    <BomCategoryCombobox
                      value={supplyForm.category ?? ''}
                      onChange={(v) => set('category', v)}
                      bomTypes={bomTypeEntries}
                      onSelectEntry={(entry) => {
                        if (entry?.facilitySupplyType) {
                          setSupplyForm(f => ({ ...f, supply_type: entry.facilitySupplyType as FacilitySupply['supply_type'], brand: '', model: '' }));
                          setCategoryRedirectLabel(entry.displayName);
                          setCategoryEquipType(null);
                        } else {
                          setSupplyForm(f => ({ ...f, supply_type: 'other' }));
                          setCategoryRedirectLabel(null);
                          setCategoryEquipType(entry?.type ?? null);
                        }
                        setPendingNewCategory(null);
                      }}
                      onNewCategory={(label) => {
                        setPendingNewCategory(label);
                        setCategoryRedirectLabel(null);
                        setCategoryEquipType(null); // resolved after creation in handleSubmit
                      }}
                    />
                    {categoryRedirectLabel && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--accent-primary)' }}>
                        This item will be saved under {categoryRedirectLabel}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label className="form-label">Brand</label>
                    {effectiveFormType === 'wheelchair' ? (
                      <DropdownCombobox
                        dropdown_type="wheelchair_brand"
                        value={supplyForm.brand ?? ''}
                        onChange={(v) => set('brand', v)}
                        placeholder="Select or type brand…"
                      />
                    ) : effectiveFormType === 'bed' ? (
                      <DropdownCombobox dropdown_type="bed_brand" value={supplyForm.brand ?? ''} onChange={(v) => { set('brand', v); set('model', ''); }} onSelectOption={(opt) => { brandJustSelectedRef.current = true; setBrandOptId(opt?.id ?? null); set('model', ''); }} placeholder="Select or type brand…" />
                    ) : effectiveFormType === 'lift' ? (
                      <DropdownCombobox dropdown_type="lift_brand" value={supplyForm.brand ?? ''} onChange={(v) => { set('brand', v); set('model', ''); }} onSelectOption={(opt) => { brandJustSelectedRef.current = true; setBrandOptId(opt?.id ?? null); set('model', ''); }} placeholder="Select or type brand…" />
                    ) : effectiveFormType === 'other' ? (
                      <DropdownCombobox dropdown_type="other_brand" value={supplyForm.brand ?? ''} onChange={(v) => { set('brand', v); set('model', ''); }} onSelectOption={(opt) => { brandJustSelectedRef.current = true; setBrandOptId(opt?.id ?? null); set('model', ''); }} placeholder="Select or type brand…" />
                    ) : (
                      <input className="form-input" type="text" value={supplyForm.brand ?? ''} onChange={(e) => set('brand', e.target.value)} />
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Model</label>
                    {effectiveFormType === 'wheelchair' ? (
                      <DropdownCombobox
                        dropdown_type="wheelchair_model"
                        value={supplyForm.model ?? ''}
                        onChange={(v) => set('model', v)}
                        placeholder="Select or type model…"
                        externalOptions={wheelchairBrandMatchId !== null ? (wcModels as DropdownOption[]) : undefined}
                        parentId={wheelchairBrandMatchId}
                      />
                    ) : effectiveFormType === 'bed' ? (
                      <DropdownCombobox dropdown_type="bed_model" value={supplyForm.model ?? ''} onChange={(v) => set('model', v)} parentId={brandOptId} placeholder="Select or type model…" />
                    ) : effectiveFormType === 'lift' ? (
                      <DropdownCombobox dropdown_type="lift_model" value={supplyForm.model ?? ''} onChange={(v) => set('model', v)} parentId={brandOptId} placeholder="Select or type model…" />
                    ) : effectiveFormType === 'other' ? (
                      <DropdownCombobox dropdown_type="other_model" value={supplyForm.model ?? ''} onChange={(v) => set('model', v)} parentId={brandOptId} placeholder="Select or type model…" />
                    ) : (
                      <input className="form-input" type="text" value={supplyForm.model ?? ''} onChange={(e) => set('model', e.target.value)} />
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Serial #</label>
                    <input className="form-input" type="text" value={supplyForm.serial_number ?? ''} onChange={(e) => set('serial_number', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Service Date</label>
                    <DatePicker value={supplyForm.last_service_date ?? ''} onChange={(v) => set('last_service_date', v)} placeholder="Select a date" />
                  </div>
                </div>
                </>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <LocationSelector
                    value={supplyForm.location ?? ''}
                    onChange={(v) => set('location', v)}
                    facilityId={currentFacilityId}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Condition</label>
                  <select className="form-select" value={supplyForm.condition ?? 'good'} onChange={(e) => set('condition', e.target.value as FacilitySupply['condition'])}>
                    {(activeType === 'other'
                      ? ['good', 'fair', 'poor', 'needs_service']
                      : CONDITION_OPTIONS
                    ).map((c) => <option key={c} value={c}>{c === 'needs_service' ? 'Needs Service' : c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={supplyForm.notes ?? ''} onChange={(e) => set('notes', e.target.value)} style={{ minHeight: 56 }} />
              </div>

              <div className="form-group">
                <label className="form-label">Barcode</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    className="form-input"
                    type="text"
                    value={supplyForm.barcode ?? ''}
                    onChange={(e) => set('barcode', e.target.value || null)}
                    onKeyDown={handleSupplyBarcodeKeyDown}
                    placeholder="Scan or enter barcode…"
                    style={{ flex: 1, fontFamily: 'monospace' }}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowSupplyCamera(true)}
                    title="Use camera to scan"
                    style={{ flexShrink: 0 }}
                  >
                    <Camera size={15} />
                  </button>
                </div>
              </div>

              {/* Image upload */}
              <div className="form-group">
                <label className="form-label">Image</label>
                <div style={{ display: 'flex', width: '100%', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border-default)', marginBottom: 8 }}>
                  {(['file', 'url'] as const).map((mode) => (
                    <button key={mode} type="button"
                      onClick={() => { setSupplyImageInputMode(mode); setSupplyImageFromCatalog(false); }}
                      style={{ flex: 1, padding: '5px 0', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500, background: supplyImageInputMode === mode ? 'var(--accent-primary)' : 'transparent', color: supplyImageInputMode === mode ? '#fff' : 'var(--text-muted)', transition: 'background 0.15s' }}
                    >{mode === 'file' ? 'Upload File' : 'Paste URL'}</button>
                  ))}
                </div>
                <input ref={supplyImageInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSupplyImageSelect(f); e.target.value = ''; }} />
                {supplyImageInputMode === 'url' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {supplyImageFromCatalog && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        From catalog — tap Upload File to use your own photo
                      </div>
                    )}
                    <input className="form-input" type="url" placeholder="https://example.com/image.jpg" value={supplyImageUrlInput}
                      onChange={(e) => { setSupplyImageUrlInput(e.target.value); setSupplyImageFromCatalog(false); }} style={{ fontSize: '0.78rem' }} />
                    {supplyImageUrlInput.trim() && (
                      <img src={supplyImageUrlInput.trim()} alt="Preview" style={{ height: 80, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border-subtle)' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }} />
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}
                      onClick={() => supplyImageInputRef.current?.click()}>
                      <Upload size={13} /> {supplyPendingImage ? 'Change' : 'Choose File'}
                    </button>
                    {(supplyImagePreview ?? supplyForm.image_url) && (
                      <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                        <img src={supplyImagePreview ?? getImageSrc(supplyForm.image_url) ?? undefined} alt="Preview"
                          style={{ height: 50, objectFit: 'contain', borderRadius: 4, border: '1px solid var(--border-subtle)' }} />
                        <button type="button"
                          onClick={() => { setSupplyPendingImage(null); setSupplyImagePreview(null); setSupplyForm((f) => ({ ...f, image_url: null })); }}
                          style={{
                            position: 'absolute', top: -6, right: -6,
                            width: 18, height: 18,
                            background: 'var(--accent-danger)', border: 'none', borderRadius: '50%',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: 0,
                          }}>
                          <X size={10} color="#fff" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              </div>
              <div style={{ flexShrink: 0, padding: '12px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={supplySaving}>
                  {supplySaving ? 'Saving…' : editSupply ? 'Save' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Blur overlay behind detail panel */}
      {viewSupply && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 998,
            pointerEvents: 'auto',
            cursor: 'pointer',
          }}
          onClick={() => setViewSupply(null)}
        />
      )}

      {/* Supply detail — full-page takeover for wheelchairs, lifts, other, and now beds (this
          round's rollout step; SupplyDetailTakeover is generic over supply.supply_type/brand/
          model already, no bed-specific branch needed inside it), slide-in panel for every
          remaining supply_type unchanged until the takeover design is settled for those too. */}
      {viewSupply && (
        ['wheelchair', 'lift', 'other', 'bed'].includes(viewSupply.supply_type) ? (
          <SupplyDetailTakeover
            supply={viewSupply}
            allSupplies={supplies}
            isEnterprise={user?.orgPlanTier === 'enterprise' || user?.role === 'admin'}
            onClose={() => setViewSupply(null)}
            onEdit={() => { openEdit(viewSupply); setViewSupply(null); }}
            onDelete={() => setConfirmDeleteSupplyId(viewSupply.id)}
          />
        ) : (
          <SupplyDetailPanel
            supply={viewSupply}
            onClose={() => setViewSupply(null)}
            onEdit={() => { openEdit(viewSupply); setViewSupply(null); }}
            onDelete={() => setConfirmDeleteSupplyId(viewSupply.id)}
            onBom={() => {
              sessionStorage.setItem('inventory_catalogue_state', JSON.stringify({ tab: 'supplies', scrollY: window.scrollY }));
              sessionStorage.setItem(SUPPLIES_STATE_KEY, JSON.stringify({ activeType }));
              navigate(`/inventory/${viewSupply.id}/bom`);
            }}
            onUsedIn={() => { setViewUsedIn(viewSupply); setViewSupply(null); }}
          />
        )
      )}

      {/* Paint supply "Used In" split-view panel */}
      {viewUsedIn && (
        <PaintUsedInPanel
          supply={viewUsedIn}
          onClose={() => setViewUsedIn(null)}
        />
      )}

      {/* Barcode camera scanner */}
      {showSupplyCamera && (
        <CameraScannerModal
          onDetect={(val) => { set('barcode', val); setShowSupplyCamera(false); }}
          onClose={() => setShowSupplyCamera(false)}
        />
      )}

      {/* Delete confirm modal */}
      {confirmDeleteSupplyId !== null && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setConfirmDeleteSupplyId(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Supply</h3>
              <button className="modal-close" onClick={() => setConfirmDeleteSupplyId(null)}><X size={18} /></button>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Permanently delete this supply record? This cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDeleteSupplyId(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(confirmDeleteSupplyId!)}>
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {activeType !== 'hvac' && activeType !== 'concentrators' && activeType !== 'extinguishers' && (
        <>
          {/* Batch delete (item 5) -- only once 2+ selected, positioned left of the Add FAB
              (right: 28+48+12 = 88, same 48px shell width + a 12px gap) so both are reachable at
              once rather than one covering the other. */}
          {selectedSupplyIds.size >= 2 && (
            <FloatingActionButton
              icon={<Trash2 size={18} className="btn-icon" />}
              onClick={() => setConfirmBatchDelete(true)}
              title={`Delete ${selectedSupplyIds.size} selected`}
              ariaLabel="Delete selected"
              tint="red"
              right={88}
            />
          )}
          {/* Deselect All -- shows at 1+ selected (batch delete itself only shows at 2+), so this
              is the only floating control at exactly 1 selected. Slides one shell-width+gap
              further left whenever batch delete is also visible, so neither ever overlaps. */}
          {selectedSupplyIds.size >= 1 && (
            <FloatingActionButton
              icon={<X size={18} className="btn-icon" />}
              onClick={() => setSelectedSupplyIds(new Set())}
              title="Deselect all"
              ariaLabel="Deselect all"
              tint="neutral"
              right={selectedSupplyIds.size >= 2 ? 148 : 88}
            />
          )}
          <FloatingActionButton
            icon={<Plus size={20} className="btn-icon" />}
            onClick={openAdd}
            title="Add supply"
            ariaLabel="Add supply"
          />
        </>
      )}

      {/* Batch delete confirm -- same modal shape/copy pattern as the single-item confirm above,
          consistent with how that one already confirms before deleting. */}
      {confirmBatchDelete && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget && !batchDeleting) setConfirmBatchDelete(false); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete {selectedSupplyIds.size} Supplies</h3>
              <button className="modal-close" onClick={() => setConfirmBatchDelete(false)} disabled={batchDeleting}><X size={18} /></button>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Permanently delete {selectedSupplyIds.size} selected supply records? This cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setConfirmBatchDelete(false)} disabled={batchDeleting}>Cancel</button>
              <button className="btn btn-danger" onClick={handleBatchDelete} disabled={batchDeleting}>
                <Trash2 size={14} /> {batchDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Category Sidebar Item (recursive, with expand/collapse for subcategories) ─
function CatSidebarItem({
  cat,
  sidebarExpanded,
  activeCategoryId,
  allCategories,
  onSelect,
  onManage,
  onExpandToggle,
  expandedCatIds,
  colorMap,
  depth = 0,
}: {
  cat: Category;
  sidebarExpanded: boolean;
  activeCategoryId: number | null;
  allCategories: Category[];
  onSelect: (id: number) => void;
  onManage: (catId: number) => void;
  onExpandToggle: (catId: number, parentId: number | null) => void;
  expandedCatIds: Set<number>;
  colorMap: Map<number, string>;
  depth?: number;
}) {
  const children = allCategories.filter((c) => c.parent_id === cat.id);
  const hasChildren = children.length > 0;

  // Helper: is this cat or any descendant currently selected?
  function isDescendantActive(catId: number): boolean {
    if (activeCategoryId === catId) return true;
    return allCategories.filter((c) => c.parent_id === catId).some((c) => isDescendantActive(c.id));
  }

  const isActive = activeCategoryId === cat.id;
  const childActive = hasChildren && isDescendantActive(cat.id) && !isActive;
  const treeExpanded = expandedCatIds.has(cat.id);
  const [hovered, setHovered] = useState(false);
  const color = colorMap.get(cat.id) ?? getCategoryColor(cat.id);

  return (
    <>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 0,
          width: '100%', borderRadius: 'var(--radius-sm)',
          background: isActive ? `${color}18` : hovered ? 'var(--bg-hover)' : 'none',
          transition: 'background var(--transition)',
          position: 'relative',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Depth indent spacer — drives indentation at every level without breaking button layout */}
        {sidebarExpanded && depth > 0 && (
          <span style={{ width: depth * 12, flexShrink: 0, display: 'inline-block' }} />
        )}
        {/* Chevron slot for expandable categories -- always reserved at the same fixed width when
            the sidebar is expanded, regardless of hasChildren, so every row's icon/label lines up
            in the same column. Categories with no subcategories get an invisible, non-interactive
            placeholder here instead of the button being omitted outright. */}
        {sidebarExpanded && (
          hasChildren ? (
            <button
              onClick={() => onExpandToggle(cat.id, cat.parent_id ?? null)}
              style={{
                flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 18, height: 32,
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)',
              }}
              title={treeExpanded ? 'Collapse' : 'Expand'}
            >
              <ChevronRight
                size={11}
                style={{ transform: treeExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }}
              />
            </button>
          ) : (
            <span style={{ flexShrink: 0, width: 18, height: 32, display: 'block' }} aria-hidden="true" />
          )
        )}
        <button
          onClick={() => onSelect(cat.id)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center',
            justifyContent: sidebarExpanded ? 'flex-start' : 'center',
            gap: 8,
            padding: sidebarExpanded ? '6px 10px' : '6px 0',
            border: 'none', cursor: 'pointer',
            background: 'none',
            color: isActive ? color : childActive ? 'var(--text-primary)' : 'var(--text-secondary)',
            fontSize: 13,
            fontWeight: (isActive || childActive) ? 500 : 400,
            textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden',
            minHeight: 36,
            minWidth: 0,
          }}
          title={cat.name}
        >
          <span style={{ flexShrink: 0 }}><CategoryIcon iconName={cat.icon_path} color={color} /></span>
          {sidebarExpanded && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</span>}
        </button>
        {hovered && sidebarExpanded && (
          <button
            onClick={(e) => { e.stopPropagation(); onManage(cat.id); }}
            style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 24, height: 24, margin: '0 4px 0 0',
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', borderRadius: 4,
            }}
            title="Edit category"
          >
            <Edit size={12} />
          </button>
        )}
      </div>

      {/* Render children when tree is expanded */}
      {hasChildren && treeExpanded && sidebarExpanded && children.map((child) => (
        <CatSidebarItem
          key={child.id}
          cat={child}
          sidebarExpanded={sidebarExpanded}
          activeCategoryId={activeCategoryId}
          allCategories={allCategories}
          onSelect={onSelect}
          onManage={onManage}
          onExpandToggle={onExpandToggle}
          expandedCatIds={expandedCatIds}
          colorMap={colorMap}
          depth={depth + 1}
        />
      ))}
    </>
  );
}

// ─── Pending Imports ─────────────────────────────────────────────────────────
interface CatalogueItem {
  id: number;
  name: string;
  part_number: string | null;
  price: number | null;
  description: string | null;
  image_url: string | null;
  image_path: string | null;
  source_url: string | null;
  supplier: string | null;
  brand: string | null;
  mfr_part_number: string | null;
  category: string | null;
  category_id: number | null;
  quantity: number | null;
  unit: string | null;
  location: string | null;
  barcode: string | null;
  notes: string | null;
  discontinued: boolean | null;
  replacement_url: string | null;
  status: 'pending' | 'active' | 'rejected';
  created_at: string;
}

// ─── Full Edit Modal for Pending Import ───────────────────────────────────────
function PendingItemEditModal({
  item,
  categories,
  onClose,
  onDraftSaved,
  onApproved,
  onRejected,
}: {
  item: CatalogueItem;
  categories: Category[];
  onClose: () => void;
  onDraftSaved: () => void;
  onApproved: () => void;
  onRejected: () => void;
}) {
  const isGrainger = !!(item.source_url?.includes('grainger.com'));

  // Try to auto-match item.category string against the facility's category list
  const initialCategoryId = (() => {
    if (item.category_id) return item.category_id;
    if (item.category) {
      const match = categories.find((c) => c.name.toLowerCase() === item.category!.toLowerCase());
      if (match) return match.id;
    }
    return 0;
  })();

  // Whether the imported category string has no exact match in the facility list
  const importedCategoryUnmatched =
    !!item.category &&
    !categories.find((c) => c.name.toLowerCase() === item.category!.toLowerCase());

  const [form, setForm] = useState({
    name:        item.name        || '',
    part_number: item.part_number || '',
    price:       item.price       ?? 0,
    description: (item.description || '').replace(/\\n/g, '\n'),
    supplier:    item.supplier    || '',
    brand:       item.brand       || '',
    category:    item.category    || '',
    category_id: initialCategoryId,
    image_url:   item.image_url   || '',
    quantity:    item.quantity    ?? 1,
    unit:        item.unit        || 'ea',
    location:    item.location    || '',
    barcode:     item.barcode     || '',
    notes:       item.notes       || '',
    used_for:    [] as string[],
    department:  '',
  });
  const qc = useQueryClient();
  const [isApproving, setIsApproving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [categoryError, setCategoryError] = useState(false);
  const [isDuplicateWarning, setIsDuplicateWarning] = useState(false);
  const [duplicateExisting, setDuplicateExisting] = useState<{ name: string; part_number?: string } | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const { data: bomEquipmentData = { types: [] } } = useQuery({
    queryKey: ['bom-equipment-types'],
    queryFn: async () => (await api.get('/inventory/bom-equipment')) as unknown as { types: any[] },
  });

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function handleImageSelect(file: File) {
    setPendingImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  function handleImageDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) handleImageSelect(file);
  }

  async function handleDraft() {
    if (isApproving) return;
    if (!form.name.trim()) { setError('Name is required'); return; }
    setIsApproving(true);
    setError(null);
    const token = localStorage.getItem('accessToken');
    try {
      console.log('[edit-modal] PUT', `/api/catalogue/items/${item.id}`);
      const res = await fetch(`/api/catalogue/items/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          price: parseFloat(String(form.price)) || null,
          category: categories.find((c) => c.id === form.category_id)?.name ?? form.category,
        }),
      });
      console.log('[edit-modal] PUT response status:', res.status);
      if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);
      qc.invalidateQueries({ queryKey: ['catalogue-pending'] });
      onDraftSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save draft');
    } finally {
      setIsApproving(false);
    }
  }

  async function handleApprove(force = false) {
    if (isApproving) return;
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.category_id) { setCategoryError(true); setError('Please select a category before approving'); return; }
    setIsApproving(true);
    setError(null);
    const token = localStorage.getItem('accessToken');
    try {
      // If user replaced the image with a local file, upload it first
      let finalImageUrl = form.image_url;
      if (pendingImageFile) {
        const fd = new FormData();
        fd.append('image', pendingImageFile);
        console.log('[edit-modal] POST', `/api/catalogue/items/${item.id}/image`);
        const uploadRes = await fetch(`/api/catalogue/items/${item.id}/image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        console.log('[edit-modal] POST image response status:', uploadRes.status);
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json() as { data?: { image_path?: string } };
          // image_path is now either a Cloudinary secure_url (already absolute) or a legacy
          // relative local-disk path -- getImageSrc handles both correctly (passes an absolute
          // URL through unchanged, only origin-prefixes a bare relative path). Previously this
          // unconditionally prepended '/uploads/', which would have produced a broken
          // '/uploads/https://...' URL once the upload route started returning Cloudinary URLs.
          const uploadedPath = uploadData.data?.image_path;
          if (uploadedPath) finalImageUrl = getImageSrc(uploadedPath) ?? finalImageUrl;
        }
      }

      console.log('[edit-modal] PATCH', `/api/catalogue/items/${item.id}/approve`, force ? '(force)' : '');
      const res = await fetch(`/api/catalogue/items/${item.id}/approve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          image_url: finalImageUrl,
          price: parseFloat(String(form.price)) || null,
          category: categories.find((c) => c.id === form.category_id)?.name ?? form.category,
          ...(force ? { force: true } : {}),
        }),
      });
      console.log('[edit-modal] PATCH approve response status:', res.status);
      if (res.status === 409) {
        const d = await res.json().catch(() => ({})) as { existing?: { name: string; part_number?: string } };
        setIsDuplicateWarning(true);
        setDuplicateExisting(d.existing ?? null);
        setIsApproving(false);
        return;
      }
      if (!res.ok) {
        const d = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? `Server error ${res.status}`);
      }
      qc.invalidateQueries({ queryKey: ['catalogue-pending-count'] });
      qc.invalidateQueries({ queryKey: ['catalogue-pending'] });
      onApproved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to approve item');
    } finally {
      setIsApproving(false);
    }
  }

  const imgSrc = imagePreview ?? getImageSrc(item.image_path) ?? getImageSrc(form.image_url);

  return ReactDOM.createPortal(
    <div className="modal-overlay"
      style={{ zIndex: 10000 }}
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, width: '100%', padding: 0, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 className="modal-title">Review &amp; Edit Import</h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              Select a category and review all fields before approving
            </p>
          </div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 200px', overflow: 'hidden', flex: 1, minHeight: 0 }}>
          {/* Left: form fields */}
          <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Discontinued warning banner */}
            {item.discontinued && (
              <div style={{
                padding: '12px 16px', marginBottom: 2,
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.4)',
                borderRadius: 8, color: 'rgba(239,68,68,0.7)', fontSize: 13,
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠️ This product has been discontinued</div>
                {item.replacement_url && (
                  <div>
                    Replacement available:{' '}
                    <a href={item.replacement_url} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#60a5fa', textDecoration: 'underline' }}>
                      View replacement product →
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Duplicate warning — inline inside scrollable area */}
            {isDuplicateWarning && (
              <div style={{
                padding: '12px 16px',
                background: 'rgba(245,158,11,0.1)',
                border: '1px solid rgba(245,158,11,0.4)',
                borderRadius: 8, color: 'var(--accent-warning)', fontSize: 13,
              }}>
                <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={14} /> Duplicate detected
                </div>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 10, fontSize: '0.8125rem' }}>
                  &ldquo;{duplicateExisting?.name ?? 'This item'}&rdquo;
                  {duplicateExisting?.part_number ? ` (Part# ${duplicateExisting.part_number})` : ''} already exists in your inventory.
                  This may be a false positive — you can approve anyway or reject the import.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={isApproving}
                    onClick={() => { void handleApprove(true); }}
                  >
                    {isApproving ? 'Approving…' : 'Approve Anyway'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => { onRejected(); }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            )}

            {/* Source link — read-only */}
            {item.source_url && (
              <div className="form-group">
                <label className="form-label">Source Link</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="form-input" value={item.source_url} readOnly style={{ flex: 1, opacity: 0.7, fontSize: '0.8125rem' }} />
                  <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}><ExternalLink size={14} /></a>
                </div>
              </div>
            )}

            {/* Name */}
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                className="form-input"
                type="text"
                value={form.name}
                onChange={(e) => { set('name', e.target.value); setError(null); setIsDuplicateWarning(false); }}
                placeholder="Item name"
              />
            </div>

            {/* Category */}
            <div className="form-group">
              <label className="form-label" style={{ color: categoryError && !form.category_id ? 'var(--accent-danger)' : undefined }}>
                Category *{categoryError && !form.category_id && <span style={{ fontWeight: 400, fontSize: '0.75rem' }}> — required before approving</span>}
              </label>
              {importedCategoryUnmatched && (
                <div style={{ fontSize: 12, color: 'var(--accent-warning)', marginBottom: 4 }}>
                  ⤵ From import: {item.category}
                </div>
              )}
              <CategoryPicker
                categories={categories}
                value={form.category_id}
                onChange={(id) => { set('category_id', id); setCategoryError(false); setError(null); }}
                hasError={categoryError && !form.category_id}
              />
            </div>

            {/* Part number + Price */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">{isGrainger ? 'Grainger Item #' : 'Part Number'}</label>
                <input
                  className="form-input"
                  type="text"
                  value={form.part_number}
                  onChange={(e) => { set('part_number', e.target.value); setIsDuplicateWarning(false); setError(null); }}
                  placeholder="MFR-0001"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Price ($)</label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.price}
                  onChange={(e) => set('price', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>

            {/* MFR Part # — Grainger only */}
            {item.mfr_part_number && (
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  MFR Part #
                  <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '1px 6px', borderRadius: 4 }}>read-only</span>
                </label>
                <input className="form-input" value={item.mfr_part_number} readOnly style={{ opacity: 0.7, fontFamily: 'monospace' }} />
              </div>
            )}

            {/* Supplier */}
            <div className="form-group">
              <label className="form-label">Supplier</label>
              <SupplierField
                value={form.supplier}
                onChange={(v) => set('supplier', v)}
              />
            </div>

            {/* Brand */}
            <div className="form-group">
              <label className="form-label">Brand</label>
              <input
                className="form-input"
                type="text"
                value={form.brand}
                onChange={(e) => set('brand', e.target.value)}
                placeholder="Manufacturer / brand"
              />
            </div>

            {/* Used For + Department */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Used For</label>
                <BomSelector
                  selected={Array.isArray(form.used_for) ? (form.used_for as string[]) : []}
                  onChange={(v) => set('used_for', v)}
                  equipmentData={bomEquipmentData}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <DarkSelect value={form.department ?? ''} onChange={(v) => set('department', v)} options={DEPT_OPTIONS} placeholder="Select…" />
              </div>
            </div>

            {/* Qty + Unit */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Qty on Hand</label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={form.quantity}
                  onChange={(e) => set('quantity', Number(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Unit</label>
                <select className="form-select" value={form.unit} onChange={(e) => set('unit', e.target.value)}>
                  {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            {/* Location + Barcode */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Storage Location</label>
                <input
                  className="form-input"
                  type="text"
                  value={form.location}
                  onChange={(e) => set('location', e.target.value)}
                  placeholder="Closet 3B, Shelf 2"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Barcode</label>
                <input
                  className="form-input"
                  type="text"
                  value={form.barcode}
                  onChange={(e) => set('barcode', e.target.value)}
                  placeholder="Scan or type barcode"
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
            </div>

            {/* Description */}
            <div className="form-group">
              <label className="form-label">Description</label>
              <RichTextEditor
                value={form.description}
                onChange={(v) => set('description', v)}
                placeholder="Product description"
                rows={4}
              />
            </div>

            {/* Notes */}
            <div className="form-group">
              <label className="form-label">Notes</label>
              <RichTextEditor
                value={form.notes}
                onChange={(v) => set('notes', v)}
                placeholder="Internal notes"
                rows={2}
              />
            </div>
          </div>

          {/* Right: image */}
          <div style={{
            borderLeft: '1px solid var(--border-subtle)',
            background: 'var(--bg-elevated)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '20px 16px', gap: 10,
          }}>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageSelect(f); e.target.value = ''; }}
            />

            {/* Preview / drop zone */}
            <div
              onClick={() => imageInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleImageDrop}
              style={{
                width: '100%', aspectRatio: '1',
                background: 'var(--bg-elevated)',
                border: '2px dashed var(--border-default)',
                borderRadius: 'var(--radius-md)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 8, color: 'var(--text-muted)', cursor: 'pointer',
                overflow: 'hidden', position: 'relative',
                transition: 'border-color var(--transition)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--accent-primary)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-default)'; }}
            >
              {imgSrc ? (
                <>
                  <img
                    src={imgSrc}
                    alt="Item preview"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 6 }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 6, color: '#fff', fontSize: '0.75rem',
                    opacity: 0, transition: 'opacity 0.2s',
                  }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '1'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '0'; }}
                  >
                    <Upload size={18} color="#fff" /> Change
                  </div>
                </>
              ) : (
                <>
                  <Upload size={24} style={{ opacity: 0.4 }} />
                  <span style={{ fontSize: '0.75rem', textAlign: 'center', lineHeight: 1.4 }}>
                    Click or drop<br/>to upload image
                  </span>
                </>
              )}
            </div>

            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>
              PNG, JPG, WebP · Max 5 MB
            </p>
          </div>
        </div>

        {/* Footer — error banner lives here so it never pushes buttons off screen */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid var(--border-subtle)',
          display: 'flex', flexDirection: 'column', gap: 10,
          background: 'var(--bg-elevated)',
        }}>
          {error && (
            <div style={{
              padding: '10px 16px',
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 6, color: 'rgba(239,68,68,0.7)', fontSize: '0.8125rem',
            }}>
              ⚠ {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isApproving}>Cancel</button>
            <button type="button" className="btn btn-ghost" onClick={() => { void handleDraft(); }} disabled={isApproving} style={{ color: 'var(--accent-primary)' }}>
              {isApproving ? '…' : 'Save as Draft'}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => { void handleApprove(); }} disabled={isApproving}>
              <CheckCircle size={14} /> {isApproving ? 'Approving…' : 'Save & Approve'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Pending Imports Panel ────────────────────────────────────────────────────
function PendingImportsPanel({ onClose, onApproved, categories }: {
  onClose: () => void;
  onApproved: () => void;
  categories: Category[];
}) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['catalogue-pending'],
    queryFn: () => api.get<CatalogueItem[]>('/catalogue/items', { status: 'pending' }),
    refetchOnMount: 'always',
    staleTime: 0,
  });
  const items: CatalogueItem[] = (data as unknown as { data: CatalogueItem[] })?.data ?? [];

  const [editItem, setEditItem] = useState<CatalogueItem | null>(null);
  const [working, setWorking] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (editItem) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [editItem]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleReject(id: number) {
    setWorking(id);
    try {
      await api.delete(`/catalogue/items/${id}`);
      showToast('Item rejected and removed');
      refetch();
      qc.invalidateQueries({ queryKey: ['catalogue-pending-count'] });
    } catch {
      showToast('Failed to reject item');
    } finally {
      setWorking(null);
    }
  }

  async function handleDirectApprove(item: CatalogueItem) {
    const catId =
      item.category_id ??
      categories.find((c) => c.name.toLowerCase() === (item.category ?? '').toLowerCase())?.id ??
      null;
    // If no category can be resolved, open the edit modal so the user can pick one
    if (!catId) {
      setEditItem(item);
      return;
    }
    setWorking(item.id);
    try {
      await api.post(`/catalogue/items/${item.id}/approve`, { category_id: catId });
      // Optimistically remove approved item from list immediately
      qc.setQueryData(['catalogue-pending'], (old: unknown) => {
        const d = old as { data: CatalogueItem[] } | undefined;
        if (!d?.data) return old;
        return { ...d, data: d.data.filter((i) => i.id !== item.id) };
      });
      showToast('Item approved and added to inventory');
      void refetch();
      onApproved();
      qc.invalidateQueries({ queryKey: ['catalogue-pending-count'] });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to approve item';
      showToast(msg);
    } finally {
      setWorking(null);
    }
  }

  return (
    <>
      {editItem && (
        <PendingItemEditModal
          item={editItem}
          categories={categories}
          onClose={() => setEditItem(null)}
          onDraftSaved={() => { refetch(); showToast('Draft saved'); }}
          onApproved={() => { refetch(); onApproved(); showToast('Item approved and added to inventory'); }}
          onRejected={() => { void handleReject(editItem.id); setEditItem(null); }}
        />
      )}

      {ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 998 }}>
          <div
            onClick={() => { if (!editItem) onClose(); }}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              zIndex: 998,
            }}
          />
          <div
            style={{
              position: 'fixed', top: 0, right: 0,
              width: 520, maxWidth: '95vw', height: '100vh',
              background: 'var(--bg-panel)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderLeft: '1px solid var(--border-subtle)',
              display: 'flex', flexDirection: 'column',
              overflowY: 'auto',
              zIndex: 999,
            }}
            onClick={(e) => e.stopPropagation()}
          >
          {/* Header */}
          <div style={{
            padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.09)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'sticky', top: 0, background: 'rgba(20,30,50,0.97)',
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            zIndex: 10, paddingBottom: '12px',
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700 }}>Pending Imports</h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                Click "Edit &amp; Review" to open the full edit form and select a category before approving
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                className="modal-close"
                onClick={() => refetch()}
                title="Refresh"
                style={{ animation: isLoading ? 'spin 0.8s linear infinite' : undefined }}
              >
                <RefreshCw size={16} />
              </button>
              <button className="modal-close" onClick={() => { setEditItem(null); onClose(); }}><X size={18} /></button>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: 20, flex: 1 }}>
            {toast && (
              <div style={{
                marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--radius-md)',
                background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
                color: 'var(--accent-success)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <CheckCircle size={15} /> {toast}
              </div>
            )}

            {isLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><div className="loading-spinner" /></div>
            ) : items.length === 0 ? (
              <div className="empty-state">
                <Clock size={40} />
                <h3>No pending imports</h3>
                <p>Items imported from the Chrome extension will appear here for review.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {items.map((item) => {
                  const imgSrc = getImageSrc(item.image_path) ?? getImageSrc(item.image_url);
                  const descPreview = item.description
                    ? item.description.replace(/\\n/g, '\n').replace(/\n/g, ' ').slice(0, 150) + (item.description.length > 150 ? '…' : '')
                    : null;

                  return (
                    <div
                      key={item.id}
                      style={{
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'border-color var(--transition)',
                      }}
                      onClick={() => setEditItem(item)}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--accent-primary)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-subtle)'; }}
                    >
                      {/* Item content */}
                      <div style={{ padding: 14, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        {/* Image */}
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={item.name}
                            style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 6, flexShrink: 0, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div style={{ width: 64, height: 64, borderRadius: 6, flexShrink: 0, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Package size={24} color="var(--text-muted)" />
                          </div>
                        )}

                        {/* Details */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9375rem', marginBottom: 4, wordBreak: 'break-word' }}>{item.name}</div>
                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                            {item.part_number && <span>Part# {item.part_number}</span>}
                            {item.price != null && <span>${Number(item.price).toFixed(2)}</span>}
                            {item.supplier && <span>{item.supplier}</span>}
                          </div>
                          {descPreview && (
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                              {descPreview}
                            </p>
                          )}
                          {item.source_url && (
                            <a
                              href={item.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: '0.75rem', color: 'var(--accent-primary)', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink size={11} /> Source
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Action row */}
                      <div
                        style={{ padding: '10px 14px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setEditItem(item)}
                          disabled={working === item.id}
                          style={{ color: 'var(--accent-primary)' }}
                        >
                          <Edit size={13} /> Edit &amp; Review
                        </button>
                        {(item.category_id || item.category) && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleDirectApprove(item)}
                            disabled={working === item.id}
                            style={{ color: 'var(--accent-success)' }}
                          >
                            <Check size={13} /> Approve
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleReject(item.id)}
                          disabled={working === item.id}
                          style={{ color: 'var(--accent-danger)' }}
                        >
                          <Trash2 size={13} /> Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
// ─── Extinguisher types ───────────────────────────────────────────────────────
interface Extinguisher {
  id: number; facility_id: number;
  type_model: string | null; location: string | null; quantity: number | null;
  serial_number: string | null; last_inspection_date: string | null;
  next_due_date: string | null; notes: string | null;
  created_at: string; updated_at: string;
}
interface ExtImportSource {
  report_id: number; report_name: string | null; task_name: string;
  uploaded_at: string;
  equipment: Array<{ type: string; location: string; quantity: number | null; condition: string | null }>;
}
interface ExtImportItem {
  type_model: string; location: string; quantity: number | null; condition: string | null;
  action: 'create' | 'update'; existing_id?: number; checked: boolean;
}

function fmtExtDate(d: string | null): string {
  if (!d) return '—';
  const dt = new Date(d.slice(0, 10) + 'T12:00:00Z');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
function nextDueColor(d: string | null): string {
  if (!d) return 'var(--text-primary)';
  const dt = new Date(d.slice(0, 10) + 'T12:00:00Z');
  const now = new Date();
  const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (dt < todayUTC) return 'var(--accent-danger)';
  const in30 = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 30));
  if (dt <= in30) return 'var(--accent-warning)';
  return 'var(--text-primary)';
}

// ─── Extinguishers Tab ────────────────────────────────────────────────────────
function ExtinguishersTab({ extinguisherId, onExtinguisherIdHandled }: { extinguisherId?: string | null; onExtinguisherIdHandled?: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ['admin', 'org_admin', 'manager'].includes(user?.role ?? '');
  const isEnterprise = user?.orgPlanTier === 'enterprise' || user?.role === 'admin';

  const { toast, showToast } = useToast();

  const { data: extRes, isLoading } = useQuery({
    queryKey: ['extinguishers'],
    queryFn: () => api.get<Extinguisher[]>('/extinguishers'),
  });
  const extinguishers: Extinguisher[] = (extRes as any)?.data ?? [];

  // Glow extinguisher row from a reactive prop (global search navigation) instead of a one-time
  // window.location.search read -- same fix as FacilitySuppliesTab's initialSupplyId: a second
  // search-result click for a DIFFERENT extinguisher while this tab is already mounted only
  // changes the query string, which a one-shot read would never see. `handledExtIdRef` guards
  // against re-glowing the same id on every unrelated `extinguishers` refetch, and resets to null
  // when the id goes away so the SAME extinguisher can be re-clicked from search later too.
  const handledExtIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!extinguisherId) { handledExtIdRef.current = null; return; }
    if (extinguishers.length === 0) return;
    if (handledExtIdRef.current === extinguisherId) return;
    handledExtIdRef.current = extinguisherId;
    const id = Number(extinguisherId);
    setTimeout(() => {
      const el = document.getElementById(`ext-row-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('task-highlight-glow');
        setTimeout(() => el.classList.remove('task-highlight-glow'), 3000);
      }
    }, 200);
    onExtinguisherIdHandled?.();
  }, [extinguishers, extinguisherId]); // eslint-disable-line react-hooks/exhaustive-deps

  const mouseDownTarget = useRef<EventTarget | null>(null);

  // ── Add/Edit modal ──────────────────────────────────────────────────────────
  const blankForm = () => ({
    type_model: '', location: '', quantity: '' as string | number,
    serial_number: '', last_inspection_date: '', next_due_date: '', notes: '',
  });
  const [modal, setModal] = useState<{ open: boolean; editing: Extinguisher | null }>({ open: false, editing: null });
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  function openAdd() {
    setForm(blankForm());
    setModal({ open: true, editing: null });
    document.body.style.overflow = 'hidden';
  }
  function openEdit(e: Extinguisher) {
    setForm({
      type_model: e.type_model ?? '', location: e.location ?? '',
      quantity: e.quantity ?? '',
      serial_number: e.serial_number ?? '',
      last_inspection_date: e.last_inspection_date ? e.last_inspection_date.slice(0, 10) : '',
      next_due_date: e.next_due_date ? e.next_due_date.slice(0, 10) : '',
      notes: e.notes ?? '',
    });
    setModal({ open: true, editing: e });
    document.body.style.overflow = 'hidden';
  }
  function closeModal() { setModal({ open: false, editing: null }); document.body.style.overflow = ''; }

  async function handleSave(ev: React.FormEvent) {
    ev.preventDefault();
    setSaving(true);
    try {
      const body = {
        type_model: form.type_model || null, location: form.location || null,
        quantity: form.quantity !== '' ? Number(form.quantity) : null,
        serial_number: form.serial_number || null,
        last_inspection_date: form.last_inspection_date || null,
        next_due_date: form.next_due_date || null,
        notes: form.notes || null,
      };
      if (modal.editing) {
        await api.put(`/extinguishers/${modal.editing.id}`, body);
        showToast('Extinguisher updated');
      } else {
        await api.post('/extinguishers', body);
        showToast('Extinguisher added');
      }
      qc.invalidateQueries({ queryKey: ['extinguishers'] });
      closeModal();
    } catch (err: any) { showToast(err.message ?? 'Save failed'); }
    finally { setSaving(false); }
  }

  // ── Delete modal ────────────────────────────────────────────────────────────
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/extinguishers/${deleteId}`);
      qc.invalidateQueries({ queryKey: ['extinguishers'] });
      setDeleteId(null);
      showToast('Extinguisher deleted');
    } catch { showToast('Delete failed'); }
    finally { setDeleting(false); }
  }

  // ── Import modal ────────────────────────────────────────────────────────────
  const [importModal, setImportModal] = useState<{
    open: boolean; step: 1 | 2; selectedSource: ExtImportSource | null;
    reviewItems: ExtImportItem[]; importing: boolean;
  }>({ open: false, step: 1, selectedSource: null, reviewItems: [], importing: false });

  const { data: importSourcesRes, isLoading: sourcesLoading } = useQuery({
    queryKey: ['extinguisher-import-sources'],
    queryFn: () => api.get<ExtImportSource[]>('/extinguishers/import-sources'),
    enabled: importModal.open && importModal.step === 1,
  });
  const importSources: ExtImportSource[] = (importSourcesRes as any)?.data ?? [];

  function openImport() {
    setImportModal({ open: true, step: 1, selectedSource: null, reviewItems: [], importing: false });
    document.body.style.overflow = 'hidden';
  }
  function closeImport() {
    setImportModal({ open: false, step: 1, selectedSource: null, reviewItems: [], importing: false });
    document.body.style.overflow = '';
  }

  function selectImportSource(src: ExtImportSource) {
    const reviewItems: ExtImportItem[] = src.equipment.map((eq: any) => {
      const matchIdx = extinguishers.findIndex(
        e => (e.location ?? '').toLowerCase() === (eq.location ?? '').toLowerCase()
      );
      const hasMatch = matchIdx !== -1;
      return {
        type_model: eq.type ?? '', location: eq.location ?? '',
        quantity: eq.quantity ?? null, condition: eq.condition ?? null,
        action: hasMatch ? 'update' : 'create',
        existing_id: hasMatch ? extinguishers[matchIdx].id : undefined,
        checked: true,
      };
    });
    setImportModal(p => ({ ...p, step: 2, selectedSource: src, reviewItems }));
  }

  async function handleImport() {
    const itemsToImport = importModal.reviewItems.filter(i => i.checked);
    if (itemsToImport.length === 0) return;
    setImportModal(p => ({ ...p, importing: true }));
    try {
      const res = await api.post('/extinguishers/import', {
        items: itemsToImport.map(i => ({
          type_model: i.type_model, location: i.location, quantity: i.quantity,
          action: i.action, existing_id: i.existing_id,
        })),
      });
      const created = (res as any)?.created ?? 0;
      const updated = (res as any)?.updated ?? 0;
      qc.invalidateQueries({ queryKey: ['extinguishers'] });
      showToast(`${created} created, ${updated} updated`);
      closeImport();
    } catch { showToast('Import failed'); setImportModal(p => ({ ...p, importing: false })); }
  }

  const checkedCount = importModal.reviewItems.filter(i => i.checked).length;

  return (
    <div style={{ background: 'transparent' }}>
      {/* Toast */}
      <Toast toast={toast} />

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8, paddingTop: 12 }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700 }}>Extinguishers</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {isEnterprise ? (
            <button
              className="btn btn-ghost"
              onClick={openImport}
              style={{ border: '1px solid var(--accent-primary)', color: 'var(--accent-primary)' }}
            >
              <Upload size={14} /> Import from Report
            </button>
          ) : (
            <button
              className="btn btn-ghost"
              disabled
              title="Enterprise feature"
              style={{ border: '1px solid var(--border-default)', color: 'var(--text-muted)', cursor: 'not-allowed' }}
            >
              🔒 Import from Report
            </button>
          )}
          {canEdit && (
            <button className="btn btn-primary" onClick={openAdd}>
              <Plus size={14} /> Add Extinguisher
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <div className="loading-spinner" />
        </div>
      ) : extinguishers.length === 0 ? (
        <div style={{
          background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(8px)', borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.08)',
          textAlign: 'center', padding: '40px 24px', color: 'var(--text-muted)',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>🧯</div>
          <p style={{ margin: 0 }}>No extinguishers added yet.</p>
        </div>
      ) : (
        <div style={{
          background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(8px)', borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {[
                    { label: 'Unit Type / Model', align: 'left' as const },
                    { label: 'Location',           align: 'left' as const },
                    { label: 'Qty',                align: 'center' as const },
                    { label: 'Serial #',           align: 'left' as const },
                    { label: 'Last Inspection',    align: 'left' as const },
                    { label: 'Next Due',           align: 'left' as const },
                    { label: 'Actions',            align: 'right' as const },
                  ].map(({ label, align }) => (
                    <th key={label} style={{
                      padding: '10px 16px', textAlign: align,
                      fontSize: '0.75rem', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                      color: 'var(--text-muted)', whiteSpace: 'nowrap',
                    }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {extinguishers.map(e => {
                  const dueBadge = (() => {
                    if (!e.next_due_date) return null;
                    const dt = new Date(e.next_due_date.slice(0, 10) + 'T12:00:00Z');
                    const now = new Date();
                    const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
                    if (dt < todayUTC) return { label: 'Overdue', color: 'var(--accent-danger)', bg: 'rgba(239,68,68,0.12)' };
                    const in30 = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 30));
                    if (dt <= in30) return { label: 'Due Soon', color: 'var(--accent-warning)', bg: 'rgba(245,158,11,0.12)' };
                    return null;
                  })();
                  return (
                    <tr key={e.id} id={`ext-row-${e.id}`}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'transparent', transition: 'background 0.1s' }}
                      onMouseEnter={ev => (ev.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                    >
                      {/* Unit Type / Model */}
                      <td style={{ padding: '14px 16px', minWidth: 180 }}>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                          {e.type_model ?? '—'}
                        </div>
                        {e.serial_number && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                            S/N: {e.serial_number}
                          </div>
                        )}
                      </td>
                      {/* Location */}
                      <td style={{ padding: '14px 16px', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        {e.location ?? '—'}
                      </td>
                      {/* Qty */}
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                        {e.quantity ?? '—'}
                      </td>
                      {/* Serial # */}
                      <td style={{ padding: '14px 16px', fontFamily: 'monospace', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                        {e.serial_number ?? '—'}
                      </td>
                      {/* Last Inspection */}
                      <td style={{ padding: '14px 16px', color: 'var(--text-secondary)', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        {fmtExtDate(e.last_inspection_date)}
                      </td>
                      {/* Next Due */}
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '0.875rem', color: nextDueColor(e.next_due_date), fontWeight: dueBadge ? 600 : 400 }}>
                          {fmtExtDate(e.next_due_date)}
                        </span>
                        {dueBadge && (
                          <span className={`badge ${dueBadge.label === 'Overdue' ? 'badge-red' : 'badge-amber'}`} style={{ marginLeft: 6 }}>
                            {dueBadge.label}
                          </span>
                        )}
                      </td>
                      {/* Actions */}
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        {canEdit && (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => openEdit(e)}
                              title="Edit"
                              className="btn btn-ghost btn-sm"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteId(e.id)}
                              title="Delete"
                              className="btn btn-danger btn-sm"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal.open && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) closeModal(); mouseDownTarget.current = null; }}
        >
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-default)', width: '100%', maxWidth: 520, maxHeight: '90vh', zIndex: 999, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 28px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700 }}>{modal.editing ? 'Edit Extinguisher' : 'Add Extinguisher'}</h3>
              <button type="button" onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: 28 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Type/Model <span style={{ color: 'var(--accent-danger)' }}>*</span></label>
                  <input className="form-input" required value={form.type_model} onChange={e => setForm(f => ({ ...f, type_model: e.target.value }))} placeholder="e.g. ABC Dry Chemical 10 lb" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Location <span style={{ color: 'var(--accent-danger)' }}>*</span></label>
                  <input className="form-input" required value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Main Hallway, Room 101" />
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input className="form-input" type="number" min={0} value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="1" />
                </div>
                <div className="form-group">
                  <label className="form-label">Serial Number</label>
                  <input className="form-input" value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} placeholder="SN-12345" />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Inspection Date</label>
                  <DatePicker value={form.last_inspection_date} onChange={v => setForm(f => ({ ...f, last_inspection_date: v }))} placeholder="Select a date" />
                </div>
                <div className="form-group">
                  <label className="form-label">Next Due Date</label>
                  <DatePicker value={form.next_due_date} onChange={v => setForm(f => ({ ...f, next_due_date: v }))} placeholder="Select a date" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes..." style={{ resize: 'vertical' }} />
                </div>
              </div>
            </div>
            <div style={{ flexShrink: 0, padding: '16px 28px', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : modal.editing ? 'Save Changes' : 'Add Extinguisher'}
              </button>
            </div>
          </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirm Modal */}
      {deleteId !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-default)', padding: 28, maxWidth: 420, width: '100%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1.0625rem', fontWeight: 700 }}>Delete Extinguisher</h3>
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 24px' }}>Are you sure you want to delete this extinguisher? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Import from Report Modal */}
      {importModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-default)', padding: 28, width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700 }}>
                {importModal.step === 1 ? 'Import from Analyzed Report' : `Review Items — ${importModal.selectedSource?.task_name}`}
              </h3>
              <button type="button" onClick={closeImport} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={18} /></button>
            </div>

            {importModal.step === 1 && (
              <div style={{ overflowY: 'auto' }}>
                {sourcesLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                    <div className="loading-spinner" />
                  </div>
                ) : importSources.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-muted)', border: '1px dashed var(--border-subtle)', borderRadius: 10 }}>
                    <div style={{ fontSize: '2rem', marginBottom: 8 }}>🔍</div>
                    <p style={{ margin: 0 }}>No analyzed fire extinguisher reports found. Analyze a report from Tasks → Schedule first.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {importSources.map(src => (
                      <button key={src.report_id} type="button" onClick={() => selectImportSource(src)}
                        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
                        onMouseEnter={ev => (ev.currentTarget.style.borderColor = 'var(--accent-primary)')}
                        onMouseLeave={ev => (ev.currentTarget.style.borderColor = 'var(--border-default)')}>
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: 2 }}>{src.task_name}</div>
                          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                            {src.report_name ?? 'Unnamed report'} · {fmtExtDate(src.uploaded_at)}
                          </div>
                          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>
                            {src.equipment.length} equipment item{src.equipment.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <span style={{ color: 'var(--accent-primary)', fontSize: '1.25rem' }}>→</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {importModal.step === 2 && (
              <>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {importModal.reviewItems.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>No equipment items found in this report.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          {['', 'Type/Model', 'Location', 'Qty', 'Condition', 'Status', 'Action'].map(h => (
                            <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importModal.reviewItems.map((item, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid var(--border-subtle)', opacity: item.checked ? 1 : 0.45 }}>
                            <td style={{ padding: '8px' }}>
                              <input type="checkbox" checked={item.checked} onChange={e => {
                                setImportModal(p => ({ ...p, reviewItems: p.reviewItems.map((r, i) => i === idx ? { ...r, checked: e.target.checked } : r) }));
                              }} style={{ cursor: 'pointer' }} />
                            </td>
                            <td style={{ padding: '8px', fontWeight: 500 }}>{item.type_model || '—'}</td>
                            <td style={{ padding: '8px' }}>{item.location || '—'}</td>
                            <td style={{ padding: '8px' }}>{item.quantity ?? '—'}</td>
                            <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>{item.condition ?? '—'}</td>
                            <td style={{ padding: '8px' }}>
                              {item.action === 'create' ? (
                                <span className="badge badge-green">New</span>
                              ) : (
                                <span className="badge badge-amber">Exists</span>
                              )}
                            </td>
                            <td style={{ padding: '8px' }}>
                              {item.action === 'update' ? (
                                <button type="button"
                                  onClick={() => setImportModal(p => ({ ...p, reviewItems: p.reviewItems.map((r, i) => i === idx ? { ...r, action: 'create', existing_id: undefined } : r) }))}
                                  style={{ background: 'none', border: '1px solid var(--accent-warning)', borderRadius: 4, color: 'var(--accent-warning)', padding: '2px 6px', fontSize: '0.75rem', cursor: 'pointer' }}
                                  title="Click to create new instead">
                                  Update existing
                                </button>
                              ) : item.existing_id !== undefined ? (
                                <button type="button"
                                  onClick={() => setImportModal(p => ({ ...p, reviewItems: p.reviewItems.map((r, i) => i === idx ? { ...r, action: 'update' } : r) }))}
                                  style={{ background: 'none', border: '1px solid var(--accent-success)', borderRadius: 4, color: 'var(--accent-success)', padding: '2px 6px', fontSize: '0.75rem', cursor: 'pointer' }}
                                  title="Click to update existing instead">
                                  Create new
                                </button>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-subtle)', flexShrink: 0 }}>
                  <button type="button" className="btn btn-ghost" onClick={() => setImportModal(p => ({ ...p, step: 1 }))}>← Back</button>
                  <button type="button" className="btn btn-primary"
                    disabled={checkedCount === 0 || importModal.importing}
                    onClick={handleImport}>
                    {importModal.importing ? 'Importing...' : `Import ${checkedCount} item${checkedCount !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Facility Keys ────────────────────────────────────────────────────────────
interface FacilityKey {
  id: number; hook_number: string|null; key_code: string|null; area: string|null;
  is_grand_master:boolean; is_new_master:boolean; is_sub_master:boolean;
  not_on_master:boolean; is_sub_master_key:boolean; key_type: string;
  status: string; checked_out_to: string|null; checked_out_at: string|null;
  notes: string|null; icon: string|null;
}
interface ResidentRoom {
  id:number; room_number:string; resident_name:string|null; key_code:string|null; notes:string|null;
}
interface KeyHistoryItem {
  id:number; key_id:number; action:string;
  performed_by:string|null; checked_out_to:string|null;
  notes:string|null; created_at:string;
}

function DeleteConfirmModal({ message, onConfirm, onClose }: {
  message?: string; onConfirm: () => void; onClose: () => void;
}) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',backdropFilter:'blur(4px)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--bg-elevated)',borderRadius:'var(--radius-xl)',border:'1px solid var(--border-default)',padding:28,maxWidth:420,width:'100%',zIndex:10000}}>
        <h3 style={{margin:'0 0 12px',fontSize:'1.0625rem',fontWeight:700}}>Confirm Delete</h3>
        <p style={{color:'var(--text-secondary)',margin:'0 0 24px',fontSize:'0.9375rem'}}>{message ?? 'Are you sure you want to delete this? This cannot be undone.'}</p>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Tab 4: Facility Keys ─────────────────────────────────────────────────────
type KeySubTab = 'facility'|'resident';

const TIER_COLORS = {
  GM:  { bg:'#1d3a6e', border:'#3b82f6', text:'#93c5fd', label:'Grand Master' },
  NM:  { bg:'#064e3b', border:'#10b981', text:'#6ee7b7', label:'New Master' },
  SM:  { bg:'#2e1065', border:'#8b5cf6', text:'#c4b5fd', label:'Sub Master' },
  NOM: { bg:'rgba(100,116,139,0.15)', border:'rgba(100,116,139,0.35)', text:'var(--text-muted)', label:'Not on Master' },
  OUT: { bg:'#450a0a', border:'#ef4444', text:'#ef4444', label:'Checked Out' },
};

function getTier(key: FacilityKey): 'GM'|'NM'|'SM'|'NOM' {
  if (key.is_grand_master) return 'GM';
  if (key.is_new_master) return 'NM';
  if (key.is_sub_master) return 'SM';
  return 'NOM';
}

// Board-view "Option E" pegboard card tints (2026-10-08) -- deliberately separate from
// TIER_COLORS above, which Table view still uses unchanged (out of scope for this redesign).
// The inline detail strip that ALSO used to reuse TIER_COLORS is gone as of the 2026-10-08 side
// panel redesign (Part 2) -- the panel uses these tints too now, for the same card/panel
// consistency reason Part 1's icon-color spec calls for. Only 3 entries by design per the spec:
// SM has no tint of its own here and falls back to NOM's style below
// (PEG_TIER_TINTS[tier] ?? PEG_TIER_TINTS.NOM) -- the tier LABEL text shown on the card/panel is
// still the real tier ('SM' included), only its color collapses to the NOM look, matching the
// spec's "any other/unknown tier -> use the NOM style" literally.
//
// 2026-10-10 (Part 3): values are now var(--peg-...) string references into global.css's
// :root/light-block tier-color variables, not literal colors -- CSS can override a var, it can't
// override a literal baked into a JS object read only at render time. Dark's var() values are
// byte-identical to what used to be hardcoded here directly (nothing changed for dark). label/
// number/pillText used to be a single shared `text` field (dark reuses one value for all three),
// split into three because light wants them to diverge (e.g. GM's HOOK label vs. its hook number
// are two different blues in light, per spec) -- the literal field names changed but every call
// site below was updated to match.
const PEG_TIER_TINTS: Record<'GM'|'NM'|'NOM', { bg: string; border: string; label: string; number: string; pillBg: string; pillText: string; pillBorder: string }> = {
  GM:  { bg: 'var(--peg-gm-col-bg)',  border: 'var(--peg-gm-col-border)',  label: 'var(--peg-gm-label)',  number: 'var(--peg-gm-number)',  pillBg: 'var(--peg-gm-pill-bg)',  pillText: 'var(--peg-gm-pill-text)',  pillBorder: 'var(--peg-gm-pill-border)' },
  NM:  { bg: 'var(--peg-nm-col-bg)',  border: 'var(--peg-nm-col-border)',  label: 'var(--peg-nm-label)',  number: 'var(--peg-nm-number)',  pillBg: 'var(--peg-nm-pill-bg)',  pillText: 'var(--peg-nm-pill-text)',  pillBorder: 'var(--peg-nm-pill-border)' },
  NOM: { bg: 'var(--peg-nom-col-bg)', border: 'var(--peg-nom-col-border)', label: 'var(--peg-nom-label)', number: 'var(--peg-nom-number)', pillBg: 'var(--peg-nom-pill-bg)', pillText: 'var(--peg-nom-pill-text)', pillBorder: 'var(--peg-nom-pill-border)' },
};

// Dedicated icon-halo vars (Part 3) -- GM/NM reuse the SAME literal value as their own
// col-bg/label in dark today, but light gives the icon halo its own distinct tint from the hook
// column, so these must be separate variables from PEG_TIER_TINTS above, not the same ones.
const PEG_GLYPH_VARS: Record<'GM'|'NM', { color: string; bg: string }> = {
  GM: { color: 'var(--peg-gm-icon-color)', bg: 'var(--peg-gm-icon-bg)' },
  NM: { color: 'var(--peg-nm-icon-color)', bg: 'var(--peg-nm-icon-bg)' },
};

// Tier -> blur-bubble glow color (Part 2; var-ized 2026-10-10 Part 3). Separate table from
// PEG_TIER_TINTS since the spec gives the panel's decorative glow its own opacity values,
// distinct from the card's hook-column tint above.
const PEG_TIER_GLOW: Record<'GM'|'NM'|'NOM', string> = {
  GM: 'var(--peg-gm-glow)',
  NM: 'var(--peg-nm-glow)',
  NOM: 'var(--peg-nom-glow)',
};

// Small local relative-time formatter -- this file doesn't import date-fns elsewhere (Dashboard.tsx
// does, Inventory.tsx doesn't), so a tiny hand-rolled helper matches this file's own existing
// convention (e.g. formatDueDate-style helpers) instead of adding a fresh dependency for one spot.
function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

const KEY_ICONS = ['🔑','🗝️','🔐','🔒','🔓','🚪','🏠','🏢','🏥','⭐','🔷','🟦'];

// Search scoped to visible/meaningful text fields only — matching on the raw row object
// (Object.values(item).some(...)) used to also match bookkeeping columns like id, so e.g.
// searching "105" matched room "219-1" purely because its internal DB id happens to be 105.
const KEY_SEARCH_FIELDS: Record<KeySubTab, (keyof FacilityKey | keyof ResidentRoom)[]> = {
  facility: ['hook_number', 'key_code', 'area', 'notes', 'checked_out_to'],
  resident: ['room_number', 'key_code', 'resident_name', 'notes'],
};

function FacilityKeysTab() {
  const { user } = useAuth();
  // Part C fix (2026-10-08): the staff picker used to key off `user?.facilityId` -- the logged-in
  // user's own HOME facility -- instead of the facility actually being viewed. For anyone who
  // switches facilities (the admin/org_admin override useFacility() exists for), that's wrong:
  // confirmed live against the DB that distinct facilities have genuinely distinct, non-
  // overlapping user sets (e.g. facility_id=11 has 4 users, facility_id=12 has 8, none shared
  // with facility 1), so switching away from your home facility while this still filtered by
  // your home facility_id would show the wrong facility's staff (or none, depending on overlap).
  const { facilityId: viewedFacilityId } = useFacility();
  const isEnterprise = user?.orgPlanTier === 'enterprise' || user?.role === 'admin';
  // Pegboard (Board view) is Pro+ per CLAUDE_RULES tier gating — Standard is table-view-only.
  // Same isPro pattern used elsewhere (Vendors.tsx, Projects.tsx, etc): orgPlanTier pro/enterprise, or admin.
  const isPro = user?.orgPlanTier === 'pro' || user?.orgPlanTier === 'enterprise' || user?.role === 'admin';
  const [subTab, setSubTab] = useState<KeySubTab>('facility');
  // Sliding highlight behind the Facility Keys/Resident Rooms sub-tabs (motion ported from the
  // Billing & Plan cycle toggle at Chris's request — surgical, this file is treated as locked).
  // JS-measured rather than a fixed 50% split like Billing's: .pill-tab labels are auto-width
  // ("Facility Keys" vs "Resident Rooms" aren't equal), so the highlight's left/width track the
  // active button's real offsetLeft/offsetWidth. See .pill-tab-slide-highlight/-btn in
  // buttonAnimations.css — new, additive classes; .pill-tab/.pill-tab-container/.pill-tab.active
  // themselves are untouched, so every other pill-tab switcher in the app is unaffected.
  const subTabBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [subTabHighlight, setSubTabHighlight] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const el = subTabBtnRefs.current[subTab];
    if (el) setSubTabHighlight({ left: el.offsetLeft, width: el.offsetWidth });
  }, [subTab]);
  const [keysView, setKeysView] = useState<'board'|'table'>('board');
  // 2026-10-09 "Toggle C" redesign: the old JS-measured highlight (offsetLeft/offsetWidth off
  // real button refs) existed because the old buttons were text-label-width-dependent. The new
  // design's two buttons are a fixed 36x36 each with a 4px gap, so the highlight's position is
  // now just a deterministic translateX(0)/(40) per the spec -- no measurement needed. keysView/
  // setKeysView and the persistence effect right below are UNCHANGED (same state, same setter,
  // same localStorage key) -- only this now-unnecessary measurement plumbing is gone.
  useEffect(() => {
    const saved = localStorage.getItem('facilityKeysView') as 'board'|'table'|null;
    if (saved === 'table') setKeysView('table');
  }, []);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('hook_number');
  const [sortDir, setSortDir] = useState<1|-1>(1);
  const [selected, setSelected] = useState<FacilityKey|ResidentRoom|null>(null);
  const [modal, setModal] = useState<{open:boolean; item?:FacilityKey|ResidentRoom}>({open:false});
  const [deleteModal, setDeleteModal] = useState<{open:boolean; onConfirm:()=>void; message?:string}>({open:false,onConfirm:()=>{}});
  const [checkoutPrompt, setCheckoutPrompt] = useState<{key:FacilityKey}|null>(null);
  const [checkoutName, setCheckoutName] = useState('');
  const [checkoutSearch, setCheckoutSearch] = useState('');
  const [checkoutShowDropdown, setCheckoutShowDropdown] = useState(false);
  const [historyModal, setHistoryModal] = useState<{open:boolean;key:FacilityKey|null}>({open:false,key:null});
  const [qrModal, setQrModal] = useState<{open:boolean;key:FacilityKey|null}>({open:false,key:null});
  const [selectedKeyId, setSelectedKeyId] = useState<number | null>(null);
  const didOpenKeyFromUrl = useRef(false);
  const [ignoredDuplicates, setIgnoredDuplicates] = useState<Set<string>>(new Set());
  const [showDuplicateFilter, setShowDuplicateFilter] = useState(false);
  // Side-panel-only UI state (Part 2, 2026-10-08): which field is unmasked, reset whenever the
  // selected key changes so switching keys never leaks a previous key's revealed code.
  const [revealCode, setRevealCode] = useState(false);
  useEffect(() => { setRevealCode(false); }, [selectedKeyId]);
  // Esc clears the board selection -- a plain additive effect (no stopPropagation/preventDefault)
  // so it never competes with useEscapeToClose on any modal that might also be open; it's a no-op
  // whenever nothing is selected.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && selectedKeyId != null) setSelectedKeyId(null);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedKeyId]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const { toast: keysToast, showToast: showKeysToast } = useToast();

  const {data:keysRes} = useQuery({
    queryKey:['keys',subTab],
    queryFn:()=>api.get<FacilityKey[]|ResidentRoom[]>(`/tasks/keys?type=${subTab}`),
  });
  const rawItems = (keysRes?.data??[]) as any[];

  // Side panel's "Recent History" section (Part 2) -- same query key/endpoint KeyHistoryModal
  // already uses (GET /tasks/keys/:id/history), not a new route. Gated the same way the
  // existing History button already is (isEnterprise), and only enabled while board view has a
  // selection, so it doesn't fire for every key up front.
  const { data: panelHistoryRes } = useQuery({
    queryKey: ['key-history', selectedKeyId],
    queryFn: () => api.get<KeyHistoryItem[]>(`/tasks/keys/${selectedKeyId}/history`),
    enabled: isEnterprise && selectedKeyId != null && keysView === 'board',
  });
  const panelHistory = (panelHistoryRes?.data ?? []) as KeyHistoryItem[];

  const [facilityUsers, setFacilityUsers] = useState<Array<{
    id: number;
    first_name: string | null;
    last_name: string | null;
    username: string;
    facility_id: number;
    is_active?: boolean;
    department_name?: string | null;
    department_color?: string | null;
  }>>([]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    // Part C fix: use the facility actually being VIEWED (useFacility(), the switcher), not the
    // logged-in user's home facility_id -- and refetch whenever that switcher value changes, not
    // just once at login. No more early-return on a falsy facilityId either: useFacility()
    // itself already falls back to 0 (never undefined), so bailing out here only ever meant "a
    // real facility is selected but we're refusing to fetch it," never a genuine missing-user
    // case worth special-casing.
    if (!token || !viewedFacilityId) return;
    fetch(`${import.meta.env.VITE_API_URL}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        const all = Array.isArray(d.data) ? d.data : [];
        setFacilityUsers(
          all.filter((u: { facility_id: number; is_active?: boolean }) =>
            Number(u.facility_id) === Number(viewedFacilityId) && u.is_active !== false
          )
        );
      })
      .catch(e => console.error('[facilityUsers]', e));
  }, [viewedFacilityId]);
  const duplicates = ((subTab==='facility' ? (keysRes as any)?.duplicates : undefined) ?? []) as string[];

  // Select and scroll to key from URL param (global search navigation)
  useEffect(() => {
    if (didOpenKeyFromUrl.current || rawItems.length === 0) return;
    const sp = new URLSearchParams(window.location.search);
    const keyId = sp.get('keyId');
    if (!keyId) { didOpenKeyFromUrl.current = true; return; }
    const key = rawItems.find((k: any) => k.id === Number(keyId));
    if (key) {
      setSelectedKeyId(key.id);
      didOpenKeyFromUrl.current = true;
      setTimeout(() => {
        const el = document.getElementById(`key-hook-${key.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [rawItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteMut = useMutation({
    mutationFn:(id:number)=>api.delete(`/tasks/keys/${id}?type=${subTab}`),
    onSuccess:()=>{qc.invalidateQueries({queryKey:['keys',subTab]});setSelected(null);},
  });

  const statusMut = useMutation({
    mutationFn:({id,status,checked_out_to}:{id:number;status:string;checked_out_to?:string})=>
      api.patch(`/tasks/keys/${id}/status`,{status,checked_out_to}),
    onSuccess:()=>qc.invalidateQueries({queryKey:['keys',subTab]}),
  });

  const toggleKeysView = (v: 'board'|'table') => {
    if (v === 'board' && !isPro) return; // Standard tier: Board view is not selectable
    setKeysView(v); localStorage.setItem('facilityKeysView', v); setSelected(null);
  };
  function downloadExport() {
    triggerExport(`/api/tasks/keys/export?type=${subTab}`, `keys-${subTab}.csv`);
  }
  async function handleImport(e:React.ChangeEvent<HTMLInputElement>) {
    const file=e.target.files?.[0]; if(!file) return;
    const fd=new FormData(); fd.append('file',file); fd.append('type',subTab);
    const token=localStorage.getItem('accessToken');
    e.target.value='';
    // Long-running upload — go straight to VITE_API_URL rather than the relative /api path, since
    // the Vercel proxy has a 10s timeout that can silently kill a larger CSV import (Rule 8). That
    // combined with the old fire-and-forget fetch (no .catch, no response.ok check) is why this
    // previously showed no feedback and appeared not to refresh the table.
    const baseUrl = (import.meta.env.VITE_API_URL as string) || apiClient.defaults.baseURL || '/api';
    try {
      const res = await fetch(`${baseUrl}/tasks/keys/import`, { method:'POST', headers:{ Authorization:`Bearer ${token}` }, body:fd });
      const body = await res.json().catch(() => ({})) as { success?: boolean; data?: { imported: number; skipped: number }; error?: string };
      if (!res.ok || !body.success) throw new Error(body.error ?? `HTTP ${res.status}`);
      qc.invalidateQueries({queryKey:['keys',subTab]});
      showKeysToast(`${body.data?.imported ?? 0} imported, ${body.data?.skipped ?? 0} skipped`);
    } catch (err) {
      showKeysToast(`Import failed: ${err instanceof Error ? err.message : String(err)}`, true);
    }
  }
  function handleSort(col:string) {
    if(sortCol===col) setSortDir(d=>d*-1 as 1|-1);
    else { setSortCol(col); setSortDir(1); }
  }

  const filtered = rawItems.filter(item=>{
    const text=search.toLowerCase();
    if (!text) return true;
    return KEY_SEARCH_FIELDS[subTab].some(f=>String((item as Record<string,unknown>)[f as string]??'').toLowerCase().includes(text));
  });
  const sorted = [...filtered].sort((a,b)=>{
    if (sortCol === 'hook_number') {
      const an = parseInt(String(a.hook_number || '').replace(/\D/g, '')) || 0;
      const bn = parseInt(String(b.hook_number || '').replace(/\D/g, '')) || 0;
      return (an - bn) * sortDir;
    }
    if (!sortCol) return 0;
    const av=String(a[sortCol]||''), bv=String(b[sortCol]||'');
    return av.localeCompare(bv,undefined,{numeric:true})*sortDir;
  });

  const facilKeys = sorted as FacilityKey[];
  const selKey = selected as FacilityKey|null;

  const sortedKeys = [...facilKeys].sort((a, b) => {
    const numA = parseInt(String(a.hook_number ?? '').replace(/\D/g, '')) || 0;
    const numB = parseInt(String(b.hook_number ?? '').replace(/\D/g, '')) || 0;
    if (numA !== numB) return numA - numB;
    return String(a.hook_number ?? '').localeCompare(String(b.hook_number ?? ''));
  });
  // Part 2 (2026-10-08): the old JS row-chunking (hooksPerRow/boardRows, sized off a measured
  // container width) existed only so the inline per-row detail drawer could be inserted right
  // after whichever row held the selected card. That drawer is gone now -- the whole board is
  // one real CSS grid (repeat(auto-fill, minmax(220px,1fr)) below) with a single side panel next
  // to it instead of a per-row insert, so the manual chunking has no remaining purpose and is
  // removed rather than kept as now-pointless indirection.
  const selectedKeyFull: FacilityKey | null = selectedKeyId != null
    ? sortedKeys.find(k => k.id === selectedKeyId) ?? null
    : null;

  const codeCounts: Record<string, number[]> = {};
  sortedKeys.forEach(k => {
    if (k.key_code) {
      if (!codeCounts[k.key_code]) codeCounts[k.key_code] = [];
      codeCounts[k.key_code].push(k.id);
    }
  });
  const duplicateCodes = Object.entries(codeCounts)
    .filter(([code, ids]) => ids.length > 1 && !ignoredDuplicates.has(code))
    .map(([code]) => code);

  // resident_name intentionally not rendered here — residents change frequently and the field
  // goes stale in this table; it's still stored in the DB and included in CSV export/import
  // (server/src/routes/tasks.ts), just not displayed in this UI table.
  const residentCols: {key:keyof ResidentRoom; label:string}[] = [
    {key:'room_number',label:'Room # / Area'},
    {key:'key_code',label:'Hook #'},{key:'notes',label:'Notes'},
  ];

  function handleCheckIn(k: FacilityKey) {
    statusMut.mutate({id:k.id,status:'in'});
    setSelected({...k,status:'in',checked_out_to:null,checked_out_at:null} as FacilityKey);
  }
  function handleCheckOut(k: FacilityKey) {
    setCheckoutName('');
    setCheckoutPrompt({key:k});
  }
  function confirmCheckOut() {
    if(!checkoutPrompt) return;
    const k = checkoutPrompt.key;
    const now = new Date().toISOString();
    statusMut.mutate({id:k.id,status:'out',checked_out_to:checkoutName});
    setSelected({...k,status:'out',checked_out_to:checkoutName,checked_out_at:now} as FacilityKey);
    setCheckoutPrompt(null);
  }

  return (
    <div style={{ background: 'transparent' }}>
      <Toast toast={keysToast} />
      {/* Sub-tabs */}
      <div className="pill-tab-container" style={{marginBottom:16,marginTop:12,width:'fit-content',position:'relative',overflow:'hidden'}}>
        <div className="pill-tab-slide-highlight" style={{width:subTabHighlight.width,transform:`translateX(${subTabHighlight.left}px)`}} />
        {([
          {id:'facility',label:'Facility Keys',icon:<Key size={14}/>},
          {id:'resident',label:'Resident Rooms',icon:<Home size={14}/>},
        ] as {id:KeySubTab;label:string;icon:React.ReactElement}[]).map(t=>(
          <button key={t.id} ref={el => { subTabBtnRefs.current[t.id] = el; }} className={`pill-tab pill-tab-slide-btn${subTab===t.id?' active':''}`} style={{position:'relative',zIndex:1}} onClick={()=>{setSubTab(t.id);setSelected(null);setSearch('');setKeysView(isPro ? 'board' : 'table');}}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>
      {/* Toolbar -- round 4 restyle: Add moved out to a page-corner FloatingActionButton (per
          Chris's explicit call after Vendors/Orders turned out to use combo5-hero for Add too,
          not the transparent icon style -- Catalog's FAB was the one real match). Edit/Delete/
          Export/Import converted to icon-only combo-jello-spin buttons, same 32x32 btn-ghost
          btn-sm shell as Orders.tsx's own ToolbarIconButton (that component is local/unexported
          to Orders.tsx, so it can't be imported here -- hand-written to the identical spec
          instead, consistent with this file already hand-writing combo-jello-spin buttons
          elsewhere, e.g. the History/QR icons a few hundred lines below in this same component,
          rather than every page sharing one component for this -- DeleteConfirmModal is the same
          per-file-local convention already). Delete keeps combo3-danger layered on top of
          combo-jello-spin, same as Orders' own Delete (shake-on-click + red tint together, not
          either alone) -- exact precedent, not a new combination. */}
      <div style={{display:'flex',gap:8,marginBottom:14,alignItems:'center',flexWrap:'wrap'}}>
        <button
          type="button"
          className="btn btn-ghost btn-sm combo-jello-spin"
          data-tint="neutral"
          disabled={!selected}
          onClick={()=>selected&&setModal({open:true,item:selected})}
          onMouseLeave={spinIconOut}
          title="Edit"
          aria-label="Edit"
          style={{
            width:32, height:32, flexShrink:0, padding:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            color: selected ? '#cbd5e1' : 'var(--text-muted)',
            border: selected ? '1px solid rgba(255,255,255,0.18)' : '1px solid var(--border-subtle)',
            opacity: selected ? 1 : 0.45,
          }}
        ><Pencil size={14} className="btn-icon"/></button>
        <button
          type="button"
          className="btn btn-ghost btn-sm combo-jello-spin combo3-danger"
          data-tint="red"
          disabled={!selected}
          onClick={()=>{if(selected) {
            const label = subTab==='facility'
              ? `key "${(selected as FacilityKey).hook_number || (selected as FacilityKey).area || 'this key'}"`
              : `room "${(selected as ResidentRoom).room_number || 'this room'}"`;
            setDeleteModal({
              open:true,
              onConfirm:()=>deleteMut.mutate((selected as any).id),
              message:`Delete ${label}? This cannot be undone.`,
            });
          }}}
          onMouseLeave={spinIconOut}
          title="Delete"
          aria-label="Delete"
          style={{
            width:32, height:32, flexShrink:0, padding:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            color: selected ? '#f87171' : 'var(--text-muted)',
            border: selected ? '1px solid rgba(239,68,68,0.4)' : '1px solid var(--border-subtle)',
            opacity: selected ? 1 : 0.45,
          }}
        ><Trash2 size={14} className="btn-icon"/></button>
        {subTab==='facility' && (
          <div style={{display:'flex', alignItems:'center', gap:10, marginLeft:4}}>
            {/* "Toggle C: icon-only with sliding highlight" (2026-10-09; sized down + retimed
                2026-10-10 -- 32px buttons/3px gaps now, highlight transition matches
                .pill-tab-slide-highlight's 400ms cubic-bezier(.34,1.56,.64,1) exactly). Board
                (pegboard) is Pro+ only -- Standard sees just the Table button, no real toggle --
                so the sliding .view-toggle-highlight only renders when there's something to
                slide between (isPro); the single Standard button gets the active look applied
                directly instead, same tier-gated UX as before this redesign. */}
            <div className="view-toggle">
              {isPro && (
                <div
                  className="view-toggle-highlight"
                  style={{ transform: `translateX(${keysView === 'table' ? 35 : 0}px)` }}
                />
              )}
              {(['board','table'] as const).filter(v => v !== 'board' || isPro).map(v => {
                const isActive = keysView === v || !isPro;
                const Icon = v === 'board' ? LayoutGrid : List;
                return (
                  <button
                    key={v}
                    type="button"
                    className={`view-toggle-btn${isActive ? ' view-toggle-btn-active' : ''}`}
                    onClick={() => toggleKeysView(v)}
                    aria-pressed={isActive}
                    aria-label={v === 'board' ? 'Board view' : 'Table view'}
                    title={v === 'board' ? 'Board view' : 'Table view'}
                  >
                    <Icon size={15} className="view-toggle-icon" />
                  </button>
                );
              })}
            </div>
            <span className="view-toggle-label">{keysView === 'board' ? 'Board' : 'Table'} view</span>
          </div>
        )}
        <div style={{marginLeft: isMobile ? 0 : 'auto', flex: isMobile ? '1 0 100%' : undefined, display:'flex',gap:8,alignItems:'center'}}>
          {/* Search -- round 4: switched from a hand-positioned absolute icon (position:absolute,
              top:50%, transform:translateY(-50%)) to the same `.search-bar search-input-wrapper`
              flex-row pill Catalog uses. That absolute+translateY setup was the cause of the
              icon-drop bug: .btn-icon's hover/focus-within iconWiggle animation sets
              `transform:rotate(...)` per keyframe, and a CSS animation's transform REPLACES the
              element's whole transform value rather than composing with it -- so every wiggle
              frame clobbered the inline `translateY(-50%)` that was holding the icon vertically
              centered, and it visibly dropped for the animation's duration. Catalog's icon never
              had this problem because `.search-bar` is `display:flex; align-items:center` --
              the icon is a plain flex child, centered by the flexbox itself, with no transform of
              its own for the animation to ever overwrite. Byte-for-byte the same markup/behavior
              as the Catalog search bar now (X-to-clear, Esc-to-clear with stopPropagation only
              when it actually cleared something, matching that implementation instead of a
              parallel one). */}
          <div className="search-bar search-input-wrapper" style={{ height: 30, boxSizing: 'border-box', flex: isMobile ? 1 : undefined }}>
            <Search size={13} color="var(--text-muted)" className="btn-icon" />
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={e=>setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.stopPropagation(); setSearch(''); } }}
            />
            {search && (
              <button
                type="button"
                className="search-clear"
                aria-label="Clear search"
                onClick={() => setSearch('')}
              >
                <X size={13} />
              </button>
            )}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm combo-jello-spin"
            data-tint="blue"
            onClick={downloadExport}
            onMouseLeave={spinIconOut}
            title="Export"
            aria-label="Export"
            style={{ width:32, height:32, flexShrink:0, padding:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#60a5fa', border:'1px solid rgba(59,130,246,0.4)' }}
          ><Download size={14} className="btn-icon"/></button>
          <button
            type="button"
            className="btn btn-ghost btn-sm combo-jello-spin"
            data-tint="blue"
            onClick={()=>fileRef.current?.click()}
            onMouseLeave={spinIconOut}
            title="Import"
            aria-label="Import"
            style={{ width:32, height:32, flexShrink:0, padding:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#60a5fa', border:'1px solid rgba(59,130,246,0.4)' }}
          ><Upload size={14} className="btn-icon"/></button>
          <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={handleImport}/>
        </div>
      </div>

      {subTab==='facility' && duplicateCodes.length > 0 && (
        <>
          {/* Part B (2026-10-08): restyle only, same duplicateCodes/handlers as before. This
              banner renders above both Board and Table view (it's outside the keysView ternary),
              so its pulse keyframes get their own always-mounted <style> tag here rather than
              living in the Board-view-only <style> block below (which doesn't exist in the DOM
              while Table view is active). */}
          <style>{`
            @keyframes dupTilePulse {
              0%   { box-shadow: 0 0 0 0 rgba(245,158,11,.45); }
              100% { box-shadow: 0 0 0 10px rgba(245,158,11,0); }
            }
            .dup-alert-tile { animation: dupTilePulse 2s ease-out infinite; }
            @media (prefers-reduced-motion: reduce) {
              .dup-alert-tile { animation: none; }
            }
            @media (max-width: 768px) {
              .dup-alert-card { flex-wrap: wrap; }
              .dup-alert-actions { width: 100%; flex-direction: column; }
              .dup-alert-actions button { width: 100%; }
            }
            /* Part B/Part 2 (2026-10-10): colours moved from inline styles to these classes so
               the light theme can override them -- every dark value below is byte-identical to
               what used to be inline here, nothing changed for dark. */
            .dup-alert-card { background: rgba(245,158,11,.08); border: 1px solid rgba(245,158,11,.25); }
            .dup-alert-tile { background: rgba(245,158,11,.14); border: 1px solid rgba(245,158,11,.4); }
            .dup-alert-icon { color: #FBBF24; }
            .dup-alert-title { color: #FDE68A; }
            .dup-chip { color: #FBBF24; background: rgba(245,158,11,.10); border: 1px solid rgba(245,158,11,.35); border-radius: 8px; }
            .dup-resolve-btn { background: rgba(245,158,11,.14); border: 1px solid rgba(245,158,11,.4); color: #FBBF24; }
            .dup-ignore-btn { border-color: var(--border-default); }
            html[data-theme="light"] .dup-alert-card {
              background: rgba(255,248,230,.66);
              -webkit-backdrop-filter: blur(16px);
              backdrop-filter: blur(16px);
              border-color: rgba(242,196,109,.7);
            }
            html[data-theme="light"] .dup-alert-tile { background: #FDEBC4; border-color: rgba(242,196,109,.7); }
            html[data-theme="light"] .dup-alert-icon { color: #B45309; }
            html[data-theme="light"] .dup-alert-title { color: #7C2D12; }
            html[data-theme="light"] .dup-chip { color: #92400E; background: #fff; border-color: #F2C46D; }
            html[data-theme="light"] .dup-resolve-btn { background: #D97706; border-color: #D97706; color: #fff; }
            html[data-theme="light"] .dup-ignore-btn { border-color: #E2D3B0; }
          `}</style>
          <div className="dup-alert-card" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 16, marginBottom: 12 }}>
            <div className="dup-alert-tile" style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={24} className="dup-alert-icon" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="dup-alert-title" style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: 6 }}>
                {duplicateCodes.length === 1 ? '1 key code is used more than once' : `${duplicateCodes.length} key codes are used more than once`}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {duplicateCodes.map(code => (
                  <span key={code} className="dup-chip" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, Consolas, monospace", fontSize: 12, padding: '2px 8px' }}>
                    {code}
                  </span>
                ))}
              </div>
            </div>
            <div className="dup-alert-actions" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                className="btn combo1 dup-resolve-btn"
                onClick={() => setShowDuplicateFilter(p => !p)}
                onMouseLeave={spinIconOut}
                style={{ fontSize: '0.8125rem', fontWeight: 600 }}
              >
                {showDuplicateFilter ? 'Hide' : 'Resolve'}
              </button>
              <button
                className="btn btn-ghost combo1 dup-ignore-btn"
                onClick={() => setIgnoredDuplicates(new Set(duplicateCodes))}
                onMouseLeave={spinIconOut}
                style={{ fontSize: '0.8125rem' }}
              >
                Ignore all
              </button>
            </div>
          </div>
        </>
      )}
      {subTab==='facility' && showDuplicateFilter && (
        <div style={{ background: 'var(--bg-elevated)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 8 }}>Select codes to ignore:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(codeCounts).filter(([_code, ids]) => ids.length > 1).map(([code]) => (
              <label key={code} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8125rem', color: ignoredDuplicates.has(code) ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                <input
                  type="checkbox"
                  checked={ignoredDuplicates.has(code)}
                  onChange={e => {
                    setIgnoredDuplicates(prev => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(code); else next.delete(code);
                      return next;
                    });
                  }}
                />
                {code} ({codeCounts[code].length} keys)
              </label>
            ))}
          </div>
        </div>
      )}

      {subTab==='facility' && keysView==='board' && isPro ? (
        /* ── Board View -- "Option E: left hook column" card + side panel (2026-10-08 redesign,
           Part 2) -- (content-level gate, not just the toggle button — protects against a stale
           'board' value in localStorage from before a downgrade to Standard) */
        <div>
          {/* Page-local styles -- card rules (peg-card, peg-hook-col, peg-icon-wrap, peg-name,
              peg-footer, peg-status-dot, peg-dup-tag) unchanged from the Part 1 redesign; the
              side-panel rules (peg-board-row, peg-side-panel, peg-panel-content and friends,
              peg-status-block, peg-field-grid, peg-history-row) are added this round for Part 2.
              Scoped here, not buttonAnimations.css/global.css -- bespoke, not one of the
              documented combos, mounts/unmounts with Board view only. All motion gated off
              under prefers-reduced-motion. */}
          <style>{`
            @keyframes pegCardIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            @keyframes pegIconSwing {
              0%   { transform: rotate(0deg); }
              20%  { transform: rotate(-16deg); }
              50%  { transform: rotate(10deg); }
              75%  { transform: rotate(-4deg); }
              100% { transform: rotate(0deg); }
            }
            .peg-card {
              position: relative;
              display: flex;
              height: 180px;
              border-radius: 18px;
              overflow: hidden;
              background: rgba(255,255,255,0.045);
              backdrop-filter: blur(16px);
              -webkit-backdrop-filter: blur(16px);
              border: 1px solid var(--border-subtle);
              cursor: pointer;
              opacity: 0;
              animation: pegCardIn 0.4s ease forwards;
              transition: transform 0.18s ease, box-shadow 0.18s ease;
            }
            /* will-change only on :hover, not globally, per the task's own performance note --
               a board can have 50+ of these cards, and will-change left on at rest promotes
               every single one to its own compositor layer for no benefit. */
            .peg-card:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(0,0,0,0.35); will-change: transform; }
            .peg-card.peg-card-dup { border-color: rgba(245,158,11,.4); }
            .peg-card.peg-card-selected {
              border-color: rgba(96,165,250,.75);
              box-shadow: 0 0 0 3px rgba(59,130,246,.18);
              background: rgba(59,130,246,.10);
            }
            /* "Light D: Warm glass" (2026-10-10, Part 1/3). Glass surface recipe on the base
               card; selected state is a WHITE card with a blue border/ring (not a blue wash --
               spec is explicit this differs from dark's rgba(59,130,246,.10) tinted background);
               radius drops to 14px in light. */
            html[data-theme="light"] .peg-card {
              background: rgba(255,254,251,.58);
              -webkit-backdrop-filter: blur(16px) saturate(150%);
              backdrop-filter: blur(16px) saturate(150%);
              border-color: rgba(255,255,255,.95);
              border-radius: 14px;
              box-shadow: inset 0 1px 0 rgba(255,255,255,.95), 0 1px 2px rgba(41,30,15,.06), 0 10px 26px -16px rgba(41,30,15,.22);
            }
            html[data-theme="light"] .peg-card.peg-card-dup { border-color: #F2C46D; }
            html[data-theme="light"] .peg-card.peg-card-selected {
              background: rgba(255,254,251,.58);
              border-color: #2563EB;
              box-shadow: 0 0 0 4px rgba(37,99,235,.12);
            }
            @supports not (backdrop-filter: blur(1px)) {
              html[data-theme="light"] .peg-card { background: #FFFEFB; }
            }
            .peg-hook-col {
              width: 70px; flex-shrink: 0; box-sizing: border-box;
              display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
              border-right: 1px solid var(--border-subtle);
            }
            .peg-hook-label { font-size: 9px; font-weight: 700; letter-spacing: 0.12em; }
            .peg-hook-number { font-size: 30px; font-weight: 700; line-height: 1; }
            .peg-tier-pill { font-size: 10px; border: 1px solid; border-radius: 4px; padding: 1px 6px; }
            .peg-body { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: center; padding: 16px 12px 14px; }
            .peg-icon-wrap {
              width: 56px; height: 56px; border-radius: 50%; flex-shrink: 0; margin-bottom: 8px;
              display: flex; align-items: center; justify-content: center;
            }
            /* Halo ring (Part 3, light only) -- an inset white ring around the icon circle so it
               reads as a distinct "halo" against the card's own now-lighter glass background. */
            html[data-theme="light"] .peg-icon-wrap { box-shadow: inset 0 0 0 1px rgba(255,255,255,.95); }
            .peg-icon-swing { display: inline-flex; transform-origin: 70% 30%; }
            .peg-card:hover .peg-icon-swing { animation: pegIconSwing 0.6s ease; }
            .peg-name {
              flex: 1; min-height: 0;
              font-size: 14px; font-weight: 500; color: var(--text-primary); text-align: center;
              display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
            }
            .peg-footer { flex-shrink: 0; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 6px; margin-top: 8px; font-size: 11px; }
            .peg-status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
            .peg-status-dot.in { background: #34d399; }
            .peg-status-dot.out { background: #fbbf24; }
            .peg-dup-tag {
              font-family: 'IBM Plex Mono', ui-monospace, Consolas, monospace; font-size: 9px; color: #fbbf24;
              background: rgba(245,158,11,.15); border: 1px solid rgba(245,158,11,.4);
              border-radius: 4px; padding: 1px 5px;
            }
            html[data-theme="light"] .peg-dup-tag { background: #FEF3C7; color: #92400E; border-color: #FCD9A0; }
            .peg-board-row { display: flex; gap: 16px; align-items: flex-start; }
            .peg-board-grid-col { flex: 999 1 640px; min-width: 0; }
            .peg-side-panel {
              flex: 1 1 340px; min-width: 0;
              position: sticky; top: 16px;
              border-radius: 20px;
              overflow: hidden;
              background: rgba(255,255,255,.045);
              backdrop-filter: blur(18px);
              -webkit-backdrop-filter: blur(18px);
              border: 1px solid var(--border-subtle);
              padding: 22px;
            }
            /* "Light D: Warm glass" (2026-10-10, Part 1) -- elevated glass recipe. */
            html[data-theme="light"] .peg-side-panel {
              background: rgba(255,254,251,.6);
              -webkit-backdrop-filter: blur(22px);
              backdrop-filter: blur(22px);
              border-color: rgba(255,255,255,.95);
              box-shadow: inset 0 1px 0 #fff, 0 1px 2px rgba(41,30,15,.05), 0 18px 40px -20px rgba(41,30,15,.28);
            }
            @supports not (backdrop-filter: blur(1px)) {
              html[data-theme="light"] .peg-side-panel { background: #FFFEFB; }
            }
            .peg-side-panel::before {
              content: '';
              position: absolute;
              top: 0; left: 24px; right: 24px; height: 1px;
              background: linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent);
              pointer-events: none;
            }
            .peg-panel-blur {
              position: absolute;
              top: -100px; right: -90px;
              width: 260px; height: 260px;
              border-radius: 50%;
              pointer-events: none;
              transition: background .4s ease;
            }
            .peg-panel-content { position: relative; display: flex; flex-direction: column; gap: 18px; }
            .peg-panel-close {
              background: none; border: none; cursor: pointer; color: var(--text-secondary);
              display: flex; align-items: center; justify-content: center;
              width: 30px; height: 30px; border-radius: 50%;
            }
            /* Part A fix (confirmed root cause): .combo-jello-spin.combo-jello-spin (2 classes,
               buttonAnimations.css) beats plain .peg-panel-close (1 class) on specificity, so its
               position:relative was winning over this rule's own position:absolute and the X
               fell into normal flow at the top-left, pushing the header down. Raised to 3
               classes here (not editing the shared combo-jello-spin rule, which is used
               app-wide) so this panel-scoped selector wins instead -- the X stays pinned in the
               corner, above the decorative blur bubble (explicit z-index, though DOM order --
               blur bubble renders first -- already puts it on top either way). */
            .peg-side-panel .peg-panel-close.combo-jello-spin {
              position: absolute; top: 14px; right: 14px; z-index: 2;
            }
            .peg-panel-header { display: flex; align-items: flex-start; gap: 12px; padding-right: 44px; }
            .peg-panel-tile { width: 56px; height: 56px; border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
            .peg-panel-hook-line { font-size: 11px; color: var(--text-muted); letter-spacing: 0.04em; }
            /* Part 4 (both themes -- font-size/weight bug fix, not theme-scoped): was 22px/600. */
            .peg-panel-name { font-size: 20px; font-weight: 700; color: var(--text-primary); line-height: 1.25; margin-top: 2px; }
            .peg-panel-tier-pill { font-size: 11px; border: 1px solid; border-radius: 4px; padding: 2px 8px; flex-shrink: 0; margin-left: auto; }
            .peg-status-block { display: flex; align-items: center; justify-content: space-between; gap: 12px; border-radius: 12px; padding: 12px 14px; flex-wrap: wrap; }
            .peg-status-block.in { background: rgba(16,185,129,.07); border: 1px solid rgba(16,185,129,.22); }
            .peg-status-block.out { background: rgba(245,158,11,.08); border: 1px solid rgba(245,158,11,.25); }
            .peg-status-title.in { color: #34d399; }
            .peg-status-title.out { color: #fbbf24; }
            .peg-status-text.in { color: var(--text-secondary); }
            .peg-status-text.out { color: #fbbf24; }
            .peg-action-checkin { background: #7f1d1d; color: rgba(239,68,68,0.7); }
            .peg-action-checkout { background: #1e3a5f; color: rgba(59,130,246,0.6); }
            html[data-theme="light"] .peg-status-block.in { background: #F0FBF4; border-color: #BFE8CF; }
            html[data-theme="light"] .peg-status-block.out { background: #FFF8EB; border-color: #F6D58E; }
            html[data-theme="light"] .peg-status-title.in { color: #166534; }
            html[data-theme="light"] .peg-status-title.out { color: #92400E; }
            html[data-theme="light"] .peg-status-text.out { color: #92400E; }
            html[data-theme="light"] .peg-action-checkin { background: #16A34A; color: #fff; }
            html[data-theme="light"] .peg-action-checkout { background: #2563EB; color: #fff; }
            .peg-field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
            .peg-field-box { background: rgba(255,255,255,0.04); border-radius: 8px; padding: 10px 12px; }
            html[data-theme="light"] .peg-field-box { background: rgba(255,255,255,.5); border: 1px solid rgba(255,255,255,.9); }
            .peg-field-label { font-size: 0.6875rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-muted); margin-bottom: 4px; }
            .peg-field-value { font-size: 0.875rem; font-weight: 600; color: var(--text-primary); display: flex; align-items: center; gap: 6px; }
            .peg-dup-warning { font-size: 0.8125rem; color: #fbbf24; background: rgba(245,158,11,.1); border: 1px solid rgba(245,158,11,.3); border-radius: 8px; padding: 8px 12px; }
            html[data-theme="light"] .peg-dup-warning { color: #92400E; background: #FFF8EB; border-color: #F6D58E; }
            .peg-notes { font-size: 0.875rem; color: var(--text-secondary); line-height: 1.5; }
            .peg-history-row { display: flex; align-items: center; gap: 10px; padding: 7px 0; border-bottom: 1px solid var(--border-subtle); }
            .peg-history-row:last-child { border-bottom: none; }
            .peg-history-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; border: 2px solid; background: transparent; }
            .peg-history-text { flex: 1; font-size: 0.8125rem; color: var(--text-secondary); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
            .peg-history-date { font-size: 0.6875rem; color: var(--text-muted); flex-shrink: 0; }
            .peg-panel-actions { display: flex; gap: 8px; flex-wrap: wrap; }
            .peg-empty-panel { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; min-height: 300px; color: var(--text-muted); text-align: center; font-size: 0.875rem; }
            @media (max-width: 1000px) {
              .peg-board-row { flex-wrap: wrap; }
              .peg-side-panel { position: static; }
            }
            @media (prefers-reduced-motion: reduce) {
              .peg-card { animation: none; opacity: 1; transition: none; }
              .peg-card:hover { transform: none; box-shadow: none; }
              .peg-card:hover .peg-icon-swing { animation: none; }
              .peg-panel-blur { transition: none; }
            }
          `}</style>

          <div className="peg-board-row">
            <div className="peg-board-grid-col">
              {sortedKeys.length === 0 && (
                <div style={{padding:40,textAlign:'center',color:'var(--text-muted)'}}>No keys found</div>
              )}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 16,
              }}>
                {sortedKeys.map((k, idx) => {
                  const tier = getTier(k);
                  const tint = PEG_TIER_TINTS[tier as 'GM'|'NM'] ?? PEG_TIER_TINTS.NOM;
                  const isOut = k.status==='out';
                  const isSel = k.id === selectedKeyId;
                  const isDuplicate = !!(k.key_code && duplicateCodes.includes(k.key_code));
                  const delayMs = Math.min(idx * 35, 600);
                  const glyphTint = getKeyGlyphTint(tier, { GM: PEG_GLYPH_VARS.GM.color, NM: PEG_GLYPH_VARS.NM.color }, { GM: PEG_GLYPH_VARS.GM.bg, NM: PEG_GLYPH_VARS.NM.bg }, k.icon, k.area);
                  return (
                    <div
                      key={k.id}
                      id={`key-hook-${k.id}`}
                      className={`peg-card${isSel ? ' peg-card-selected' : ''}${isDuplicate && !isSel ? ' peg-card-dup' : ''}`}
                      onClick={()=>setSelectedKeyId(prev => prev === k.id ? null : k.id)}
                      onMouseLeave={spinIconOut}
                      style={{ animationDelay: `${delayMs}ms` }}
                    >
                      <div className="peg-hook-col" style={{ background: tint.bg, borderRightColor: tint.border }}>
                        <div className="peg-hook-label" style={{ color: tint.label }}>HOOK</div>
                        <div className="peg-hook-number" style={{ color: k.hook_number ? tint.number : 'var(--text-muted)' }}>
                          {k.hook_number ? String(k.hook_number).padStart(2,'0') : '—'}
                        </div>
                        <div className="peg-tier-pill" style={{ background: tint.pillBg, color: tint.pillText, borderColor: tint.pillBorder }}>{tier}</div>
                      </div>
                      <div className="peg-body">
                        <div className="peg-icon-wrap" style={{ background: glyphTint.bg }}>
                          <KeyGlyph icon={k.icon} name={k.area} size={24} className="peg-icon-swing" style={{ color: glyphTint.color }} />
                        </div>
                        <div className="peg-name">{k.area || k.key_code || '—'}</div>
                        <div className="peg-footer">
                          <span className={`peg-status-dot ${isOut ? 'out' : 'in'}`} />
                          <span className={`peg-status-text ${isOut ? 'out' : 'in'}`}>
                            {isOut ? `Out${k.checked_out_to ? ` · ${k.checked_out_to}` : ''}` : 'On hook'}
                          </span>
                          {isDuplicate && <span className="peg-dup-tag">DUP {k.key_code}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div style={{display:'flex',gap:12,marginTop:16,flexWrap:'wrap',alignItems:'center'}}>
                <span style={{fontSize:'0.75rem',color:'var(--text-muted)',fontWeight:600}}>Legend:</span>
                {Object.entries(TIER_COLORS).map(([tk,c])=>(
                  <div key={tk} style={{display:'flex',alignItems:'center',gap:4}}>
                    <div style={{width:10,height:10,borderRadius:2,background:c.bg,border:`1px solid ${c.border}`}}/>
                    <span style={{fontSize:'0.75rem',color:'var(--text-secondary)'}}>{tk} — {c.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="peg-side-panel">
              {selectedKeyFull ? (() => {
                const panelTier = getTier(selectedKeyFull);
                const panelTint = PEG_TIER_TINTS[panelTier as 'GM'|'NM'] ?? PEG_TIER_TINTS.NOM;
                const panelGlow = PEG_TIER_GLOW[panelTier as 'GM'|'NM'] ?? PEG_TIER_GLOW.NOM;
                const panelGlyphTint = getKeyGlyphTint(panelTier, { GM: PEG_GLYPH_VARS.GM.color, NM: PEG_GLYPH_VARS.NM.color }, { GM: PEG_GLYPH_VARS.GM.bg, NM: PEG_GLYPH_VARS.NM.bg }, selectedKeyFull.icon, selectedKeyFull.area);
                const isOut = selectedKeyFull.status === 'out';
                const isDup = !!(selectedKeyFull.key_code && duplicateCodes.includes(selectedKeyFull.key_code));
                // Part A fix (confirmed root cause): this used to always compute AND render the
                // masked form, never checking `revealCode` at all -- the eye button toggled state
                // that nothing downstream read. Now branches on it: full code when revealed,
                // masked (all but last 2 chars as bullets; <=2 chars shown as-is) otherwise.
                const maskedCode = selectedKeyFull.key_code
                  ? (selectedKeyFull.key_code.length <= 2 ? selectedKeyFull.key_code : '•'.repeat(selectedKeyFull.key_code.length - 2) + selectedKeyFull.key_code.slice(-2))
                  : '—';
                const displayCode = selectedKeyFull.key_code
                  ? (revealCode ? selectedKeyFull.key_code : maskedCode)
                  : '—';
                return (
                  <>
                    <div className="peg-panel-blur" style={{ background: `radial-gradient(circle, ${panelGlow}, transparent 65%)` }} />
                    <button type="button" className="peg-panel-close combo-jello-spin" data-tint="neutral" onClick={() => setSelectedKeyId(null)} onMouseLeave={spinIconOut} title="Close" aria-label="Close">
                      <X size={16} className="btn-icon" />
                    </button>
                    <div className="peg-panel-content">
                      <div className="peg-panel-header">
                        <div className="peg-panel-tile" style={{ background: panelGlyphTint.bg }}>
                          <KeyGlyph icon={selectedKeyFull.icon} name={selectedKeyFull.area} size={26} style={{ color: panelGlyphTint.color }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="peg-panel-hook-line">
                            {selectedKeyFull.hook_number ? `Hook #${String(selectedKeyFull.hook_number).padStart(2,'0')}` : 'Not assigned to a hook'}
                          </div>
                          <div className="peg-panel-name">{selectedKeyFull.area || selectedKeyFull.key_code || '—'}</div>
                        </div>
                        <div className="peg-panel-tier-pill" style={{ background: panelTint.pillBg, color: panelTint.pillText, borderColor: panelTint.pillBorder }}>{panelTier}</div>
                      </div>

                      <div className={`peg-status-block ${isOut ? 'out' : 'in'}`}>
                        <div className={`peg-status-title ${isOut ? 'out' : 'in'}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', fontWeight: 600 }}>
                          <span className={`peg-status-dot ${isOut ? 'out' : 'in'}`} />
                          {isOut
                            ? `Checked out · ${selectedKeyFull.checked_out_to || '—'}${selectedKeyFull.checked_out_at ? ` · ${formatRelativeTime(selectedKeyFull.checked_out_at)}` : ''}`
                            : 'On hook · Available to check out'}
                        </div>
                        <button
                          type="button"
                          className={`combo-jello-spin peg-action-${isOut ? 'checkin' : 'checkout'}`}
                          data-tint="neutral"
                          onClick={() => isOut ? handleCheckIn(selectedKeyFull) : handleCheckOut(selectedKeyFull)}
                          onMouseLeave={spinIconOut}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600, flexShrink: 0 }}
                        >
                          {isOut ? <LogIn size={14} className="btn-icon"/> : <LogOut size={14} className="btn-icon"/>} {isOut ? 'Check In' : 'Check Out'}
                        </button>
                      </div>

                      <div className="peg-field-grid">
                        <div className="peg-field-box">
                          <div className="peg-field-label">Key Code</div>
                          <div className="peg-field-value">
                            {displayCode}
                            {selectedKeyFull.key_code && (
                              <button type="button" className="combo-jello-spin" data-tint="neutral" onClick={() => setRevealCode(r => !r)} onMouseLeave={spinIconOut} title={revealCode ? 'Hide code' : 'Reveal code'} aria-label={revealCode ? 'Hide code' : 'Reveal code'} style={{ width:22, height:22, display:'flex', alignItems:'center', justifyContent:'center', border:'none', borderRadius:6, color:'var(--text-muted)', cursor:'pointer', padding:0 }}>
                                {revealCode ? <LucideIcons.EyeOff size={13} className="btn-icon"/> : <Eye size={13} className="btn-icon"/>}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="peg-field-box">
                          <div className="peg-field-label">Hook</div>
                          <div className="peg-field-value">{selectedKeyFull.hook_number || '—'}</div>
                        </div>
                      </div>

                      {isDup && (
                        <div className="peg-dup-warning">
                          ⚠ Code {selectedKeyFull.key_code} is also assigned to another key.
                        </div>
                      )}

                      <div className="peg-notes">{selectedKeyFull.notes || '—'}</div>

                      {isEnterprise && (
                        <div>
                          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8 }}>
                            Recent History
                          </div>
                          {panelHistory.length === 0 ? (
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>No history yet.</div>
                          ) : (
                            panelHistory.slice(0, 3).map(h => {
                              const dotColor = h.action === 'checkout' ? '#fbbf24' : h.action === 'checkin' ? '#34d399' : 'var(--text-muted)';
                              const text = h.action === 'checkout'
                                ? `Checked out to ${h.checked_out_to || h.performed_by || '—'}`
                                : h.action === 'checkin'
                                ? `Checked in by ${h.performed_by || '—'}`
                                : `${h.action} by ${h.performed_by || '—'}`;
                              return (
                                <div key={h.id} className="peg-history-row">
                                  <span className="peg-history-dot" style={{ borderColor: dotColor }} />
                                  <span className="peg-history-text">{text}</span>
                                  <span className="peg-history-date">{formatRelativeTime(h.created_at)}</span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}

                      <div className="peg-panel-actions">
                        <button className="combo-jello-spin" data-tint="neutral" onClick={() => setModal({open:true,item:selectedKeyFull})} onMouseLeave={spinIconOut} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8125rem' }}>
                          <Pencil size={14} className="btn-icon"/> Edit
                        </button>
                        {isEnterprise && <>
                          <button className="combo-jello-spin" data-tint="blue" onClick={() => setHistoryModal({open:true,key:selectedKeyFull})} onMouseLeave={spinIconOut} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8125rem' }}>
                            <Clock size={14} className="btn-icon"/> History
                          </button>
                          <button className="combo-jello-spin" data-tint="blue" onClick={() => setQrModal({open:true,key:selectedKeyFull})} onMouseLeave={spinIconOut} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8125rem' }}>
                            <QrCode size={14} className="btn-icon"/> QR
                          </button>
                        </>}
                      </div>
                    </div>
                  </>
                );
              })() : (
                <div className="peg-empty-panel">
                  <KeyGlyph icon={null} size={32} style={{ color: 'var(--text-muted)' }} />
                  <div>Select a key to see its details</div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── Table View (facility or resident) ── */
        <div style={{overflowX:'auto',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'var(--radius-md)'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8125rem'}}>
            <thead>
              <tr style={{background:'var(--bg-elevated)'}}>
                {subTab==='facility' ? (
                  <>
                    {([
                      {key:'hook_number',label:'Hook #'},
                      {key:'Key Code',label:'Key Code'},
                      {key:'Area / Description',label:'Area / Description'},
                      {key:'Tier',label:'Tier'},
                      {key:'Status',label:'Status'},
                    ]).map(col=>(
                      <th key={col.label} onClick={()=>handleSort(col.key)} style={{
                        padding:'10px 12px',textAlign:'left',cursor:'pointer',userSelect:'none',
                        fontWeight:600,fontSize:'0.75rem',textTransform:'uppercase',
                        letterSpacing:'0.06em',color:'var(--text-muted)',whiteSpace:'nowrap',
                        borderBottom:'1px solid var(--border-subtle)',
                        minWidth: col.label==='Tier' ? 150 : undefined,
                      }}>{col.label} {sortCol===col.key?(sortDir===1?'↑':'↓'):''}</th>
                    ))}
                    {isEnterprise && subTab==='facility' && <th style={{padding:'10px 12px',fontWeight:600,fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text-muted)',borderBottom:'1px solid var(--border-subtle)'}}>Actions</th>}
                  </>
                ) : residentCols.map(col=>(
                  <th key={col.key} onClick={()=>handleSort(col.key as string)} style={{
                    padding:'10px 12px',textAlign:'left',cursor:'pointer',userSelect:'none',
                    fontWeight:600,fontSize:'0.75rem',textTransform:'uppercase',
                    letterSpacing:'0.06em',color:'var(--text-muted)',whiteSpace:'nowrap',
                    borderBottom:'1px solid var(--border-subtle)',
                  }}>{col.label} {sortCol===col.key?(sortDir===1?'↑':'↓'):''}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length===0 && (
                <tr><td colSpan={9} style={{padding:30,textAlign:'center',color:'var(--text-muted)'}}>No records</td></tr>
              )}
              {sorted.map((item:any)=>{
                const isSel=(selected as any)?.id===item.id;
                const isDup = subTab==='facility' && item.key_code && duplicates.includes(item.key_code);
                return (
                  <tr key={item.id} onClick={()=>setSelected(isSel?null:item)} style={{
                    cursor:'pointer',background:isSel?'var(--bg-active)':'transparent',
                    borderBottom:'1px solid var(--border-subtle)',transition:'background 100ms',
                    borderLeft: isDup ? '3px solid var(--accent-danger)' : undefined,
                  }}
                  onMouseEnter={e=>{if(!isSel)(e.currentTarget as HTMLElement).style.background='var(--bg-hover)';}}
                  onMouseLeave={e=>{if(!isSel)(e.currentTarget as HTMLElement).style.background='transparent';}}>
                    {subTab==='facility' ? (
                      <>
                        <td style={{padding:'10px 12px',verticalAlign:'middle'}}>
                          <span style={{background:'rgba(59,130,246,0.15)',color:'#60a5fa',borderRadius:4,padding:'2px 7px',fontWeight:700,fontSize:'0.75rem'}}>
                            #{item.hook_number||'—'}
                          </span>
                        </td>
                        <td style={{padding:'10px 12px',verticalAlign:'middle'}}>
                          {item.key_code
                            ? <span style={{background:'var(--bg-elevated)',padding:'2px 8px',borderRadius:4,fontFamily:"'IBM Plex Mono', ui-monospace, Consolas, monospace",fontSize:11,color:'rgba(59,130,246,0.6)',border:'0.5px solid var(--bg-elevated)'}}>{item.key_code}</span>
                            : <span style={{color:'var(--text-muted)'}}>—</span>}
                        </td>
                        <td style={{padding:'10px 12px',verticalAlign:'middle',color:'var(--text-primary)'}}>{item.area||<span style={{color:'var(--text-muted)'}}>—</span>}</td>
                        <td style={{padding:'10px 12px',verticalAlign:'middle'}}>
                          {(()=>{ const t=getTier(item as FacilityKey); const c=TIER_COLORS[t]; return (
                            <span style={{background:c.bg,color:c.text,border:`1px solid ${c.border}`,borderRadius:4,padding:'2px 7px',fontSize:'0.75rem',fontWeight:700}}>{c.label}</span>
                          ); })()}
                        </td>
                        <td style={{padding:'10px 12px',verticalAlign:'middle'}}>
                          {(item as FacilityKey).status==='out'
                            ? <span className="badge badge-red">OUT</span>
                            : <span className="badge badge-green">IN</span>
                          }
                        </td>
                        {isEnterprise && (
                          <td style={{padding:'8px 12px',verticalAlign:'middle'}} onClick={e=>e.stopPropagation()}>
                            <div style={{display:'flex',gap:4}}>
                              <button className="btn btn-ghost combo-jello-spin" data-tint="blue" style={{fontSize:'0.75rem',padding:'3px 8px'}} onClick={()=>setHistoryModal({open:true,key:item as FacilityKey})} onMouseLeave={spinIconOut}><Clock size={12} className="btn-icon"/></button>
                              <button className="btn btn-ghost combo-jello-spin" data-tint="blue" style={{fontSize:'0.75rem',padding:'3px 8px'}} onClick={()=>setQrModal({open:true,key:item as FacilityKey})} onMouseLeave={spinIconOut}><QrCode size={12} className="btn-icon"/></button>
                            </div>
                          </td>
                        )}
                      </>
                    ) : residentCols.map(col=>(
                      <td key={col.key} style={{padding:'10px 12px',verticalAlign:'middle'}}>
                        <span style={{color:'var(--text-primary)'}}>{item[col.key]||<span style={{color:'var(--text-muted)'}}>—</span>}</span>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal.open && (
        <KeyModal subTab={subTab} item={modal.item} onClose={()=>setModal({open:false})}
          onSaved={()=>{qc.invalidateQueries({queryKey:['keys',subTab]});setModal({open:false});setSelected(null);}}/>
      )}
      {deleteModal.open && <DeleteConfirmModal
        message={deleteModal.message}
        onConfirm={()=>{deleteModal.onConfirm();setDeleteModal({open:false,onConfirm:()=>{}}); }}
        onClose={()=>setDeleteModal({open:false,onConfirm:()=>{}})}/>}

      {historyModal.open && historyModal.key && (
        <KeyHistoryModal key_={historyModal.key} onClose={()=>setHistoryModal({open:false,key:null})}/>
      )}
      {qrModal.open && qrModal.key && (
        <QRModal key_={qrModal.key} onClose={()=>setQrModal({open:false,key:null})}/>
      )}

      {/* Check-out name prompt modal */}
      {checkoutPrompt && ReactDOM.createPortal(
        <div
          style={{
            position:'fixed', inset:0, zIndex:1000,
            background:'rgba(0,0,0,0.65)',
            backdropFilter:'blur(8px)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:16,
          }}
          onMouseDown={() => {
            setCheckoutPrompt(null);
            setCheckoutName('');
            setCheckoutSearch('');
          }}
        >
          <div
            style={{
              background:'rgba(20,30,50,0.97)',
              borderRadius:14,
              width:'100%', maxWidth:380,
              border:'1px solid rgba(255,255,255,0.1)',
              padding:'24px',
            }}
            onMouseDown={e => e.stopPropagation()}
          >
            <h3 style={{margin:'0 0 4px', fontSize:'1rem', fontWeight:600, display:'flex', alignItems:'center', gap:8}}>
              <LogOut size={16} className="modal-icon-intro" style={{color:'#3b82f6'}}/> Check Out Key #{checkoutPrompt.key.hook_number}
            </h3>
            {checkoutPrompt.key.key_code && (
              <div style={{fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:16}}>
                Code: {checkoutPrompt.key.key_code}
              </div>
            )}

            <label className="form-label" style={{marginBottom:6, display:'block'}}>
              Checked out to
            </label>

            <div style={{position:'relative', marginBottom:16}}>
              <input
                className="form-input"
                autoFocus
                placeholder="Search staff or type any name…"
                value={checkoutName}
                onChange={e => {
                  setCheckoutName(e.target.value);
                  setCheckoutSearch(e.target.value);
                  setCheckoutShowDropdown(true);
                }}
                onFocus={() => setCheckoutShowDropdown(true)}
                onBlur={() => setTimeout(() => setCheckoutShowDropdown(false), 200)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && checkoutName.trim()) confirmCheckOut();
                  if (e.key === 'Escape') {
                    setCheckoutPrompt(null);
                    setCheckoutName('');
                    setCheckoutSearch('');
                  }
                }}
              />

              {checkoutShowDropdown && (
                <div style={{
                  position:'absolute', top:'calc(100% + 4px)', left:0, right:0,
                  background:'rgba(20,30,50,0.97)',
                  border:'1px solid rgba(255,255,255,0.1)',
                  borderRadius:8, zIndex:1001,
                  maxHeight:200, overflowY:'auto',
                  boxShadow:'0 8px 24px rgba(0,0,0,0.5)',
                }}>
                  {facilityUsers
                    .filter(u => {
                      const full = [u.first_name, u.last_name, u.username]
                        .filter(Boolean).join(' ').toLowerCase();
                      return !checkoutSearch || full.includes(checkoutSearch.toLowerCase());
                    })
                    .slice(0, 8)
                    .map(u => {
                      const displayName = [u.first_name, u.last_name].filter(Boolean).join(' ')
                        || u.username;
                      const color = getUserColor(u.id);
                      const initials = avatarInitials(
                        [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username
                      );
                      return (
                        <div
                          key={u.id}
                          onMouseDown={e => {
                            e.preventDefault();
                            setCheckoutName(displayName);
                            setCheckoutSearch(displayName);
                            setCheckoutShowDropdown(false);
                          }}
                          style={{
                            padding:'8px 12px', cursor:'pointer',
                            fontSize:'0.875rem', color:'var(--text-primary)',
                            borderBottom:'1px solid rgba(255,255,255,0.06)',
                            display:'flex', alignItems:'center', gap:8,
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background='rgba(255,255,255,0.06)')}
                          onMouseLeave={e => (e.currentTarget.style.background='transparent')}
                        >
                          <div style={{
                            width:28, height:28, borderRadius:'50%',
                            background: color + '33',
                            border: `1px solid ${color}55`,
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:11, fontWeight:700, color, flexShrink:0,
                          }}>
                            {initials}
                          </div>
                          <div>
                            <div style={{fontWeight:500}}>{displayName}</div>
                            <div style={{fontSize:'0.75rem', color:'var(--text-muted)'}}>
                              @{u.username}
                            </div>
                            {u.department_name && u.department_color && (
                              <span style={deptBadge(u.department_color, u.department_name)}>{u.department_name}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  {checkoutName.trim() &&
                    !facilityUsers.some(u =>
                      [u.first_name, u.last_name].filter(Boolean).join(' ') === checkoutName.trim()
                      || u.username === checkoutName.trim()
                    ) && (
                    <div
                      onMouseDown={e => {
                        e.preventDefault();
                        setCheckoutShowDropdown(false);
                      }}
                      style={{
                        padding:'8px 12px', cursor:'pointer',
                        fontSize:'0.8rem', color:'var(--text-muted)',
                        fontStyle:'italic',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background='rgba(255,255,255,0.06)')}
                      onMouseLeave={e => (e.currentTarget.style.background='transparent')}
                    >
                      Use &ldquo;{checkoutName.trim()}&rdquo; (not a staff member)
                    </div>
                  )}

                  {facilityUsers.length === 0 && (
                    <div style={{padding:'10px 12px', fontSize:'0.8rem', color:'var(--text-muted)'}}>
                      No staff found — type any name
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              <button
                className="btn btn-ghost combo-cancel-jello"
                onClick={() => {
                  setCheckoutPrompt(null);
                  setCheckoutName('');
                  setCheckoutSearch('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary combo2"
                onClick={confirmCheckOut}
                disabled={!checkoutName.trim()}
              >
                Confirm Check Out
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add -- round 4: moved out of the inline toolbar to the same page-corner
          FloatingActionButton Catalog/Learning Hub use (Chris's explicit call once Vendors' and
          Orders' own "Add" buttons turned out to be combo5-hero, not the transparent icon style --
          this FAB was the one real match). Label follows subTab so it reads correctly for either
          sub-tab sharing this one toolbar. */}
      <FloatingActionButton
        icon={<Plus size={20} className="btn-icon" />}
        onClick={() => setModal({ open: true })}
        title={subTab === 'facility' ? 'Add key' : 'Add room'}
        ariaLabel={subTab === 'facility' ? 'Add key' : 'Add room'}
      />
    </div>
  );
}

function KeyModal({subTab,item,onClose,onSaved}:{subTab:KeySubTab;item?:any;onClose:()=>void;onSaved:()=>void}) {
  const isEdit=!!item;
  const [saving,setSaving]=useState(false);
  const mouseDownTarget=useRef<EventTarget|null>(null);
  const [form,setForm]=useState({
    room_number:item?.room_number||'', resident_name:item?.resident_name||'',
    hook_number:item?.hook_number||'', key_code:item?.key_code||'', area:item?.area||'',
    is_grand_master:item?.is_grand_master??false, is_new_master:item?.is_new_master??false,
    is_sub_master:item?.is_sub_master??false, not_on_master:item?.not_on_master??false,
    is_sub_master_key:item?.is_sub_master_key??false,
    notes:item?.notes||'', icon:item?.icon||'🔑',
    key_type:subTab,
  });

  async function handleSubmit(e:React.FormEvent) {
    e.preventDefault(); setSaving(true);
    try {
      if(isEdit) await api.put(`/tasks/keys/${item.id}`,form);
      else await api.post('/tasks/keys',form);
      onSaved();
    } finally { setSaving(false); }
  }
  const CB=(key:keyof typeof form,label:string)=>(
    <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:'0.875rem'}}>
      <input type="checkbox" checked={!!form[key]} onChange={e=>setForm(p=>({...p,[key]:e.target.checked}))} style={{accentColor:'var(--accent-primary)'}}/>
      {label}
    </label>
  );
  return ReactDOM.createPortal(
    <div
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)',zIndex:998,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
      onMouseDown={(e)=>{mouseDownTarget.current=e.target;}}
      onMouseUp={(e)=>{if(mouseDownTarget.current===e.currentTarget&&e.target===e.currentTarget)onClose();mouseDownTarget.current=null;}}
    >
      <div style={{background:'var(--bg-elevated)',borderRadius:'var(--radius-xl)',width:'100%',maxWidth:480,border:'1px solid var(--border-default)',display:'flex',flexDirection:'column',maxHeight:'90vh',zIndex:999}} onClick={(e)=>e.stopPropagation()}>
        <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border-subtle)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
          <h3 style={{margin:0,display:'flex',alignItems:'center',gap:8}}>
            {subTab==='resident'
              ? <Home size={18} className="modal-icon-intro" style={{color:'#3b82f6'}}/>
              : <Key size={18} className="modal-icon-intro" style={{color:'#3b82f6'}}/>}
            {isEdit?'Edit':'Add'} {subTab==='resident'?'Resident Room':'Key'}
          </h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)'}}><X size={18} className="btn-icon"/></button>
        </div>
        <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',flex:1,minHeight:0}}>
          <div style={{flex:1,overflowY:'auto',padding:'20px 24px',display:'flex',flexDirection:'column',gap:14}}>
          {subTab==='resident' ? (
            <>
              <div><label className="form-label">Room # / Area *</label><input className="form-input" required value={form.room_number} onChange={e=>setForm(p=>({...p,room_number:e.target.value}))}/></div>
              <div><label className="form-label">Resident Name</label><input className="form-input" value={form.resident_name} onChange={e=>setForm(p=>({...p,resident_name:e.target.value}))}/></div>
              <div><label className="form-label">Hook #</label><input className="form-input" value={form.key_code} onChange={e=>setForm(p=>({...p,key_code:e.target.value}))}/></div>
              <div><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
            </>
          ) : (
            <>
              {/* Icon picker */}
              <div>
                <label className="form-label">Icon</label>
                <div style={{display:'flex',gap:6,flexWrap:'wrap',paddingTop:4}}>
                  {KEY_ICONS.map(ic=>(
                    <button key={ic} type="button" onClick={()=>setForm(p=>({...p,icon:ic}))} style={{
                      width:36,height:36,borderRadius:6,border:`2px solid ${form.icon===ic?'var(--accent-primary)':'var(--border-default)'}`,
                      background:form.icon===ic?'rgba(59,130,246,0.15)':'transparent',
                      fontSize:18,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',
                    }}>{ic}</button>
                  ))}
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label className="form-label">Hook #</label><input className="form-input" value={form.hook_number} onChange={e=>setForm(p=>({...p,hook_number:e.target.value}))}/></div>
                <div><label className="form-label">Key Code</label><input className="form-input" value={form.key_code} onChange={e=>setForm(p=>({...p,key_code:e.target.value}))}/></div>
              </div>
              <div><label className="form-label">Area / Description</label><input className="form-input" value={form.area} onChange={e=>setForm(p=>({...p,area:e.target.value}))}/></div>
              <div><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,paddingTop:4}}>
                {CB('is_grand_master','Grand Master')}{CB('is_new_master','New Master')}
                {CB('is_sub_master','Sub Master')}{CB('not_on_master','Not on Master')}
                {CB('is_sub_master_key','Is Sub Master')}
              </div>
            </>
          )}
          </div>
          <div style={{flexShrink:0,padding:'16px 24px',borderTop:'1px solid var(--border-subtle)',display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary combo2" disabled={saving}>{saving?'Saving…':'Save'}</button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function KeyHistoryModal({key_,onClose}:{key_:FacilityKey;onClose:()=>void}) {
  const {data:histRes,isLoading} = useQuery({
    queryKey:['key-history',key_.id],
    queryFn:()=>api.get<KeyHistoryItem[]>(`/tasks/keys/${key_.id}/history`),
  });
  const history = (histRes?.data??[]) as KeyHistoryItem[];
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',backdropFilter:'blur(4px)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--bg-elevated)',borderRadius:'var(--radius-xl)',width:'100%',maxWidth:620,border:'1px solid var(--border-default)',overflow:'hidden',maxHeight:'80vh',display:'flex',flexDirection:'column',zIndex:10000}}>
        <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border-subtle)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <h3 style={{margin:0,fontSize:'1rem',display:'flex',alignItems:'center',gap:8}}>
              <Clock size={16} className="modal-icon-intro" style={{color:'#3b82f6'}}/> Key History
            </h3>
            <div style={{fontSize:'0.8125rem',color:'var(--text-muted)',marginTop:2}}>Hook #{key_.hook_number} — {key_.key_code||'No code'}</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)'}}><X size={18} className="btn-icon"/></button>
        </div>
        <div style={{overflowY:'auto',flex:1}}>
          {isLoading ? (
            <div style={{padding:40,textAlign:'center',color:'var(--text-muted)'}}>Loading…</div>
          ) : history.length===0 ? (
            <div style={{padding:40,textAlign:'center',color:'var(--text-muted)'}}>No history yet.</div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8125rem'}}>
              <thead>
                <tr style={{background:'var(--bg-elevated)'}}>
                  {['Date','Action','Checked Out To','Performed By'].map(h=>(
                    <th key={h} style={{padding:'10px 14px',textAlign:'left',fontWeight:600,fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text-muted)',borderBottom:'1px solid var(--border-subtle)'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map(row=>(
                  <tr key={row.id} style={{borderBottom:'1px solid var(--border-subtle)'}}>
                    <td style={{padding:'10px 14px',color:'var(--text-secondary)',whiteSpace:'nowrap'}}>{new Date(row.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</td>
                    <td style={{padding:'10px 14px'}}>
                      <span className={`badge ${row.action==='checked_out'?'badge-red':'badge-green'}`}>
                        {row.action==='checked_out'?'Checked Out':'Returned'}
                      </span>
                    </td>
                    <td style={{padding:'10px 14px',color:'var(--text-primary)'}}>{row.checked_out_to||<span style={{color:'var(--text-muted)'}}>—</span>}</td>
                    <td style={{padding:'10px 14px',color:'var(--text-secondary)'}}>{row.performed_by||<span style={{color:'var(--text-muted)'}}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function QRModal({key_,onClose}:{key_:FacilityKey;onClose:()=>void}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const qrData = encodeURIComponent(`ProQure Key: ${key_.area||'No area'} | Code: ${key_.key_code||'—'} | Hook: ${key_.hook_number||'—'}`);
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${qrData}`;

  function handlePrint() {
    const styleEl = document.createElement('style');
    styleEl.id = '__qr_print__';
    styleEl.textContent = `@media print { body > * { visibility:hidden!important; } #qr-print-area, #qr-print-area * { visibility:visible!important; } #qr-print-area { position:fixed;top:0;left:0;width:100%;display:flex;flex-direction:column;align-items:center;padding-top:60px; } }`;
    document.head.appendChild(styleEl);
    window.print();
    setTimeout(()=>{ document.getElementById('__qr_print__')?.remove(); }, 1000);
  }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',backdropFilter:'blur(4px)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--bg-elevated)',borderRadius:'var(--radius-xl)',width:'100%',maxWidth: isMobile ? '95vw' : 320,border:'1px solid var(--border-default)',overflow:'hidden',zIndex:10000}}>
        <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border-subtle)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <h3 style={{margin:0,fontSize:'1rem'}}><QrCode size={16} className="modal-icon-intro" style={{verticalAlign:'middle',marginRight:6,color:'#3b82f6'}}/>QR Code</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)'}}><X size={18} className="btn-icon"/></button>
        </div>
        <div id="qr-print-area" style={{padding:'24px',display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
          <div style={{fontWeight:700,color:'var(--text-primary)',fontSize:'0.9375rem'}}>Hook #{key_.hook_number||'—'} — {key_.key_code||'No code'}</div>
          <div style={{fontSize:'0.8125rem',color:'var(--text-muted)'}}>{key_.area||'No area'}</div>
          <img src={qrUrl} alt="QR Code" width={200} height={200} style={{borderRadius:8,border:'4px solid #fff',background:'#fff'}}/>
        </div>
        <div style={{padding:'0 24px 24px',display:'flex',gap:8}}>
          <button className="btn btn-ghost combo1" style={{flex:1}} onClick={handlePrint} onMouseLeave={spinIconOut}><Download size={13} className="btn-icon"/> Print</button>
          <button className="btn btn-ghost combo-cancel-jello" style={{flex:1}} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function InventoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemId = searchParams.get('itemId');
  const stateOpenItemId: number | null = (location.state as { openItemId?: number } | null)?.openItemId ?? null;
  // Per-click nonce (searchNav.ts's navigateToResult) -- lets the effect below tell "the same
  // item, clicked again" apart from "still the same navigation that already opened it", since
  // openItemId alone would be identical in both cases.
  const stateOpenItemNonce: number | null = (location.state as { _t?: number } | null)?._t ?? null;
  const queryClient = useQueryClient();

  // Read + clear sessionStorage once on mount (lazy initializer only runs on first render)
  const INVENTORY_STATE_KEY = 'inventory_catalogue_state';
  const [_saved] = useState<Record<string, unknown>>(() => {
    try {
      const raw = sessionStorage.getItem(INVENTORY_STATE_KEY);
      if (!raw) return {};
      sessionStorage.removeItem(INVENTORY_STATE_KEY);
      return JSON.parse(raw) as Record<string, unknown>;
    } catch { return {}; }
  });

  // Global-search deep links (?supplyId=, ?extinguisherId=, ?sub=) -- read LIVE off searchParams
  // every render, never a one-time useState snapshot, so a second search-result click while
  // Inventory is already mounted (same path, new query string) is actually seen. Each consumer
  // below still needs its own ref-based "have I already handled THIS id" guard -- otherwise every
  // unrelated re-render would re-trigger the open/scroll/glow for whatever id happens to still be
  // in the URL.
  const initialSupplyId = searchParams.get('supplyId');
  const initialExtinguisherId = searchParams.get('extinguisherId');
  const subParam = searchParams.get('sub');

  // Removes a single deep-link query param once it's been acted on (opened, or determined
  // not-found) -- without this, clicking the SAME search result a second time after closing its
  // detail view would never re-open it, since the param would already match what was "last
  // handled". Stripping it means the id going from present -> absent -> present again is always a
  // real, detectable transition.
  const clearDeepLinkParam = useCallback((key: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(key);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [activeTab, setActiveTab] = useState<'catalogue' | 'supplies' | 'facility-keys' | 'facility-map' | 'logbook-templates'>(() => {
    // BOM navigation return takes priority (INVENTORY_STATE_KEY is set before navigating to BOM)
    // 'extinguishers' is no longer a top-level tab (moved under Facility Supplies as a category,
    // sub=extinguishers) -- a stale saved value from before that move safely falls back to 'supplies'.
    if (_saved.tab === 'supplies' || _saved.tab === 'extinguishers' || _saved.tab === 'facility-keys') {
      return (_saved.tab === 'extinguishers' ? 'supplies' : _saved.tab) as 'supplies' | 'facility-keys';
    }
    // URL param (from global search navigation)
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('tab') === 'facility-supplies') return 'supplies';
    // Old-style extinguisher deep link (tab=extinguishers) -- TopBar.tsx now generates
    // tab=facility-supplies&sub=extinguishers directly, but keep this as a harmless fallback.
    if (sp.get('tab') === 'extinguishers') return 'supplies';
    if (sp.get('tab') === 'facility-keys') return 'facility-keys';
    if (sp.get('tab') === 'facility-map') return 'facility-map';
    if (sp.get('tab') === 'logbook-templates') return 'logbook-templates';
    // Persistent restore across page refreshes
    const t = sessionStorage.getItem('inventory_active_tab');
    if (t === 'extinguishers') return 'supplies';
    if (t === 'supplies' || t === 'facility-keys' || t === 'facility-map' || t === 'logbook-templates') return t as 'supplies' | 'facility-keys' | 'facility-map' | 'logbook-templates';
    return 'catalogue';
  });

  // Reactive counterpart to the one-time `tab=` read in the lazy initializer above -- a global
  // search navigation to a DIFFERENT tab while Inventory is already mounted only changes the
  // query string (same '/inventory' path), which never re-runs a useState initializer. This only
  // ever pushes activeTab FORWARD to match a recognized `tab=` value; it never fires from the
  // user's own manual tab clicks, since those only call setActiveTab directly and never touch the
  // URL's `tab` param.
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (!tab) return;
    if ((tab === 'facility-supplies' || tab === 'extinguishers') && activeTab !== 'supplies') setActiveTab('supplies');
    else if (tab === 'facility-keys' && activeTab !== 'facility-keys') setActiveTab('facility-keys');
    else if (tab === 'facility-map' && activeTab !== 'facility-map') setActiveTab('facility-map');
    else if (tab === 'logbook-templates' && activeTab !== 'logbook-templates') setActiveTab('logbook-templates');
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const [search, setSearch] = useState(() => {
    if (typeof _saved.query === 'string' && _saved.query) return _saved.query;
    return new URLSearchParams(window.location.search).get('search') ?? '';
  });
  const [status, setStatus] = useState('Active');
  const [page, setPage] = useState(1);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(() => {
    if (typeof _saved.categoryId === 'number') return _saved.categoryId;
    const saved = sessionStorage.getItem('catalog_active_category');
    return saved ? Number(saved) : null;
  });
  const [expandedCatIds, setExpandedCatIds] = useState<Set<number>>(new Set());
  const _savedScrollY = typeof _saved.scrollY === 'number' ? _saved.scrollY : 0;
  const [categoryExpanded, setCategoryExpanded] = useState(() => localStorage.getItem('inventory_categories_collapsed') !== 'true');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [categoryManagerInitId, setCategoryManagerInitId] = useState<number | null>(null);
  const [editItem, setEditItem] = useState<InventoryItem | null>(null);
  const [orderItem, setOrderItem] = useState<InventoryItem | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [showPendingPanel, setShowPendingPanel] = useState(false);
  const [extensionToast, setExtensionToast] = useState(false);
  const { toast: saveToast, showToast: showSaveToast } = useToast();
  const prevPendingCount = useRef<number | null>(null);
  const openedItemIdRef = useRef<string | null>(null);
  const stateItemHandledRef = useRef<string | null>(null);
  const scrollRestoredRef = useRef(false);
  const mouseDownTarget = useRef<EventTarget | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [facilityFlyoutOpen, setFacilityFlyoutOpen] = useState(false);
  const facilityFlyoutCloseTimer = useRef<ReturnType<typeof setTimeout>>();
  const facilityFlyoutRef = useRef<HTMLDivElement>(null);
  const facilityTriggerRef = useRef<HTMLButtonElement>(null);
  const catalogueTabBtnRef = useRef<HTMLButtonElement>(null);
  // Fixed, known panel width (not just minWidth) so the boundary math below is exact rather than
  // guessed — same getBoundingClientRect()+window.innerWidth clamp approach already used by this
  // file's other edge-aware dropdowns (see the category-select dropdown's openDropdown() around
  // line 2316). Portaled to document.body (backlog #60 fix) — position:fixed descendants of a
  // scrolling container (.page-content, global.css) don't reliably escape to the true viewport on
  // mobile Safari/WebView once the page has scrolled, which is why raising z-index alone (see the
  // comment on the panel below) never fixed it. Click-outside-to-close is now handled by an
  // invisible full-screen backdrop rendered in the same portal, same pattern as that other
  // dropdown — a document-level mousedown listener checking facilityFlyoutRef.current.contains()
  // no longer works once the panel is a portal, since it's no longer a real DOM descendant of
  // that ref's node.
  const FACILITY_FLYOUT_WIDTH = 210;
  const [facilityFlyoutPos, setFacilityFlyoutPos] = useState<{ top: number; left: number } | null>(null);
  function openFacilityFlyout() {
    if (!facilityTriggerRef.current) return;
    const rect = facilityTriggerRef.current.getBoundingClientRect();
    const rawLeft = rect.left;
    const left = rawLeft + FACILITY_FLYOUT_WIDTH > window.innerWidth ? window.innerWidth - FACILITY_FLYOUT_WIDTH - 8 : rawLeft;
    setFacilityFlyoutPos({ top: rect.bottom + 6, left });
    setFacilityFlyoutOpen(true);
  }

  function handleSetActiveTab(tab: 'catalogue' | 'supplies' | 'facility-keys' | 'facility-map' | 'logbook-templates') {
    sessionStorage.setItem('inventory_active_tab', tab);
    setActiveTab(tab);
  }

  // Persist active category across page refreshes
  useEffect(() => {
    if (activeTab !== 'catalogue') return;
    if (activeCategoryId !== null) {
      sessionStorage.setItem('catalog_active_category', String(activeCategoryId));
    } else {
      sessionStorage.removeItem('catalog_active_category');
    }
  }, [activeCategoryId, activeTab]);

  // Persist scroll position with 500ms debounce
  useEffect(() => {
    if (activeTab !== 'catalogue') return;
    let timer: ReturnType<typeof setTimeout>;
    function onScroll() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        sessionStorage.setItem('catalog_scroll_position', String(window.scrollY));
      }, 500);
    }
    window.addEventListener('scroll', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); clearTimeout(timer); };
  }, [activeTab]);

  // Scroll restoration is handled after items first load — see effect after items query

  const { data: categoriesData } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: () => api.get<Category[]>('/inventory/categories'),
  });

  const { data: topLevelCountsData } = useQuery({
    queryKey: ['category-top-level-counts'],
    queryFn: () => api.get<CategoryChild[]>('/inventory/categories/top-level-counts'),
    enabled: activeTab === 'catalogue',
  });
  const topLevelCounts: CategoryChild[] = (topLevelCountsData as unknown as { data: CategoryChild[] })?.data ?? [];

  const { data: pendingCountData } = useQuery({
    queryKey: ['catalogue-pending-count'],
    queryFn: () => api.get<{ count: number }>('/catalogue/pending-count'),
    enabled: activeTab === 'catalogue',
    refetchInterval: 10_000,
  });
  const pendingCount: number = (pendingCountData as unknown as { count: number })?.count ?? 0;

  // Show toast + invalidate pending list when new items arrive from the extension
  useEffect(() => {
    if (prevPendingCount.current !== null && pendingCount > prevPendingCount.current) {
      // Invalidate cached pending list so panel always shows fresh data when opened
      queryClient.invalidateQueries({ queryKey: ['catalogue-pending'] });
      setExtensionToast(true);
      setTimeout(() => setExtensionToast(false), 6000);
    }
    prevPendingCount.current = pendingCount;
  }, [pendingCount, queryClient]);

  // Respond to URL param changes from SPA navigation (BOM button, global search)
  // Runs when already on the Inventory page and URL params change (no remount)
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const tabParam = sp.get('tab');
    const searchParam = sp.get('search');
    if (tabParam === 'catalogue') handleSetActiveTab('catalogue');
    else if (tabParam === 'facility-supplies' || tabParam === 'supplies') handleSetActiveTab('supplies');
    // Old-style extinguisher deep link -- extinguishers now lives under Facility Supplies
    // (sub=extinguishers, read directly by FacilitySuppliesTab's own activeType init).
    else if (tabParam === 'extinguishers') handleSetActiveTab('supplies');
    else if (tabParam === 'facility-keys') handleSetActiveTab('facility-keys');
    else if (tabParam === 'logbook-templates') handleSetActiveTab('logbook-templates');
    if (searchParam !== null) { setSearch(searchParam); setPage(1); }
  }, [location.search]);

  const categories = categoriesData?.data ?? [];

  // Item 3c: a cross-folder search result needs its folder/path visible so it's clearly
  // identifiable once the folder scope is lifted (see the inventory-items query above). Walks the
  // same parent_id chain categoryPath already walks for the breadcrumb header, just parameterized
  // to an arbitrary category id instead of always activeCategoryId, so it can run per search
  // result card rather than once for the currently-open folder.
  function getCategoryFullPath(categoryId: number | null | undefined): string {
    if (categoryId == null) return '';
    const parts: string[] = [];
    let cur = categories.find((c) => c.id === categoryId);
    while (cur) {
      parts.unshift(cur.name);
      cur = cur.parent_id !== null ? categories.find((c) => c.id === cur!.parent_id) : undefined;
    }
    return parts.join(' › ');
  }

  // Sequential color assignment for the sidebar tree -- moved into the shared useCategoryColorMap
  // hook ("Categories B", 2026-10-11) so CategoryManager's own tree/editor/popover use the exact
  // same positional map instead of a second copy. Same algorithm, same output: walks the tree in
  // the exact depth-first order CatSidebarItem actually renders it (top-level siblings in their
  // existing order, each one's children before its next sibling) so adjacent rows never repeat
  // purely by DB-id coincidence (Chris-flagged: e.g. Equipment/Kitchen both landing on pink via
  // the old getCategoryColor id%N hash). Deliberately independent of expandedCatIds -- a
  // category's color stays stable whether or not its parent happens to be expanded.
  const categoryColorMap = useCategoryColorMap(categories);

  // Accordion: only one top-level category open at a time; subcategory siblings also collapse
  function handleCatExpand(catId: number, parentId: number | null) {
    // Recursively remove a cat and all its descendants from the expanded set
    function collapseTree(id: number, target: Set<number>) {
      target.delete(id);
      categories.filter((c) => c.parent_id === id).forEach((c) => collapseTree(c.id, target));
    }

    setExpandedCatIds((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) {
        // Toggle off: collapse this category and its entire subtree
        collapseTree(catId, next);
      } else {
        if (parentId === null) {
          // Top-level expand: collapse ALL other top-level cats and their subtrees
          categories
            .filter((c) => c.parent_id === null && c.id !== catId)
            .forEach((c) => collapseTree(c.id, next));
        } else {
          // Subcategory expand: collapse only same-level siblings
          categories
            .filter((c) => c.parent_id === parentId && c.id !== catId)
            .forEach((c) => collapseTree(c.id, next));
        }
        next.add(catId);
      }
      return next;
    });
  }

  // Select a category by name: sets active + expands it (no toggle-off) + collapses siblings
  function handleCatSelect(catId: number) {
    setActiveCategoryId(catId);
    setExpandedCatIds((prev) => {
      if (prev.has(catId)) return prev; // Already expanded — nothing to change
      const next = new Set(prev);
      function collapseTree(id: number) {
        next.delete(id);
        categories.filter((c) => c.parent_id === id).forEach((c) => collapseTree(c.id));
      }
      const parentId = categories.find((c) => c.id === catId)?.parent_id ?? null;
      if (parentId === null) {
        categories.filter((c) => c.parent_id === null && c.id !== catId).forEach((c) => collapseTree(c.id));
      } else {
        categories.filter((c) => c.parent_id === parentId && c.id !== catId).forEach((c) => collapseTree(c.id));
      }
      next.add(catId);
      return next;
    });
  }

  // Auto-expand ancestors when activeCategoryId changes
  useEffect(() => {
    if (activeCategoryId === null) return;
    const toExpand = new Set<number>();
    let current = categories.find((c) => c.id === activeCategoryId);
    while (current && current.parent_id !== null) {
      toExpand.add(current.parent_id);
      current = categories.find((c) => c.id === current!.parent_id);
    }
    if (toExpand.size > 0) {
      setExpandedCatIds((prev) => new Set([...prev, ...toExpand]));
    }
  }, [activeCategoryId, categories.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Breadcrumb path: array from root ancestor to current selected category
  const categoryPath: Category[] = [];
  if (activeCategoryId !== null) {
    let cur = categories.find((c) => c.id === activeCategoryId);
    while (cur) {
      categoryPath.unshift(cur);
      if (cur.parent_id === null) break;
      cur = categories.find((c) => c.id === cur!.parent_id);
    }
  }
  const currentCatParentId = activeCategoryId !== null
    ? (categories.find((c) => c.id === activeCategoryId)?.parent_id ?? null)
    : null;

  // Item 3c (functional bug, not cosmetic): search used to always pass the current folder's
  // categoryId through, so it only ever matched items in whatever folder you happened to be
  // standing in -- confirmed by reading this exact call, `categoryId: activeCategoryId ?? undefined`
  // was unconditional. Now search omits BOTH the folder scope and the status filter while a term
  // is actually typed, so it searches the entire catalogue regardless of folder OR status --
  // the latter also directly closes item 2's "cannot be found via search" gap for a Discontinued
  // item (status defaults to 'Active' below, which search used to inherit same as folder scope).
  // Browsing with an empty search box is completely unchanged -- still scoped to the current
  // folder and the status dropdown, exactly as before.
  const { data, isLoading } = useQuery({
    queryKey: ['inventory-items', search, status, activeCategoryId, page],
    queryFn: () => api.get<InventoryItem[]>('/inventory/items', {
      search: search || undefined,
      status: search ? undefined : (status || undefined),
      categoryId: search ? undefined : (activeCategoryId ?? undefined),
      page,
      limit: 40,
    }),
    enabled: activeTab === 'catalogue',
  });

  const items = (data as unknown as { data: InventoryItem[]; total: number })?.data ?? [];
  const total = (data as unknown as { total?: number })?.total ?? 0;

  // Restore catalog scroll position after items first load (handles both cache hits and fresh loads)
  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (!data) return;
    const pos = sessionStorage.getItem('catalog_scroll_position');
    if (pos) {
      scrollRestoredRef.current = true;
      requestAnimationFrame(() => window.scrollTo(0, Number(pos)));
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to item detail page from URL param (global search navigation)
  useEffect(() => {
    if (!itemId) return;
    if (items.length === 0) return;
    if (openedItemIdRef.current === itemId) return;
    const found = items.find(i => String(i.id) === String(itemId));
    if (found) {
      openedItemIdRef.current = itemId;
      navigate(`/inventory/item/${found.id}`);
    }
  }, [itemId, items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open item edit panel from location.state (global search navigation via navigate with state).
  // stateItemHandledRef used to be a plain boolean latch -- true forever after the FIRST open, so
  // a SECOND catalogue search-result click (a different item) while already on Inventory did
  // nothing, since the latch was already tripped. Comparing against openItemId+_t (a unique
  // per-click nonce, searchNav.ts) instead of a boolean means both a genuinely different item AND
  // the SAME item clicked again later are recognized as fresh and open -- id alone wouldn't catch
  // the latter, since openItemId would be identical both times.
  useEffect(() => {
    if (!stateOpenItemId) return;
    if (items.length === 0) return;
    const key = `${stateOpenItemId}:${stateOpenItemNonce}`;
    if (stateItemHandledRef.current === key) return;
    const found = items.find(i => i.id === stateOpenItemId);
    if (found) {
      stateItemHandledRef.current = key;
      setEditItem(found);
      setShowAddModal(true);
    }
  }, [stateOpenItemId, stateOpenItemNonce, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: childCatsData } = useQuery({
    queryKey: ['category-children', activeCategoryId],
    queryFn: () => api.get<CategoryChild[]>(`/inventory/categories/${activeCategoryId}/children`),
    enabled: activeTab === 'catalogue' && activeCategoryId !== null,
  });
  const childCategories: CategoryChild[] = (childCatsData as unknown as { data: CategoryChild[] })?.data ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/inventory/items/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['category-children'] });
      queryClient.invalidateQueries({ queryKey: ['category-top-level-counts'] });
      setConfirmDeleteId(null);
    },
  });

  function handleSaved(msg?: string) {
    queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
    queryClient.invalidateQueries({ queryKey: ['category-children'] });
    queryClient.invalidateQueries({ queryKey: ['category-top-level-counts'] });
    if (msg) {
      showSaveToast(msg);
    }
  }

  function handleViewItem(id: number) {
    const found = items.find((i) => i.id === id);
    if (found) setEditItem(found);
  }

  // ── Multi-select ──
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkOrderModal, setShowBulkOrderModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTargetId, setShareTargetId] = useState<number | null>(null);

  const { data: sisterFacilitiesData } = useQuery({
    queryKey: ['sister-facilities'],
    queryFn: () => api.get<Array<{ id: number; name: string }>>('/catalogue/sister-facilities'),
    enabled: showShareModal,
  });
  const sisterFacilities = (sisterFacilitiesData as unknown as { data: Array<{ id: number; name: string }> })?.data ?? [];

  function toggleSelect(id: number) {
    setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function clearSelection() { setSelectedIds(new Set()); }

  // Clear selection when tab or category changes
  useEffect(() => { clearSelection(); }, [activeTab, activeCategoryId]);

  async function handleBulkDelete() {
    const ids = Array.from(selectedIds);
    if (!confirm(`Delete ${ids.length} item${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      await api.post('/inventory/items/bulk-delete', { ids });
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['category-children'] });
      queryClient.invalidateQueries({ queryKey: ['category-top-level-counts'] });
      clearSelection();
    } catch {
      alert('Bulk delete failed. Please try again.');
    }
  }

  async function handleBulkShare() {
    if (!shareTargetId) return;
    const ids = Array.from(selectedIds);
    try {
      await api.post('/catalogue/share', { ids, targetFacilityId: shareTargetId });
      setShowShareModal(false);
      setShareTargetId(null);
      clearSelection();
    } catch {
      alert('Share failed. Please try again.');
    }
  }

  // Restore scroll position before the browser paints (avoids visible jump)
  useLayoutEffect(() => {
    if (_savedScrollY > 0) window.scrollTo(0, _savedScrollY);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { id: 'catalogue', label: 'Catalog', icon: <LayoutGrid size={15} /> },
  ] as const;
  // Consolidated under the "Facility" hover-flyout instead of 4 separate always-visible main
  // tabs — Facility Supplies, Facility Keys, Facility Map, and Logbook Templates all move here.
  const FACILITY_MENU_ITEMS = [
    { id: 'supplies', label: 'Facility Supplies', icon: <Package size={15} /> },
    { id: 'facility-keys', label: 'Facility Keys', icon: <Key size={15} /> },
    { id: 'facility-map', label: 'Facility Map', icon: <Map size={15} /> },
    { id: 'logbook-templates', label: 'Logbook Templates', icon: <BookOpen size={15} /> },
  ] as const;
  const isFacilityTab = FACILITY_MENU_ITEMS.some(m => m.id === activeTab);
  // Sliding highlight for the main Catalog/Facility bar -- 2 slots: Catalog, and the Facility
  // flyout trigger (collapsed from its own 4 sub-items via isFacilityTab). Same JS-measured
  // technique as FacilityKeysTab's sub-tabs, needed here too since "Catalog" and "Facility ▾"
  // are different widths. The flyout's OWN dropdown list (FACILITY_MENU_ITEMS, the portaled panel
  // below) is a separate, transient open/close menu, not a persistent inline tab strip --
  // deliberately not given a sliding highlight; its trigger button is treated as one slot, same
  // as its existing isFacilityTab-driven active/inactive styling already treats it as one unit.
  const [mainTabHighlight, setMainTabHighlight] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  useLayoutEffect(() => {
    // facilityFlyoutRef (the wrapper div), not facilityTriggerRef (the button) -- the button's own
    // offsetParent is the wrapper (position:relative), not .pill-tab-container, since the wrapper
    // sits between them. The wrapper's box exactly matches the button's (no padding of its own).
    const el = isFacilityTab ? facilityFlyoutRef.current : catalogueTabBtnRef.current;
    if (el) setMainTabHighlight({ left: el.offsetLeft, width: el.offsetWidth });
  }, [isFacilityTab]);

  return (
    <div>
      {/* Save success toast */}
      <Toast toast={saveToast} />

      {/* Extension import toast */}
      {extensionToast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: 'var(--accent-primary)', color: '#fff',
          borderRadius: 10, padding: '12px 18px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: '0.9rem', fontWeight: 500,
          animation: 'fadeInUp 0.3s ease',
        }}>
          <span>📦</span>
          <span>New item from Chrome extension — Review pending imports</span>
          <button
            onClick={() => { setExtensionToast(false); setShowPendingPanel(true); }}
            style={{ marginLeft: 8, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.8rem' }}
          >
            View
          </button>
        </div>
      )}

      {/* Combined header + tabs row */}
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
        gap: isMobile ? 10 : 0,
        padding: '12px 20px',
        borderBottom: '0.5px solid var(--border-subtle)',
      }}>
        {/* Left: title + subtitle */}
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>Inventory</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 12, whiteSpace: 'nowrap' }}>Catalog &amp; facility supplies</span>
        </div>
        {/* Right: tabs — pill style. On mobile the header stacks into two full-width rows (title
            row, then tabs row) instead of squeezing both into one line — that squeeze was what
            wrapped the subtitle into a narrow 4-line stack and truncated "Facility Supplies" to
            "Facility Suppl...". With the tabs row given the full viewport width, overflowX:'auto'
            here lets .pill-tab's existing white-space:nowrap (buttonAnimations.css) scroll
            horizontally instead of truncating.
            WebkitOverflowScrolling:'touch' + touchAction:'pan-x' — a real iPhone screenshot
            showed the tab text still getting clipped mid-word instead of scrolling, i.e.
            overflow-x:auto was present but inert. Plain overflow-x:auto can fail to actually
            engage touch-scroll on iOS Safari inside a small pill-shaped flex strip like this one
            (a well-documented WebKit quirk, worse when the gesture is ambiguous against the
            page's own vertical scroll) — WebkitOverflowScrolling:'touch' forces native momentum
            scrolling, and touchAction:'pan-x' tells the browser up front that this element owns
            horizontal panning, so the swipe isn't swallowed by the page's vertical scroll before
            it reaches this container. */}
        <div className="pill-tab-container" style={{
          position: 'relative',
          // Desktop-only clip for the sliding highlight's edge-overshoot (belt-and-suspenders,
          // same as the other two sliding pill-tab rows) -- NOT applied on mobile, where this
          // exact overflowX:'auto'/WebkitOverflowScrolling/touchAction trio is a deliberate,
          // previously-debugged iOS Safari touch-scroll fix (see comment above) that a blanket
          // overflow:hidden would silently break.
          overflowX: isMobile ? 'auto' : 'hidden',
          overflowY: isMobile ? undefined : 'hidden',
          width: isMobile ? '100%' : undefined,
          WebkitOverflowScrolling: isMobile ? 'touch' : undefined,
          touchAction: isMobile ? 'pan-x' : undefined,
        }}>
          <div className="pill-tab-slide-highlight" style={{width:mainTabHighlight.width,transform:`translateX(${mainTabHighlight.left}px)`}} />
          {tabs.map((t) => (
            <button
              key={t.id}
              ref={catalogueTabBtnRef}
              className={`pill-tab pill-tab-slide-btn${activeTab === t.id ? ' active' : ''}`}
              style={{ position: 'relative', zIndex: 1 }}
              onClick={() => handleSetActiveTab(t.id as 'catalogue' | 'supplies' | 'facility-keys' | 'facility-map' | 'logbook-templates')}
            >
              {t.icon}{t.label}
            </button>
          ))}
          {/* "Facility" hover-flyout — consolidates Facility Supplies/Keys/Map/Logbook Templates
              under one nav slot instead of 4 separate always-visible pills. Hover opens it
              (desktop); click also toggles it (keyboard/touch, since hover doesn't exist there).
              No direct-click destination for the trigger itself — it only ever opens the flyout,
              per the confirmed plan (Facility Supplies is a flyout item, not a fallback click
              target). A short close-delay on mouseleave (not an instant close) lets the pointer
              travel from the trigger down into the flyout panel without it disappearing first. */}
          <div
            ref={facilityFlyoutRef}
            style={{ position: 'relative', zIndex: 1 }}
            onMouseEnter={() => { clearTimeout(facilityFlyoutCloseTimer.current); openFacilityFlyout(); }}
            onMouseLeave={() => { facilityFlyoutCloseTimer.current = setTimeout(() => setFacilityFlyoutOpen(false), 150); }}
          >
            <button
              ref={facilityTriggerRef}
              className={`pill-tab pill-tab-slide-btn${isFacilityTab ? ' active' : ''}`}
              onClick={() => (facilityFlyoutOpen ? setFacilityFlyoutOpen(false) : openFacilityFlyout())}
            >
              <Building2 size={15} />Facility
              <ChevronDown size={13} style={{ transform: facilityFlyoutOpen ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s ease', marginLeft: 2 }} />
            </button>
            {facilityFlyoutOpen && facilityFlyoutPos && ReactDOM.createPortal(
              // zIndex 9999 (was 400): this flyout's trigger lives in normal scrolling page
              // content, but on mobile (@media max-width:768px, TopBar.css) the header (.topbar,
              // z-index:1001) and its search toolbar row (.mobile-toolbar-row, a literal
              // text-field strip, z-index:999) are BOTH position:fixed at the viewport top,
              // unaffected by scroll. Bumping z-index alone (the original attempt) never fixed
              // backlog #60 because the real cause was structural, not stacking order: this panel
              // was still a plain DOM descendant of .page-content (overflow-y:auto), and mobile
              // Safari/WebView has a well-known non-spec quirk where position:fixed descendants of
              // a scrolling container don't reliably escape to the true viewport once the page has
              // scrolled — no z-index value fixes that. Portaling to document.body (matching every
              // other overlay in this file, see the top-of-file "DESIGN_AUDIT Phase 11B" comment)
              // removes it from that scrolling ancestor entirely.
              <>
                {/* Invisible full-screen backdrop for click-outside-to-close — same pattern as the
                    category-select dropdown (line ~2316) uses for its own portaled menu, since a
                    document-level mousedown + ref.contains() check doesn't work once the panel is
                    no longer a real DOM descendant of the trigger's wrapper. */}
                <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setFacilityFlyoutOpen(false)} />
                <div style={{ position: 'fixed', top: facilityFlyoutPos.top, left: facilityFlyoutPos.left, width: FACILITY_FLYOUT_WIDTH, zIndex: 9999, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', padding: 4 }}>
                  {FACILITY_MENU_ITEMS.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { handleSetActiveTab(m.id as 'supplies' | 'facility-keys' | 'facility-map' | 'logbook-templates'); setFacilityFlyoutOpen(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
                        background: activeTab === m.id ? 'var(--bg-hover)' : 'transparent', border: 'none',
                        borderRadius: 'var(--radius-sm)', color: activeTab === m.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        cursor: 'pointer', fontSize: '0.8125rem', textAlign: 'left',
                      }}
                      onMouseEnter={(e) => { if (activeTab !== m.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { if (activeTab !== m.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                    >
                      {m.icon}{m.label}
                    </button>
                  ))}
                </div>
              </>,
              document.body
            )}
          </div>
        </div>
      </div>

      {/* Facility Supplies tab */}
      {activeTab === 'supplies' && (
        <FacilitySuppliesTab
          categories={categories}
          navigate={navigate}
          initialSupplyId={initialSupplyId}
          subParam={subParam}
          extinguisherId={initialExtinguisherId}
          onSupplyIdHandled={() => clearDeepLinkParam('supplyId')}
          onExtinguisherIdHandled={() => clearDeepLinkParam('extinguisherId')}
          onSubHandled={() => clearDeepLinkParam('sub')}
        />
      )}

      {/* Facility Keys tab */}
      {activeTab === 'facility-keys' && <FacilityKeysTab />}

      {/* Facility Map tab */}
      {activeTab === 'facility-map' && (
        <div style={{ padding: '16px 20px' }}>
          <FacilityMap />
        </div>
      )}

      {/* Logbook Templates tab */}
      {activeTab === 'logbook-templates' && <LogbookTemplates />}

      {/* Catalogue tab */}
      {activeTab === 'catalogue' && (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', background: 'transparent', paddingTop: 12 }}>

          {/* Category sidebar */}
          <div style={{
            width: categoryExpanded ? 220 : 36,
            flexShrink: 0, transition: 'width 200ms ease',
            overflow: 'hidden',
            display: isMobile ? 'none' : undefined,
          }}>
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-subtle)',
              overflow: 'hidden',
            }}>
              <div
                style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: categoryExpanded ? 'space-between' : 'center',
                  padding: categoryExpanded ? '10px 12px' : '10px 0',
                  borderBottom: '1px solid var(--border-subtle)',
                  cursor: 'pointer', userSelect: 'none',
                }}
                onClick={() => setCategoryExpanded((e) => { const next = !e; localStorage.setItem('inventory_categories_collapsed', String(!next)); return next; })}
              >
                {categoryExpanded && (
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Categories
                  </span>
                )}
                <ChevronRight
                  size={14}
                  color="var(--text-muted)"
                  style={{ transform: categoryExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }}
                />
              </div>

              <div style={{ padding: categoryExpanded ? '6px 6px' : '6px 0' }}>
                {categories.filter((c) => !c.parent_id).map((cat) => (
                  <CatSidebarItem
                    key={cat.id}
                    cat={cat}
                    sidebarExpanded={categoryExpanded}
                    activeCategoryId={activeCategoryId}
                    allCategories={categories}
                    onSelect={handleCatSelect}
                    onManage={(catId) => { setCategoryManagerInitId(catId); setShowCategoryManager(true); }}
                    onExpandToggle={handleCatExpand}
                    expandedCatIds={expandedCatIds}
                    colorMap={categoryColorMap}
                    depth={0}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Mobile category toggle (above toolbar on mobile) */}
            {isMobile && (
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <button
                  onClick={() => setMobileCatOpen((o) => !o)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                    padding: '8px 12px',
                    background: activeCategoryId ? 'rgba(59,130,246,0.1)' : 'var(--bg-elevated)',
                    border: `1px solid ${activeCategoryId ? 'var(--accent-primary)' : 'var(--border-default)'}`,
                    borderRadius: 'var(--radius-md)', cursor: 'pointer',
                    color: activeCategoryId ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    fontSize: '0.875rem', fontWeight: 500,
                  }}
                >
                  <FolderOpen size={14} />
                  <span style={{ flex: 1, textAlign: 'left' }}>
                    {activeCategoryId
                      ? (categories.find((c) => c.id === activeCategoryId)?.name ?? 'Category')
                      : 'All Categories'}
                  </span>
                  <ChevronDown size={14} style={{ transform: mobileCatOpen ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
                </button>
                {mobileCatOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={() => setMobileCatOpen(false)} />
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 51,
                      marginTop: 4, background: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      maxHeight: 260, overflowY: 'auto',
                    }}>
                      <button
                        onClick={() => { setActiveCategoryId(null); setMobileCatOpen(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                          padding: '10px 14px',
                          background: activeCategoryId === null ? 'rgba(59,130,246,0.12)' : 'none',
                          border: 'none', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                          color: activeCategoryId === null ? 'var(--accent-primary)' : 'var(--text-secondary)',
                          fontSize: '0.875rem', fontWeight: activeCategoryId === null ? 600 : 400,
                          textAlign: 'left',
                        }}
                      >
                        <Package size={14} /> All Items
                      </button>
                      {categories.filter((c) => !c.parent_id).map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => { setActiveCategoryId(cat.id); setMobileCatOpen(false); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                            padding: '10px 14px',
                            background: activeCategoryId === cat.id ? 'rgba(59,130,246,0.12)' : 'none',
                            border: 'none', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                            color: activeCategoryId === cat.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                            fontSize: '0.875rem', fontWeight: activeCategoryId === cat.id ? 600 : 400,
                            textAlign: 'left',
                          }}
                        >
                          <CategoryIcon iconName={cat.icon_path} size={14} /> {cat.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Catalogue toolbar — unified single-row.
                Item 3a: search input restyled onto the shared `.search-bar` convention
                (DESIGN_SYSTEM.md §24 -- pill border-radius 999px, 280px→360px on focus-within,
                same class TopBar.tsx/Orders.tsx already use) instead of a one-off 6px-radius box
                with an absolutely-positioned icon.
                Item 3b: `search-input-wrapper` + `btn-icon` wires up the existing
                `.search-input-wrapper:focus-within .btn-icon` wiggle (BUTTON_SYSTEM.md's "Other
                single-purpose animation classes") -- this page had it on zero buttons before.
                Categories/Pending Imports get the same combo1 + spinIconOut hover treatment
                Vendors/Orders already migrated to, since they're the other buttons in this exact
                toolbar -- not a sweep of the rest of this file's other tabs. */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '8px 16px', background: 'transparent', borderBottom: '0.5px solid var(--border-subtle)', marginBottom: 16 }}>
              <div className="search-bar search-input-wrapper" style={{ flex: isMobile ? '1 1 100%' : '1 1 200px', maxWidth: isMobile ? '100%' : 320, height: 30, boxSizing: 'border-box' }}>
                <Search size={14} color="var(--text-muted)" className="btn-icon" />
                <input
                  type="text"
                  placeholder="Search name, part#, barcode… (entire catalog)"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.stopPropagation(); setSearch(''); setPage(1); } }}
                />
                {/* Item C: clear button -- reuses TopBar.css's `.search-clear` class
                    (already loaded app-wide since AppLayout always mounts TopBar), same
                    affordance as the global search's own clear button. Right-aligned inside
                    the pill via the icon on the left + input flex-fill, so it never collides
                    with the Search icon. Only rendered when there's a term to clear, so it
                    never sits in the way of the `.search-input-wrapper:focus-within .btn-icon`
                    wiggle on the Search icon itself (that selector only targets the icon
                    element, untouched here). */}
                {search && (
                  <button
                    type="button"
                    className="search-clear"
                    aria-label="Clear search"
                    onClick={() => { setSearch(''); setPage(1); }}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                style={{ height: 30, width: 120, background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, padding: '0 8px', outline: 'none', cursor: 'pointer', flexShrink: 0 }}
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Discontinued">Discontinued</option>
              </select>
              <button className="btn btn-ghost combo1" style={{ height: 30, flexShrink: 0 }} onMouseLeave={spinIconOut} onClick={() => setShowCategoryManager(true)}>
                <FolderOpen size={14} className="btn-icon" /> Categories
              </button>
              <button
                className="btn btn-ghost combo1"
                style={{ height: 30, flexShrink: 0 }}
                onMouseLeave={spinIconOut}
                onClick={() => setShowPendingPanel(true)}
              >
                <Clock size={14} className="btn-icon" /> Pending Imports
                {pendingCount > 0 && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, marginLeft: 8,
                    fontSize: 11, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                    background: 'var(--accent-danger)', color: '#fff',
                    pointerEvents: 'none',
                  }}>
                    {pendingCount > 9 ? '9+' : pendingCount}
                  </span>
                )}
              </button>
            </div>

            {/* Category back button + breadcrumb */}
            {activeCategoryId !== null && (
              <div style={{ padding: '4px 16px 0' }}>
                <button
                  onClick={() => setActiveCategoryId(currentCatParentId)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', fontSize: 13, border: '1px solid var(--border-default)', borderRadius: 6, background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', marginBottom: 12 }}
                >
                  ← Back
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
                  <span
                    onClick={() => setActiveCategoryId(null)}
                    style={{ cursor: 'pointer', transition: 'color 0.15s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
                  >Catalog</span>
                  {categoryPath.map((cat, i) => (
                    <Fragment key={cat.id}>
                      <span style={{ color: 'rgba(255,255,255,0.2)' }}>›</span>
                      {i < categoryPath.length - 1 ? (
                        <span
                          onClick={() => setActiveCategoryId(cat.id)}
                          style={{ cursor: 'pointer', transition: 'color 0.15s' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
                        >{cat.name}</span>
                      ) : (
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{cat.name}</span>
                      )}
                    </Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* Items grid */}
            {activeCategoryId === null && !search ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                {topLevelCounts.map((cat) => (
                    <SubfolderCard
                      key={cat.id}
                      cat={cat}
                      onClick={() => handleCatSelect(cat.id)}
                    />
                  ))}
              </div>
            ) : isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                <div className="loading-spinner" />
              </div>
            ) : childCategories.length === 0 && items.length === 0 ? (
              <div className="empty-state" style={{ padding: '48px 20px' }}> {/* DS: EMPTY_STATE padding 48px 20px */}
                <Package size={36} style={{ opacity: 0.25, marginBottom: 14 }} /> {/* DS: EMPTY_STATE icon size 36 opacity 0.25 */}
                <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>No items found</h3> {/* DS: EMPTY_STATE title font-size 16 weight 600 */}
                <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 12 }}> {/* DS: EMPTY_STATE subtitle font-size 13 text-muted */}
                  Try adjusting your search or category filter.
                </p>
                <button className="btn btn-primary" onClick={() => { setEditItem(null); setShowAddModal(true); }}>
                  <Plus size={16} /> Add Item
                </button>
              </div>
            ) : (
              <>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(160px, 1fr))', // DS: ITEMS_GRID minmax 160px
                  gap: isMobile ? 10 : 12, // DS: ITEMS_GRID gap 12
                  marginBottom: 16,
                }}>
                  {/* Subcategory folder cards first */}
                  {childCategories.map((cat) => (
                    <SubfolderCard
                      key={`cat-${cat.id}`}
                      cat={cat}
                      onClick={() => setActiveCategoryId(cat.id)}
                    />
                  ))}
                  {/* Direct items in this category */}
                  {items.map((item) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      onView={() => {
                        sessionStorage.setItem(INVENTORY_STATE_KEY, JSON.stringify({
                          tab: activeTab,
                          categoryId: activeCategoryId,
                          query: search,
                          scrollY: window.scrollY,
                        }));
                        navigate(`/inventory/item/${item.id}`);
                      }}
                      onEdit={() => { setEditItem(item); setShowAddModal(true); }}
                      onOrder={() => setOrderItem(item)}
                      onDelete={() => setConfirmDeleteId(item.id)}
                      isSelected={selectedIds.has(item.id)}
                      onSelect={() => toggleSelect(item.id)}
                      categoryPath={search ? getCategoryFullPath(item.category_id) : undefined}
                      categoryColor={resolveCategoryColor(categories.find((c) => c.id === item.category_id), categoryColorMap)}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {total > 40 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                    <span style={{ color: 'var(--text-muted)', padding: '5px 8px', fontSize: '0.875rem' }}>
                      Page {page} of {Math.ceil(total / 40)}
                    </span>
                    <button className="btn btn-ghost btn-sm" disabled={page * 40 >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'catalogue' && (
        <FloatingActionButton
          icon={<Plus size={20} className="btn-icon" />}
          onClick={() => { setEditItem(null); setShowAddModal(true); }}
          title="Add item"
          ariaLabel="Add item"
        />
      )}

      {/* Category Manager */}
      {showCategoryManager && (
        <CategoryManager
          categories={categories}
          onClose={() => { setShowCategoryManager(false); setCategoryManagerInitId(null); }}
          onChanged={() => {
            queryClient.invalidateQueries({ queryKey: ['inventory-categories'] });
            queryClient.invalidateQueries({ queryKey: ['category-children'] });
          }}
          initialCategoryId={categoryManagerInitId}
        />
      )}

      {/* Add/Edit Item Modal */}
      {showAddModal && (
        <ItemModal
          initial={editItem ?? (activeCategoryId ? { category_id: activeCategoryId } : {})}
          categories={categories}
          onClose={() => { setShowAddModal(false); setEditItem(null); }}
          onSaved={handleSaved}
          onViewItem={handleViewItem}
        />
      )}

      {/* Add to Order modal */}
      {orderItem && (
        <AddToOrderModal key={orderItem.id} item={orderItem} onClose={() => setOrderItem(null)} />
      )}

      {/* Pending Imports Panel */}
      {showPendingPanel && (
        <PendingImportsPanel
          onClose={() => setShowPendingPanel(false)}
          onApproved={() => {
            queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
            queryClient.invalidateQueries({ queryKey: ['category-children'] });
            queryClient.invalidateQueries({ queryKey: ['category-top-level-counts'] });
          }}
          categories={categories}
        />
      )}

      {/* Bulk selection toolbar */}
      {selectedIds.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
          borderRadius: 12, padding: '12px 20px',
          display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 40,
        }}>
          <span style={{ color: 'var(--accent-primary)', fontWeight: 600, fontSize: 14, paddingRight: 8, borderRight: '1px solid var(--border-subtle)' }}> {/* DS: bulk bar count */}
            {selectedIds.size} selected
          </span>
          <button
            onClick={() => selectedIds.size === items.length ? clearSelection() : setSelectedIds(new Set(items.map((i) => i.id)))}
            className="btn btn-ghost btn-sm"
          >
            {selectedIds.size === items.length ? 'Deselect All' : 'Select All'}
          </button>
          <button
            onClick={() => { setShowBulkOrderModal(true); }}
            className="btn btn-ghost btn-sm" // DS: bulk order button → design system class
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <ShoppingCart size={14} /> Add to Order
          </button>
          <button
            onClick={() => setShowShareModal(true)}
            className="btn btn-ghost btn-sm" // DS: bulk share button → design system class
          >
            🔗 Share to Facility
          </button>
          <button
            onClick={handleBulkDelete}
            className="btn btn-ghost btn-sm" // DS: bulk delete button → design system class
            style={{ color: 'var(--accent-danger)' }}
          >
            <Trash2 size={14} /> Delete
          </button>
          <button onClick={clearSelection} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20, padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Bulk Order Modal — shared PO# + Order From for all selected items. Placeholder (not-yet-
          sourced) items are excluded here rather than at the checkbox itself, since that same
          checkbox also feeds bulk-delete/bulk-share, which placeholders should stay eligible for. */}
      {showBulkOrderModal && (() => {
        const selectedItems = items.filter((i) => selectedIds.has(i.id) && i.sourcing_status !== 'placeholder');
        const excludedPlaceholderCount = items.filter((i) => selectedIds.has(i.id) && i.sourcing_status === 'placeholder').length;
        let bulkOrderFrom = selectedItems[0]?.supplier || KNOWN_SUPPLIERS[0] || 'DSSI';
        let bulkPoNumber = '';
        return (
          <div className="modal-overlay"
            onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
            onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setShowBulkOrderModal(false); mouseDownTarget.current = null; }}
          >
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
              <div className="modal-header">
                <h3 className="modal-title"><ShoppingCart size={16} style={{ marginRight: 6 }} />Bulk Order ({selectedItems.length} items)</h3>
                <button type="button" className="modal-close" onClick={() => setShowBulkOrderModal(false)}><X size={18} /></button>
              </div>
              {excludedPlaceholderCount > 0 && (
                <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(107,114,128,0.12)', border: '1px solid rgba(107,114,128,0.3)', color: '#9ca3af', fontSize: 13 }}>
                  {excludedPlaceholderCount} selected item{excludedPlaceholderCount > 1 ? 's' : ''} excluded — not sourced yet.
                </div>
              )}
              {selectedItems.length === 0 ? (
                <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  Nothing to order — every selected item is a placeholder.
                </div>
              ) : (
                <BulkOrderForm
                  items={selectedItems}
                  onClose={() => setShowBulkOrderModal(false)}
                  onDone={() => { setShowBulkOrderModal(false); clearSelection(); queryClient.invalidateQueries({ queryKey: ['orders'] }); }}
                  defaultOrderFrom={bulkOrderFrom}
                  defaultPoNumber={bulkPoNumber}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* Share to Facility Modal */}
      {showShareModal && (
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setShowShareModal(false); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3 className="modal-title">Share {selectedIds.size} item{selectedIds.size > 1 ? 's' : ''} to:</h3>
              <button type="button" className="modal-close" onClick={() => setShowShareModal(false)}><X size={18} /></button>
            </div>
            {sisterFacilities.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No other facilities in your organization.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {sisterFacilities.map((f) => (
                  <div
                    key={f.id}
                    onClick={() => setShareTargetId(f.id)}
                    style={{ padding: 12, borderRadius: 8, border: `2px solid ${shareTargetId === f.id ? 'var(--accent-primary)' : 'var(--border-subtle)'}`, cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    🏢 {f.name}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setShowShareModal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!shareTargetId} onClick={handleBulkShare}>
                Share Items →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDeleteId !== null && (
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setConfirmDeleteId(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Item</h3>
              <button className="modal-close" onClick={() => setConfirmDeleteId(null)}><X size={18} /></button>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Permanently delete this item? This cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={() => deleteMut.mutate(confirmDeleteId!)}
                disabled={deleteMut.isPending}
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
